import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { useGlobalFilters } from '../contexts/FilterContext';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Chip,
  Button,
  Link,
  Alert,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  TextField,
  Tooltip as MuiTooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fab,
  Tabs,
  Tab,
  Snackbar,
  Checkbox,
  FormControlLabel,
  Switch,
  Popover,
} from '@mui/material';
import { ApplicationCertification } from './ApplicationCertification';
import { ChangeOnboarding } from './ChangeOnboarding';
import { JiraActions } from './JiraActions';
import PostmortemTrends from './PostmortemTrends';
import DecommissionTrends from './DecommissionTrends';
import PostmortemActionStatus from './PostmortemActionStatus';
import { PORTFOLIO_HEALTH, INCIDENTS, RELIABILITY_FRAMEWORK, OPERATIONAL_EXCELLENCE, POSTMORTEM, SPRINGPM, API_BASE_URL, DCO } from '../config/api';
import { authFetch } from '../services/authFetch';
import {
  Dashboard as DashboardIcon,
  SmartToy,
  TrendingUp,
  TrendingDown,
  Warning,
  CheckCircle,
  Error as ErrorIcon,
  Timeline,
  ExpandMore,
  Search,
  InfoOutlined,
  GetApp,
  PictureAsPdf as PictureAsPdfIcon,
  KeyboardArrowUp,
  FileDownload,
  ArrowBack,
  Close,
  Settings,
  FilterAlt,
  FilterAltOutlined,
  Edit as EditIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, AreaChart, Area } from 'recharts';
import { PDFExportService } from '../services/pdfExportService';
import ReactMarkdown from 'react-markdown';
import { red } from '@mui/material/colors';
import { apiClient } from '../apiClient';

interface PortfolioApp {
  appId: string;
  platformPortfolio: string;
  platform: string;
  application: string;
  appType: string;
  fullStackMonitoring: number;
  signalToNoise: string;
  calculatedSnr: number | null;
  snrScope: string | null;
  snrAlertCount: number | null;
  snrLastUpdated: string | null;
  snrLink: string | null;
  hedwigAlertAnalysisLink: string;
  incidentViaAlerts: string;
  p1p2: number;
  p3p4mi: number;
  incidentsCausedByChange: string;
  outageDuration: number;
  sloAvailabilityMtd: number;
  sloObjective: number;
  ebInMinutes: number;
  // Golden Signal fields
  goldenSignalScore: number | null;
  goldenSignalTotalIncidents: number | null;
  goldenSignalCapturePercentage: number | null;
  // Hedwig Team fields
  hasHedwigTeam: boolean;
  hedwigTeams: string[];
  hedwigTeamIds: string[];
  hedwigServices: Array<{
    serviceName: string;
    onCallUsers: string[];
    userNames: string[];
  }>;
  // On Call Rota fields
  onCallRotaStatus: string;
  onCallUserNames: string | null;
  uniqueOnCallUsers: number | null;
  // NFR field
  nfr: string;
}

interface AIInsight {
  type: 'critical' | 'warning' | 'info' | 'success';
  title: string;
  description: string;
  action?: string;
  confidence: number;
  causedByChangePercentage?: number;
}

// API Response interface
interface PortfolioApiResponse {
  alerting: string;
  app_group: string;
  app_id: string;
  app_type: string;
  application: string;
  chaos_app_infra_tested_date: string | null;
  chaos_onboarded: string | null;
  crawl_completed_for_db: string;
  crawl_completed_for_infra: string;
  crawl_completed_for_mw: string;
  crawl_completed_for_network: string;
  created_at: string;
  cyber_risk_tolerated_mitigated: string;
  deployment_methods: string | null;
  descoped: string;
  em_sem: string;
  eol_components_visibility_roadmap: string;
  failover_type: string;
  golden_signals: string | null;
  hedwig_rota: string | null;
  hosting_scope: string;
  id: number;
  load_test_completed_date: string | null;
  monitoring: string;
  multi_site_dc_multi_zone_cloud_setup: string;
  nfr: string;
  nfr_test_completed: string | null;
  pe_onboarded: string | null;
  platform: string;
  platform_portfolio: string;
  recovery_sequence: string | null;
  reference_id: string;
  reliability_patterns: string | null;
  runbooks: string;
  signal_to_noise_ratio: string | null;
  calculated_snr: number | null;
  snr_scope: string | null;
  snr_alert_count: number | null;
  snr_last_updated: string | null;
  snr_link: string | null;
  slis: string | null;
  slos: string | null;
  sre_point_of_contact: string;
  status_page_integration: string | null;
  support_readiness: string | null;
  updated_at: string;
  vendor_matrix: string;
}

// SLO API Response interface
interface SLOApiResponse {
  app_id: string;
  app_name: string;
  availability?: string | number;
  average_availability: string | number;
  error_budget: string | number;
  error_budget_minutes: string | number;
  platform_name: string;
  slo_objective: string | number;
}

// NFR Test Insight API Response interface
interface NFRTestInsight {
  week: number;
  portfolioShortName: string;
  platformName: string;
  applicationName: string;
  macId: string[];
  region: string[];
  testType: string[];
  nfrStatus: string[];
  status: string[];
  lastTestExecutionDate: string;
  testDurationMins: number;
  testCount: number;
}

// P1/P2 Incidents API Response interface
interface IncidentApiResponse {
  assignment_group_display_value: string;
  category_display_value: string;
  caused_by_display_value: string;
  child_incidents: string;
  close_code: string;
  cmdb_ci_display_value: string;
  cmdb_ci_u_application_id: string;
  cmdb_ci_u_environment_display_value: string;
  cmdb_ci_u_environment_value: string;
  cmdb_ci_u_owning_business_unit_display_value: string;
  contact_type_display_value: string;
  created_at: string;
  id: number;
  impact: string;
  location_display_value: string;
  number: string;
  opened_by_display_value: string;
  outage_duration: string | null;
  outage_number: string;
  priority: string;
  resolved_at: string;
  short_description: string;
  state_display_value: string;
  subcategory_display_value: string;
  sys_created_on_display_value: string;
  sys_created_on_value: string;
  u_client_name_display_value: string;
  u_owner_display_value: string;
  updated_at: string;
  urgency: string;
  platform_name?: string;
  portfolio_name?: string;
  caused_by_platform?: string;
  caused_by_portfolio?: string;
  caused_by_app?: string;
  opened_at?: string;
}

// MTTR Data API Response interface
interface MTTRData {
  mttr: string;
  mttd: string;
  number: string;
  priority: number;
}

// P3/P4 Incidents API Response interface (same structure as P1/P2)
interface P3P4IncidentApiResponse extends IncidentApiResponse {}

// AI Insights API Response interface
interface AIInsightsResponse {
  data: {
    analysis_scope: {
      app_ids_in_scope: string[];
      applications_in_scope: string[];
      platforms_in_scope: string[];
      portfolios_in_scope: string[];
    };
    health_assessment: {
      ai_generated: boolean;
      analysis_timestamp: string;
      key_concerns: string[];
      overall_health_score: number;
      p1_p2_health: string;
      p3_p4_health: string;
      portfolio_health_report: string;
    };
  };
}

// Decommissioning Data API Response interface
interface DecommissioningDataResponse {
  success: boolean;
  data: DecommissioningItem[];
  total_records: number;
  filters: {
    portfolio: string | null;
    platform: string | null;
  };
  timestamp: string;
}

interface StrategicIntent {
  id: string;
  intent: string;
}

interface Revision {
  values: RevisionValue[];
  modifiedBy: string;
  modifiedDate: string;
  revisionType: string;
}

interface RevisionValue {
  newValue: string;
  oldValue: string;
  fieldName: string;
}

interface DecommissioningItem {
  portfolio: string;
  platform: string;
  app_id: string;
  app_name: string;
  mvc_group?: string | null;
  decom_change_description: string | null;
  target_date_decom: string | null;
  decom_date: string | null;
  decom_status: number;
  owner_names: string;
  owners: string;
  status: string;
  decom_extension_reason: string | null;
  strategic_intent: StrategicIntent | null;
  backup_tested_last_12_months: string | null;
  failover_tested_date_last_12_months: string | null;
  total_cost: number | null;
  bia_status: boolean | null;
  application_labels: Array<{
    classification?: {
      color: string;
      id: string;
      name: string;
    };
    color: string;
    description: string;
    id: number;
    name: string;
  }> | null;
  revisions?: Revision[];
  tag?: {
    id: number;
    decomm_tag: string | null;
    remarks: string;
    comments: string | null;
    created_by: string;
    updated_by: string;
    created_at: string;
    updated_at: string;
  } | null;
}

// Application Certification Data API Response interface
interface ApplicationCertificationItem {
  name: string;
  u_application_id: string;
  'u_business_platform.value': string;
  owned_by_name: string;
  managed_by_name: string | null;
  u_engineering_manager_name: string | null;
  u_sr_engineering_manager_name: string | null;
  u_engineering_director_name: string | null;
  portfolio: string;
  platform: string;
  portfolio_id: string;
  platform_id: string;
  has_server_relation: boolean;
  has_app_relation: boolean;
  has_cloud: boolean;
  is_saas: boolean;
  mac_owners: string | string[];
  status: string;
  is_mvc: boolean;
  is_bca: boolean;
  has_repo: boolean;
  is_standalone: boolean;
  test_app: boolean;
  verified: string | null;
  verified_at: string | null;
  app_verified: string | null;
  app_verified_at: string | null;
  own_verified: string;
  own_verified_at: string;
  verified_owner: string | null;
  app_verified_owner: string | null;
  own_verified_owner: string;
  verified_expired: string | null;
  app_verified_expired: string | null;
  completeness_percentage: number;
  certification_percentage: number;
  id: number;
  overall_app_id_completeness_percentage: number;
  overall_app_id_certification_percentage: number;
}

interface ApplicationCertificationResponse {
  combined_data: ApplicationCertificationItem[];
}

// Change Onboarding Data API Response interface
interface ChangeOnboardingItem {
  application_id: string;
  application_name: string;
  platform: string;
  portfolio: string;
  app_owners: string | null;
  bca_status: boolean | null;
  mvc_status: boolean | null;
  onboarding_info: {
    onboarding_status: string;
    deployment_channel: string;
    change_process_type: string;
    last_change_date: string;
  } | null;
  owners_role_mac?: string;
  change_success_rate?: number | null;
}

interface ChangeOnboardingResponse {
  status: number;
  data: ChangeOnboardingItem[];
}

interface PortfolioHealthTrackerProps {
  graphData: any;
  accessToken: string | null;
}

// Hardcoded list of users allowed to manage DCO Load CSV (email addresses)
const DCO_ALLOWED_USERS: string[] = [
  'k.harish@maersk.com',
  'shilpitha.swarna@maersk.com',
  'nilesh.chakkar@maersk.com',
  'nitish.yn@maersk.com',
  'girish.kumsi@maersk.com',
  'mahesh.s.bhosale@maersk.com',
  'paramjeet.nagpal@maersk.com',
  'vaishali.parande@maersk.com',
];

const PortfolioHealthTracker: React.FC<PortfolioHealthTrackerProps> = ({ graphData, accessToken }) => {
  // Current signed-in user (Azure AD)
  const { instance, accounts } = useMsal();
  const currentAccount = accounts[0];
  const currentUserEmail = (currentAccount as any)?.username || (currentAccount as any)?.idTokenClaims?.preferred_username || '';

  const canManageDcoCsv = !!currentUserEmail && DCO_ALLOWED_USERS.some(
    u => u.toLowerCase() === currentUserEmail.toLowerCase()
  );

  // Use global filters from context
  const {
    selectedPortfolio,
    selectedPlatform,
    selectedTimeRange,
    customDateRange,
    customDateApplied,
    setSelectedPortfolio,
    setSelectedPlatform,
    setSelectedTimeRange,
    setCustomDateRange,
    setCustomDateApplied,
  } = useGlobalFilters();

  const [selectedApp, setSelectedApp] = useState<string | null>(null);
  const [selectedApplication, setSelectedApplication] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [apiData, setApiData] = useState<PortfolioApiResponse[]>([]);
  const [sloData, setSloData] = useState<SLOApiResponse[]>([]);
  const [nfrData, setNfrData] = useState<NFRTestInsight[]>([]);
  const [nfrLoading, setNfrLoading] = useState(false);
  const [nfrError, setNfrError] = useState<string | null>(null);
  const [incidentData, setIncidentData] = useState<IncidentApiResponse[]>([]);
  const [p1p2Loading, setP1P2Loading] = useState(false);
  const [p1p2Error, setP1P2Error] = useState<string | null>(null);
  const [p3p4IncidentData, setP3P4IncidentData] = useState<P3P4IncidentApiResponse[]>([]);
  const [p3p4Loading, setP3P4Loading] = useState(false);
  const [p3p4Fetched, setP3P4Fetched] = useState(false);
  const [p1p2LastWeekCount, setP1P2LastWeekCount] = useState<number>(0);
  const [p1p2PrevWeekCount, setP1P2PrevWeekCount] = useState<number>(0);
  const [p3p4LastWeekCount, setP3P4LastWeekCount] = useState<number>(0);
  const [p3p4PrevWeekCount, setP3P4PrevWeekCount] = useState<number>(0);
  const [outageLastWeekDuration, setOutageLastWeekDuration] = useState<number>(0);
  const [outagePrevWeekDuration, setOutagePrevWeekDuration] = useState<number>(0);
  // Month data loaded on-demand when user toggles to month view
  const [p1p2LastMonthCount, setP1P2LastMonthCount] = useState<number>(0);
  const [p1p2PrevMonthCount, setP1P2PrevMonthCount] = useState<number>(0);
  const [p3p4LastMonthCount, setP3P4LastMonthCount] = useState<number>(0);
  const [p3p4PrevMonthCount, setP3P4PrevMonthCount] = useState<number>(0);
  const [outageLastMonthDuration, setOutageLastMonthDuration] = useState<number>(0);
  const [outagePrevMonthDuration, setOutagePrevMonthDuration] = useState<number>(0);
  const [monthDataLoaded, setMonthDataLoaded] = useState<boolean>(false);
  const [trendPeriod, setTrendPeriod] = useState<'week' | 'month'>('week');
  // Tab-specific data loading flags for lazy loading
  const [tab0DataLoaded, setTab0DataLoaded] = useState<boolean>(false);
  const [tab2DataLoaded, setTab2DataLoaded] = useState<boolean>(false);
  const [tab3DataLoaded, setTab3DataLoaded] = useState<boolean>(false);
  const [tab4DataLoaded, setTab4DataLoaded] = useState<boolean>(false);
  const [tab5DataLoaded, setTab5DataLoaded] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [portfolioOptions, setPortfolioOptions] = useState<string[]>([]);
  const [platformOptions, setPlatformOptions] = useState<string[]>([]);
  const [applicationOptions, setApplicationOptions] = useState<string[]>([]);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  
  // Pagination and search state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');

  // AI Insights state
  const [aiInsightsOpen, setAiInsightsOpen] = useState(false);
  const [aiInsightsData, setAiInsightsData] = useState<AIInsightsResponse | null>(null);
  const [aiInsightsLoading, setAiInsightsLoading] = useState(false);
  const [aiInsightsError, setAiInsightsError] = useState<string | null>(null);

  // Postmortem status state
  const [postmortemStatus, setPostmortemStatus] = useState<any>(null);
  const [postmortemDetailsData, setPostmortemDetailsData] = useState<any[]>([]);
  const [postmortemLoading, setPostmortemLoading] = useState(false);
  const [postmortemFetched, setPostmortemFetched] = useState(false);
  const [postmortemPage, setPostmortemPage] = useState(0);
  const [postmortemRowsPerPage, setPostmortemRowsPerPage] = useState(10);
  const [postmortemSortConfig, setPostmortemSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const [postmortemStatusFilter, setPostmortemStatusFilter] = useState<string>('all');
  const [postmortemSearchQuery, setPostmortemSearchQuery] = useState<string>('');
  const [postmortemView, setPostmortemView] = useState<'actions' | 'trends'>('actions');
  const [decomView, setDecomView] = useState<'table' | 'trends'>('table');
  const [actionItemsPage, setActionItemsPage] = useState(0);
  const [actionItemsRowsPerPage, setActionItemsRowsPerPage] = useState(10);
  const [actionItemsSortConfig, setActionItemsSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const [actionItemsDueDateFilter, setActionItemsDueDateFilter] = useState<'all' | 'within15' | 'after15' | 'pastdue'>('all');
  const [actionItemsCategoryFilter, setActionItemsCategoryFilter] = useState<string>('all');
  const [actionItemsSearchQuery, setActionItemsSearchQuery] = useState<string>('');

  // Full Stack Monitoring Progress state (NEW)
  const [fullStackProgress, setFullStackProgress] = useState<any>(null);
  const [fullStackProgressLoading, setFullStackProgressLoading] = useState(false);
  const [fullStackProgressError, setFullStackProgressError] = useState<string | null>(null);

  // Full Stack Resiliency Progress state (NEW)
  const [fullStackResiliencyProgress, setFullStackResiliencyProgress] = useState<any>(null);
  const [fullStackResiliencyProgressLoading, setFullStackResiliencyProgressLoading] = useState(false);
  const [fullStackResiliencyProgressError, setFullStackResiliencyProgressError] = useState<string | null>(null);

  // Signal-to-Noise Ratio data state
  const [snrData, setSNRData] = useState<any[]>([]);
  const [snrDataLoading, setSNRDataLoading] = useState(false);
  const [snrDataError, setSNRDataError] = useState<string | null>(null);

  // Hedwig Team data state
  const [hedwigData, setHedwigData] = useState<any[]>([]);
  const [hedwigDataLoading, setHedwigDataLoading] = useState(false);
  const [hedwigDataError, setHedwigDataError] = useState<string | null>(null);
  const [hedwigFetchVersion, setHedwigFetchVersion] = useState(0);
  const hedwigInitialFetchDoneRef = React.useRef(false);

  // Golden Signal data state
  const [goldenSignalData, setGoldenSignalData] = useState<Map<string, any>>(new Map());
  const [goldenSignalLoading, setGoldenSignalLoading] = useState(false);
  const [goldenSignalError, setGoldenSignalError] = useState<string | null>(null);

  // Reliability Actions state (NEW)
  const [reliabilityActionsData, setReliabilityActionsData] = useState<any>(null);
  const [reliabilityActionsLoading, setReliabilityActionsLoading] = useState(false);
  const [reliabilityActionsError, setReliabilityActionsError] = useState<string | null>(null);
  const [reliabilityActionsView, setReliabilityActionsView] = useState<'application' | 'platform'>('application');
  const [selectedReliabilityPlatform, setSelectedReliabilityPlatform] = useState<string>('');

  // Share Link notification state
  const [shareLinkCopied, setShareLinkCopied] = useState(false);
  
  // Tag success notification state
  const [tagSuccessMessage, setTagSuccessMessage] = useState<string>('');

  // AI Insights validation dialog state
  const [showAIValidationDialog, setShowAIValidationDialog] = useState(false);

  // Track previous portfolio to detect actual changes
  const prevPortfolioRef = React.useRef<string>('');
  
  // Track if data is currently being fetched to prevent duplicate calls
  const isFetchingRef = React.useRef<boolean>(false);
  const p3p4FetchingRef = React.useRef(false);
  const postmortemFetchingRef = React.useRef(false);
  const secondaryFetchKeyRef = React.useRef<string | null>(null);

  // Health Summary Tabs state
  const [healthSummaryTab, setHealthSummaryTab] = useState(0);

  // Decommissioning Data state
  const [decommissioningData, setDecommissioningData] = useState<DecommissioningItem[]>([]);
  const [decommissioningLoading, setDecommissioningLoading] = useState(false);
  const [decommissioningError, setDecommissioningError] = useState<string | null>(null);
  const [decomPage, setDecomPage] = useState(0);
  const [decomRowsPerPage, setDecomRowsPerPage] = useState(10);
  const [decomSortConfig, setDecomSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({ key: '', direction: 'asc' });
  const [decomSearchQuery, setDecomSearchQuery] = useState('');
  const [decomTrackingFilter, setDecomTrackingFilter] = useState<'all' | 'completed' | 'ontrack' | 'delayed-overdue' | 'delayed-repeatedly' | 'delayed-extended' | 'cancelled'>('all');
  const [dcoCsvLoading, setDcoCsvLoading] = useState(false);
  const dcoCsvAbortControllerRef = React.useRef<AbortController | null>(null);
  
  // Column filters state
  const [decomPortfolioFilter, setDecomPortfolioFilter] = useState<string[]>([]);
  const [decomPortfolioTempFilter, setDecomPortfolioTempFilter] = useState<string[]>([]);
  const [decomPortfolioFilterAnchor, setDecomPortfolioFilterAnchor] = useState<null | HTMLElement>(null);
  const [decomPortfolioSearchText, setDecomPortfolioSearchText] = useState('');
  
  const [decomPlatformFilter, setDecomPlatformFilter] = useState<string[]>([]);
  const [decomPlatformTempFilter, setDecomPlatformTempFilter] = useState<string[]>([]);
  const [decomPlatformFilterAnchor, setDecomPlatformFilterAnchor] = useState<null | HTMLElement>(null);
  const [decomPlatformSearchText, setDecomPlatformSearchText] = useState('');
  
  const [decomStrategicIntentFilter, setDecomStrategicIntentFilter] = useState<string[]>([]);
  const [decomStrategicIntentTempFilter, setDecomStrategicIntentTempFilter] = useState<string[]>([]);
  const [decomStrategicIntentFilterAnchor, setDecomStrategicIntentFilterAnchor] = useState<null | HTMLElement>(null);
  const [decomStrategicIntentSearchText, setDecomStrategicIntentSearchText] = useState('');
  
  const [decomStatusFilter, setDecomStatusFilter] = useState<string[]>([]);
  const [decomStatusTempFilter, setDecomStatusTempFilter] = useState<string[]>([]);
  const [decomStatusFilterAnchor, setDecomStatusFilterAnchor] = useState<null | HTMLElement>(null);
  const [decomStatusSearchText, setDecomStatusSearchText] = useState('');
  
  const [decomDecomStatusFilter, setDecomDecomStatusFilter] = useState<string[]>([]);
  const [decomDecomStatusTempFilter, setDecomDecomStatusTempFilter] = useState<string[]>([]);
  const [decomDecomStatusFilterAnchor, setDecomDecomStatusFilterAnchor] = useState<null | HTMLElement>(null);
  const [decomDecomStatusSearchText, setDecomDecomStatusSearchText] = useState('');
  
  const [decomApplicationLabelsFilter, setDecomApplicationLabelsFilter] = useState<string[]>([]);
  const [decomApplicationLabelsTempFilter, setDecomApplicationLabelsTempFilter] = useState<string[]>([]);
  const [decomApplicationLabelsFilterAnchor, setDecomApplicationLabelsFilterAnchor] = useState<null | HTMLElement>(null);
  const [decomApplicationLabelsSearchText, setDecomApplicationLabelsSearchText] = useState('');
  
  const [decomOwnersFilter, setDecomOwnersFilter] = useState<string>('');
  const [decomOwnersTempFilter, setDecomOwnersTempFilter] = useState<string>('');
  const [decomOwnersFilterAnchor, setDecomOwnersFilterAnchor] = useState<null | HTMLElement>(null);
  
  const [decomDelayedCommentsFilter, setDecomDelayedCommentsFilter] = useState<string>('');
  const [decomDelayedCommentsTempFilter, setDecomDelayedCommentsTempFilter] = useState<string>('');
  const [decomDelayedCommentsFilterAnchor, setDecomDelayedCommentsFilterAnchor] = useState<null | HTMLElement>(null);
  
  const [decomPrevTargetDateFilter, setDecomPrevTargetDateFilter] = useState<string>('');
  const [decomPrevTargetDateTempFilter, setDecomPrevTargetDateTempFilter] = useState<string>('');
  const [decomPrevTargetDateOperator, setDecomPrevTargetDateOperator] = useState<'equals' | 'after' | 'before' | 'custom'>('equals');
  const [decomPrevTargetDateTempOperator, setDecomPrevTargetDateTempOperator] = useState<'equals' | 'after' | 'before' | 'custom'>('equals');
  const [decomPrevTargetDateFilterAnchor, setDecomPrevTargetDateFilterAnchor] = useState<null | HTMLElement>(null);
  const [decomPrevTargetDateCustomStart, setDecomPrevTargetDateCustomStart] = useState<string>('');
  const [decomPrevTargetDateCustomEnd, setDecomPrevTargetDateCustomEnd] = useState<string>('');
  const [decomPrevTargetDateTempCustomStart, setDecomPrevTargetDateTempCustomStart] = useState<string>('');
  const [decomPrevTargetDateTempCustomEnd, setDecomPrevTargetDateTempCustomEnd] = useState<string>('');
  
  const [decomDecomDateFilter, setDecomDecomDateFilter] = useState<string>('');
  const [decomDecomDateTempFilter, setDecomDecomDateTempFilter] = useState<string>('');
  const [decomDecomDateOperator, setDecomDecomDateOperator] = useState<'equals' | 'after' | 'before' | 'custom'>('equals');
  const [decomDecomDateTempOperator, setDecomDecomDateTempOperator] = useState<'equals' | 'after' | 'before' | 'custom'>('equals');
  const [decomDecomDateFilterAnchor, setDecomDecomDateFilterAnchor] = useState<null | HTMLElement>(null);
  const [decomDecomDateCustomStart, setDecomDecomDateCustomStart] = useState<string>('');
  const [decomDecomDateCustomEnd, setDecomDecomDateCustomEnd] = useState<string>('');
  const [decomDecomDateTempCustomStart, setDecomDecomDateTempCustomStart] = useState<string>('');
  const [decomDecomDateTempCustomEnd, setDecomDecomDateTempCustomEnd] = useState<string>('');

  const [decomBiaStatusFilter, setDecomBiaStatusFilter] = useState<string[]>([]);
  const [decomBiaStatusTempFilter, setDecomBiaStatusTempFilter] = useState<string[]>([]);
  const [decomBiaStatusFilterAnchor, setDecomBiaStatusFilterAnchor] = useState<null | HTMLElement>(null);
  
  const [decomFailoverTestedDateFilter, setDecomFailoverTestedDateFilter] = useState<string>('');
  const [decomFailoverTestedDateTempFilter, setDecomFailoverTestedDateTempFilter] = useState<string>('');
  const [decomFailoverTestedDateOperator, setDecomFailoverTestedDateOperator] = useState<'equals' | 'after' | 'before' | 'custom'>('equals');
  const [decomFailoverTestedDateTempOperator, setDecomFailoverTestedDateTempOperator] = useState<'equals' | 'after' | 'before' | 'custom'>('equals');
  const [decomFailoverTestedDateFilterAnchor, setDecomFailoverTestedDateFilterAnchor] = useState<null | HTMLElement>(null);
  const [decomFailoverTestedDateCustomStart, setDecomFailoverTestedDateCustomStart] = useState<string>('');
  const [decomFailoverTestedDateCustomEnd, setDecomFailoverTestedDateCustomEnd] = useState<string>('');
  const [decomFailoverTestedDateTempCustomStart, setDecomFailoverTestedDateTempCustomStart] = useState<string>('');
  const [decomFailoverTestedDateTempCustomEnd, setDecomFailoverTestedDateTempCustomEnd] = useState<string>('');
  
  const [decomBackupTestedDateFilter, setDecomBackupTestedDateFilter] = useState<string>('');
  const [decomBackupTestedDateTempFilter, setDecomBackupTestedDateTempFilter] = useState<string>('');
  const [decomBackupTestedDateOperator, setDecomBackupTestedDateOperator] = useState<'equals' | 'after' | 'before' | 'custom'>('equals');
  const [decomBackupTestedDateTempOperator, setDecomBackupTestedDateTempOperator] = useState<'equals' | 'after' | 'before' | 'custom'>('equals');
  const [decomBackupTestedDateFilterAnchor, setDecomBackupTestedDateFilterAnchor] = useState<null | HTMLElement>(null);
  const [decomBackupTestedDateCustomStart, setDecomBackupTestedDateCustomStart] = useState<string>('');
  const [decomBackupTestedDateCustomEnd, setDecomBackupTestedDateCustomEnd] = useState<string>('');
  const [decomBackupTestedDateTempCustomStart, setDecomBackupTestedDateTempCustomStart] = useState<string>('');
  const [decomBackupTestedDateTempCustomEnd, setDecomBackupTestedDateTempCustomEnd] = useState<string>('');
  
  // Column visibility state for decommissioning table
  const [decomColumnVisibility, setDecomColumnVisibility] = useState({
    portfolio: true,
    platform: true,
    app_id: true,
    app_name: true,
    mvc_group: false,
    owners: true,
    target_date: true,
    prev_target_date: true, // Default visible
    decom_date: true,
    delayed_comments: false, // Hidden by default
    strategic_intent: false, // Hidden by default
    backup_tested: false, // Hidden by default
    failover_tested: false, // Hidden by default
    status: true,
    decom_status: true,
    tag: false, // Hidden by default, available in column settings
    application_labels: false, // Hidden by default
    p1p2: false, // Hidden by default
    p3p4mi: false, // Hidden by default
    total_cost: false, // Hidden by default
    bia_status: false // Hidden by default
  });
  const [decomColumnDialogOpen, setDecomColumnDialogOpen] = useState(false);

  // Tag management state
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [selectedTagApp, setSelectedTagApp] = useState<DecommissioningItem | null>(null);
  const [tagFormData, setTagFormData] = useState({
    decomm_tag: '',
    remarks: '',
    comments: ''
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [tagLoading, setTagLoading] = useState(false);

  // Year filter for decommissioning table
  const currentYear = new Date().getFullYear();
  const previousYear = currentYear - 1;
  const nextYear = currentYear + 1;
  
  const [decomYearFilter, setDecomYearFilter] = useState<string>(currentYear.toString());
  const [decomYearFilterAnchor, setDecomYearFilterAnchor] = useState<null | HTMLElement>(null);
  const [allYearsExpanded, setAllYearsExpanded] = useState<boolean>(false);
  const [selectedYears, setSelectedYears] = useState<string[]>([]);
  const [yearSearchText, setYearSearchText] = useState<string>('');
  const [customRangeExpanded, setCustomRangeExpanded] = useState<boolean>(false);
  const [customRangeStart, setCustomRangeStart] = useState<string>('');
  const [customRangeEnd, setCustomRangeEnd] = useState<string>('');

  // Helper function to get years that actually have decommissioning data
  const getAvailableYears = useMemo(() => {
    return Array.from(new Set(
      decommissioningData
        .filter(item => item.target_date_decom !== null && item.target_date_decom !== undefined)
        .map(item => new Date(item.target_date_decom!).getFullYear())
        .sort((a, b) => a - b)
    ));
  }, [decommissioningData]);

  // Helper function to get the oldest previous target date from revisions
  const getOldestPrevTargetDate = (item: DecommissioningItem): Date | null => {
    if (!item.revisions || item.revisions.length === 0) return null;
    
    let oldestDate: Date | null = null;
    
    item.revisions.forEach(rev => {
      rev.values.forEach(val => {
        if (val.fieldName === 'targetDecommissioningDate' && val.oldValue && val.oldValue !== 'null') {
          const date = new Date(val.oldValue);
          if (!oldestDate || date < oldestDate) {
            oldestDate = date;
          }
        }
      });
    });
    
    return oldestDate;
  };

  // Helper function to count previous target dates in revisions
  const countPrevTargetDates = (item: DecommissioningItem): number => {
    if (!item.revisions || item.revisions.length === 0) return 0;
    
    let count = 0;
    item.revisions.forEach(rev => {
      rev.values.forEach(val => {
        if (val.fieldName === 'targetDecommissioningDate' && val.oldValue && val.oldValue !== 'null') {
          count++;
        }
      });
    });
    
    return count;
  };

  // Helper function to check if previous strategic intent was eliminate or migrate
  const hadPreviousEliminateOrMigrateIntent = (item: DecommissioningItem): boolean => {
    if (!item.revisions || item.revisions.length === 0) return false;
    
    // Check if the strategic intent changed FROM eliminate/migrate TO something else
    let foundIntentChangeFromEliminateMigrate = false;
    item.revisions.forEach(rev => {
      rev.values.forEach(val => {
        if ((val.fieldName === 'strategicIntent' || val.fieldName === 'strategic_intent') && val.oldValue && val.oldValue !== 'null') {
          const oldIntent = val.oldValue.toLowerCase();
          const currentIntent = item.strategic_intent?.intent?.toLowerCase() || '';
          
          // If old intent was eliminate/migrate but current is different, then it changed
          if ((oldIntent === 'eliminate' || oldIntent === 'migrate') && 
              (currentIntent !== 'eliminate' && currentIntent !== 'migrate')) {
            foundIntentChangeFromEliminateMigrate = true;
          }
        }
      });
    });
    
    return foundIntentChangeFromEliminateMigrate;
  };

  // Helper function to calculate decommissioning status based on new logic
  // 1. Decommissioned: MAC status is inactive
  // 2. Cancelled: Target ever existed AND strategic intent changed FROM eliminate/migrate TO something else, OR target decom date is blank
  // 3. To be Decommissioned: MAC status active + Today ≤ Target Decomm Date + No prev_target_date in past
  // 4. Delayed - Repeatedly: MAC Status = Active + application has two or more prev target dates in past
  // 5. Delayed - Date Extended: MAC Status = Active + oldest prev target date < current target date + current_target - prev_target > 14 days
  // 6. Delayed - Overdue: MAC status active + today > target_decom date +7 days and no prev_target_date in past
  const calculateDecommissioningStatus = (item: DecommissioningItem) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // 1. Decommissioned: MAC status is inactive
    if (item.status === 'Inactive') {
      return 'Decommissioned';
    }

    // Get previous target dates count and previous strategic intent info
    const prevTargetDatesCount = countPrevTargetDates(item);
    const hadPrevEliminateOrMigrate = hadPreviousEliminateOrMigrateIntent(item);

    // 2. Cancelled: Target ever existed AND strategic intent changed FROM eliminate/migrate TO something else, OR target decom date is blank
    const targetEverExisted = !!item.target_date_decom || prevTargetDatesCount > 0;
    const targetDecomDateIsBlank = !item.target_date_decom;

    if (targetEverExisted && (hadPrevEliminateOrMigrate || targetDecomDateIsBlank)) {
      return 'Cancelled';
    }

    // 3. For active applications
    if (item.status === 'Active' && item.target_date_decom) {
      const targetDate = new Date(item.target_date_decom);
      const oldestPrevDate = getOldestPrevTargetDate(item);

      // Check if oldest prev date is in the past
      const hasPrevTargetDateInPast = oldestPrevDate && oldestPrevDate < today;

      // To be Decommissioned: Today ≤ Target Decomm Date + No prev_target_date in past
      if (targetDate >= today && !hasPrevTargetDateInPast) {
        return 'To be Decommissioned';
      }

      // Delayed logic
      if (targetDate < today || hasPrevTargetDateInPast) {
        // Delayed - Repeatedly: application has two or more prev target dates in past
        if (prevTargetDatesCount >= 2) {
          return 'Delayed - Repeatedly';
        }

        // Delayed - Date Extended: oldest prev target date < current target date
        if (oldestPrevDate && oldestPrevDate < targetDate) {
          // Note: if current_target date - prev_target_date <=14 then count the status as delayed overdue
          const daysBetweenTargets = Math.floor((targetDate.getTime() - oldestPrevDate.getTime()) / (1000 * 60 * 60 * 24));
          if (daysBetweenTargets <= 14) {
            return 'Delayed - Overdue';
          }
          return 'Delayed - Date Extended';
        }

        // Delayed - Overdue: today > target_decom date +7 days and no prev_target_date in past
        if (!hasPrevTargetDateInPast && targetDate < today) {
          const daysOverdue = Math.floor((today.getTime() - targetDate.getTime()) / (1000 * 60 * 60 * 24));
          if (daysOverdue > 7) {
            return 'Delayed - Overdue';
          }
        }

        // Default delayed status - treat as overdue
        return 'Delayed - Overdue';
      }
    }

    return 'Unknown';
  };

  // Authorized users for tag management
  const authorizedTagUsers = [
    'mahesh.s.bhosale@maersk.com',
    'shilpitha.swarna@maersk.com',
    'nilesh.chakkar@maersk.com',
    'girish.kumsi@maersk.com',
    'paramjeet.nagpal@maersk.com',
    'vaishali.parande@maersk.com'
  ];

  // Check if current user is authorized for tag management
  const isUserAuthorizedForTags = () => {
    const userEmail = graphData?.mail?.toLowerCase();
    if (!userEmail) return false;
    return authorizedTagUsers.some(email => email.toLowerCase() === userEmail);
  };

  // Outage Analytics state
  const [outageViewMode, setOutageViewMode] = useState<'daily' | 'weekly' | 'monthly'>('daily');
  const [outagePage, setOutagePage] = useState(0);
  const [showMvcApps, setShowMvcApps] = useState(true); // Toggle to show/hide MVC apps
  const [showOtherApps, setShowOtherApps] = useState(false); // Toggle to show/hide Other apps
  
  // P1/P2 Incident View Mode state
  const [p1p2ViewMode, setP1P2ViewMode] = useState<'impacted' | 'caused'>('caused');
  const [p1p2IncidentDialogOpen, setP1P2IncidentDialogOpen] = useState(false);
  const [p1p2DialogPage, setP1P2DialogPage] = useState(0);
  const [p1p2DialogRowsPerPage, setP1P2DialogRowsPerPage] = useState(10);
  const [outageDialogOpen, setOutageDialogOpen] = useState(false);
  const [outageDialogPage, setOutageDialogPage] = useState(0);
  const [outageDialogRowsPerPage, setOutageDialogRowsPerPage] = useState(10);
  
  // MTTR state
  const [mttrData, setMttrData] = useState<Map<string, number>>(new Map());
  const [mttdData, setMttdData] = useState<Map<string, number>>(new Map());
  const [mttrLoading, setMttrLoading] = useState(false);
  
  const [p3p4IncidentDialogOpen, setP3P4IncidentDialogOpen] = useState(false);
  const [p3p4DialogPage, setP3P4DialogPage] = useState(0);
  const [p3p4DialogRowsPerPage, setP3P4DialogRowsPerPage] = useState(10);
  const [outageRowsPerPage, setOutageRowsPerPage] = useState(10);
  const [outageSortConfig, setOutageSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>({ key: 'totalDuration', direction: 'desc' });
  const [outageSearchQuery, setOutageSearchQuery] = useState('');

  // Resilience Action Pending state
  const [resilienceData, setResilienceData] = useState<any[]>([]);
  const [resilienceLoading, setResilienceLoading] = useState(false);
  const [resilienceError, setResilienceError] = useState<string | null>(null);
  const [resiliencePage, setResiliencePage] = useState(0);
  const [resilienceRowsPerPage, setResilienceRowsPerPage] = useState(10);
  const [resilienceSearchQuery, setResilienceSearchQuery] = useState('');
  const [resilienceActionsView, setResilienceActionsView] = useState<'application' | 'platform'>('application');
  const [selectedResiliencePlatform, setSelectedResiliencePlatform] = useState<string>('');

  // Application Certification state
  const [certificationData, setCertificationData] = useState<any[]>([]);
  const [certificationLoading, setCertificationLoading] = useState(false);
  const [certificationError, setCertificationError] = useState<string | null>(null);
  const [certificationFetched, setCertificationFetched] = useState(false);
  const [certificationSearchQuery, setCertificationSearchQuery] = useState('');
  const [mvcStatusFilter, setMvcStatusFilter] = useState<boolean>(false);
  const [bcaStatusFilter, setBcaStatusFilter] = useState<boolean>(false);
  const [certificationView, setCertificationView] = useState<'detailed' | 'platform'>('detailed');
  const [platformViewSearchQuery, setPlatformViewSearchQuery] = useState('');
  const [platformViewMvcFilter, setPlatformViewMvcFilter] = useState<boolean>(false);
  const [platformViewBcaFilter, setPlatformViewBcaFilter] = useState<boolean>(false);

  // Change Onboarding state
  const [changeOnboardingData, setChangeOnboardingData] = useState<ChangeOnboardingItem[]>([]);
  const [changeOnboardingLoading, setChangeOnboardingLoading] = useState(false);
  const [changeOnboardingError, setChangeOnboardingError] = useState<string | null>(null);
  const [changeOnboardingFetched, setChangeOnboardingFetched] = useState(false);
  const [changeOnboardingSearchQuery, setChangeOnboardingSearchQuery] = useState('');
  const [changeOnboardingMvcFilter, setChangeOnboardingMvcFilter] = useState<boolean>(false);
  const [changeOnboardingBcaFilter, setChangeOnboardingBcaFilter] = useState<boolean>(false);
  const [changeOnboardingStatusFilter, setChangeOnboardingStatusFilter] = useState<string>('all');
  const [changeOnboardingDeploymentChannelFilter, setChangeOnboardingDeploymentChannelFilter] = useState<string>('all');
  const [changeOnboardingChangeProcessTypeFilter, setChangeOnboardingChangeProcessTypeFilter] = useState<string>('all');

  // Helper function to get date range based on selection
  const getDateRange = (timeRange: string) => {
    const endDate = new Date();
    let startDate = new Date();
    
    console.log('🔍 ===== DATE RANGE CALCULATION =====');
    console.log('🔍 Input timeRange:', timeRange);
    console.log('🔍 Current date (endDate):', endDate.toString());
    
    switch (timeRange) {
      case '7d':
        startDate.setDate(endDate.getDate() - 7);
        startDate.setHours(0, 0, 0, 0);
        break;
      case '30d':
        startDate.setDate(endDate.getDate() - 30);
        startDate.setHours(0, 0, 0, 0);
        break;
      case '90d':
        startDate.setDate(endDate.getDate() - 90);
        startDate.setHours(0, 0, 0, 0);
        break;
      case '1y':
        // Year to Date: from January 1st of current year to today
        startDate.setFullYear(endDate.getFullYear(), 0, 1); // January 1st of current year
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'custom':
        // Use custom date range only if both dates are set AND applied
        if (customDateApplied && customDateRange.start && customDateRange.end) {
          console.log('🔍 Using custom date range:', customDateRange);
          return {
            start_time: customDateRange.start,
            end_time: customDateRange.end
          };
        }
        // Fall back to 30 days if custom dates are not set or not applied yet
        startDate.setDate(endDate.getDate() - 30);
        startDate.setHours(0, 0, 0, 0);
        break;
      default:
        startDate.setDate(endDate.getDate() - 30);
        startDate.setHours(0, 0, 0, 0);
    }
    
    // Set end date to end of day
    endDate.setHours(23, 59, 59, 999);
    
    const dateRange = {
      start_time: startDate.getFullYear() + '-' + 
                  String(startDate.getMonth() + 1).padStart(2, '0') + '-' + 
                  String(startDate.getDate()).padStart(2, '0'),
      end_time: endDate.getFullYear() + '-' + 
                String(endDate.getMonth() + 1).padStart(2, '0') + '-' + 
                String(endDate.getDate()).padStart(2, '0')
    };
    
    console.log('📅 Date range calculated:', {
      timeRange,
      startDate: startDate.toLocaleDateString(),
      endDate: endDate.toLocaleDateString(),
      start_time: dateRange.start_time,
      end_time: dateRange.end_time,
      daysDifference: Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
    });
    
    return dateRange;
  };

  // Note: SLO, Incident, P3/P4, and Postmortem data fetching moved inline to fetchPortfolioData
  // This prevents unnecessary re-creation of callback functions and duplicate API calls

  // Function to fetch MTTR data for incidents
  const fetchMTTRData = useCallback(async () => {
    try {
      setMttrLoading(true);
      console.log('🔄 Fetching MTTR data for all incidents');
      
      const response = await authFetch(`${API_BASE_URL}/api/inc-mttr`, {
        method: 'GET'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch MTTR data: ${response.statusText}`);
      }
      
      const result = await response.json();
      console.log('📦 MTTR API Response:', result);
      
      if (result.data && Array.isArray(result.data)) {
        const mttrMap = new Map<string, number>();
        const mttdMap = new Map<string, number>();
        result.data.forEach((item: MTTRData) => {
          const mttrSeconds = parseFloat(item.mttr);
          if (!isNaN(mttrSeconds)) {
            // Store MTTR in seconds
            mttrMap.set(item.number, mttrSeconds);
          }
          const mttdSeconds = parseFloat(item.mttd);
          if (!isNaN(mttdSeconds)) {
            // Store MTTD in seconds
            mttdMap.set(item.number, mttdSeconds);
          }
        });
        setMttrData(mttrMap);
        setMttdData(mttdMap);
        console.log('✅ MTTR data loaded for', mttrMap.size, 'incidents from API');
        console.log('✅ MTTD data loaded for', mttdMap.size, 'incidents from API');
        console.log('📊 Sample MTTR data:', Array.from(mttrMap.entries()).slice(0, 5));
        console.log('📊 Sample MTTD data:', Array.from(mttdMap.entries()).slice(0, 5));
      } else {
        console.warn('⚠️ MTTR API returned no data or invalid format');
      }
    } catch (err) {
      console.error('❌ Error fetching MTTR data:', err);
    } finally {
      setMttrLoading(false);
    }
  }, []);

  // Fetch incidents and trends using new simplified API (single call returns everything)
  // Fetch P1/P2 incidents with trends (for main page load)
  const fetchP1P2IncidentsWithTrends = useCallback(async () => {
    setP1P2Loading(true);
    setP1P2Error(null);
    try {
      const dateRange = getDateRange(selectedTimeRange);
      const requestBody = {
        start_date: dateRange.start_time,
        end_date: dateRange.end_time,
        page: 1,
        pageSize: 20000
      };

      const p1p2Response = await authFetch(INCIDENTS.P1_P2_WITH_TRENDS, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      if (!p1p2Response.ok) {
        throw new Error(`Failed to fetch P1/P2 incidents (${p1p2Response.status})`);
      }

      const p1p2Data = await p1p2Response.json();
      let p1p2Incidents: IncidentApiResponse[] = [];

      if (p1p2Data.success && p1p2Data.data) {
        const { incidents, trends } = p1p2Data.data;
        p1p2Incidents = incidents || [];

        if (trends.weekly) {
          setP1P2LastWeekCount(trends.weekly.current_period.incident_count);
          setP1P2PrevWeekCount(trends.weekly.previous_period.incident_count);
          setOutageLastWeekDuration(Math.round(trends.weekly.current_period.outage_duration_minutes || 0));
          setOutagePrevWeekDuration(Math.round(trends.weekly.previous_period.outage_duration_minutes || 0));
        }

        if (trends.monthly) {
          setP1P2LastMonthCount(trends.monthly.current_period.incident_count);
          setP1P2PrevMonthCount(trends.monthly.previous_period.incident_count);
          setOutageLastMonthDuration(Math.round(trends.monthly.current_period.outage_duration_minutes || 0));
          setOutagePrevMonthDuration(Math.round(trends.monthly.previous_period.outage_duration_minutes || 0));
          setMonthDataLoaded(true);
        }

        console.log('✅ P1/P2 Data loaded:', {
          incidents: p1p2Incidents.length,
          weekly_trend: trends.weekly?.comparison?.incident_display_text || 'N/A',
          monthly_trend: trends.monthly?.comparison?.incident_display_text || 'N/A',
          filters: p1p2Data.data.filters,
          request: requestBody
        });
      } else {
        console.warn('⚠️ P1/P2 API returned no data:', p1p2Data);
      }

      setIncidentData(p1p2Incidents);
    } catch (err: any) {
      console.error('❌ Error fetching P1/P2 incidents with trends:', err);
      setP1P2Error(err?.message || 'Failed to fetch P1/P2 incidents');
      setIncidentData([]);
    } finally {
      setP1P2Loading(false);
    }
  }, [selectedPortfolio, selectedPlatform, selectedTimeRange, customDateRange, customDateApplied]);

  // Fetch P3/P4 incidents with trends (async, doesn't block main page load)
  const fetchP3P4IncidentsWithTrends = useCallback(async () => {
    // Prevent duplicate fetches
    if (p3p4FetchingRef.current) {
      console.log('Skipping P3/P4 fetch - already loading');
      return;
    }
    
    try {
      p3p4FetchingRef.current = true;
      setP3P4Loading(true);
      setP3P4Fetched(false);
      // Get date range for the API call
      const dateRange = getDateRange(selectedTimeRange);
      
      // Fetch P3/P4 incidents with weekly and monthly trends in single API call
      const p3p4Response = await authFetch(INCIDENTS.P3_P4_WITH_TRENDS, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          start_date: dateRange.start_time,
          end_date: dateRange.end_time,
          portfolio: selectedPortfolio && selectedPortfolio !== 'all' ? selectedPortfolio : null,
          platform: selectedPlatform && selectedPlatform !== 'all' ? selectedPlatform : null,
          page: 1,
          pageSize: 20000
        })
      });
      
      if (p3p4Response.ok) {
        const p3p4Data = await p3p4Response.json();
        if (p3p4Data.success && p3p4Data.data) {
          const { incidents, trends } = p3p4Data.data;
          
          // Store incidents
          setP3P4IncidentData(incidents || []);
          
          // Store weekly trend metrics
          if (trends.weekly) {
            setP3P4LastWeekCount(trends.weekly.current_period.incident_count);
            setP3P4PrevWeekCount(trends.weekly.previous_period.incident_count);
          }
          
          // Store monthly trend metrics
          if (trends.monthly) {
            setP3P4LastMonthCount(trends.monthly.current_period.incident_count);
            setP3P4PrevMonthCount(trends.monthly.previous_period.incident_count);
          }
          
          console.log('✅ P3/P4 Data loaded:', {
            incidents: incidents?.length || 0,
            weekly_trend: trends.weekly?.comparison?.incident_display_text || 'N/A',
            monthly_trend: trends.monthly?.comparison?.incident_display_text || 'N/A',
            filters: p3p4Data.data.filters
          });
        }
      }
    } catch (err: any) {
      console.error('❌ Error fetching P3/P4 incidents with trends:', err);
    } finally {
      setP3P4Loading(false);
      setP3P4Fetched(true);
      p3p4FetchingRef.current = false;
    }
  }, [selectedPortfolio, selectedPlatform, selectedTimeRange, customDateRange, customDateApplied]);

  // REMOVED: Redundant fetchPostmortemStatus function
  // All postmortem data loading is handled by fetchPostmortemData() (triggered via the parallel data useEffect)

  // Fetch Decommissioning Data from API
  const fetchDecommissioningData = useCallback(async () => {
    setDecommissioningLoading(true);
    setDecommissioningError(null);
    
    try {
      console.log('🔄 Fetching decommissioning data...');
      console.log('   Selected Portfolio:', selectedPortfolio || 'All');
      console.log('   Selected Platform:', selectedPlatform || 'All');
      
      // Build query params
      const params = new URLSearchParams();
      if (selectedPortfolio && selectedPortfolio !== 'all') {
        params.append('portfolio', selectedPortfolio);
      }
      if (selectedPlatform && selectedPlatform !== 'all') {
        params.append('platform', selectedPlatform);
      }
      
      const url = `${API_BASE_URL}/api/reliability-framework/decommissioning-data${params.toString() ? '?' + params.toString() : ''}`;
      console.log('   API URL:', url);
      
      // Fetch decommissioning data
      const response = await authFetch(url);
      
      if (!response.ok) {
        const errorMsg = `API returned status ${response.status}`;
        throw errorMsg;
      }
      
      const result: DecommissioningDataResponse = await response.json();
      console.log('✅ Decommissioning data received:', {
        total_records: result.total_records,
        data_count: result.data?.length || 0,
        filters: result.filters
      });
      
      // Fetch all tags with cache busting
      console.log('🏷️ Fetching decommissioning tags...');
      const tagsResponse = await authFetch(`${API_BASE_URL}/api/decommissioning-tags/?t=${Date.now()}`);
      let tagsData: any[] = [];
      
      if (tagsResponse.ok) {
        const tagsResult = await tagsResponse.json();
        if (tagsResult.success && tagsResult.data) {
          tagsData = tagsResult.data;
          console.log('✅ Tags data received:', {
            count: tagsData.length,
            tags: tagsData.map(tag => ({ app_id: tag.app_id, decomm_tag: tag.decomm_tag, remarks: tag.comments }))
          });
        }
      } else {
        console.warn('⚠️ Failed to fetch tags:', tagsResponse.status);
      }
      
      // Merge tag data with decommissioning data (use latest tag per app)
      const mergedData = (result.data || []).map((item: any) => {
        // Find all tags for this app_id and get the latest one
        const appTags = tagsData.filter(tag => tag.app_id === item.app_id);
        const latestTag = appTags.length > 0 
          ? appTags.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0]
          : null;
        
        return {
          ...item,
          tag: latestTag ? {
            id: latestTag.id,
            decomm_tag: latestTag.decomm_tag,
            remarks: latestTag.comments, // Use comments field as remarks
            comments: latestTag.comments,
            created_by: latestTag.created_by,
            updated_by: latestTag.updated_by,
            created_at: latestTag.created_at,
            updated_at: latestTag.updated_at
          } : null
        };
      }) as DecommissioningItem[];
      
      console.log('🔗 Merged data with tags:', {
        total_items: mergedData.length,
        items_with_tags: mergedData.filter(item => item.tag).length,
        sample_tagged_items: mergedData
          .filter(item => item.tag)
          .slice(0, 3)
          .map(item => ({ 
            app_id: item.app_id, 
            tag: item.tag?.decomm_tag, 
            remarks: item.tag?.comments 
          }))
      });
      
      setDecommissioningData(mergedData);
    } catch (err: any) {
      console.error('❌ Error fetching decommissioning data:', err);
      setDecommissioningError(err.message);
      setDecommissioningData([]);
    } finally {
      setDecommissioningLoading(false);
    }
  }, [selectedPortfolio, selectedPlatform]);

  // Dedicated function to refresh postmortem data when switching tabs
  const fetchPostmortemData = useCallback(async () => {
    // Prevent duplicate fetches
    if (postmortemFetchingRef.current) {
      console.log('Skipping Postmortem refresh - already loading');
      return;
    }

    // Ensure Tab 4 is marked as loading as soon as we kick off the fetch
    setTab4DataLoaded(true);

    try {
      postmortemFetchingRef.current = true;
      setPostmortemLoading(true);
      console.log('🔄 Refreshing postmortem data from Maersk API...');
      
      const response = await authFetch(POSTMORTEM.ALL_REPORTS);
      
      if (!response.ok) {
        console.warn('⚠️ Postmortem API error! status:', response.status);
        const errorText = await response.text();
        console.error('Error response:', errorText);
        return;
      }
      
      const responseData = await response.json();
      console.log('📦 Raw API response:', responseData);
      console.log('📦 Response type:', typeof responseData);
      console.log('📦 Is array?', Array.isArray(responseData));
      
      // Handle both wrapped and unwrapped responses
      let allPostmortems;
      if (Array.isArray(responseData)) {
        // Direct array response
        allPostmortems = responseData;
      } else if (responseData && typeof responseData === 'object') {
        // Wrapped response - try common wrapper keys
        allPostmortems = responseData.data || responseData.items || responseData.postmortems || responseData.results || [];
        if (!Array.isArray(allPostmortems)) {
          console.error('❌ Unexpected response structure:', responseData);
          allPostmortems = [];
        }
      } else {
        console.error('❌ Invalid response format:', responseData);
        allPostmortems = [];
      }
      
      console.log('✅ Refreshed postmortems received:', allPostmortems.length);
      if (allPostmortems.length > 0) {
        console.log('📋 Sample postmortem data (first record):', allPostmortems[0]);
      }

      const startDate = new Date('2025-01-01T00:00:00');
      const endDate = new Date();
      
      let filteredPostmortems = allPostmortems.filter((pm: any) => 
        pm.priority === "0" || pm.priority === "1" || pm.priority === "2"
      );
      
      filteredPostmortems = filteredPostmortems.filter((pm: any) => {
        if (!pm.createdOn) return false;
        const createdDate = new Date(pm.createdOn);
        return createdDate >= startDate && createdDate <= endDate;
      });
      
      // Store ALL postmortems without portfolio/platform filtering
      // Tables will apply their own filters
      const allPostmortemsForStorage = [...filteredPostmortems];
      
      // Apply portfolio filter for statistics only (not for table storage)
      if (selectedPortfolio && selectedPortfolio !== 'all') {
        filteredPostmortems = filteredPostmortems.filter((pm: any) => 
          pm.causedBy_portfolio === selectedPortfolio
        );
      }
      
      // Apply platform filter for statistics only
      if (selectedPlatform && selectedPlatform !== 'all') {
        filteredPostmortems = filteredPostmortems.filter((pm: any) => 
          pm.causedBy_platform === selectedPlatform
        );
      }
      
      const openPostmortemsArray = filteredPostmortems.filter((pm: any) => {
        const status = pm.incidentStatus || '';
        return status.toUpperCase() !== 'DONE' && status.toUpperCase() !== 'LTREVIEW';
      });
      const openPostmortemsCount = openPostmortemsArray.length;
      
      const ltReviewPostmortems = filteredPostmortems.filter((pm: any) => {
        const status = pm.incidentStatus || '';
        return status.toUpperCase() === 'LTREVIEW';
      });
      const ltReviewCount = ltReviewPostmortems.length;
      
      // Store ALL postmortems for table (without portfolio filter)
      setPostmortemDetailsData(allPostmortemsForStorage);
      
      // Calculate action items statistics from ALL postmortems (not just status-filtered ones)
      let allPostmortemsForActions = allPostmortems.filter((pm: any) => 
        pm.priority === "0" || pm.priority === "1" || pm.priority === "2"
      );
      
      // Apply date filter for action items too
      allPostmortemsForActions = allPostmortemsForActions.filter((pm: any) => {
        if (!pm.createdOn) return false;
        const createdDate = new Date(pm.createdOn);
        return createdDate >= startDate && createdDate <= endDate;
      });
      
      // Note: We don't filter postmortems by causedBy_portfolio here because
      // actions can have different portfolios than the causing portfolio.
      // We'll filter individual actions by their portfolio field instead.
      
      // Apply platform filter for action items if selected (but not if "all")
      if (selectedPlatform && selectedPlatform !== 'all') {
        allPostmortemsForActions = allPostmortemsForActions.filter((pm: any) => 
          pm.causedBy_platform === selectedPlatform
        );
      }
      
      // Calculate action items statistics
      let totalActions = 0;
      let openActions = 0;
      let within15Count = 0;
      let after15Count = 0;
      let pastDueCount = 0;
      
      const today = new Date();
      const fifteenDaysFromNow = new Date();
      fifteenDaysFromNow.setDate(today.getDate() + 15);
      
      allPostmortemsForActions.forEach((pm: any, pmIndex: number) => {
        if (pm.actionDetails && Array.isArray(pm.actionDetails)) {
          pm.actionDetails.forEach((action: any, actionIndex: number) => {
            // Apply portfolio filter only if a specific portfolio is selected (not "all")
            // Case-insensitive comparison
            const actionPortfolio = (action.portfolio || '').toLowerCase();
            const selectedPortfolioLower = (selectedPortfolio || '').toLowerCase();
            if (selectedPortfolio && selectedPortfolio !== 'all' && actionPortfolio !== selectedPortfolioLower) {
              return;
            }
            
            totalActions++;
            const actionStatus = action.status ? action.status.toLowerCase() : '';
            
            if (actionStatus !== 'done' && actionStatus !== 'closed' && actionStatus !== '3') {
              openActions++;
              if (action.dueDate && action.dueDate !== '') {
                try {
                  const dateStr = action.dueDate.trim();
                  let dueDate: Date | null = null;
                  if (dateStr.includes(' ') || dateStr.split('-')[0].length <= 2) {
                    const dateTimeParts = dateStr.split(' ');
                    const datePart = dateTimeParts[0];
                    const parts = datePart.split('-');
                    if (parts.length === 3) {
                      const day = parts[0];
                      const month = parts[1];
                      const year = parts[2];
                      dueDate = new Date(`${year}-${month}-${day}`);
                    }
                  } else {
                    dueDate = new Date(dateStr);
                  }
                  if (dueDate && !isNaN(dueDate.getTime())) {
                    if (dueDate < today) {
                      pastDueCount++;
                    } else if (dueDate <= fifteenDaysFromNow) {
                      within15Count++;
                    } else {
                      after15Count++;
                    }
                  }
                } catch (error) {
                  // Skip invalid dates
                }
              }
            }
          });
        }
      });
      
      console.log('🔄 Refreshed Action Items Statistics:');
      console.log('   Total Actions:', totalActions);
      console.log('   Open Actions:', openActions);
      console.log('   Open Actions Breakdown:', {
        pastDue: pastDueCount,
        within15Days: within15Count,
        after15Days: after15Count
      });
      
      const statusBreakdown: { [key: string]: number } = {};
      openPostmortemsArray.forEach((pm: any) => {
        const status = pm.incidentStatus || 'Unknown';
        statusBreakdown[status] = (statusBreakdown[status] || 0) + 1;
      });
      
      // Update postmortem state with refreshed data
      setPostmortemStatus({
        openPostmortems: openPostmortemsArray,
        ltReviewPostmortems: ltReviewPostmortems,
        allPostmortems: filteredPostmortems,
        totals: {
          total_postmortems: filteredPostmortems.length,
          open_postmortems: openPostmortemsCount,
          lt_review_postmortems: ltReviewCount,
          total_actions: totalActions,
          total_open_actions: openActions,
          closed_percentage: totalActions > 0 ? Math.round(((totalActions - openActions) / totalActions) * 100) : 100,
          actions_past_due: pastDueCount,
          actions_within_15_days: within15Count,
          actions_after_15_days: after15Count
        },
        by_status: Object.entries(statusBreakdown).map(([status, count]) => ({
          status,
          count
        }))
      });
      
      console.log('✅ Postmortem data refreshed successfully');
    } catch (err: any) {
      console.error('❌ Error refreshing postmortem data:', err);
    } finally {
      setPostmortemLoading(false);
      postmortemFetchingRef.current = false;
    }
  }, [selectedPortfolio, selectedPlatform]);

  // Fetch data from API
  const fetchPortfolioData = useCallback(async () => {
    // Prevent duplicate simultaneous calls
    if (isFetchingRef.current) {
      console.log('⏭️ Skipping duplicate fetch - already in progress');
      return;
    }
    
    try {
      isFetchingRef.current = true;
      setLoading(true);
      setError(null);
      
      console.log('🔄 Fetching portfolio overview data...');
      
      // Fetch portfolio data
      const portfolioResponse = await authFetch(RELIABILITY_FRAMEWORK.PORTFOLIO_OVERVIEW);
      
      if (!portfolioResponse.ok) {
        setError('HTTP error! status: ' + portfolioResponse.status);
        return;
      }
      
      const portfolioResult = await portfolioResponse.json();
      console.log('✅ Portfolio data received:', portfolioResult);
      
      const portfolioData = portfolioResult.data || portfolioResult;
      setApiData(Array.isArray(portfolioData) ? portfolioData : [portfolioData]);
      
      // SLO and NFR data load in the background so a slow source can't hold the page spinner
      const loadSloData = async () => {
      try {
        console.log('🔄 Fetching SLO data from:', PORTFOLIO_HEALTH.ALL_APPS_SLO_INFO);
        const sloResponse = await authFetch(PORTFOLIO_HEALTH.ALL_APPS_SLO_INFO);
        if (sloResponse.ok) {
          const sloResult = await sloResponse.json();
          console.log('📦 Raw SLO response:', sloResult);
          const sloPayload = sloResult?.data || sloResult;
          const normalizedSlo = Array.isArray(sloPayload) ? sloPayload : sloPayload ? [sloPayload] : [];
          setSloData(normalizedSlo);
          console.log('✅ SLO data received:', normalizedSlo.length, 'apps', normalizedSlo.slice(0, 2));
        } else {
          console.warn('⚠️ Failed to fetch SLO data:', sloResponse.status, sloResponse.statusText);
          setSloData([]);
        }
      } catch (sloErr: any) {
        console.error('❌ Error fetching SLO data:', sloErr);
        setSloData([]);
      }
      };

      // Fetch NFR Test Insights data from database
      const loadNfrData = async () => {
      try {
        console.log('🔄 Fetching NFR Test Insights data...');
        setNfrLoading(true);
        setNfrError(null);
        
        const nfrResponse = await authFetch(PORTFOLIO_HEALTH.NFR_TEST_INSIGHTS, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (nfrResponse.ok) {
          const nfrResult = await nfrResponse.json();
          console.log('📦 Raw NFR response:', nfrResult);
          const normalizedNfr = Array.isArray(nfrResult) ? nfrResult : [];
          setNfrData(normalizedNfr);
          console.log('✅ NFR data received:', normalizedNfr.length, 'test records');
        } else {
          console.warn('⚠️ Failed to fetch NFR data:', nfrResponse.status, nfrResponse.statusText);
          setNfrError(`Failed to fetch NFR data: ${nfrResponse.status}`);
          setNfrData([]);
        }
      } catch (nfrErr: any) {
        console.error('❌ Error fetching NFR data:', nfrErr);
        setNfrError(nfrErr.message);
        setNfrData([]);
      } finally {
        setNfrLoading(false);
      }
      };
      void loadSloData();
      void loadNfrData();

      // Note: Postmortem data is loaded asynchronously via fetchPostmortemData() in useEffect
      // This prevents blocking the main page load
      
      // Extract unique portfolios, platforms, and applications
      const portfolios = Array.from(new Set(portfolioData.map((item: PortfolioApiResponse) => item.platform_portfolio))) as string[];
      const platforms = Array.from(new Set(portfolioData.map((item: PortfolioApiResponse) => item.platform))) as string[];
      const applications = Array.from(new Set(portfolioData.map((item: PortfolioApiResponse) => item.application))) as string[];
      
      const validPortfolios = portfolios.filter((p: string) => p && p.trim());
      const validPlatforms = platforms.filter((p: string) => p && p.trim());
      const validApplications = applications.filter((a: string) => a && a.trim());
      
      setPortfolioOptions(validPortfolios);
      // Don't set platformOptions here - let the useEffect that filters by portfolio handle it
      // setPlatformOptions(validPlatforms);
      setApplicationOptions(validApplications);
      
      // Validate and reset filters if they don't exist in current data
      if (selectedPortfolio !== 'all' && !validPortfolios.includes(selectedPortfolio)) {
        console.log('⚠️ Selected portfolio not found in Portfolio Health data, resetting to all');
        setSelectedPortfolio('all');
      }
      
      if (selectedPlatform !== 'all' && !validPlatforms.includes(selectedPlatform)) {
        console.log('⚠️ Selected platform not found in Portfolio Health data, resetting to all');
        setSelectedPlatform('all');
      }
      
      console.log('📊 Extracted options:', {
        portfolios: portfolios.length,
        platforms: platforms.length,
        applications: applications.length
      });
      
      // Trigger Tab 0 data loading after main data is fetched (if on Tab 0)
      if (healthSummaryTab === 0 && !tab0DataLoaded) {
        // Will be triggered by the useEffect watching apiData
      }
      
    } catch (err: any) {
      console.error('❌ Error fetching portfolio data:', err);
      setError(err?.message || 'Failed to fetch portfolio data');
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [selectedTimeRange, customDateApplied, customDateRange, selectedPortfolio, selectedPlatform]);

  const reloadHedwigData = useCallback(() => {
    setHedwigFetchVersion((prev) => prev + 1);
  }, []);

  useEffect(() => {
    let isCancelled = false;
    const skipStrictModeDuplicate = hedwigFetchVersion === 0 && hedwigInitialFetchDoneRef.current;
    if (skipStrictModeDuplicate) {
      return;
    }

    const loadHedwigData = async () => {
      setHedwigDataLoading(true);
      setHedwigDataError(null);

      try {
        console.log('🔄 Fetching Hedwig team data...');
        const response = await authFetch(`${API_BASE_URL}/api/hedwig/data`);
        if (!response.ok) {
          throw new Error(`Failed to fetch Hedwig data (${response.status})`);
        }
        const result = await response.json();
        const payload = result?.data ?? result;
        const normalized = Array.isArray(payload) ? payload : payload ? [payload] : [];
        if (!isCancelled) {
          console.log('✅ Hedwig data received:', normalized);
          setHedwigData(normalized);
        }
      } catch (err: any) {
        if (!isCancelled) {
          console.error('❌ Error fetching Hedwig data:', err);
          setHedwigDataError(err?.message || 'Failed to fetch Hedwig team data');
        }
      } finally {
        if (!isCancelled) {
          setHedwigDataLoading(false);
          if (hedwigFetchVersion === 0) {
            hedwigInitialFetchDoneRef.current = true;
          }
        }
      }
    };

    loadHedwigData();

    return () => {
      isCancelled = true;
    };
  }, [hedwigFetchVersion]);

  // Calculate Golden Signal score based on P1/P2 incident alert capture rate
  const calculateGoldenSignalScore = (percentage: number): number => {
    if (percentage === 100) return 100;
    if (percentage >= 75 && percentage <= 99) return 80;
    if (percentage >= 50 && percentage <= 74) return 60;
    return 0;
  };

  // Get Golden Signal data for an app based on P1/P2 incidents
  const getGoldenSignalData = (appId: string): { score: number | null, totalIncidents: number, capturePercentage: number } => {
    const appIncidents = incidentData.filter(incident => 
      incident.cmdb_ci_u_application_id === appId &&
      (incident.priority === '1' || incident.priority === '2')
    );
    
    const totalIncidents = appIncidents.length;
    
    // If no P1/P2 incidents, perfect score (100) - no incidents = perfect reliability
    if (totalIncidents === 0) {
      return { score: 100, totalIncidents: 0, capturePercentage: 100 };
    }
    
    const alertIncidents = appIncidents.filter(incident => 
      incident.contact_type_display_value && 
      incident.contact_type_display_value.toLowerCase() === 'alert'
    ).length;
    
    const capturePercentage = Math.round((alertIncidents / totalIncidents) * 100);
    const score = calculateGoldenSignalScore(capturePercentage);
    
    return { score, totalIncidents, capturePercentage };
  };

  // Fetch AI Insights data
  const fetchAIInsights = useCallback(async () => {
    // Validate that a portfolio is selected (not "All Portfolios")
    if (!selectedPortfolio) {
      setShowAIValidationDialog(true);
      return;
    }
    
    setAiInsightsLoading(true);
    setAiInsightsError(null);
    
    try {
      console.log('🤖 ===== AI INSIGHTS FETCH START =====');
      console.log('🤖 selectedTimeRange state:', selectedTimeRange);
      console.log('🤖 selectedPortfolio state:', selectedPortfolio);
      console.log('🤖 selectedPlatform state:', selectedPlatform);
      
      const dateRange = getDateRange(selectedTimeRange);
      const portfolioName = selectedPortfolio;
      
      const payload: any = {
        portfolio_name: portfolioName,
        start_date: dateRange.start_time,
        end_date: dateRange.end_time
      };
      
      // Only add platform_name if a specific platform is selected (not empty string)
      if (selectedPlatform) {
        payload.platform_name = selectedPlatform;
      }
      
      console.log('🤖 AI Insights API Request Details:');
      console.log('   Selected Time Range:', selectedTimeRange);
      console.log('   Selected Portfolio:', selectedPortfolio);
      console.log('   Selected Platform:', selectedPlatform);
      console.log('   Calculated Date Range:', dateRange);
      console.log('   Final Payload:', payload);
      console.log('🤖 ===== SENDING TO API =====');
      
      const response = await authFetch(
        'https://topsreliabilityframework-backend.westeurope.dev.maersk.io/api/ai/ops-insights',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload)
        }
      );
      
      if (!response.ok) {
        const errorMsg = `HTTP error! status: ${response.status}`;
        throw { message: errorMsg, name: 'HTTPError' };
      }
      
      const result: AIInsightsResponse = await response.json();
      console.log('✅ AI insights data received:', result);
      
      setAiInsightsData(result);
      setAiInsightsOpen(true);
      
    } catch (err) {
      console.error('❌ Error fetching AI insights:', err);
      const errorMessage = typeof err === 'object' && err && 'message' in err 
        ? String(err.message) 
        : 'Failed to fetch AI insights';
      setAiInsightsError(errorMessage);
    } finally {
      setAiInsightsLoading(false);
    }
  }, [selectedPortfolio, selectedPlatform, selectedTimeRange, customDateApplied, customDateRange]);

  // Read URL parameters on component mount and set dropdown values
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const portfolioParam = urlParams.get('portfolio');
    const platformParam = urlParams.get('platform');
    const timeRangeParam = urlParams.get('timeRange');
    const startDateParam = urlParams.get('startDate');
    const endDateParam = urlParams.get('endDate');
    
    if (portfolioParam) {
      setSelectedPortfolio(portfolioParam);
      prevPortfolioRef.current = portfolioParam;
    }
    if (platformParam) {
      setSelectedPlatform(platformParam);
    }
    if (timeRangeParam) {
      setSelectedTimeRange(timeRangeParam);
      if (timeRangeParam === 'custom' && startDateParam && endDateParam) {
        setCustomDateRange({ start: startDateParam, end: endDateParam });
        setShowCustomDatePicker(true);
        setCustomDateApplied(true); // Auto-apply if loaded from URL
      }
    }
  }, []); // Run only on mount

  useEffect(() => {
    // Fetch data when:
    // 1. Non-custom time ranges are selected
    // 2. Custom range is selected AND applied (customDateApplied is true)
    if (selectedTimeRange !== 'custom') {
      fetchPortfolioData();
    } else if (selectedTimeRange === 'custom' && customDateApplied) {
      fetchPortfolioData();
    }
  }, [fetchPortfolioData, selectedTimeRange, customDateApplied]);

  useEffect(() => {
    fetchMTTRData();
  }, [fetchMTTRData]);

  useEffect(() => {
    if (selectedTimeRange !== 'custom') {
      fetchP1P2IncidentsWithTrends();
    } else if (selectedTimeRange === 'custom' && customDateApplied) {
      fetchP1P2IncidentsWithTrends();
    }
  }, [fetchP1P2IncidentsWithTrends, selectedTimeRange, customDateApplied]);

  // Golden Signal data is now calculated directly from incident data, no API call needed

  // Reset tab data loaded flags when portfolio or platform changes
  useEffect(() => {
    setTab0DataLoaded(false);
    setTab2DataLoaded(false);
    setTab3DataLoaded(false);
    setTab4DataLoaded(false);
    setTab5DataLoaded(false);
    setMonthDataLoaded(false); // Also reset month trend data to refetch with new filters
  }, [selectedPortfolio, selectedPlatform]);

  // Monthly data is now loaded automatically with the initial API call
  // No need for separate lazy loading

  // Lazy load tab-specific data when user switches tabs
  useEffect(() => {
    const loadTabData = async () => {
      // Tab 0: MVC App Health Summary
      if (healthSummaryTab === 0 && !tab0DataLoaded) {
        setTab0DataLoaded(true);
        
        // Fetch Full Stack Monitoring Progress
        const fetchFullStackProgress = async () => {
          setFullStackProgressLoading(true);
          setFullStackProgressError(null);
          try {
            const params = new URLSearchParams();
            if (selectedPortfolio && selectedPortfolio !== 'all') {
              params.append('portfolio', selectedPortfolio);
            }
            if (selectedPlatform && selectedPlatform !== 'all') {
              params.append('platform', selectedPlatform);
            }
            const url = `${RELIABILITY_FRAMEWORK.FULLSTACK_MONITORING_PROGRESS}${params.toString() ? '?' + params.toString() : ''}`;
            console.log('🔍 [Tab 0] Fetching Full Stack Progress from:', url);
            const resp = await authFetch(url);
            if (!resp.ok) {
              setFullStackProgressError(`Failed to fetch (${resp.status}): ${resp.statusText}`);
              return;
            }
            const result = await resp.json();
            const progressData = result.data || null;
            if (progressData && result.improvements) {
              progressData.improvements = result.improvements;
            }
            setFullStackProgress(progressData);
          } catch (err: any) {
            console.error('❌ Full Stack Progress error:', err);
            setFullStackProgressError(err.message || 'Unknown error');
            setFullStackProgress(null);
          } finally {
            setFullStackProgressLoading(false);
          }
        };

        // Fetch Full Stack Resiliency Progress — disabled, under development
        const fetchFullStackResiliencyProgress = async () => {
          // API call disabled: resiliency-framework/fullstack-resiliency-progress is no longer used
          setFullStackResiliencyProgress(null);
          setFullStackResiliencyProgressError(null);
          setFullStackResiliencyProgressLoading(false);
        };

        // Fetch SNR Data - Use already loaded portfolio data instead of fetching again
        const fetchSNRData = async () => {
          setSNRDataLoading(true);
          setSNRDataError(null);
          try {
            console.log('🔍 [Tab 0] Using already-loaded portfolio data for SNR (no additional API call)');
            // Use apiData that was already fetched in fetchPortfolioData
            const apps = apiData || [];
            const filteredApps = apps.filter((app: any) => {
              const snr = parseFloat(app.calculated_snr);
              const alertCount = parseInt(app.snr_alert_count);
              const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || app.platform_portfolio === selectedPortfolio;
              const platformMatch = !selectedPlatform || selectedPlatform === 'all' || app.platform === selectedPlatform;
              return snr > 1 && alertCount > 0 && portfolioMatch && platformMatch;
            });
            const topApps = filteredApps
              .sort((a: any, b: any) => parseFloat(a.calculated_snr) - parseFloat(b.calculated_snr))
              .slice(0, 4);
            setSNRData(topApps);
          } catch (err: any) {
            console.error('❌ SNR Data error:', err);
            setSNRDataError(err.message || 'Unknown error');
            setSNRData([]);
          } finally {
            setSNRDataLoading(false);
          }
        };

        // Fetch all Tab 0 data in parallel
        await Promise.all([
          fetchFullStackProgress(),
          fetchFullStackResiliencyProgress(),
          fetchSNRData()
        ]);
      }

      // Tab 2: Reliability Actions
      if (healthSummaryTab === 2 && !tab2DataLoaded) {
        setTab2DataLoaded(true);
        
        // Reliability should work when at least any portfolio is selected
        // Only show message when both portfolio and platform are 'all' or empty
        if ((!selectedPortfolio || selectedPortfolio === 'all') && (!selectedPlatform || selectedPlatform === 'all')) {
          setReliabilityActionsData(null);
          setReliabilityActionsError('Please select at least a portfolio to view reliability action items');
          setReliabilityActionsLoading(false);
          return;
        }

        setReliabilityActionsLoading(true);
        setReliabilityActionsError(null);
        try {
          const params = new URLSearchParams();
          if (selectedPortfolio && selectedPortfolio !== 'all') {
            params.append('portfolio', selectedPortfolio);
          }
          if (selectedPlatform && selectedPlatform !== 'all') {
            params.append('platform', selectedPlatform);
          }
          const url = `${RELIABILITY_FRAMEWORK.PLATFORM_PENDING_ITEMS}?${params.toString()}`;
          console.log('🔍 [Tab 2] Fetching Reliability Actions from:', url);
          const resp = await authFetch(url);
          if (!resp.ok) {
            setReliabilityActionsError(`Failed to fetch (${resp.status}): ${resp.statusText}`);
            return;
          }
          const result = await resp.json();
          setReliabilityActionsData(result.data || null);
        } catch (err: any) {
          console.error('❌ Reliability Actions error:', err);
          setReliabilityActionsError(err.message || 'Unknown error');
          setReliabilityActionsData(null);
        } finally {
          setReliabilityActionsLoading(false);
        }
      }

      // Tab 6: Decommissioning Status (moved from tab 5)
      if (healthSummaryTab === 6 && !tab5DataLoaded) {
        setTab5DataLoaded(true);
        await fetchDecommissioningData();
      }
    };

    // Only load tab data after main portfolio data is available
    if (apiData.length > 0 || healthSummaryTab !== 0) {
      loadTabData();
    }
  }, [healthSummaryTab, tab0DataLoaded, tab2DataLoaded, tab5DataLoaded, selectedPortfolio, selectedPlatform, fetchDecommissioningData, apiData]);

  // Full Stack Monitoring Progress - Moved to lazy loading for Tab 0

  // Full Stack Resiliency Progress - Moved to lazy loading for Tab 0

  // SNR Data - Moved to lazy loading for Tab 0

  // Reliability Actions - Moved to lazy loading for Tab 2

  // Decommissioning Data - Moved to lazy loading for Tab 5

  // Fetch Resilience Data - Lazy load when Tab 3 is selected
  useEffect(() => {
    const fetchResilienceData = async () => {
      if (healthSummaryTab !== 3 || tab3DataLoaded) return;
      
      setTab3DataLoaded(true);
      
      setResilienceLoading(true);
      setResilienceError(null);
      try {
        const response = await authFetch(`${API_BASE_URL}/api/resiliency-msa`);
        if (!response.ok) {
          const error: any = new window.Error('Failed to fetch resilience data');
          throw error;
        }
        const result = await response.json();
        
        // Handle different response formats
        const data = Array.isArray(result) ? result : (result.data || []);
        
        console.log('🔍 Resilience Raw Data Sample:', data[0]);
        console.log('🔍 Selected Portfolio:', selectedPortfolio);
        console.log('🔍 Selected Platform:', selectedPlatform);
        
        // Filter by portfolio and platform first
        const filteredData = data.filter((item: any) => {
          const itemPortfolio = item.platform_portfolio;
          const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || itemPortfolio === selectedPortfolio;
          
          const itemPlatform = item.platform;
          const platformMatch = !selectedPlatform || selectedPlatform === 'all' || itemPlatform === selectedPlatform;
          
          return portfolioMatch && platformMatch;
        });
        
        console.log('🔍 Filtered Data Count:', filteredData.length);
        
        // Process data to identify missing items per application (MSA field names)
        const appData = filteredData.map((item: any) => {
          const missingItems: string[] = [];
          const oneYearAgo = new Date();
          oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

          const isOOS = (v: any) => String(v ?? '').toUpperCase() === 'OOS';

          // Skip entirely OOS items (whole app out of scope)
          if (isOOS(item.backup_available) || item.descoped === 'Yes') return null;

          // Check RPO (rpo_business_expectation)
          const rpo = item.rpo_business_expectation;
          if (!isOOS(rpo) && (!rpo || rpo === '' || rpo === 'No' || rpo === '0')) {
            missingItems.push('RPO Not Defined');
          }

          // Check RTO (rto_business_expectation)
          const rto = item.rto_business_expectation;
          if (!isOOS(rto) && (!rto || rto === '' || rto === 'No' || rto === '0')) {
            missingItems.push('RTO Not Defined');
          }

          // Check TRP (trp_available — may be boolean true or string "yes"/"true")
          const trp = item.trp_available;
          const trpOOS = isOOS(trp);
          const trpYes = trp === true || String(trp).toLowerCase() === 'yes' || String(trp).toLowerCase() === 'true';
          if (!trpOOS && !trpYes) {
            missingItems.push('TRP Not Available');
          }

          // Check Failover (failover_setup)
          const failoverSetup = item.failover_setup;
          const failoverOOS = isOOS(failoverSetup);
          if (!failoverOOS) {
            if (!failoverSetup || failoverSetup === 'Not Setup' || failoverSetup === 'No') {
              missingItems.push('Failover Not Present');
            } else {
              const failoverTested = item.failover_tested_date_last_12_months;
              if (isOOS(failoverTested)) {
                // OOS — skip failover test check
              } else if (failoverTested) {
                const testDate = new Date(failoverTested);
                if (isNaN(testDate.getTime()) || testDate < oneYearAgo) {
                  missingItems.push('Failover Test Overdue (>12 months)');
                }
              } else {
                missingItems.push('Failover Not Tested');
              }
            }
          }

          // Check Backup (backup_available)
          const backup = item.backup_available;
          const backupOOS = isOOS(backup);
          if (!backupOOS) {
            if (!backup || backup === 'No' || backup === false) {
              missingItems.push('Backup Not Available');
            } else {
              const restoreTested = item.restore_from_backup_tested_last_12_months;
              if (isOOS(restoreTested)) {
                // OOS — skip restore test check
              } else if (restoreTested) {
                const testDate = new Date(restoreTested);
                if (isNaN(testDate.getTime()) || testDate < oneYearAgo) {
                  missingItems.push('Restore Test Overdue (>12 months)');
                }
              } else {
                missingItems.push('Restore Not Tested');
              }
            }
          }

          return {
            app_id: item.app_id,
            application: item.application,
            platform: item.platform,
            portfolio: item.platform_portfolio,
            missingItems,
            failover_tested_date: item.failover_tested_date_last_12_months,
            restore_tested_date: item.restore_from_backup_tested_last_12_months
          };
        }).filter((item: any) => item !== null && item.missingItems.length > 0);

        console.log('🔍 Apps with Missing Items:', appData.length);

        // Group by platform
        const platformMap: { [key: string]: any } = {};
        
        appData.forEach((app: any) => {
          const platformName = app.platform || 'Unknown Platform';
          
          if (!platformMap[platformName]) {
            platformMap[platformName] = {
              platform_name: platformName,
              total_apps: 0,
              applications: []
            };
          }
          
          platformMap[platformName].total_apps += 1;
          platformMap[platformName].applications.push({
            app_id: app.app_id,
            application: app.application,
            portfolio: app.portfolio,
            pending_items: app.missingItems,
            failover_tested_date: app.failover_tested_date,
            restore_tested_date: app.restore_tested_date
          });
        });

        const platforms = Object.values(platformMap);
        
        console.log('🔍 Final Platforms:', platforms);
        console.log('🔍 Platform Count:', platforms.length);

        setResilienceData(platforms);
      } catch (err) {
        console.error('Error fetching resilience data:', err);
        setResilienceError((err as any)?.message || 'Failed to load data');
      } finally {
        setResilienceLoading(false);
      }
    };

    fetchResilienceData();
  }, [healthSummaryTab, selectedPortfolio, selectedPlatform]);

  // Application Certification fetch helper
  const fetchCertificationData = useCallback(async () => {
    // Check if we have access token
    if (!accessToken) {
      console.warn('Access token not available yet');
      return;
    }

    // Prevent refetching if already loaded or currently loading
    if (certificationFetched || certificationLoading) {
      console.log('Skipping certification fetch - already fetched or loading');
      return;
    }

    setCertificationLoading(true);
    setCertificationError(null);
    try {
      const certificationBaseUrl = process.env.REACT_APP_APPLICATION_CERTIFICATION_DASHBOARD_API;

      const response = await authFetch(
        `${certificationBaseUrl}/get-certification-dashboard-data`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        const error: any = new window.Error('Failed to fetch certification data');
        throw error;
      }
      const result: ApplicationCertificationResponse = await response.json();

      setCertificationData(result.combined_data || []);
    } catch (err) {
      console.error('Error fetching certification data:', err);
      setCertificationError((err as any)?.message || 'Failed to load certification data');
    } finally {
      setCertificationLoading(false);
      setCertificationFetched(true);
    }
  }, [accessToken, certificationFetched, certificationLoading]);

  // Change Onboarding fetch helper
  const fetchChangeOnboardingData = useCallback(async () => {
    // Check if we have access token
    if (!accessToken) {
      console.warn('Access token not available yet');
      return;
    }

    // Prevent refetching if already loaded or currently loading
    if (changeOnboardingFetched || changeOnboardingLoading) {
      console.log('Skipping change onboarding fetch - already fetched or loading');
      return;
    }

    setChangeOnboardingLoading(true);
    setChangeOnboardingError(null);
    try {
      const onboardingBaseUrl = process.env.REACT_APP_CHANGE_ONBOARDING_API;

      const response = await authFetch(
        `${onboardingBaseUrl}/change/get_all_applications`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        const error: any = new window.Error('Failed to fetch change onboarding data');
        throw error;
      }
      const result: ChangeOnboardingResponse = await response.json();

      setChangeOnboardingData(result.data || []);
    } catch (err) {
      console.error('Error fetching change onboarding data:', err);
      setChangeOnboardingError((err as any)?.message || 'Failed to load change onboarding data');
    } finally {
      setChangeOnboardingLoading(false);
      setChangeOnboardingFetched(true);
    }
  }, [accessToken, changeOnboardingFetched, changeOnboardingLoading]);

  const authFetchKeyRef = React.useRef<string | null>(null);
  // Kick off certification/onboarding fetches immediately once auth context is ready
  useEffect(() => {
    if (!accessToken) {
      return;
    }

    const authKey = accessToken;
    if (authFetchKeyRef.current === authKey) {
      return;
    }

    authFetchKeyRef.current = authKey;
    fetchCertificationData();
    fetchChangeOnboardingData();
  }, [accessToken, fetchCertificationData, fetchChangeOnboardingData]);

  // Trigger secondary data sources in parallel with the main load
  useEffect(() => {
    // Wait until custom range is applied
    if (selectedTimeRange === 'custom' && !customDateApplied) {
      return;
    }

    const customKey = customDateRange ? `${customDateRange.start || ''}|${customDateRange.end || ''}` : 'no-custom-range';
    const tokenKey = `${accessToken || 'no-token'}|${graphData?.mail || 'no-mail'}`;
    const secondaryKey = [
      selectedPortfolio || 'all',
      selectedPlatform || 'all',
      selectedTimeRange,
      customDateApplied ? customKey : 'custom-not-applied',
      tokenKey
    ].join('|');

    if (secondaryFetchKeyRef.current === secondaryKey) {
      return;
    }

    secondaryFetchKeyRef.current = secondaryKey;

    fetchP3P4IncidentsWithTrends();
    fetchPostmortemData();
  }, [
    selectedPortfolio,
    selectedPlatform,
    selectedTimeRange,
    customDateRange?.start,
    customDateRange?.end,
    customDateApplied,
    fetchP3P4IncidentsWithTrends,
    fetchPostmortemData
  ]);

  // Update platform options based on selected portfolio
  useEffect(() => {
    if (selectedPortfolio && selectedPortfolio !== 'all' && apiData.length > 0) {
      const filteredPlatforms = Array.from(
        new Set(
          apiData
            .filter(item => item.platform_portfolio === selectedPortfolio)
            .map(item => item.platform)
        )
      ) as string[];
      const validPlatforms = filteredPlatforms.filter((p: string) => p && p.trim());
      setPlatformOptions(validPlatforms);
      
      // Only reset platform if:
      // 1. Portfolio actually changed (not just re-rendering)
      // 2. Current platform is not 'all' AND not in the filtered platforms
      if (prevPortfolioRef.current !== selectedPortfolio && prevPortfolioRef.current !== '' && prevPortfolioRef.current !== 'all') {
        if (selectedPlatform !== 'all' && !validPlatforms.includes(selectedPlatform)) {
          setSelectedPlatform('all');
        }
      }
      prevPortfolioRef.current = selectedPortfolio;
    } else if ((!selectedPortfolio || selectedPortfolio === 'all') && apiData.length > 0) {
      // Show all platforms when no portfolio is selected or 'all' is selected
      const allPlatforms = Array.from(new Set(apiData.map((item: PortfolioApiResponse) => item.platform))) as string[];
      setPlatformOptions(allPlatforms.filter((p: string) => p && p.trim()));
      prevPortfolioRef.current = selectedPortfolio || '';
    }
  }, [selectedPortfolio, apiData, selectedPlatform]);

  // Reset postmortem page when filter changes
  useEffect(() => {
    setPostmortemPage(0);
  }, [postmortemStatusFilter]);

  // Reset action items page when filter changes
  useEffect(() => {
    setActionItemsPage(0);
  }, [actionItemsDueDateFilter]);

  // Sorting functions
  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey: string) => {
    if (!sortConfig || sortConfig.key !== columnKey) {
      return <TrendingUp sx={{ fontSize: '16px', opacity: 0.3, ml: 0.5 }} />;
    }
    return sortConfig.direction === 'asc' 
      ? <TrendingUp sx={{ fontSize: '16px', ml: 0.5 }} />
      : <TrendingDown sx={{ fontSize: '16px', ml: 0.5 }} />;
  };

  const sortApps = (apps: PortfolioApp[]) => {
    if (!sortConfig) return apps;
    
    return [...apps].sort((a, b) => {
      let aVal: any = a[sortConfig.key as keyof PortfolioApp];
      let bVal: any = b[sortConfig.key as keyof PortfolioApp];
      
      // Convert string numbers to actual numbers for proper sorting
      if (sortConfig.key === 'incidentViaAlerts' || sortConfig.key === 'incidentsCausedByChange') {
        aVal = parseInt(aVal) || 0;
        bVal = parseInt(bVal) || 0;
      }
      
      // Handle calculatedSnr sorting (can be null or string)
      if (sortConfig.key === 'calculatedSnr') {
        aVal = aVal !== null && aVal !== undefined ? (typeof aVal === 'string' ? parseFloat(aVal) : aVal) : -1; // Put nulls at the end
        bVal = bVal !== null && bVal !== undefined ? (typeof bVal === 'string' ? parseFloat(bVal) : bVal) : -1;
      }

      // Handle goldenSignalScore sorting (can be null)
      if (sortConfig.key === 'goldenSignalScore') {
        aVal = aVal !== null && aVal !== undefined ? aVal : -1; // Put nulls at the end
        bVal = bVal !== null && bVal !== undefined ? bVal : -1;
      }

      // Handle onCallRotaStatus sorting (Yes > No Service > No)
      if (sortConfig.key === 'onCallRotaStatus') {
        const statusOrder: { [key: string]: number } = { 'Yes': 3, 'No Service': 2, 'No': 1 };
        aVal = statusOrder[aVal] || 0;
        bVal = statusOrder[bVal] || 0;
      }

      // Handle nfr sorting (treat NA as lowest)
      if (sortConfig.key === 'nfr') {
        if (aVal === 'NA' || !aVal) aVal = '';
        if (bVal === 'NA' || !bVal) bVal = '';
      }

      // Handle sloAvailabilityMtd sorting (0 means NA, put at end)
      if (sortConfig.key === 'sloAvailabilityMtd') {
        aVal = aVal > 0 ? aVal : -1;
        bVal = bVal > 0 ? bVal : -1;
      }

      // Handle hasHedwigTeam sorting (boolean values)
      if (sortConfig.key === 'hasHedwigTeam') {
        aVal = aVal === true ? 1 : 0;
        bVal = bVal === true ? 1 : 0;
      }
      
      if (aVal < bVal) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aVal > bVal) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  // Tag management functions
  const handleAddTag = (app: DecommissioningItem) => {
    if (!isUserAuthorizedForTags()) {
      alert('You are not authorized to manage tags. Please contact an administrator.');
      return;
    }
    setSelectedTagApp(app);
    setTagFormData({ decomm_tag: '', remarks: '', comments: '' });
    setIsEditMode(false);
    setTagModalOpen(true);
  };

  const handleEditTag = (app: DecommissioningItem) => {
    console.log('Editing tag for app:', app);
    console.log('Tag data:', app.tag);
    setSelectedTagApp(app);
    
    // Ensure we populate with existing values, fallback to empty strings
    const existingTag = app.tag;
    setTagFormData({
      decomm_tag: existingTag?.decomm_tag || '',
      remarks: existingTag?.comments || '', // Use comments as remarks
      comments: existingTag?.comments || '' // Keep for backend compatibility
    });
    
    console.log('Form populated with:', {
      decomm_tag: existingTag?.decomm_tag || '',
      remarks: existingTag?.comments || ''
    });
    
    setIsEditMode(true);
    setTagModalOpen(true);
  };

  const handleTagFormChange = (field: string, value: string) => {
    setTagFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveTag = async () => {
    if (!selectedTagApp) return;
    
    if (!isUserAuthorizedForTags()) {
      alert('You are not authorized to manage tags. Please contact an administrator.');
      return;
    }
    
    setTagLoading(true);
    try {
      const userEmail = graphData?.mail || 'unknown.user@company.com';
      
      // For edit mode, use existing values if form fields are empty
      const existingTag = isEditMode ? selectedTagApp.tag : null;
      
      const tagData = {
        app_id: selectedTagApp.app_id,
        app_name: selectedTagApp.app_name,
        decomm_tag: tagFormData.decomm_tag || existingTag?.decomm_tag || '',
        remarks: tagFormData.remarks || existingTag?.remarks || '',
        comments: '', // Always empty since we removed the field
        created_by: userEmail,
        updated_by: userEmail
      };

      console.log('Saving tag data:', tagData);

      // Always create a new entry (even for edits)
      const response = await authFetch(`${API_BASE_URL}/api/decommissioning-tags/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(tagData)
      });
      
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const result = await response.json();
      console.log('Tag save response:', result);

      // Update local state immediately to reflect the changes
      if (result.success && result.data) {
        setDecommissioningData(prevData => {
          return prevData.map(item => {
            if (item.app_id === selectedTagApp.app_id) {
              return {
                ...item,
                tag: {
                  id: result.data.id || Date.now(),
                  decomm_tag: tagData.decomm_tag || null,
                  remarks: tagData.remarks || '',
                  comments: tagData.remarks || null,
                  created_by: tagData.created_by || '',
                  updated_by: tagData.updated_by || '',
                  created_at: result.data.created_at || new Date().toISOString(),
                  updated_at: result.data.created_at || new Date().toISOString()
                }
              };
            }
            return item;
          });
        });
      }

      const successMessage = isEditMode ? 'Tag updated successfully!' : 'Tag created successfully!';
      
      handleCloseTagModal();
      setTagSuccessMessage(successMessage);
    } catch (error) {
      console.error('Error saving tag:', error);
      alert('Failed to save tag');
    } finally {
      setTagLoading(false);
    }
  };

  const handleCloseTagModal = () => {
    setTagModalOpen(false);
    setSelectedTagApp(null);
    setTagFormData({ decomm_tag: '', remarks: '', comments: '' });
    setIsEditMode(false);
  };

  // CSV Export function for Portfolio Health table
  const handleExportToCSV = () => {
    try {
      const dataToExport = sortedAndFilteredApps; // Export all filtered data, not just paginated
      const portfolioName = selectedPortfolio || 'All Portfolios';
      
      const csvHeaders = [
        'App ID',
        'Platform Portfolio',
        'Platform',
        'Application',
        'P1/P2',
        'P3/P4 (MI)',
        'Incident Caused by Change',
        'Change IDs',
        'Golden Signal Score',
        'Golden Signal Total Incidents',
        'Golden Signal Capture %',
        'NFR Certification Status',
        'NFR Test Count',
        'NFR Last Test Date',
        'On Call Rota Status',
        'On Call Users Count',
        'On Call User Names',
        'SLO Availability (%)',
        'SLO Objective (%)',
        'Error Budget (minutes)',
        'SNR (%)',
        'SNR Scope',
        'SNR Alert Count',
        'SNR Last Updated',
        'Legacy Signal to Noise',
        'Hedwig Team',
        'Hedwig Teams',
        'Hedwig Services'
      ];

      const csvData = dataToExport.map(app => {
        const healthScore = getHealthScore(app);
        const incidentIds = getIncidentIdsForChangeApp(app.appId);
        return [
          app.appId,
          app.platformPortfolio,
          app.platform,
          app.application,
          app.p1p2,
          app.p3p4mi,
          app.incidentsCausedByChange,
          incidentIds.length > 0 ? incidentIds.join('; ') : 'None',
          app.goldenSignalScore !== null ? app.goldenSignalScore.toString() : 'NA',
          app.goldenSignalTotalIncidents !== null ? app.goldenSignalTotalIncidents.toString() : 'NA',
          app.goldenSignalCapturePercentage !== null ? `${app.goldenSignalCapturePercentage}%` : 'NA',
          (() => {
            const appNfrRecords = nfrData.filter(nfr => nfr.macId.includes(app.appId));
            if (appNfrRecords.length === 0) return 'No data';
            const allStatuses = appNfrRecords.flatMap(record => record.nfrStatus);
            const passedCount = allStatuses.filter(status => status.toLowerCase().includes('passed')).length;
            const failedCount = allStatuses.filter(status => status.toLowerCase().includes('failed')).length;
            if (failedCount > 0 && passedCount === 0) return `Failed - ${failedCount}`;
            if (passedCount > 0 && failedCount === 0) return `Passed - ${passedCount}`;
            if (passedCount > 0 && failedCount > 0) return `Mixed (P:${passedCount}/F:${failedCount})`;
            return `Tests: ${allStatuses.length}`;
          })(),
          (() => {
            const appNfrRecords = nfrData.filter(nfr => nfr.macId.includes(app.appId));
            return appNfrRecords.length > 0 ? appNfrRecords.length.toString() : '0';
          })(),
          (() => {
            const appNfrRecords = nfrData.filter(nfr => nfr.macId.includes(app.appId));
            if (appNfrRecords.length === 0) return 'NA';
            const latestDate = appNfrRecords.reduce((latest, record) => {
              const recordDate = new Date(record.lastTestExecutionDate);
              return recordDate > latest ? recordDate : latest;
            }, new Date(0));
            return latestDate.getTime() > 0 ? latestDate.toISOString().split('T')[0] : 'NA';
          })(),
          app.onCallRotaStatus,
          app.uniqueOnCallUsers !== null ? app.uniqueOnCallUsers.toString() : 'NA',
          app.onCallUserNames || 'NA',
          app.sloAvailabilityMtd > 0 ? `${app.sloAvailabilityMtd.toFixed(2)}%` : 'NA',
          app.sloObjective > 0 ? `${app.sloObjective}%` : 'NA',
          app.ebInMinutes > 0 ? app.ebInMinutes.toString() : 'NA',
          app.calculatedSnr !== null ? app.calculatedSnr.toString() : 'NA',
          app.snrScope || 'NA',
          app.snrAlertCount !== null ? app.snrAlertCount.toString() : 'NA',
          app.snrLastUpdated || 'NA',
          app.signalToNoise,
          app.hasHedwigTeam ? 'Yes' : 'No',
          app.hedwigTeams.length > 0 ? app.hedwigTeams.join('; ') : 'Not Found',
          app.hedwigServices.length > 0 ? app.hedwigServices.map(service => 
            `${service.serviceName}: ${service.userNames.join(', ') || 'No on-call users'}`
          ).join(' | ') : 'No services found'
        ];
      });

      const csvContent = [
        csvHeaders.join(','),
        ...csvData.map(row => 
          row.map(field => 
            typeof field === 'string' && field.includes(',') 
              ? `"${field.replace(/"/g, '""')}"` 
              : field
          ).join(',')
        )
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `portfolio-health-${portfolioName.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting to CSV:', error);
    }
  };

  // Export Postmortem Data to CSV
  const handleExportPostmortemToCSV = () => {
    try {
      // Get filtered postmortems (same logic as displayed in table)
      // Exclude DONE and INCOMPLETE statuses
      const openPostmortems = postmortemDetailsData.filter((pm: any) => 
        pm.incidentStatus && 
        pm.incidentStatus.toUpperCase() !== 'DONE' &&
        pm.incidentStatus.toUpperCase() !== 'INCOMPLETE'
      );
      
      let filteredPostmortems = openPostmortems;
      if (postmortemStatusFilter !== 'all') {
        filteredPostmortems = openPostmortems.filter((pm: any) => 
          pm.incidentStatus === postmortemStatusFilter
        );
      }
      
      // Apply search filter
      if (postmortemSearchQuery) {
        const query = postmortemSearchQuery.toLowerCase();
        filteredPostmortems = filteredPostmortems.filter((pm: any) => {
          const incidentNumber = pm.incidentID?.toLowerCase() || '';
          const impactedApp = pm.cmdb_ci?.toLowerCase() || '';
          const causingPortfolio = pm.causedBy_portfolio?.toLowerCase() || '';
          const causingApp = pm.causedBy_application?.toLowerCase() || '';
          const causingPlatform = pm.causedBy_platform?.toLowerCase() || '';
          
          return incidentNumber.includes(query) ||
                 impactedApp.includes(query) ||
                 causingPortfolio.includes(query) ||
                 causingApp.includes(query) ||
                 causingPlatform.includes(query);
        });
      }
      
      const portfolioName = selectedPortfolio || 'All Portfolios';
      
      const csvHeaders = [
        'Incident ID',
        'Priority',
        'Created On',
        'Postmortem Owner',
        'Impacted App',
        'Causing App',
        'Causing Platform',
        'Causing Portfolio',
        'Description',
        'Status'
      ];

      const csvData = filteredPostmortems.map((pm: any) => {
        // Extract incident number
        const extractIncidentNumber = (idField: string) => {
          if (!idField) return '';
          if (idField.includes('incidentID=')) {
            const match = idField.match(/incidentID=([A-Z0-9]+)/);
            return match ? match[1] : idField;
          }
          return idField;
        };
        
        return [
          extractIncidentNumber(pm.incidentID),
          pm.priority ? `P${pm.priority}` : '',
          pm.createdOn ? new Date(pm.createdOn).toLocaleDateString() : '',
          pm.assigned_to || '',
          pm.cmdb_ci || '',
          pm.causedBy_application || '',
          pm.causedBy_platform || '',
          pm.causedBy_portfolio || '',
          pm.description || '',
          pm.incidentStatus || ''
        ];
      });

      const csvContent = [
        csvHeaders.join(','),
        ...csvData.map(row => 
          row.map(field => 
            typeof field === 'string' && (field.includes(',') || field.includes('"') || field.includes('\n'))
              ? `"${field.replace(/"/g, '""')}"` 
              : field
          ).join(',')
        )
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `open-postmortems-${portfolioName.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting postmortems to CSV:', error);
    }
  };

  // Export Platform View to CSV
  const handleExportPlatformViewToCSV = () => {
    try {
      const headers = [
        'Portfolio',
        'Platform',
        'No. of Apps',
        'Fully Certified',
        'Partially Certified',
        'Not Certified',
        'Overall Certification %',
        'Overall Completeness %'
      ];
      
      const rows = platformViewData.map(platform => [
        platform.portfolio,
        platform.platform,
        platform.totalApps.toString(),
        platform.fullyCertified.toString(),
        platform.partiallyCertified.toString(),
        platform.notCertified.toString(),
        platform.overallCertification.toString(),
        platform.overallCompleteness.toString()
      ]);
      
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `platform-certification-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting platform view to CSV:', error);
    }
  };

  // Export Action Items to CSV
  const handleExportActionItemsToCSV = () => {
    try {
      const today = new Date();
      const fifteenDaysFromNow = new Date();
      fifteenDaysFromNow.setDate(today.getDate() + 15);
      
      // Get all open action items from all postmortems
      const allOpenActionItems: any[] = [];
      postmortemDetailsData.forEach((pm: any) => {
        if (pm.actionDetails && Array.isArray(pm.actionDetails)) {
          pm.actionDetails.forEach((action: any) => {
            // Filter by portfolio if selected (case-insensitive)
            const actionPortfolio = (action.portfolio || '').toLowerCase();
            const selectedPortfolioLower = (selectedPortfolio || '').toLowerCase();
            if (!selectedPortfolio || selectedPortfolio === 'all' || actionPortfolio === selectedPortfolioLower) {
              // Only include OPEN action items (status not "done" and not "closed")
              const actionStatus = action.status ? action.status.toLowerCase() : '';
              if (actionStatus !== 'done' && actionStatus !== 'closed' && actionStatus !== '3') {
                allOpenActionItems.push({
                  ...action,
                  incidentID: pm.incidentID,
                  customID: pm.customID,
                  causedBy_application: pm.causedBy_application || ''
                });
              }
            }
          });
        }
      });
      
      // Apply due date filter
      let filteredActionItems = allOpenActionItems;
      if (actionItemsDueDateFilter !== 'all') {
        filteredActionItems = allOpenActionItems.filter((action: any) => {
          if (!action.dueDate) return false;
          
          try {
            let dueDate: Date | null = null;
            
            if (action.dueDate.includes('-')) {
              const parts = action.dueDate.split('-');
              if (parts.length === 3) {
                if (parts[0].length === 4) {
                  dueDate = new Date(action.dueDate);
                } else {
                  const [day, month, year] = parts;
                  dueDate = new Date(`${year}-${month}-${day}`);
                }
              }
            }
            
            if (dueDate && !isNaN(dueDate.getTime())) {
              if (actionItemsDueDateFilter === 'within15') {
                return dueDate >= today && dueDate <= fifteenDaysFromNow;
              } else if (actionItemsDueDateFilter === 'after15') {
                return dueDate > fifteenDaysFromNow;
              } else if (actionItemsDueDateFilter === 'pastdue') {
                return dueDate < today;
              }
            }
          } catch (error) {
            return false;
          }
          return false;
        });
      }
      
      // Apply category filter
      if (actionItemsCategoryFilter !== 'all') {
        filteredActionItems = filteredActionItems.filter((action: any) => {
          const { category } = categorizeAction(action.description || '');
          return category === actionItemsCategoryFilter;
        });
      }
      
      // Apply search filter
      if (actionItemsSearchQuery) {
        const query = actionItemsSearchQuery.toLowerCase();
        filteredActionItems = filteredActionItems.filter((action: any) => {
          const incidentID = action.incidentID?.toLowerCase() || '';
          const actionID = Array.isArray(action.id) 
            ? action.id.join(' ').toLowerCase() 
            : (action.id?.toLowerCase() || '');
          const portfolio = action.portfolio?.toLowerCase() || '';
          
          return incidentID.includes(query) ||
                 actionID.includes(query) ||
                 portfolio.includes(query);
        });
      }
      
      const portfolioName = selectedPortfolio || 'All Portfolios';
      
      const csvHeaders = [
        'Incident ID',
        'Action ID',
        'Assigned To',
        'Action Description',
        'Category',
        'Caused By Application',
        'Action Due Date',
        'Action Status'
      ];

      const csvData = filteredActionItems.map((action: any) => {
        const extractIncidentNumber = (idField: string) => {
          if (!idField) return '';
          if (idField.includes('incidentID=')) {
            const match = idField.match(/incidentID=([A-Z0-9]+)/);
            return match ? match[1] : idField;
          }
          return idField;
        };
        
        // Handle action ID (can be array or string)
        const actionID = Array.isArray(action.id) 
          ? action.id.join(', ') 
          : (action.id || '');
        
        // Get category
        const { category } = categorizeAction(action.description || '');
        
        return [
          extractIncidentNumber(action.incidentID),
          actionID,
          action.assignee || '',
          action.description || '',
          category,
          action.causedBy_application || '',
          action.dueDate || '',
          action.status || ''
        ];
      });

      const csvContent = [
        csvHeaders.join(','),
        ...csvData.map(row => 
          row.map(field => 
            typeof field === 'string' && (field.includes(',') || field.includes('"') || field.includes('\n'))
              ? `"${field.replace(/"/g, '""')}"` 
              : field
          ).join(',')
        )
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `open-action-items-${portfolioName.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting action items to CSV:', error);
    }
  };

  // Share Link function - generates URL with current selections
  const handleShareLink = () => {
    try {
      const baseUrl = window.location.origin + window.location.pathname;
      const params = new URLSearchParams();
      
      // Add current selections to URL params
      if (selectedPortfolio) {
        params.set('portfolio', selectedPortfolio);
      }
      if (selectedPlatform) {
        params.set('platform', selectedPlatform);
      }
      if (selectedTimeRange) {
        params.set('timeRange', selectedTimeRange);
      }
      if (selectedTimeRange === 'custom' && customDateRange.start && customDateRange.end) {
        params.set('startDate', customDateRange.start);
        params.set('endDate', customDateRange.end);
      }
      
      const shareUrl = `${baseUrl}?${params.toString()}`;
      
      // Copy to clipboard
      navigator.clipboard.writeText(shareUrl).then(() => {
        setShareLinkCopied(true);
        setTimeout(() => setShareLinkCopied(false), 3000); // Hide after 3 seconds
      }).catch((err) => {
        console.error('Failed to copy link:', err);
        // Fallback: show the link in a prompt
        prompt('Copy this link:', shareUrl);
      });
    } catch (error) {
      console.error('Error generating share link:', error);
    }
  };

  // PDF Export function for Full Page Dashboard
  const handleExportFullPageToPDF = async () => {
    try {
      const dashboardElement = document.querySelector('[data-dashboard-root]') as HTMLElement;
      if (!dashboardElement) {
        console.error('Dashboard root element not found');
        return;
      }

      const portfolioName = selectedPortfolio || 'All Portfolios';
      
      await PDFExportService.exportFullPageToPDF(dashboardElement, {
        filename: `portfolio-health-dashboard-${portfolioName.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`
      });

    } catch (error) {
      console.error('Error exporting full page to PDF:', error);
      // You could add a toast notification here if you have one
    }
  };  // Helper function to get P1/P2 incident counts for an app
  const getIncidentCounts = (appId: string): { p1: number; p2: number; p3p4mi: number } => {
    const appIncidents = incidentData.filter(incident => incident.cmdb_ci_u_application_id === appId);
    
    const p1Count = appIncidents.filter(incident => incident.priority === '1').length;
    const p2Count = appIncidents.filter(incident => incident.priority === '2').length;
    
    // Get P3/P4 incidents from the dedicated P3/P4 API - only for this specific app
    const appP3P4Incidents = p3p4IncidentData.filter(incident => 
      incident.cmdb_ci_u_application_id === appId && 
      (incident.priority === '3' || incident.priority === '4')
    );
    const p3p4Count = appP3P4Incidents.length;
    
    // Debug logging for specific apps to see what's happening
    if (appId === 'A0338' || appId === 'A5179' || appId === 'A0407') {
      console.log(`🔍 Debug for ${appId}:`, {
        p3p4IncidentsForApp: appP3P4Incidents.length,
        allP3P4WithThisAppId: p3p4IncidentData.filter(inc => inc.cmdb_ci_u_application_id === appId).length,
        sampleP3P4Records: p3p4IncidentData.filter(inc => inc.cmdb_ci_u_application_id === appId).slice(0, 3)
      });
    }
    
    return {
      p1: p1Count,
      p2: p2Count,
      p3p4mi: p3p4Count
    };
  };

  // Helper function to get incident caused by change for an app (check both P1/P2 and P3/P4 data)
  const getIncidentsDueToChange = (appId: string): number => {
    const appIncidents = incidentData.filter(incident => incident.cmdb_ci_u_application_id === appId);
    const appP3P4Incidents = p3p4IncidentData.filter(incident => incident.cmdb_ci_u_application_id === appId);
    
    const p1p2ChangeIncidents = appIncidents.filter(incident => 
      incident.caused_by_display_value && 
      (incident.caused_by_display_value.toUpperCase().startsWith('CHG') || incident.caused_by_display_value === 'Change')
    );
    
    const p3p4ChangeIncidents = appP3P4Incidents.filter(incident => 
      incident.caused_by_display_value && 
      (incident.caused_by_display_value.toUpperCase().startsWith('CHG') || incident.caused_by_display_value === 'Change')
    );
    
    // Debug log to verify data
    if (p1p2ChangeIncidents.length > 0 || p3p4ChangeIncidents.length > 0) {
      console.log(`📊 Incidents caused by change for app ${appId}:`, {
        p1p2: p1p2ChangeIncidents.map(i => ({ number: i.number, caused_by: i.caused_by_display_value })),
        p3p4: p3p4ChangeIncidents.map(i => ({ number: i.number, caused_by: i.caused_by_display_value })),
        total: p1p2ChangeIncidents.length + p3p4ChangeIncidents.length
      });
    }
    
    return p1p2ChangeIncidents.length + p3p4ChangeIncidents.length;
  };

  // Helper function to get incident IDs for incidents caused by change
  const getIncidentIdsForChangeApp = (appId: string): string[] => {
    const appIncidents = incidentData.filter(incident => incident.cmdb_ci_u_application_id === appId);
    const appP3P4Incidents = p3p4IncidentData.filter(incident => incident.cmdb_ci_u_application_id === appId);
    
    const p1p2IncidentNumbers = appIncidents
      .filter(incident => incident.caused_by_display_value && 
        (incident.caused_by_display_value.toUpperCase().startsWith('CHG') || incident.caused_by_display_value === 'Change'))
      .map(incident => incident.number);
    
    const p3p4IncidentNumbers = appP3P4Incidents
      .filter(incident => incident.caused_by_display_value && 
        (incident.caused_by_display_value.toUpperCase().startsWith('CHG') || incident.caused_by_display_value === 'Change'))
      .map(incident => incident.number);
    
    // Return all incident IDs (no deduplication needed as incident numbers are unique)
    return [...p1p2IncidentNumbers, ...p3p4IncidentNumbers];
  };

  // Helper function to get incidents via alerts for an app (check both P1/P2 and P3/P4 data)
  const getIncidentsViaAlerts = (appId: string): number => {
    const appIncidents = incidentData.filter(incident => incident.cmdb_ci_u_application_id === appId);
    const appP3P4Incidents = p3p4IncidentData.filter(incident => incident.cmdb_ci_u_application_id === appId);
    
    const p1p2AlertIncidents = appIncidents.filter(incident => 
      incident.contact_type_display_value && 
      incident.contact_type_display_value.toLowerCase() === 'alert'
    ).length;
    
    const p3p4AlertIncidents = appP3P4Incidents.filter(incident => 
      incident.contact_type_display_value && 
      incident.contact_type_display_value.toLowerCase() === 'alert'
    ).length;
    
    return p1p2AlertIncidents + p3p4AlertIncidents;
  };

  // Get MTTR for an incident in minutes (converts from seconds in API)
  const getMTTRMinutes = (incidentNumber: string): number => {
    const mttrSeconds = mttrData.get(incidentNumber);
    if (!mttrSeconds || isNaN(mttrSeconds)) return 0;
    return Math.round(mttrSeconds / 60); // Convert seconds to minutes
  };

  // Get MTTD for an incident in minutes (converts from seconds in API)
  const getMTTDMinutes = (incidentNumber: string): number => {
    const mttdSeconds = mttdData.get(incidentNumber);
    if (!mttdSeconds || isNaN(mttdSeconds)) return 0;
    return Math.round(mttdSeconds / 60); // Convert seconds to minutes
  };

  // Get outage duration from incident in minutes (from outage_duration field in API)
  const getOutageDurationMinutes = (incident: IncidentApiResponse): number => {
    if (!incident.outage_duration) return 0;
    const duration = parseInt(incident.outage_duration, 10);
    if (isNaN(duration)) return 0;
    return duration; // Already in minutes
  };

  // Helper function to calculate total outage duration for an app from P1/P2 incidents
  const getOutageDuration = (appId: string): number => {
    const appIncidents = incidentData.filter(incident => 
      incident.cmdb_ci_u_application_id === appId &&
      (incident.priority === '1' || incident.priority === '2')
    );
    
    const totalMinutes = appIncidents.reduce((sum, incident) => {
      const duration = getOutageDurationMinutes(incident);
      return sum + duration;
    }, 0);
    
    return totalMinutes;
  };

  // Helper function to categorize action items based on description keywords
  const categorizeAction = (description: string): { category: string; color: string } => {
    const descLower = description.toLowerCase();
    
    // Define category keywords with priority order
    const categories = [
      {
        name: 'Monitoring, Alerting & Observability',
        keywords: ['monitor', 'alert', 'observability', 'grafana', 'dashboard', 'synthetic test', 'probe', 'metrics', 'tracking', 'visibility'],
        color: '#1976d2' // Blue
      },
      {
        name: 'Database Performance & Optimization',
        keywords: ['database', 'db', 'query', 'sql', 'index', 'archival', 'stored procedure', 'dba', 'oracle', 'table', 'backup', 'restoration'],
        color: '#7b1fa2' // Purple
      },
      {
        name: 'Automation & Infrastructure Resilience',
        keywords: ['automat', 'script', 'cluster', 'infrastructure', 'resilience', 'recovery', 'hpa', 'pod', 'deployment', 'migration', 'temporal', 'autotron'],
        color: '#388e3c' // Green
      },
      {
        name: 'Change Management, Testing & Governance',
        keywords: ['change management', 'deployment', 'testing', 'validation', 'rollback', 'peer review', 'approval', 'sign-off', 'governance', 'pre-production', 'phased'],
        color: '#f57c00' // Orange
      },
      {
        name: 'Documentation, Training & Process Improvement',
        keywords: ['document', 'runbook', 'sop', 'training', 'user guideline', 'knowledge', 'process improvement', 'educate', 'rca portal', 'handover'],
        color: '#c2185b' // Pink
      }
    ];
    
    // Score each category
    let maxScore = 0;
    let selectedCategory = { category: 'Uncategorized', color: '#757575' }; // Grey for uncategorized
    
    categories.forEach(category => {
      let score = 0;
      category.keywords.forEach(keyword => {
        if (descLower.includes(keyword)) {
          score++;
        }
      });
      
      if (score > maxScore) {
        maxScore = score;
        selectedCategory = { category: category.name, color: category.color };
      }
    });
    
    return selectedCategory;
  };

  // Helper function to calculate availability percentage based on outage duration
  const calculateAvailabilityPercentage = (outageDurationMinutes: number): number => {
    // Get actual date range from getDateRange function
    const dateRange = getDateRange(selectedTimeRange);
    const startDate = new Date(dateRange.start_time);
    const endDate = new Date(dateRange.end_time);
    
    // Calculate actual duration in minutes
    const totalPeriodMinutes = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60));
    const uptimeMinutes = totalPeriodMinutes - outageDurationMinutes;
    const availability = totalPeriodMinutes > 0 ? (uptimeMinutes / totalPeriodMinutes) * 100 : 100;
    
    return Math.max(0, Math.min(100, availability)); // Ensure it's between 0-100%
  };

  // Helper function to get Full Stack Monitoring breakdown
  const getFullStackMonitoringBreakdown = (appId: string): string => {
    const appData = apiData.find(api => api.app_id === appId);
    if (!appData) return 'No data available';
    
    const components = [
      `DB: ${appData.crawl_completed_for_db || 'NA'}`,
      `Infra: ${appData.crawl_completed_for_infra || 'NA'}`,
      `MW: ${appData.crawl_completed_for_mw || 'NA'}`,
      `Network: ${appData.crawl_completed_for_network || 'NA'}`
    ];
    
    return components.join(', ');
  };

  // Helper function to format SNR display with tooltip
  const getSnrDisplayInfo = (app: PortfolioApp) => {
    if (app.calculatedSnr !== null && app.calculatedSnr !== undefined) {
      const snrValue = typeof app.calculatedSnr === 'string' ? parseFloat(app.calculatedSnr) : app.calculatedSnr;
      if (isNaN(snrValue)) {
        // If it's not a valid number, fall back to legacy
        const label = app.signalToNoise;
        const tooltip = 'Legacy SNR data - upgrade to calculated SNR for detailed metrics';
        const color: 'success' | 'warning' | 'error' = parseFloat(app.signalToNoise.replace('%', '')) > 50 ? 'success' : 'warning';
        return { label, tooltip, color };
      }
      const percentage = snrValue.toFixed(1);
      const label = `${percentage}%`;
      const tooltip = `SNR: ${percentage}%\nScope: ${app.snrScope || 'N/A'}\nAlerts: ${app.snrAlertCount || 0}\nLast Updated: ${app.snrLastUpdated ? new Date(app.snrLastUpdated).toLocaleDateString() : 'N/A'}`;
      
      // Color coding based on SNR quality: green if >60%, orange if ≤60%
      let color: 'success' | 'warning' | 'error' = snrValue > 60 ? 'success' : 'warning';
      
      return { label, tooltip, color };
    } else {
      // Handle cases where SNR is 0, null, or blank
      let tooltip = '';
      let label = 'N/A';
      let color: 'success' | 'warning' | 'error' = 'warning';

      if (!app.snrScope || app.snrScope === '' || app.snrScope === null) {
        // Case 1: No scopes tagged to that MAC ID
        tooltip = 'No scopes tagged to this application - SNR analysis not available';
        label = 'No Scope';
        color = 'error';
      } else {
        // Case 2: Scope exists but SNR is null/blank (no alerts fired)
        tooltip = `Scope tagged (${app.snrScope}) but no alerts fired - SNR cannot be calculated`;
        label = 'No Alerts';
        color = 'warning';
      }

      return { label, tooltip, color };
    }
  };

  // Helper function to get health chip color
  const getHealthChipColor = (health: string): 'success' | 'warning' | 'error' | 'default' => {
    const healthLower = health.toLowerCase();
    if (healthLower === 'excellent' || healthLower === 'good') return 'success';
    if (healthLower === 'fair' || healthLower === 'moderate') return 'warning';
    if (healthLower === 'poor' || healthLower === 'critical') return 'error';
    return 'default';
  };

  // Export AI Insights to PDF
  const handleExportAIInsightsToPDF = async (insightsData: AIInsightsResponse) => {
    try {
      const portfolioName = selectedPortfolio || 'All Portfolios';
      
      // Create a temporary div for the AI insights content
      const tempDiv = document.createElement('div');
      tempDiv.style.padding = '20px';
      tempDiv.style.fontFamily = 'Arial, sans-serif';
      
      tempDiv.innerHTML = `
        <h1 style="color: #1565c0; margin-bottom: 20px;">AI Insights - Portfolio Health Assessment</h1>
        
        <h2 style="color: #2c3e50; margin-top: 30px;">Analysis Scope</h2>
        <p><strong>Applications Analyzed:</strong> ${insightsData.data.analysis_scope.applications_in_scope.length}</p>
        <p><strong>Overall Health Score:</strong> ${insightsData.data.health_assessment.overall_health_score.toFixed(1)}%</p>
        
        <h2 style="color: #2c3e50; margin-top: 30px;">Health Assessment</h2>
        <p><strong>P1/P2 Health:</strong> ${insightsData.data.health_assessment.p1_p2_health}</p>
        <p><strong>P3/P4 Health:</strong> ${insightsData.data.health_assessment.p3_p4_health}</p>
        
        ${insightsData.data.health_assessment.key_concerns.length > 0 ? `
        <h2 style="color: #2c3e50; margin-top: 30px;">Key Concerns</h2>
        <ul>
          ${insightsData.data.health_assessment.key_concerns.map(concern => `<li>${concern}</li>`).join('')}
        </ul>
        ` : ''}
        
        <h2 style="color: #2c3e50; margin-top: 30px;">Portfolio Health Report</h2>
        <div style="background-color: #f8f9fa; padding: 15px; white-space: pre-wrap; font-family: monospace; line-height: 1.6;">
          ${insightsData.data.health_assessment.portfolio_health_report}
        </div>
        
        <p style="margin-top: 30px; color: #666; font-size: 12px;">
          Generated on: ${new Date(insightsData.data.health_assessment.analysis_timestamp).toLocaleString()}
        </p>
      `;
      
      document.body.appendChild(tempDiv);
      
      await PDFExportService.exportFullPageToPDF(tempDiv, {
        filename: `ai-insights-${portfolioName.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`
      });
      
      document.body.removeChild(tempDiv);
      
    } catch (error) {
      console.error('Error exporting AI insights to PDF:', error);
    }
  };

  // Convert API data to PortfolioApp format
  // Note: All apps in apiData are MVC apps
  const portfolioApps: PortfolioApp[] = apiData.map((item) => {
    // Find matching SLO data for this app
    const sloInfo = sloData.find(slo => slo.app_id === item.app_id);
    
    // Debug SLO matching (only log first 3 apps to avoid spam)
    if (apiData.indexOf(item) < 3) {
      console.log(`🔍 App ${item.app_id} (${item.application}):`, {
        hasSloData: !!sloInfo,
        sloAvailability: sloInfo?.average_availability || sloInfo?.availability,
        sloInfo: sloInfo
      });
    }
    
    // Get incident counts for this app
    const incidentCounts = getIncidentCounts(item.app_id);

    // Find matching Hedwig data for this app
    // Helper function to convert team name to team ID for URL
    const getTeamId = (teamName: string): string => {
      return teamName.toLowerCase()
        .replace(/^(amps\s+|maersk\s+)/i, '') // Remove common company prefixes
        .replace(/[\s-]+/g, '')  // Remove spaces and dashes
        .replace(/[^a-z0-9]/g, ''); // Remove any non-alphanumeric characters
    };

    const hedwigInfo = hedwigData.find((hedwig: any) => hedwig.app_id === item.app_id);
    const hedwigTeams = hedwigInfo?.teams_data ? 
      (Array.isArray(hedwigInfo.teams_data) ? hedwigInfo.teams_data : JSON.parse(hedwigInfo.teams_data || '[]')) : [];
    const teamNames = hedwigTeams.map((team: any) => team.name || team.teamName || 'Unknown').filter(Boolean);
    const teamIds = teamNames.map((teamName: string) => getTeamId(teamName));
    
    // Process Hedwig services data
    const hedwigServicesData = hedwigInfo?.services_data ? 
      (Array.isArray(hedwigInfo.services_data) ? hedwigInfo.services_data : JSON.parse(hedwigInfo.services_data || '[]')) : [];
    const processedServices = hedwigServicesData.map((service: any) => {
      const onCallUserDetails = service.full_data?.onCallUserDetails || [];
      return {
        serviceName: service.serviceName || service.full_data?.name || 'Unknown Service',
        onCallUsers: service.onCallUsers || [],
        userNames: onCallUserDetails.map((user: any) => user.userName || user.emailId || 'Unknown User')
      };
    });
    
    // Calculate Full Stack Monitoring score based on crawl completion fields
    const calculateFullStackMonitoring = (item: PortfolioApiResponse): number => {
      const crawlFields = [
        item.crawl_completed_for_db,
        item.crawl_completed_for_infra,
        item.crawl_completed_for_mw,
        item.crawl_completed_for_network
      ];
      
      let score = 0;
      crawlFields.forEach(field => {
        if (field === 'Yes' || field === 'NA') score += 1;
        // 'No' = 0, null/undefined = 0
      });
      
      return score;
    };
    
    // Calculate Golden Signal data for this app based on P1/P2 incidents
    const goldenSignalInfo = getGoldenSignalData(item.app_id);
    
    // Get On Call Rota status
    const onCallStatus = hedwigInfo?.has_hedwig_team 
      ? (hedwigInfo.unique_oncall_users > 0 ? 'Yes' : 'No Service')
      : 'No';
    
    // Debug log for troubleshooting (can be removed after verification)
    if (hedwigInfo && hedwigInfo.unique_oncall_users > 0) {
      console.log(`✅ On-Call Rota found for ${item.application} (${item.app_id}):`, {
        has_hedwig_team: hedwigInfo.has_hedwig_team,
        unique_oncall_users: hedwigInfo.unique_oncall_users,
        oncall_user_names: hedwigInfo.oncall_user_names,
        status: onCallStatus
      });
    }
    
    return {
      appId: item.app_id,
      platformPortfolio: item.platform_portfolio,
      platform: item.platform,
      application: item.application,
      appType: item.app_type || '',
      nfr: item.nfr || 'NA',
      fullStackMonitoring: calculateFullStackMonitoring(item),
      signalToNoise: item.signal_to_noise_ratio || 'NA',
      calculatedSnr: item.calculated_snr,
      snrScope: item.snr_scope,
      snrAlertCount: item.snr_alert_count,
      snrLastUpdated: item.snr_last_updated,
      snrLink: item.snr_link,
      hedwigAlertAnalysisLink: 'Link', // Mock value - will be removed
      incidentViaAlerts: getIncidentsViaAlerts(item.app_id).toString(),
      // Use real incident data from P1/P2 API
      p1p2: incidentCounts.p1 + incidentCounts.p2,
      p3p4mi: incidentCounts.p3p4mi,
      incidentsCausedByChange: getIncidentsDueToChange(item.app_id).toString(),
      // Calculate total outage duration from P1/P2 incidents
      outageDuration: getOutageDuration(item.app_id),
      // Use SLO API data if available, otherwise mark as NA (0 will be displayed as NA in UI)
      sloAvailabilityMtd: sloInfo ? (
        typeof sloInfo.average_availability === 'number' ? sloInfo.average_availability :
        typeof (sloInfo as any).availability === 'number' ? (sloInfo as any).availability :
        parseFloat((sloInfo.average_availability || (sloInfo as any).availability || 0).toString())
      ) : 0,
      sloObjective: sloInfo ? (
        typeof sloInfo.slo_objective === 'number' ? sloInfo.slo_objective :
        parseFloat((sloInfo.slo_objective || 0).toString())
      ) : 0,
      ebInMinutes: sloInfo ? (
        typeof sloInfo.error_budget_minutes === 'number' ? sloInfo.error_budget_minutes :
        parseFloat((sloInfo.error_budget_minutes || 0).toString())
      ) : 0,
      // Golden Signal fields
      goldenSignalScore: goldenSignalInfo?.score ?? null,
      goldenSignalTotalIncidents: goldenSignalInfo?.totalIncidents ?? null,
      goldenSignalCapturePercentage: goldenSignalInfo?.capturePercentage ?? null,
      // Hedwig Team fields
      hasHedwigTeam: hedwigInfo?.has_hedwig_team === true || hedwigInfo?.has_hedwig_team === 'true',
      hedwigTeams: teamNames,
      hedwigTeamIds: teamIds,
      hedwigServices: processedServices,
      // On Call Rota fields
      onCallRotaStatus: onCallStatus,
      onCallUserNames: hedwigInfo?.oncall_user_names || null,
      uniqueOnCallUsers: hedwigInfo?.unique_oncall_users || null
    };
  });

  // Helper functions to calculate alert capture percentages
  const calculateP1AlertCapturePercentage = (): number => {
    const p1Incidents = incidentData.filter(inc => {
      const priorityMatch = inc.priority === '1';
      const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || inc.portfolio_name === selectedPortfolio;
      const platformMatch = !selectedPlatform || selectedPlatform === 'all' || inc.platform_name === selectedPlatform;
      return priorityMatch && portfolioMatch && platformMatch;
    });
    
    if (p1Incidents.length === 0) return 0;
    
    const p1AlertIncidents = p1Incidents.filter(incident => 
      incident.contact_type_display_value && 
      incident.contact_type_display_value.toLowerCase() === 'alert'
    );
    
    return Math.round((p1AlertIncidents.length / p1Incidents.length) * 100);
  };

  const calculateP1P2AlertCapturePercentage = (): number => {
    const p1p2Incidents = incidentData.filter(inc => {
      const priorityMatch = inc.priority === '1' || inc.priority === '2';
      const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || inc.portfolio_name === selectedPortfolio;
      const platformMatch = !selectedPlatform || selectedPlatform === 'all' || inc.platform_name === selectedPlatform;
      return priorityMatch && portfolioMatch && platformMatch;
    });
    
    if (p1p2Incidents.length === 0) return 0;
    
    const p1p2AlertIncidents = p1p2Incidents.filter(incident => 
      incident.contact_type_display_value && 
      incident.contact_type_display_value.toLowerCase() === 'alert'
    );
    
    return Math.round((p1p2AlertIncidents.length / p1p2Incidents.length) * 100);
  };

  const calculateP2AlertCapturePercentage = (): number => {
    const p2Incidents = incidentData.filter(inc => {
      const priorityMatch = inc.priority === '2';
      const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || inc.portfolio_name === selectedPortfolio;
      const platformMatch = !selectedPlatform || selectedPlatform === 'all' || inc.platform_name === selectedPlatform;
      return priorityMatch && portfolioMatch && platformMatch;
    });
    
    if (p2Incidents.length === 0) return 0;
    
    const p2AlertIncidents = p2Incidents.filter(incident => 
      incident.contact_type_display_value && 
      incident.contact_type_display_value.toLowerCase() === 'alert'
    );
    
    return Math.round((p2AlertIncidents.length / p2Incidents.length) * 100);
  };

  const calculateP3P4AlertCapturePercentage = (): number => {
    const p3p4Incidents = p3p4IncidentData.filter(inc => {
      const priorityMatch = inc.priority === '3' || inc.priority === '4';
      const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || inc.portfolio_name === selectedPortfolio;
      const platformMatch = !selectedPlatform || selectedPlatform === 'all' || inc.platform_name === selectedPlatform;
      return priorityMatch && portfolioMatch && platformMatch;
    });
    
    if (p3p4Incidents.length === 0) return 0;
    
    const p3p4AlertIncidents = p3p4Incidents.filter(incident => 
      incident.contact_type_display_value && 
      incident.contact_type_display_value.toLowerCase() === 'alert'
    );
    
    return Math.round((p3p4AlertIncidents.length / p3p4Incidents.length) * 100);
  };

  // Helper functions to calculate percentage of incidents caused by change
  const calculateP1P2CausedByChangePercentage = (): number => {
    // Get all P1 and P2 incidents
    const p1p2Incidents = incidentData.filter(inc => {
      const priorityMatch = inc.priority === '1' || inc.priority === '2';
      const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || inc.portfolio_name === selectedPortfolio;
      const platformMatch = !selectedPlatform || selectedPlatform === 'all' || inc.platform_name === selectedPlatform;
      return priorityMatch && portfolioMatch && platformMatch;
    });
    
    if (p1p2Incidents.length === 0) return 0;
    
    // Count how many P1/P2 incidents were caused by change
    const p1p2ChangeIncidents = p1p2Incidents.filter(incident => 
      incident.caused_by_display_value && 
      incident.caused_by_display_value.startsWith('CHG')
    );
    
    return Math.round((p1p2ChangeIncidents.length / p1p2Incidents.length) * 100);
  };

  const calculateP1CausedByChangePercentage = (): number => {
    const p1Incidents = incidentData.filter(inc => {
      const priorityMatch = inc.priority === '1';
      const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || inc.portfolio_name === selectedPortfolio;
      const platformMatch = !selectedPlatform || selectedPlatform === 'all' || inc.platform_name === selectedPlatform;
      return priorityMatch && portfolioMatch && platformMatch;
    });
    
    if (p1Incidents.length === 0) return 0;
    
    const p1ChangeIncidents = p1Incidents.filter(incident => 
      incident.caused_by_display_value && 
      incident.caused_by_display_value.startsWith('CHG')
    );
    
    return Math.round((p1ChangeIncidents.length / p1Incidents.length) * 100);
  };

  const calculateP2CausedByChangePercentage = (): number => {
    const p2Incidents = incidentData.filter(inc => {
      const priorityMatch = inc.priority === '2';
      const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || inc.portfolio_name === selectedPortfolio;
      const platformMatch = !selectedPlatform || selectedPlatform === 'all' || inc.platform_name === selectedPlatform;
      return priorityMatch && portfolioMatch && platformMatch;
    });
    
    if (p2Incidents.length === 0) return 0;
    
    const p2ChangeIncidents = p2Incidents.filter(incident => 
      incident.caused_by_display_value && 
      incident.caused_by_display_value.startsWith('CHG')
    );
    
    return Math.round((p2ChangeIncidents.length / p2Incidents.length) * 100);
  };

  const calculateP3P4CausedByChangePercentage = (): number => {
    const p3p4Incidents = p3p4IncidentData.filter(inc => {
      const priorityMatch = inc.priority === '3' || inc.priority === '4';
      const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || inc.portfolio_name === selectedPortfolio;
      const platformMatch = !selectedPlatform || selectedPlatform === 'all' || inc.platform_name === selectedPlatform;
      return priorityMatch && portfolioMatch && platformMatch;
    });
    
    if (p3p4Incidents.length === 0) return 0;
    
    const p3p4ChangeIncidents = p3p4Incidents.filter(incident => 
      incident.caused_by_display_value && 
      incident.caused_by_display_value.startsWith('CHG')
    );
    
    return Math.round((p3p4ChangeIncidents.length / p3p4Incidents.length) * 100);
  };

  // Calculate filtered incident counts for P1/P2 and P3/P4
  const filteredP1P2Count = incidentData.filter(inc => {
    const priorityMatch = inc.priority === '1' || inc.priority === '2';
    const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || inc.portfolio_name === selectedPortfolio;
    const platformMatch = !selectedPlatform || selectedPlatform === 'all' || inc.platform_name === selectedPlatform;
    return priorityMatch && portfolioMatch && platformMatch;
  }).length;

  const filteredP3P4Count = p3p4IncidentData.filter(inc => {
    const priorityMatch = inc.priority === '3' || inc.priority === '4';
    const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || inc.portfolio_name === selectedPortfolio;
    const platformMatch = !selectedPlatform || selectedPlatform === 'all' || inc.platform_name === selectedPlatform;
    return priorityMatch && portfolioMatch && platformMatch;
  }).length;

  const aiInsights: AIInsight[] = [
    {
      type: fullStackProgressError ? 'warning' : 'success',
      title: 'Reliability Assessment Summary',
      description: fullStackProgressError
        ? `Error: ${fullStackProgressError}`
        : fullStackProgress
          ? (() => {
              return `• **Reliability Assessment:** Crawl ${fullStackProgress.crawl_percentage}%, Walk ${fullStackProgress.walk_percentage}%, Run ${fullStackProgress.run_percentage}% across ${fullStackProgress.total_apps} apps.\n• **Full Stack Monitoring Coverage:** Infrastructure – ${fullStackProgress.infra_percentage}%, Database – ${fullStackProgress.db_percentage}%, Middleware – ${fullStackProgress.mw_percentage}%, Network – ${fullStackProgress.network_percentage}%.`;
            })()
          : fullStackProgressLoading
            ? 'Loading progress...'
            : 'Tracking monitoring maturity across crawl, walk, run phases and technology stack coverage',
      action: fullStackProgress && (fullStackProgress.crawl_percentage < 100 || fullStackProgress.walk_percentage < 100 || fullStackProgress.run_percentage < 100 || fullStackProgress.infra_percentage < 100 || fullStackProgress.db_percentage < 100 || fullStackProgress.mw_percentage < 100 || fullStackProgress.network_percentage < 100)
        ? 'Complete remaining monitoring capabilities across all stacks to reach 100% full-stack observability and drive operational excellence'
        : 'Excellent progress – Sustain full-stack observability at 100% coverage, refine monitoring processes, and drive continuous operational improvement and resilience',
      confidence: fullStackProgress ? Math.round((fullStackProgress.crawl_percentage + fullStackProgress.walk_percentage + fullStackProgress.run_percentage) / 3) : 100
    },
    {
      type: 'info',
      title: 'Resiliency Assessment Summary',
      description: 'This feature is under development',
      action: '',
      confidence: 0
    },
    ...(postmortemLoading ? [
      {
        type: 'info' as 'critical' | 'warning' | 'info' | 'success',
        title: 'Postmortem Insights',
        description: 'Loading postmortem data...',
        action: '',
        confidence: 0
      }
    ] : postmortemStatus ? [
      {
        type: (postmortemStatus.totals?.closed_percentage >= 80 ? 'success' : postmortemStatus.totals?.closed_percentage >= 60 ? 'info' : 'warning') as 'critical' | 'warning' | 'info' | 'success',
        title: 'Postmortem Insights',
        description: (() => {
          // Handle both possible API response structures
          const statusCounts = postmortemStatus.by_status || postmortemStatus.data?.by_status || [];
          
          console.log('📊 Postmortem Status Counts:', statusCounts);
          console.log('🔍 UI DISPLAY - Full postmortemStatus:', postmortemStatus);
          console.log('🔍 UI DISPLAY - postmortemStatus.totals:', postmortemStatus.totals);
          
          const totalPostmortems = postmortemStatus.totals?.total_postmortems || 0;
          const totalActions = postmortemStatus.totals?.total_actions || 0;
          const openActions = postmortemStatus.totals?.total_open_actions || 0;
          const openPercentage = totalActions > 0 ? ((openActions / totalActions) * 100).toFixed(1) : '0.0';
          
          // Calculate open postmortems excluding INCOMPLETE and LTREVIEW status
          const openPostmortems = statusCounts.reduce((sum: number, s: any) => {
            return (s.status !== 'INCOMPLETE' && s.status !== 'LTREVIEW') ? sum + (s.count || 0) : sum;
          }, 0);
          
          // Get LT Review count separately
          const ltReviewCount = postmortemStatus.totals?.lt_review_postmortems || 0;
          
          // Get action items breakdown by due date
          const pastDueCount = postmortemStatus.totals?.actions_past_due || 0;
          const within15Count = postmortemStatus.totals?.actions_within_15_days || 0;
          const after15Count = postmortemStatus.totals?.actions_after_15_days || 0;
          
          let description = `• **Open Postmortems:** ${openPostmortems} total`;
          
          // Add detailed status breakdown - exclude INCOMPLETE status
          // Order: New → Progress → SRE Review → LT Review
          if (statusCounts && statusCounts.length > 0) {
            // Create a map of status counts for easy lookup
            const statusMap = new Map<string, number>();
            statusCounts.forEach((s: any) => {
              if (s.status !== 'INCOMPLETE') {
                statusMap.set(s.status, s.count || 0);
              }
            });
            
            // Define the desired order and labels (excluding LT Review)
            const orderedStatuses = [
              { key: 'NEW', label: 'New' },
              { key: 'PROGRESS', label: 'Progress' },
              { key: 'SREREVIEW', label: 'SRE Review' }
            ];
            
            const statusParts: string[] = [];
            orderedStatuses.forEach(({ key, label }) => {
              const count = statusMap.get(key);
              if (count && count > 0) {
                statusParts.push(`${label} (${count})`);
              }
            });
            
            if (statusParts.length > 0) {
              description += ` — with actions in ${statusParts.join(', ')} status`;
            }
          }
          
          // Add Ready for LT Review as separate bullet
          if (ltReviewCount > 0) {
            description += `\n• **Ready for LT Review:** ${ltReviewCount} postmortem${ltReviewCount !== 1 ? 's' : ''}`;
          }
          
          description += `\n• **Action Items:** ${openActions} open out of ${totalActions} total (${openPercentage}% pending)`;
          
          // Add breakdown by due date if there are open actions
          if (openActions > 0) {
            const breakdownParts: string[] = [];
            if (pastDueCount > 0) {
              breakdownParts.push(`${pastDueCount} past due`);
            }
            if (within15Count > 0) {
              breakdownParts.push(`${within15Count} due within 15 days`);
            }
            if (after15Count > 0) {
              breakdownParts.push(`${after15Count} due after 15 days`);
            }
            
            if (breakdownParts.length > 0) {
              description += ` — ${breakdownParts.join(', ')}`;
            }
          }
          
          console.log('📝 Postmortem Insights Description:', description);
          
          return description;
        })(),
        action: postmortemStatus.totals?.open_postmortems > 0 || postmortemStatus.totals?.total_open_actions > 0 ? 'Review and complete open P1/P2 postmortems and action items to prevent incident recurrence' : 'All P1/P2 postmortems completed and action items resolved',
        confidence: 0
      }
    ] : []),
    {
      type: 'warning',
      title: 'Top Signal-to-Noise Insights (Last 30 Days – Hedwig Alert Analysis)',
      description: snrDataLoading
        ? 'Loading SNR data...'
        : snrDataError
          ? `Error: ${snrDataError}`
          : snrData.length > 0
            ? snrData.map((app: any) => {
                const alertCount = parseInt(app.snr_alert_count);
                const quickResolved = parseInt(app.snr_quick_resolved_alerts);
                const snr = parseFloat(app.calculated_snr).toFixed(1);
                const snrLastWeek = app.snr_last_week ? parseFloat(app.snr_last_week).toFixed(1) : null;
                const snrLastMonth = app.snr_last_month ? parseFloat(app.snr_last_month).toFixed(1) : null;
                
                let trendText = '';
                if (trendPeriod === 'week' && snrLastWeek) {
                  const diff = parseFloat(snr) - parseFloat(snrLastWeek);
                  trendText = ` (${diff > 0 ? '↑' : diff < 0 ? '↓' : '→'} ${Math.abs(diff).toFixed(1)}% vs last week)`;
                } else if (trendPeriod === 'month' && snrLastMonth) {
                  const diff = parseFloat(snr) - parseFloat(snrLastMonth);
                  trendText = ` (${diff > 0 ? '↑' : diff < 0 ? '↓' : '→'} ${Math.abs(diff).toFixed(1)}% vs last month)`;
                }
                
                return `• **${app.application}** – ${alertCount} alerts: ~${quickResolved} auto-resolved within 15 minutes${quickResolved > 0 ? ', indicating ' + (parseFloat(snr) < 25 ? 'high' : parseFloat(snr) < 40 ? 'moderate' : 'elevated') + ' noise levels' : ''}; SNR ≈ ${snr}%${trendText}`;
              }).join('\n')
            : 'No applications with significant alert noise detected',
      action: snrData.length > 0
        ? 'Review and optimize alerting rules for applications with low SNR to reduce alert fatigue and improve incident detection accuracy'
        : 'Continue monitoring alert quality across portfolio applications',
      confidence: snrData.length > 0 ? Math.round(snrData.reduce((sum: number, app: any) => sum + parseFloat(app.calculated_snr), 0) / snrData.length) : 100
    }
  ];

  const healthTrends = [
    { date: '2024-01-01', availability: 99.8, incidents: 2, errorBudget: 150 },
    { date: '2024-01-02', availability: 99.9, incidents: 1, errorBudget: 145 },
    { date: '2024-01-03', availability: 99.7, incidents: 3, errorBudget: 140 },
    { date: '2024-01-04', availability: 99.85, incidents: 1, errorBudget: 138 },
    { date: '2024-01-05', availability: 99.95, incidents: 0, errorBudget: 135 },
    { date: '2024-01-06', availability: 99.6, incidents: 2, errorBudget: 130 },
    { date: '2024-01-07', availability: 99.8, incidents: 1, errorBudget: 128 }
  ];

  const getHealthScore = (app: PortfolioApp): number => {
    let score = 0;
    
    // Full stack monitoring (30% - increased from 25%)
    score += (app.fullStackMonitoring / 4) * 30;
    
    // SLO compliance (40% - increased from 30%)
    score += (app.sloAvailabilityMtd >= app.sloObjective ? 40 : (app.sloAvailabilityMtd / app.sloObjective) * 40);
    
    // Signal to noise (20%)
    let snrScore = 0;
    if (app.calculatedSnr !== null && app.calculatedSnr !== undefined) {
      const snrValue = typeof app.calculatedSnr === 'string' ? parseFloat(app.calculatedSnr) : app.calculatedSnr;
      if (!isNaN(snrValue)) {
        snrScore = snrValue / 100;
      }
    } else if (!app.snrScope || app.snrScope === '' || app.snrScope === null) {
      // No scope tagged - give neutral score
      snrScore = 0.5; // 50% for SNR when no scope is available
    } else {
      // Scope exists but no alerts - give low score
      snrScore = 0.2; // 20% for SNR when scope exists but no alerts
    }
    score += snrScore * 20;
    
    // Incidents (10% - reduced from 15%)
    score += Math.max(0, 10 - (app.p1p2 * 3 + app.p3p4mi * 1));
    
    return Math.round(Math.max(0, Math.min(100, score)));
  };

  const getHealthColor = (score: number): string => {
    if (score >= 80) return '#4caf50';
    if (score >= 60) return '#ff9800';
    return '#f44336';
  };

  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'critical': return <ErrorIcon color="error" />;
      case 'warning': return <Warning color="warning" />;
      case 'info': return <Timeline color="info" />;
      case 'success': return <CheckCircle color="success" />;
      default: return <Timeline />;
    }
  };

  const getInsightColor = (type: string) => {
    switch (type) {
      case 'critical': return 'error';
      case 'warning': return 'warning';
      case 'info': return 'info';
      case 'success': return 'success';
      default: return 'info';
    }
  };

  // Filter apps by portfolio and platform only (for MVC count)
  // Step 1: Filter by category first
  const baseApps = selectedCategory === 'mission-critical'
    ? portfolioApps.filter(a => a.appType === 'MVC' || a.appType === 'Critical Infra')
    : selectedCategory === 'business-critical'
      ? portfolioApps.filter(a => a.appType === 'Shared Service')
      : selectedCategory === 'operational'
        ? portfolioApps.filter(a => !a.appType || (a.appType !== 'MVC' && a.appType !== 'Critical Infra' && a.appType !== 'Shared Service'))
        : portfolioApps;

  // Step 1.5: Build cascading dropdown options (Portfolio → Platform → Application)
  // Platform options based on selected portfolio and category
  const platformBaseForOptions = selectedPortfolio && selectedPortfolio !== 'all'
    ? baseApps.filter(a => a.platformPortfolio === selectedPortfolio)
    : baseApps;
  
  const platformOptionsFiltered = Array.from(
    new Set(platformBaseForOptions.map(a => a.platform).filter(Boolean))
  ).sort();

  // Application options based on selected portfolio, platform, and category
  const appFilterBaseForOptions = selectedPlatform && selectedPlatform !== 'all'
    ? platformBaseForOptions.filter(a => a.platform === selectedPlatform)
    : platformBaseForOptions;
  
  const applicationOptionsFiltered = Array.from(
    new Set(appFilterBaseForOptions.map(a => a.appId).filter(Boolean))
  ).sort();

  // Step 2: Apply portfolio and platform filters
  const appsFilteredByPortfolioAndPlatform = baseApps.filter(app => {
    const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || app.platformPortfolio === selectedPortfolio;
    const platformMatch = !selectedPlatform || selectedPlatform === 'all' || app.platform === selectedPlatform;
    const applicationMatch = !selectedApplication || app.appId === selectedApplication;
    return portfolioMatch && platformMatch && applicationMatch;
  });

  // Filter apps based on selected portfolio, platform, and search query
  const filteredApps = appsFilteredByPortfolioAndPlatform.filter(app => {
    const searchMatch = searchQuery === '' || 
      app.application.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.appId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.platformPortfolio.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.platform.toLowerCase().includes(searchQuery.toLowerCase());
    return searchMatch;
  });

  // Apply sorting to filtered apps
  const sortedAndFilteredApps = sortApps(filteredApps);

  // Calculate paginated apps for the table
  const paginatedApps = sortedAndFilteredApps.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Calculate average availability only for apps that have SLO data (not 0)
  const appsWithSloData = sortedAndFilteredApps.filter(app => app.sloAvailabilityMtd > 0);
  const avgAvailability = appsWithSloData.length > 0 
    ? appsWithSloData.reduce((sum, app) => sum + app.sloAvailabilityMtd, 0) / appsWithSloData.length 
    : 0;
  const totalP1P2 = sortedAndFilteredApps.reduce((sum, app) => sum + app.p1p2, 0);
  const totalP3P4MI = sortedAndFilteredApps.reduce((sum, app) => sum + app.p3p4mi, 0);
  
  // Calculate filtered counts for selected portfolio
  const filteredAppIds = sortedAndFilteredApps.map(app => app.appId);
  const filteredSloCount = sloData.filter(slo => filteredAppIds.includes(slo.app_id)).length;
  const filteredP1P2IncidentCount = incidentData.filter(inc => filteredAppIds.includes(inc.cmdb_ci_u_application_id)).length;
  const filteredP3P4IncidentCount = p3p4IncidentData.filter(inc => filteredAppIds.includes(inc.cmdb_ci_u_application_id)).length;
  
  // Get total P3/P4 incidents from API filtered by selected portfolio
  const totalP3P4FromAPI = React.useMemo(() => {
    const filteredIncidents = p3p4IncidentData.filter(inc => {
      const priorityMatch = inc.priority === '3' || inc.priority === '4';
      const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || inc.portfolio_name === selectedPortfolio;
      const platformMatch = !selectedPlatform || selectedPlatform === 'all' || inc.platform_name === selectedPlatform;
      return priorityMatch && portfolioMatch && platformMatch;
    });
    // Return total incident count
    return filteredIncidents.length;
  }, [p3p4IncidentData, selectedPortfolio, selectedPlatform]);
  
  // Get total P1/P2 incidents from API filtered by selected portfolio
  const totalP1P2FromAPI = React.useMemo(() => {
    const filteredIncidents = incidentData.filter(inc => {
      const priorityMatch = inc.priority === '1' || inc.priority === '2';
      const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || 
        (p1p2ViewMode === 'impacted' 
          ? inc.portfolio_name === selectedPortfolio
          : inc.caused_by_portfolio === selectedPortfolio);
      const platformMatch = !selectedPlatform || selectedPlatform === 'all' || 
        (p1p2ViewMode === 'impacted'
          ? inc.platform_name === selectedPlatform
          : inc.caused_by_platform === selectedPlatform);
      return priorityMatch && portfolioMatch && platformMatch;
    });
    // Return total incident count
    return filteredIncidents.length;
  }, [incidentData, selectedPortfolio, selectedPlatform, p1p2ViewMode]);

  // Calculate total outage duration from P1/P2 incidents
  const totalOutageDuration = React.useMemo(() => {
    const filteredIncidents = incidentData.filter(inc => {
      const priorityMatch = inc.priority === '1' || inc.priority === '2';
      const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || inc.portfolio_name === selectedPortfolio;
      const platformMatch = !selectedPlatform || selectedPlatform === 'all' || inc.platform_name === selectedPlatform;
      return priorityMatch && portfolioMatch && platformMatch;
    });
    
    const totalMinutes = filteredIncidents.reduce((sum, incident) => {
      return sum + getOutageDurationMinutes(incident);
    }, 0);
    
    return totalMinutes;
  }, [incidentData, selectedPortfolio, selectedPlatform]);

  // Format outage duration for display
  const formatOutageDuration = (minutes: number): string => {
    if (minutes === 0) return '0 min';
    
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (hours > 0) {
      return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
    }
    
    return `${remainingMinutes}m`;
  };

  const p1p2SectionLoading = p1p2Loading || mttrLoading;
  const p3p4SectionLoading = p3p4Loading || mttrLoading;

  // Calculate outage durations from outage_duration field instead of MTTR
  React.useMemo(() => {
    if (incidentData.length === 0) return;
    
    const now = new Date();
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const twoWeeksAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
    const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const twoMonthsAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);
    
    // Filter P1/P2 incidents
    const p1p2Incidents = incidentData.filter(inc => 
      (inc.priority === '1' || inc.priority === '2')
    );
    
    // Calculate last week outage duration
    const lastWeekIncidents = p1p2Incidents.filter(inc => {
      const createdDate = new Date(inc.created_at);
      return createdDate >= oneWeekAgo && createdDate <= now;
    });
    const lastWeekDuration = lastWeekIncidents.reduce((sum, inc) => sum + getOutageDurationMinutes(inc), 0);
    
    // Calculate previous week outage duration
    const prevWeekIncidents = p1p2Incidents.filter(inc => {
      const createdDate = new Date(inc.created_at);
      return createdDate >= twoWeeksAgo && createdDate < oneWeekAgo;
    });
    const prevWeekDuration = prevWeekIncidents.reduce((sum, inc) => sum + getOutageDurationMinutes(inc), 0);
    
    // Calculate last month outage duration
    const lastMonthIncidents = p1p2Incidents.filter(inc => {
      const createdDate = new Date(inc.created_at);
      return createdDate >= oneMonthAgo && createdDate <= now;
    });
    const lastMonthDuration = lastMonthIncidents.reduce((sum, inc) => sum + getOutageDurationMinutes(inc), 0);
    
    // Calculate previous month outage duration
    const prevMonthIncidents = p1p2Incidents.filter(inc => {
      const createdDate = new Date(inc.created_at);
      return createdDate >= twoMonthsAgo && createdDate < oneMonthAgo;
    });
    const prevMonthDuration = prevMonthIncidents.reduce((sum, inc) => sum + getOutageDurationMinutes(inc), 0);
    
    setOutageLastWeekDuration(lastWeekDuration);
    setOutagePrevWeekDuration(prevWeekDuration);
    setOutageLastMonthDuration(lastMonthDuration);
    setOutagePrevMonthDuration(prevMonthDuration);
    
    console.log('📊 Outage durations calculated from outage_duration:', {
      lastWeek: `${lastWeekDuration}min (${lastWeekIncidents.length} incidents)`,
      prevWeek: `${prevWeekDuration}min (${prevWeekIncidents.length} incidents)`,
      lastMonth: `${lastMonthDuration}min (${lastMonthIncidents.length} incidents)`,
      prevMonth: `${prevMonthDuration}min (${prevMonthIncidents.length} incidents)`
    });
  }, [incidentData]);

  // Export to CSV function
  const exportToCSV = (data: any[], filename: string, headers: string[]) => {
    const csvContent = [
      headers.join(','),
      ...data.map(row => 
        headers.map(header => {
          const value = row[header] || '';
          // Escape quotes and wrap in quotes if contains comma
          const escaped = String(value).replace(/"/g, '""');
          return escaped.includes(',') ? `"${escaped}"` : escaped;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Calculate availability percentage based on outage duration
  const availabilityPercentage = React.useMemo(() => {
    // Get actual date range from getDateRange function
    const dateRange = getDateRange(selectedTimeRange);
    const startDate = new Date(dateRange.start_time);
    const endDate = new Date(dateRange.end_time);
    
    // Calculate actual duration in minutes
    const totalPeriodMinutes = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60));
    const uptimeMinutes = totalPeriodMinutes - totalOutageDuration;
    const availability = totalPeriodMinutes > 0 ? (uptimeMinutes / totalPeriodMinutes) * 100 : 100;
    
    return Math.max(0, Math.min(100, availability)); // Ensure it's between 0-100%
  }, [totalOutageDuration, selectedTimeRange, customDateRange, customDateApplied]);

  // Helper function to transform portfolio names
  const transformPortfolioName = (portfolio: string): string => {
    if (!portfolio) return portfolio;
    
    if (portfolio === 'Business Enabling Platforms Portfolio' || portfolio === 'Corporate Platforms Portfolio') {
      return portfolio.replace(' Portfolio', '');
    }
    
    if (portfolio === 'Managed By Maersk Platforms Portfolio' ||
        portfolio === 'Fulfilled By Maersk Platforms Portfolio' ||
        portfolio === 'Transported By Maersk Platforms Portfolio' ||
        portfolio === 'APMT, IOT and Automation Platforms Portfolio') {
      return portfolio.replace(' Platforms Portfolio', '');
    }
    
    return portfolio;
  };

  // Filter certification data based on portfolio, platform, and filters
  const filteredCertificationData = React.useMemo(() => {
    if (!certificationData || certificationData.length === 0) return [];
    
    console.log('🔍 Certification Filter Debug:', {
      totalData: certificationData.length,
      selectedPortfolio,
      selectedPlatform,
      mvcStatusFilter,
      bcaStatusFilter,
      certificationSearchQuery
    });
    
    let filtered = certificationData;
    
    // Apply portfolio filter - certification API returns raw portfolio names, transform them to match dropdown
    if (selectedPortfolio && selectedPortfolio !== 'all' && selectedPortfolio.trim() !== '') {
      filtered = filtered.filter(item => {
        // Transform the raw portfolio name from API to match the filter dropdown values
        const transformedItemPortfolio = transformPortfolioName(item.portfolio);
        return transformedItemPortfolio === selectedPortfolio;
      });
      console.log('🔍 After portfolio filter:', filtered.length);
    }
    
    // Apply platform filter
    if (selectedPlatform && selectedPlatform !== 'all' && selectedPlatform.trim() !== '') {
      filtered = filtered.filter(item => item.platform === selectedPlatform);
      console.log('🔍 After platform filter:', filtered.length);
    }
    
    // Apply MVC status filter
    if (mvcStatusFilter) {
      filtered = filtered.filter(item => item.is_mvc === true);
    }
    
    // Apply BCA status filter
    if (bcaStatusFilter) {
      filtered = filtered.filter(item => item.is_bca === true);
    }
    
    // Apply search filter
    if (certificationSearchQuery) {
      const query = certificationSearchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        item.u_application_id?.toLowerCase().includes(query) ||
        item.name?.toLowerCase().includes(query) ||
        item.portfolio?.toLowerCase().includes(query) ||
        item.platform?.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  }, [certificationData, selectedPortfolio, selectedPlatform, mvcStatusFilter, bcaStatusFilter, certificationSearchQuery]);

  // Calculate average certification percentage
  const avgCertification = React.useMemo(() => {
    if (!filteredCertificationData || filteredCertificationData.length === 0) return 0;
    
    // Group by unique application ID and calculate averages per app
    const appIdGroups = filteredCertificationData.reduce((acc: any, app) => {
      const appId = app.u_application_id;
      if (!appId) return acc;

      if (!acc[appId]) {
        acc[appId] = [];
      }
      acc[appId].push(app);
      return acc;
    }, {});

    // Calculate certification status for each unique app
    let totalCertPercentage = 0;
    let totalAppsCount = 0;

    Object.values(appIdGroups).forEach((appInstances: any) => {
      const avgCertPercentage =
        appInstances.reduce((sum: number, instance: any) => 
          sum + (instance.overall_app_id_certification_percentage || 0), 0) / appInstances.length;

      totalCertPercentage += avgCertPercentage;
      totalAppsCount++;
    });

    // Simple average: total_certification / total_apps
    return totalAppsCount > 0 ? Math.round(totalCertPercentage / totalAppsCount) : 0;
  }, [filteredCertificationData]);

  // Calculate platform view data
  const platformViewData = React.useMemo(() => {
    if (!filteredCertificationData || filteredCertificationData.length === 0) return [];
    
    // Apply platform view specific filters (MVC, BCA, search)
    let platformFilteredData = filteredCertificationData;
    
    // Apply MVC filter
    if (platformViewMvcFilter) {
      platformFilteredData = platformFilteredData.filter(item => 
        item.is_mvc === true
      );
    }
    
    // Apply BCA filter
    if (platformViewBcaFilter) {
      platformFilteredData = platformFilteredData.filter(item => 
        item.is_bca === true
      );
    }
    
    // Apply search filter
    if (platformViewSearchQuery) {
      const query = platformViewSearchQuery.toLowerCase();
      platformFilteredData = platformFilteredData.filter(item =>
        item.portfolio?.toLowerCase().includes(query) ||
        item.platform?.toLowerCase().includes(query)
      );
    }
    
    // Group by unique application ID first to get unique apps
    const appIdGroups = platformFilteredData.reduce((acc: any, app) => {
      const appId = app.u_application_id;
      if (!appId) return acc;

      if (!acc[appId]) {
        acc[appId] = [];
      }
      acc[appId].push(app);
      return acc;
    }, {});

    // Calculate average certification for each unique app
    const uniqueApps = Object.entries(appIdGroups).map(([appId, instances]: [string, any]) => {
      const avgCert = instances.reduce((sum: number, instance: any) => 
        sum + (instance.overall_app_id_certification_percentage || 0), 0) / instances.length;
      const avgComplete = instances.reduce((sum: number, instance: any) => 
        sum + (instance.overall_app_id_completeness_percentage || 0), 0) / instances.length;
      
      return {
        appId,
        portfolio: instances[0].portfolio,
        platform: instances[0].platform,
        certification: avgCert,
        completeness: avgComplete
      };
    });

    // Group by portfolio and platform
    const platformGroups = uniqueApps.reduce((acc: any, app) => {
      const key = `${app.portfolio}|${app.platform}`;
      if (!acc[key]) {
        acc[key] = {
          portfolio: app.portfolio,
          platform: app.platform,
          apps: []
        };
      }
      acc[key].apps.push(app);
      return acc;
    }, {});

    // Calculate metrics for each platform
    return Object.values(platformGroups).map((group: any) => {
      const apps = group.apps;
      const totalApps = apps.length;
      const fullyCertified = apps.filter((app: any) => app.certification === 100).length;
      const partiallyCertified = apps.filter((app: any) => app.certification > 0 && app.certification < 100).length;
      const notCertified = apps.filter((app: any) => app.certification === 0).length;
      
      const totalCert = apps.reduce((sum: number, app: any) => sum + app.certification, 0);
      const totalComplete = apps.reduce((sum: number, app: any) => sum + app.completeness, 0);
      
      return {
        portfolio: transformPortfolioName(group.portfolio),
        platform: group.platform,
        totalApps,
        fullyCertified,
        partiallyCertified,
        notCertified,
        overallCertification: Math.round(totalCert / totalApps),
        overallCompleteness: Math.round(totalComplete / totalApps)
      };
    });
  }, [filteredCertificationData, platformViewMvcFilter, platformViewBcaFilter, platformViewSearchQuery]);

  // Calculate average completeness percentage
  const avgCompleteness = React.useMemo(() => {
    if (!filteredCertificationData || filteredCertificationData.length === 0) return 0;
    
    return Math.round(
      filteredCertificationData.reduce((sum, app) => 
        sum + (app.overall_app_id_completeness_percentage || 0), 0) / filteredCertificationData.length
    );
  }, [filteredCertificationData]);

  // Calculate unique application IDs
  const uniqueCertificationAppIds = React.useMemo(() => {
    if (!filteredCertificationData || filteredCertificationData.length === 0) return new Set();
    return new Set(
      filteredCertificationData.map(app => app.u_application_id).filter(id => id)
    );
  }, [filteredCertificationData]);

  const totalCertificationApplications = uniqueCertificationAppIds.size;

  // Calculate platform view filtered data for application certification tab metrics (applies MVC/BCA filters)
  const platformViewFilteredData = React.useMemo(() => {
    if (!filteredCertificationData || filteredCertificationData.length === 0) return [];
    
    let platformFiltered = filteredCertificationData;
    
    // Apply platform view MVC filter
    if (platformViewMvcFilter) {
      platformFiltered = platformFiltered.filter(item => item.is_mvc === true);
    }
    
    // Apply platform view BCA filter
    if (platformViewBcaFilter) {
      platformFiltered = platformFiltered.filter(item => item.is_bca === true);
    }
    
    // Apply platform view search filter
    if (platformViewSearchQuery) {
      const query = platformViewSearchQuery.toLowerCase();
      platformFiltered = platformFiltered.filter(item =>
        item.portfolio?.toLowerCase().includes(query) ||
        item.platform?.toLowerCase().includes(query) ||
        item.u_application_id?.toLowerCase().includes(query) ||
        item.name?.toLowerCase().includes(query)
      );
    }
    
    return platformFiltered;
  }, [filteredCertificationData, platformViewMvcFilter, platformViewBcaFilter, platformViewSearchQuery]);

  // Calculate unique app IDs for platform view
  const uniquePlatformViewAppIds = React.useMemo(() => {
    if (!platformViewFilteredData || platformViewFilteredData.length === 0) return new Set();
    return new Set(
      platformViewFilteredData.map(app => app.u_application_id).filter(id => id)
    );
  }, [platformViewFilteredData]);

  const uniqueAppIdsCountForCertificationPlatformView = React.useMemo(() => {
    if (!changeOnboardingData || changeOnboardingData.length === 0) return 0;
    
    let filtered = changeOnboardingData;
    
    // Apply portfolio filter
    if (selectedPortfolio && selectedPortfolio !== 'all' && selectedPortfolio.trim() !== '') {
      filtered = filtered.filter(item => {
        const transformedItemPortfolio = transformPortfolioName(item.portfolio);
        return transformedItemPortfolio === selectedPortfolio;
      });
    }
    
    // Apply platform filter
    if (selectedPlatform && selectedPlatform !== 'all' && selectedPlatform.trim() !== '') {
      filtered = filtered.filter(item => item.platform === selectedPlatform);
    }
    
    // Apply MVC status filter from platform view
    if (platformViewMvcFilter) {
      filtered = filtered.filter(item => item.mvc_status === true);
    }
    
    // Apply BCA status filter from platform view
    if (platformViewBcaFilter) {
      filtered = filtered.filter(item => item.bca_status === true);
    }
    
    // Apply search filter from platform view
    if (platformViewSearchQuery) {
      const query = platformViewSearchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        item.application_id?.toLowerCase().includes(query) ||
        item.application_name?.toLowerCase().includes(query) ||
        item.portfolio?.toLowerCase().includes(query) ||
        item.platform?.toLowerCase().includes(query)
      );
    }
    
    const uniqueAppIds = new Set(
      filtered
        .map(item => item.application_id)
        .filter(id => id && id.trim() !== '')
    );
    return uniqueAppIds.size;
  }, [changeOnboardingData, selectedPortfolio, selectedPlatform, platformViewMvcFilter, platformViewBcaFilter, platformViewSearchQuery]);

  // Calculate total apps excluding test apps for platform view
  const totalCertificationApplicationsPlatformView = React.useMemo(() => {
    if (!platformViewFilteredData || platformViewFilteredData.length === 0) return 0;
    const uniqueNonTestApps = new Set(
      platformViewFilteredData
        .filter(app => !app.test_app)
        .map(app => app.u_application_id)
        .filter(id => id)
    );
    return uniqueNonTestApps.size;
  }, [platformViewFilteredData]);

  // Calculate average certification percentage for platform view
  const avgCertificationPlatformView = React.useMemo(() => {
    if (!platformViewFilteredData || platformViewFilteredData.length === 0) return 0;
    
    // Group by unique application ID and calculate averages per app
    const appIdGroups = platformViewFilteredData.reduce((acc: any, app) => {
      const appId = app.u_application_id;
      if (!appId) return acc;

      if (!acc[appId]) {
        acc[appId] = [];
      }
      acc[appId].push(app);
      return acc;
    }, {});

    // Calculate certification status for each unique app
    let totalCertPercentage = 0;
    let totalAppsCount = 0;

    Object.values(appIdGroups).forEach((appInstances: any) => {
      const avgCertPercentage =
        appInstances.reduce((sum: number, instance: any) => 
          sum + (instance.overall_app_id_certification_percentage || 0), 0) / appInstances.length;

      totalCertPercentage += avgCertPercentage;
      totalAppsCount++;
    });

    return totalAppsCount > 0 ? Math.round(totalCertPercentage / totalAppsCount) : 0;
  }, [platformViewFilteredData]);

  // Calculate average completeness percentage for platform view
  const avgCompletenessPlatformView = React.useMemo(() => {
    if (!platformViewFilteredData || platformViewFilteredData.length === 0) return 0;
    
    return Math.round(
      platformViewFilteredData.reduce((sum, app) => 
        sum + (app.overall_app_id_completeness_percentage || 0), 0) / platformViewFilteredData.length
    );
  }, [platformViewFilteredData]);

  // Helper function to format owners from owners_role_mac
  const formatOwners = (ownersRoleMac: string | null | undefined): string => {
    if (!ownersRoleMac) return 'N/A';
    
    const pairs = ownersRoleMac.split('\n').filter(pair => pair.trim());
    return pairs.map(pair => {
      const [owner, role] = pair.split('-');
      return `${owner?.trim() || ''}-${role?.trim() || ''}`;
    }).join(', ');
  };

  // Format onboarding owners - extract only names, comma separated
  const formatOnboardingOwners = (ownersRoleMac: string | null | undefined): string => {
    if (!ownersRoleMac) return 'N/A';
    
    const pairs = ownersRoleMac.split('\n').filter(pair => pair.trim());
    return pairs.map(pair => {
      const [owner] = pair.split('-');
      return owner?.trim() || '';
    }).filter(name => name).join(', ');
  };

  // Filter Change Onboarding data based on portfolio, platform, and filters
  const filteredChangeOnboardingData = React.useMemo(() => {
    if (!changeOnboardingData || changeOnboardingData.length === 0) return [];
    
    console.log('🔍 Change Onboarding Filter Debug:', {
      totalData: changeOnboardingData.length,
      selectedPortfolio,
      selectedPlatform,
      changeOnboardingMvcFilter,
      changeOnboardingBcaFilter,
      changeOnboardingStatusFilter,
      changeOnboardingSearchQuery
    });
    
    let filtered = changeOnboardingData;
    
    // Apply portfolio filter
    if (selectedPortfolio && selectedPortfolio !== 'all' && selectedPortfolio.trim() !== '') {
      filtered = filtered.filter(item => {
        const transformedItemPortfolio = transformPortfolioName(item.portfolio);
        return transformedItemPortfolio === selectedPortfolio;
      });
      console.log('🔍 After portfolio filter:', filtered.length);
    }
    
    // Apply platform filter
    if (selectedPlatform && selectedPlatform !== 'all' && selectedPlatform.trim() !== '') {
      filtered = filtered.filter(item => item.platform === selectedPlatform);
      console.log('🔍 After platform filter:', filtered.length);
    }
    
    // Apply MVC status filter
    if (changeOnboardingMvcFilter) {
      filtered = filtered.filter(item => item.mvc_status === true);
    }
    
    // Apply BCA status filter
    if (changeOnboardingBcaFilter) {
      filtered = filtered.filter(item => item.bca_status === true);
    }
    
    // Apply Onboarding Status filter
    if (changeOnboardingStatusFilter !== 'all') {
      filtered = filtered.filter(item => {
        const status = item.onboarding_info?.onboarding_status || 'not onboarded';
        return status === changeOnboardingStatusFilter;
      });
    }
    
    // Apply Deployment Channel filter
    if (changeOnboardingDeploymentChannelFilter !== 'all') {
      filtered = filtered.filter(item => {
        const channel = item.onboarding_info?.deployment_channel || 'N/A';
        return channel === changeOnboardingDeploymentChannelFilter;
      });
    }
    
    // Apply Change Process Type filter
    if (changeOnboardingChangeProcessTypeFilter !== 'all') {
      filtered = filtered.filter(item => {
        const processType = item.onboarding_info?.change_process_type || 'N/A';
        return processType === changeOnboardingChangeProcessTypeFilter;
      });
    }
    
    // Apply search filter
    if (changeOnboardingSearchQuery) {
      const query = changeOnboardingSearchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        item.application_id?.toLowerCase().includes(query) ||
        item.application_name?.toLowerCase().includes(query) ||
        item.portfolio?.toLowerCase().includes(query) ||
        item.platform?.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  }, [changeOnboardingData, selectedPortfolio, selectedPlatform, changeOnboardingMvcFilter, changeOnboardingBcaFilter, changeOnboardingStatusFilter, changeOnboardingDeploymentChannelFilter, changeOnboardingChangeProcessTypeFilter, changeOnboardingSearchQuery]);

  // Calculate onboarded apps count - count distinct app IDs that are onboarded
  const onboardedAppsCount = React.useMemo(() => {
    const onboardedAppIds = new Set(
      filteredChangeOnboardingData
        .filter(item => item.onboarding_info && item.onboarding_info.onboarding_status === 'onboarded')
        .map(item => item.application_id)
        .filter(id => id && id.trim() !== '')
    );
    return onboardedAppIds.size;
  }, [filteredChangeOnboardingData]);

  // Calculate unique app IDs count
  const uniqueAppIdsCount = React.useMemo(() => {
    const uniqueAppIds = new Set(
      filteredChangeOnboardingData
        .map(item => item.application_id)
        .filter(id => id && id.trim() !== '')
    );
    return uniqueAppIds.size;
  }, [filteredChangeOnboardingData]);

  // Calculate unique app IDs count for certification tab (responsive to certification MVC/BCA filters and search)
  const uniqueAppIdsCountForCertification = React.useMemo(() => {
    if (!changeOnboardingData || changeOnboardingData.length === 0) return 0;
    
    let filtered = changeOnboardingData;
    
    // Apply portfolio filter
    if (selectedPortfolio && selectedPortfolio !== 'all' && selectedPortfolio.trim() !== '') {
      filtered = filtered.filter(item => {
        const transformedItemPortfolio = transformPortfolioName(item.portfolio);
        return transformedItemPortfolio === selectedPortfolio;
      });
    }
    
    // Apply platform filter
    if (selectedPlatform && selectedPlatform !== 'all' && selectedPlatform.trim() !== '') {
      filtered = filtered.filter(item => item.platform === selectedPlatform);
    }
    
    // Apply MVC status filter from certification tab
    if (mvcStatusFilter) {
      filtered = filtered.filter(item => item.mvc_status === true);
    }
    
    // Apply BCA status filter from certification tab
    if (bcaStatusFilter) {
      filtered = filtered.filter(item => item.bca_status === true);
    }
    
    // Apply search filter from certification tab
    if (certificationSearchQuery) {
      const query = certificationSearchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        item.application_id?.toLowerCase().includes(query) ||
        item.application_name?.toLowerCase().includes(query) ||
        item.portfolio?.toLowerCase().includes(query) ||
        item.platform?.toLowerCase().includes(query)
      );
    }
    
    const uniqueAppIds = new Set(
      filtered
        .map(item => item.application_id)
        .filter(id => id && id.trim() !== '')
    );
    return uniqueAppIds.size;
  }, [changeOnboardingData, selectedPortfolio, selectedPlatform, mvcStatusFilter, bcaStatusFilter, certificationSearchQuery]);

  // Calculate Red Channel Apps count - count number of records with deployment channel as red
  const redChannelAppsCount = React.useMemo(() => {
    return filteredChangeOnboardingData.filter(
      item => item.onboarding_info && item.onboarding_info.deployment_channel === 'red'
    ).length;
  }, [filteredChangeOnboardingData]);

  // Calculate MVC-specific metrics from change onboarding data
  const mvcOnboardingMetrics = React.useMemo(() => {
    if (!changeOnboardingData || changeOnboardingData.length === 0) {
      return {
        totalMVCs: 0,
        onboardedMVCs: 0,
        percentageOnboarded: 0
      };
    }

    // Apply portfolio and platform filters
    let filtered = changeOnboardingData;
    if (selectedPortfolio && selectedPortfolio !== 'all' && selectedPortfolio.trim() !== '') {
      filtered = filtered.filter(item => {
        const transformedItemPortfolio = transformPortfolioName(item.portfolio);
        return transformedItemPortfolio === selectedPortfolio;
      });
    }
    if (selectedPlatform && selectedPlatform !== 'all' && selectedPlatform.trim() !== '') {
      filtered = filtered.filter(item => item.platform === selectedPlatform);
    }

    // Get unique MVC app IDs
    const mvcApps = filtered.filter(item => item.mvc_status === true);
    const uniqueMVCAppIds = new Set(
      mvcApps
        .map(item => item.application_id)
        .filter(id => id && id.trim() !== '')
    );
    const totalMVCs = uniqueMVCAppIds.size;

    // Get onboarded MVCs
    const onboardedMVCAppIds = new Set(
      mvcApps
        .filter(item => item.onboarding_info && item.onboarding_info.onboarding_status === 'onboarded')
        .map(item => item.application_id)
        .filter(id => id && id.trim() !== '')
    );
    const onboardedMVCs = onboardedMVCAppIds.size;

    const percentageOnboarded = totalMVCs > 0 ? Math.round((onboardedMVCs / totalMVCs) * 100) : 0;

    return {
      totalMVCs,
      onboardedMVCs,
      percentageOnboarded
    };
  }, [changeOnboardingData, selectedPortfolio, selectedPlatform]);

  // Calculate MVC Certification % from certification data
  const mvcCertificationPercentage = React.useMemo(() => {
    if (!certificationData || certificationData.length === 0) return 0;

    // Apply portfolio and platform filters
    let filtered = certificationData;
    if (selectedPortfolio && selectedPortfolio !== 'all' && selectedPortfolio.trim() !== '') {
      filtered = filtered.filter(item => {
        const transformedItemPortfolio = transformPortfolioName(item.portfolio);
        return transformedItemPortfolio === selectedPortfolio;
      });
    }
    if (selectedPlatform && selectedPlatform !== 'all' && selectedPlatform.trim() !== '') {
      filtered = filtered.filter(item => item.platform === selectedPlatform);
    }

    // Filter for MVC apps only
    const mvcApps = filtered.filter(item => item.is_mvc === true);

    if (mvcApps.length === 0) return 0;

    // Group by unique application ID and calculate averages per app
    const appIdGroups = mvcApps.reduce((acc: any, app) => {
      const appId = app.u_application_id;
      if (!appId) return acc;

      if (!acc[appId]) {
        acc[appId] = [];
      }
      acc[appId].push(app);
      return acc;
    }, {});

    // Calculate certification status for each unique app
    let totalCertPercentage = 0;
    let totalAppsCount = 0;

    Object.values(appIdGroups).forEach((appInstances: any) => {
      const avgCertPercentage =
        appInstances.reduce((sum: number, instance: any) => 
          sum + (instance.overall_app_id_certification_percentage || 0), 0) / appInstances.length;
      
      totalCertPercentage += avgCertPercentage;
      totalAppsCount += 1;
    });

    return totalAppsCount > 0 ? Math.round(totalCertPercentage / totalAppsCount) : 0;
  }, [certificationData, selectedPortfolio, selectedPlatform]);

  // Calculate Overall Certification % from certification data (all apps, not just MVCs)
  const overallCertificationPercentage = React.useMemo(() => {
    if (!certificationData || certificationData.length === 0) return 0;

    // Apply portfolio and platform filters
    let filtered = certificationData;
    if (selectedPortfolio && selectedPortfolio !== 'all' && selectedPortfolio.trim() !== '') {
      filtered = filtered.filter(item => {
        const transformedItemPortfolio = transformPortfolioName(item.portfolio);
        return transformedItemPortfolio === selectedPortfolio;
      });
    }
    if (selectedPlatform && selectedPlatform !== 'all' && selectedPlatform.trim() !== '') {
      filtered = filtered.filter(item => item.platform === selectedPlatform);
    }

    if (filtered.length === 0) return 0;

    // Group by unique application ID and calculate averages per app
    const appIdGroups = filtered.reduce((acc: any, app) => {
      const appId = app.u_application_id;
      if (!appId) return acc;

      if (!acc[appId]) {
        acc[appId] = [];
      }
      acc[appId].push(app);
      return acc;
    }, {});

    // Calculate certification status for each unique app
    let totalCertPercentage = 0;
    let totalAppsCount = 0;

    Object.values(appIdGroups).forEach((appInstances: any) => {
      const avgCertPercentage =
        appInstances.reduce((sum: number, instance: any) => 
          sum + (instance.overall_app_id_certification_percentage || 0), 0) / appInstances.length;
      
      totalCertPercentage += avgCertPercentage;
      totalAppsCount += 1;
    });

    return totalAppsCount > 0 ? Math.round(totalCertPercentage / totalAppsCount) : 0;
  }, [certificationData, selectedPortfolio, selectedPlatform]);

  // Helper function to format date from ISO to dd-mm-yyyy
  const formatChangeDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (e) {
      return 'Invalid Date';
    }
  };

  // Debug logging for P1/P2 outage & availability analysis
  React.useEffect(() => {
    if (incidentData.length > 0) {
      const p1p2Incidents = incidentData.filter(inc => inc.priority === '1' || inc.priority === '2');
      const withOutage = p1p2Incidents.filter(inc => mttrData.has(inc.number) && getMTTRMinutes(inc.number) > 0);
      
      // Calculate total period for availability calculation using actual date range
      const dateRange = getDateRange(selectedTimeRange);
      const startDate = new Date(dateRange.start_time);
      const endDate = new Date(dateRange.end_time);
      const totalPeriodMinutes = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60));
      const uptimeMinutes = totalPeriodMinutes - totalOutageDuration;
      
      console.log('📊 P1/P2 Outage & Availability Analysis:', {
        selectedTimeRange,
        totalP1P2Incidents: p1p2Incidents.length,
        incidentsWithOutage: withOutage.length,
        totalOutageMinutes: totalOutageDuration,
        formattedDuration: formatOutageDuration(totalOutageDuration),
        totalPeriodMinutes,
        uptimeMinutes,
        availabilityPercentage: availabilityPercentage.toFixed(2) + '%',
        selectedPortfolio,
        sampleIncidentWithOutage: withOutage[0]
      });
    }
  }, [incidentData, selectedPortfolio, totalOutageDuration, formatOutageDuration, selectedTimeRange, availabilityPercentage]);
  
  // Debug logging for P3/P4 data analysis
  React.useEffect(() => {
    if (p3p4IncidentData.length > 0) {
      const uniqueAppIds = Array.from(new Set(p3p4IncidentData.map(inc => inc.cmdb_ci_u_application_id).filter(Boolean)));
      const allP3P4 = p3p4IncidentData.filter(inc => inc.priority === '3' || inc.priority === '4');
      const portfolioP3P4 = !selectedPortfolio 
        ? allP3P4 
        : allP3P4.filter(inc => inc.portfolio_name === selectedPortfolio);
        
      console.log('📊 P3/P4 API Data Summary:', {
        totalRecords: p3p4IncidentData.length,
        allP3P4Count: allP3P4.length,
        portfolioP3P4Count: portfolioP3P4.length,
        selectedPortfolio: selectedPortfolio,
        recordsWithAppId: p3p4IncidentData.filter(inc => inc.cmdb_ci_u_application_id).length,
        uniqueAppIds: uniqueAppIds.length,
        sampleAppIds: uniqueAppIds.slice(0, 10),
        uniquePortfolios: Array.from(new Set(allP3P4.map(inc => inc.portfolio_name).filter(Boolean))),
        sampleRecords: p3p4IncidentData.slice(0, 3)
      });
    }
    
    if (incidentData.length > 0) {
      const allP1P2 = incidentData.filter(inc => inc.priority === '1' || inc.priority === '2');
      const portfolioP1P2 = !selectedPortfolio 
        ? allP1P2 
        : allP1P2.filter(inc => inc.portfolio_name === selectedPortfolio);
        
      console.log('📊 P1/P2 API Data Summary:', {
        totalRecords: incidentData.length,
        allP1P2Count: allP1P2.length,
        portfolioP1P2Count: portfolioP1P2.length,
        selectedPortfolio: selectedPortfolio,
        p1Count: portfolioP1P2.filter(inc => inc.priority === '1').length,
        p2Count: portfolioP1P2.filter(inc => inc.priority === '2').length,
        recordsWithAppId: portfolioP1P2.filter(inc => inc.cmdb_ci_u_application_id).length,
        uniquePortfolios: Array.from(new Set(allP1P2.map(inc => inc.portfolio_name).filter(Boolean)))
      });
    }
  }, [p3p4IncidentData, totalP3P4FromAPI, incidentData, totalP1P2FromAPI, selectedPortfolio]);

  // Calculate totals for all applications in the portfolio
  const calculatePortfolioTotals = () => {
    if (!portfolioApps || portfolioApps.length === 0) {
      return {
        totalP1: 0,
        totalP2: 0,
        totalP3P4: 0,
        totalIncidentViaAlerts: 0,
        totalIncidentViaChange: 0,
        totalOutageDuration: 0,
        avgAvailability: 0,
        avgSLOObjective: 0,
        avgErrorBudget: 0
      };
    }

    // Use the same filtered apps that are displayed in the table
    const appsToCalculate = portfolioApps.filter(app => {
      const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || app.platformPortfolio === selectedPortfolio;
      const platformMatch = !selectedPlatform || selectedPlatform === 'all' || app.platform === selectedPlatform;
      return portfolioMatch && platformMatch;
    });

    if (appsToCalculate.length === 0) {
      return {
        totalP1: 0,
        totalP2: 0,
        totalP3P4: 0,
        totalIncidentViaAlerts: 0,
        totalIncidentViaChange: 0,
        totalOutageDuration: 0,
        avgAvailability: 0,
        avgSLOObjective: 0,
        avgErrorBudget: 0
      };
    }

    let totalP1 = 0;
    let totalP2 = 0;
    let totalP3P4 = 0;
    let totalIncidentViaAlerts = 0;
    let totalIncidentViaChange = 0;
    let totalOutageDuration = 0;
    let totalAvailability = 0;
    let totalSLOObjective = 0;
    let totalErrorBudget = 0;
    let validAppsCount = 0;

    appsToCalculate.forEach(app => {
      const incidents = getIncidentCounts(app.appId);
      const alertIncidents = getIncidentsViaAlerts(app.appId);
      const changeIncidents = getIncidentsDueToChange(app.appId);
      const sloInfo = sloData.find(slo => slo.app_id === app.appId);

      totalP1 += incidents.p1;
      totalP2 += incidents.p2;
      totalP3P4 += incidents.p3p4mi;
      totalIncidentViaAlerts += alertIncidents;
      totalIncidentViaChange += changeIncidents;
      totalOutageDuration += app.outageDuration;

      // Only include apps with valid SLO data (not NA) for averaging
      if (sloInfo && 
          sloInfo.average_availability && 
          sloInfo.average_availability !== 'NA' && 
          (typeof sloInfo.average_availability === 'number' ? sloInfo.average_availability : parseFloat(String(sloInfo.average_availability))) > 0) {
        totalAvailability += typeof sloInfo.average_availability === 'number' ? sloInfo.average_availability : parseFloat(String(sloInfo.average_availability));
        validAppsCount++;
      }

      if (sloInfo && 
          sloInfo.slo_objective && 
          sloInfo.slo_objective !== 'NA' && 
          (typeof sloInfo.slo_objective === 'number' ? sloInfo.slo_objective : parseFloat(String(sloInfo.slo_objective))) > 0) {
        totalSLOObjective += typeof sloInfo.slo_objective === 'number' ? sloInfo.slo_objective : parseFloat(String(sloInfo.slo_objective));
      }

      if (sloInfo && 
          sloInfo.error_budget_minutes && 
          sloInfo.error_budget_minutes !== 'NA' && 
          (typeof sloInfo.error_budget_minutes === 'number' ? sloInfo.error_budget_minutes : parseFloat(String(sloInfo.error_budget_minutes))) >= 0) {
        totalErrorBudget += typeof sloInfo.error_budget_minutes === 'number' ? sloInfo.error_budget_minutes : parseFloat(String(sloInfo.error_budget_minutes));
      }
    });

    return {
      totalP1,
      totalP2,
      totalP3P4,
      totalIncidentViaAlerts,
      totalIncidentViaChange,
      totalOutageDuration,
      avgAvailability: validAppsCount > 0 ? totalAvailability / validAppsCount : 0,
      avgSLOObjective: validAppsCount > 0 ? totalSLOObjective / validAppsCount : 0,
      avgErrorBudget: validAppsCount > 0 ? totalErrorBudget / validAppsCount : 0
    };
  };

  const portfolioTotals = calculatePortfolioTotals();

  // Check if any core data is still loading - secondary widgets (change onboarding, certification,
  // P3/P4, SpringPM) now manage their own spinners so they don't block the initial render
  const isAnyDataLoading = loading;
  
  if (isAnyDataLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading portfolio, SLO, and incident data...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        <Typography variant="h6">Error Loading Data</Typography>
        <Typography>{error}</Typography>
        <Button onClick={fetchPortfolioData} sx={{ mt: 1 }}>Retry</Button>
      </Alert>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#ffffff', minHeight: '100%' }} data-dashboard-root>
      {/* Tag Management Modal */}
      <Dialog
        open={tagModalOpen}
        onClose={handleCloseTagModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          {isEditMode ? 'Edit Tag' : 'Add Tag'} - {selectedTagApp?.app_name}
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Decommissioning Tag"
                  value={tagFormData.decomm_tag}
                  onChange={(e) => handleTagFormChange('decomm_tag', e.target.value)}
                  placeholder="e.g. OP26, OP27"
                  helperText="Enter a tag to categorize this application"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Remarks"
                  value={tagFormData.remarks}
                  onChange={(e) => handleTagFormChange('remarks', e.target.value)}
                  placeholder="Enter remarks or notes about this decommissioning tag"
                  helperText="Remarks will appear as a tooltip when hovering over the tag"
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button onClick={handleCloseTagModal} disabled={tagLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveTag}
            variant="contained"
            disabled={tagLoading || !tagFormData.decomm_tag}
          >
            {tagLoading ? 'Saving...' : (isEditMode ? 'Update' : 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Header Section with Dropdowns */}
      <Box sx={{ 
        pb: 3, 
        mb: 3,
        backgroundColor: '#ffffff',
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {/* Left side - Dropdowns */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl 
              size="small"
              sx={{ 
                minWidth: 220,
                '& .MuiInputLabel-root': {
                  fontSize: '12px',
                  transform: 'translate(14px, -9px) scale(0.75)',
                  backgroundColor: '#fff',
                  px: 0.5
                }
              }}
            >
              <InputLabel shrink>Select Portfolio</InputLabel>
              <Select
                value={selectedPortfolio}
                label="Select Portfolio"
                onChange={(e) => setSelectedPortfolio(e.target.value)}
                displayEmpty
                sx={{
                  fontSize: '13px',
                  height: '40px',
                  backgroundColor: '#ffffff',
                }}
              >
                <MenuItem value="all" sx={{ fontSize: '13px' }}>
                  All Portfolios
                </MenuItem>
                {portfolioOptions.map((portfolio) => (
                  <MenuItem key={portfolio} value={portfolio} sx={{ fontSize: '13px' }}>
                    {portfolio}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl 
              size="small"
              sx={{ 
                minWidth: 220,
                '& .MuiInputLabel-root': {
                  fontSize: '12px',
                  transform: 'translate(14px, -9px) scale(0.75)',
                  backgroundColor: '#fff',
                  px: 0.5
                }
              }}
            >
              <InputLabel shrink>Select Platform</InputLabel>
              <Select
                value={selectedPlatform}
                label="Select Platform"
                onChange={(e) => setSelectedPlatform(e.target.value)}
                displayEmpty
                sx={{
                  fontSize: '13px',
                  height: '40px',
                  backgroundColor: '#ffffff',
                }}
              >
                <MenuItem value="all" sx={{ fontSize: '13px' }}>
                  All Platforms
                </MenuItem>
                {platformOptions.map((platform) => (
                  <MenuItem key={platform} value={platform} sx={{ fontSize: '13px' }}>
                    {platform}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl 
              size="small"
              sx={{ 
                minWidth: 280,
                '& .MuiInputLabel-root': {
                  fontSize: '12px',
                  transform: 'translate(14px, -9px) scale(0.75)',
                  backgroundColor: '#fff',
                  px: 0.5
                }
              }}
            >
              <InputLabel shrink>Application</InputLabel>
              <Select
                value={selectedApplication}
                label="Application"
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedApplication(value);
                  // Auto-populate portfolio, platform, and category when an application is selected
                  if (value && apiData.length > 0) {
                    const selectedApp = apiData.find(app => app.app_id === value);
                    if (selectedApp) {
                      if (selectedApp.platform_portfolio) {
                        setSelectedPortfolio(selectedApp.platform_portfolio);
                      }
                      if (selectedApp.platform) {
                        setSelectedPlatform(selectedApp.platform);
                      }
                      // Determine category based on app_type
                      let appCategory = '';
                      if (selectedApp.app_type === 'MVC' || selectedApp.app_type === 'Critical Infra') {
                        appCategory = 'mission-critical';
                      } else if (selectedApp.app_type === 'Shared Service') {
                        appCategory = 'business-critical';
                      } else {
                        appCategory = 'operational';
                      }
                      setSelectedCategory(appCategory);
                    }
                  }
                }}
                displayEmpty
                sx={{
                  fontSize: '13px',
                  height: '40px',
                  backgroundColor: '#ffffff',
                }}
                renderValue={(selected) => {
                  if (!selected) return 'All Applications';
                  const app = apiData.find(a => a.app_id === selected);
                  return app ? `${app.app_id} - ${app.application}` : selected;
                }}
              >
                <MenuItem value="" sx={{ fontSize: '13px' }}>
                  All Applications
                </MenuItem>
                {applicationOptionsFiltered.map((app) => (
                  <MenuItem key={app} value={app} sx={{ fontSize: '13px' }}>
                    {(() => {
                      const appData = apiData.find(a => a.app_id === app);
                      return appData ? `${appData.app_id} - ${appData.application}` : app;
                    })()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl 
              size="small"
              sx={{ 
                minWidth: 200,
                '& .MuiInputLabel-root': {
                  fontSize: '12px',
                  transform: 'translate(14px, -9px) scale(0.75)',
                  backgroundColor: '#fff',
                  px: 0.5
                }
              }}
            >
              <InputLabel shrink>All Category</InputLabel>
              <Select
                value={selectedCategory}
                label="All Category"
                onChange={(e) => setSelectedCategory(e.target.value)}
                displayEmpty
                sx={{
                  fontSize: '13px',
                  height: '40px',
                  backgroundColor: '#ffffff',
                }}
                renderValue={(selected) => {
                  if (!selected) return 'All Category';
                  if (selected === 'mission-critical') return 'Mission Critical';
                  if (selected === 'business-critical') return 'Business Critical';
                  if (selected === 'operational') return 'Operational';
                  return selected;
                }}
              >
                <MenuItem value="" sx={{ fontSize: '13px' }}>
                  All Category
                </MenuItem>
                <MenuItem value="mission-critical" sx={{ fontSize: '13px' }}>
                  Mission Critical
                </MenuItem>
                <MenuItem value="business-critical" sx={{ fontSize: '13px' }}>
                  Business Critical
                </MenuItem>
                <MenuItem value="operational" sx={{ fontSize: '13px' }}>
                  Operational
                </MenuItem>
              </Select>
            </FormControl>
            
            <FormControl 
              size="small"
              sx={{ 
                minWidth: 180,
                '& .MuiInputLabel-root': {
                  fontSize: '12px',
                  transform: 'translate(14px, 9px) scale(1)',
                  '&.MuiInputLabel-shrink': {
                    transform: 'translate(14px, -9px) scale(0.75)',
                  },
                  backgroundColor: '#fff',
                  px: 0.5
                }
              }}
            >
              <InputLabel>Time Range</InputLabel>
              <Select
                value={selectedTimeRange}
                label="Time Range"
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedTimeRange(value);
                  if (value === 'custom') {
                    setShowCustomDatePicker(true);
                  } else {
                    setShowCustomDatePicker(false);
                  }
                }}
                sx={{
                  fontSize: '13px',
                  height: '40px',
                  backgroundColor: '#ffffff',
                }}
              >
                <MenuItem value="7d" sx={{ fontSize: '13px' }}>Last 7 Days</MenuItem>
                <MenuItem value="30d" sx={{ fontSize: '13px' }}>Last 30 Days</MenuItem>
                <MenuItem value="90d" sx={{ fontSize: '13px' }}>Last 90 Days</MenuItem>
                <MenuItem value="1y" sx={{ fontSize: '13px' }}>Year to Date</MenuItem>
                <MenuItem value="custom" sx={{ fontSize: '13px' }}>Custom Range</MenuItem>
              </Select>
            </FormControl>

            {/* Inline Custom Date Range Picker */}
            {showCustomDatePicker && (
              <>
                <TextField
                  label="Start Date"
                  type="date"
                  size="small"
                  value={customDateRange.start}
                  onChange={(e) => {
                    const newStart = e.target.value;
                    setCustomDateRange({ 
                      start: newStart, 
                      end: customDateRange.end && new Date(customDateRange.end) >= new Date(newStart) 
                        ? customDateRange.end 
                        : '' 
                    });
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{ 
                    bgcolor: 'white', 
                    minWidth: 150,
                    '& .MuiInputBase-root': {
                      fontSize: '13px',
                      height: '40px'
                    },
                    '& input[type="date"]::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      display: 'none'
                    },
                    '& input[type="date"]::-webkit-calendar-picker-indicator': {
                      cursor: 'pointer'
                    },
                    '& input[type="date"]': {
                      color: 'rgba(0, 0, 0, 0.87) !important',
                      WebkitTextFillColor: 'rgba(0, 0, 0, 0.87) !important'
                    }
                  }}
                />
                <TextField
                  label="End Date"
                  type="date"
                  size="small"
                  value={customDateRange.end}
                  onChange={(e) => setCustomDateRange({ ...customDateRange, end: e.target.value })}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    min: customDateRange.start || undefined,
                    max: new Date().toISOString().split('T')[0]
                  }}
                  disabled={!customDateRange.start}
                  sx={{ 
                    bgcolor: 'white', 
                    minWidth: 150,
                    '& .MuiInputBase-root': {
                      fontSize: '13px',
                      height: '40px'
                    },
                    '& input[type="date"]::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      display: 'none'
                    },
                    '& input[type="date"]::-webkit-calendar-picker-indicator': {
                      cursor: 'pointer'
                    },
                    '& input[type="date"]': {
                      color: 'rgba(0, 0, 0, 0.87) !important',
                      WebkitTextFillColor: 'rgba(0, 0, 0, 0.87) !important'
                    },
                    '& input[type="date"]:disabled': {
                      color: 'rgba(0, 0, 0, 0.38)',
                      WebkitTextFillColor: 'rgba(0, 0, 0, 0.38)'
                    }
                  }}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => {
                    if (customDateRange.start && customDateRange.end && customDateRange.end >= customDateRange.start) {
                      setCustomDateApplied(true);
                      setSelectedTimeRange('custom');
                    }
                  }}
                  disabled={!customDateRange.start || !customDateRange.end || customDateRange.end < customDateRange.start}
                  sx={{ 
                    height: '40px',
                    minWidth: '80px',
                    bgcolor: '#0067b3',
                    '&:hover': {
                      bgcolor: '#004c8c'
                    }
                  }}
                >
                  Apply
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setShowCustomDatePicker(false);
                    setSelectedTimeRange('30d');
                    setCustomDateRange({ start: '', end: '' });
                    setCustomDateApplied(false);
                  }}
                  sx={{ 
                    height: '40px',
                    minWidth: '80px',
                    borderColor: '#666',
                    color: '#666',
                    '&:hover': {
                      borderColor: '#333',
                      bgcolor: '#f5f5f5'
                    }
                  }}
                >
                  Cancel
                </Button>
              </>
            )}
            
            {/* Trend Period Toggle */}
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ml: 2 }}>
              <Button
                size="small"
                variant={trendPeriod === 'week' ? 'contained' : 'outlined'}
                onClick={() => setTrendPeriod('week')}
                sx={{
                  minWidth: '100px',
                  fontSize: '12px',
                  py: 0.8,
                  px: 1.5,
                  height: '40px',
                  textTransform: 'none',
                  backgroundColor: trendPeriod === 'week' ? '#5a32d3' : 'transparent',
                  color: trendPeriod === 'week' ? '#fff' : '#666',
                  borderColor: '#5a32d3',
                  '&:hover': {
                    backgroundColor: trendPeriod === 'week' ? '#4a22c3' : 'rgba(90, 50, 211, 0.1)',
                  }
                }}
              >
                vs Last Week
              </Button>
              <Button
                size="small"
                variant={trendPeriod === 'month' ? 'contained' : 'outlined'}
                onClick={() => setTrendPeriod('month')}
                sx={{
                  minWidth: '100px',
                  fontSize: '12px',
                  py: 0.8,
                  px: 1.5,
                  height: '40px',
                  textTransform: 'none',
                  backgroundColor: trendPeriod === 'month' ? '#5a32d3' : 'transparent',
                  color: trendPeriod === 'month' ? '#fff' : '#666',
                  borderColor: '#5a32d3',
                  '&:hover': {
                    backgroundColor: trendPeriod === 'month' ? '#4a22c3' : 'rgba(90, 50, 211, 0.1)',
                  }
                }}
              >
                vs Last Month
              </Button>
            </Box>
            
            {/* Validation Alert */}
            {showCustomDatePicker && customDateRange.start && customDateRange.end && customDateRange.end < customDateRange.start && (
              <Alert severity="error" sx={{ fontSize: '12px', py: 0.5, px: 1 }}>
                End date cannot be earlier than start date
              </Alert>
            )}
            
            {/* Date range applied indicator */}
            {customDateApplied && selectedTimeRange === 'custom' && customDateRange.start && customDateRange.end && (
              <Chip
                icon={<CheckCircle sx={{ fontSize: '16px' }} />}
                label="Date range applied"
                size="small"
                sx={{
                  bgcolor: '#e8f5e9',
                  color: '#2e7d32',
                  fontWeight: 500,
                  fontSize: '12px',
                  height: '32px',
                  '& .MuiChip-icon': {
                    color: '#2e7d32'
                  }
                }}
              />
            )}
          </Box>

          {/* Right side - Buttons */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="outlined"
              onClick={handleShareLink}
              sx={{
                minWidth: 130,
                height: 40,
                backgroundColor: '#ffffff',
                color: '#1565c0',
                borderColor: '#1565c0',
                fontSize: '13px',
                fontWeight: 500,
                textTransform: 'none',
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                  borderColor: '#0d47a1',
                },
              }}
              startIcon={
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="18" cy="5" r="3"></circle>
                  <circle cx="6" cy="12" r="3"></circle>
                  <circle cx="18" cy="19" r="3"></circle>
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                </svg>
              }
            >
              Share Link
            </Button>

            <Button
              variant="contained"
              startIcon={<SmartToy />}
              onClick={fetchAIInsights}
              disabled={aiInsightsLoading}
              sx={{
                minWidth: 150,
                height: 40,
                backgroundColor: '#1565c0',
                color: 'white',
                fontSize: '13px',
                fontWeight: 500,
                textTransform: 'none',
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: '#0d47a1',
                },
              }}
            >
              {aiInsightsLoading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'AI Insights'}
            </Button>
          </Box>
        </Box>

        {/* Clean Separated KPI Cards */}
        {/* <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          gap: 4,
          flexWrap: 'wrap',
          '@media (max-width: 768px)': {
            flexDirection: 'column',
            gap: 2,
          }
        }}>
          <Box sx={{ 
            flex: 1,
            textAlign: 'center',
            py: 2,
            px: 2,
            backgroundColor: 'transparent',
          }}>
            <Typography variant="h4" sx={{ 
              fontWeight: 700, 
              color: '#2c3e50',
              fontSize: '28px',
              fontFamily: "'Maersk Text', 'Inter', 'Segoe UI', Arial, sans-serif",
              mb: 0.5,
            }}>
              {filteredApps.length}
            </Typography>
            <Typography variant="body2" sx={{ 
              color: '#6c757d', 
              fontSize: '11px',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.8px',
            }}>
              Portfolio Apps
            </Typography>
            <Typography variant="caption" sx={{ 
              color: '#6c757d', 
              fontSize: '10px',
              display: 'block',
              mt: 0.5,
            }}>
              {selectedPortfolio || 'Portfolio'} Applications
            </Typography>
          </Box>

          <Box sx={{ 
            flex: 1,
            textAlign: 'center',
            py: 2,
            px: 2,
            backgroundColor: 'transparent',
          }}>
            <Typography variant="h4" sx={{ 
              fontWeight: 700, 
              color: '#27ae60',
              fontSize: '28px',
              fontFamily: "'Maersk Text', 'Inter', 'Segoe UI', Arial, sans-serif",
              mb: 0.5,
            }}>
              {avgAvailability > 0 
                ? `${Math.round(avgAvailability * 100) / 100}%`
                : 'NA'
              }
            </Typography>
            <Typography variant="body2" sx={{ 
              color: '#6c757d', 
              fontSize: '11px',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.8px',
            }}>
              Avg Availability
            </Typography>
            <Typography variant="caption" sx={{ 
              color: '#6c757d', 
              fontSize: '10px',
              display: 'block',
              mt: 0.5,
            }}>
              Target: 99.6%
            </Typography>
          </Box>

          <Box sx={{ 
            flex: 1,
            textAlign: 'center',
            py: 2,
            px: 2,
            backgroundColor: 'transparent',
          }}>
            <Typography variant="h4" sx={{ 
              fontWeight: 700, 
              color: totalP1P2FromAPI > 0 ? '#e74c3c' : '#2c3e50',
              fontSize: '28px',
              fontFamily: "'Maersk Text', 'Inter', 'Segoe UI', Arial, sans-serif",
              mb: 0.5,
            }}>
              {totalP1P2FromAPI}
            </Typography>
            <Typography variant="body2" sx={{ 
              color: '#6c757d', 
              fontSize: '11px',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.8px',
            }}>
              P1/P2 Incidents
            </Typography>
            <Typography variant="caption" sx={{ 
              color: '#6c757d', 
              fontSize: '10px',
              display: 'block',
              mt: 0.5,
            }}>
              P1/P2 Count
            </Typography>
          </Box>

          <Box sx={{ 
            flex: 1,
            textAlign: 'center',
            py: 2,
            px: 2,
            backgroundColor: 'transparent',
          }}>
            <Typography variant="h4" sx={{ 
              fontWeight: 700, 
              color: '#2c3e50',
              fontSize: '28px',
              fontFamily: "'Maersk Text', 'Inter', 'Segoe UI', Arial, sans-serif",
              mb: 0.5,
            }}>
              {p3p4IncidentData.length}
            </Typography>
            <Typography variant="body2" sx={{ 
              color: '#6c757d', 
              fontSize: '11px',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.8px',
            }}>
              P3/P4 Major Incidents
            </Typography>
            <Typography variant="caption" sx={{ 
              color: '#6c757d', 
              fontSize: '10px',
              display: 'block',
              mt: 0.5,
            }}>
              P3/P4 Count
            </Typography>
          </Box>
        </Box> */}
      </Box>

      {/* Summary Cards - All 4 on same line */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {/* MVC Apps Card */}
        <Box sx={{ 
          minWidth: '140px',
          p: 1.5, 
          bgcolor: '#fff', 
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          boxShadow: 1
        }}>
          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666', display: 'block', mb: 0.5 }}>
            MVC Apps
          </Typography>
          <Typography variant="h4" sx={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#2c3e50' }}>
            {appsFilteredByPortfolioAndPlatform.length}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#999' }}>
            Total Applications
          </Typography>
        </Box>

        {/* Total Outage Duration Card */}
        {/* <Box 
          sx={{ 
            minWidth: '140px',
            p: 1.5, 
            bgcolor: '#fff', 
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            boxShadow: 1,
            cursor: 'pointer',
            '&:hover': {
              boxShadow: 3,
              backgroundColor: '#f9f9f9'
            }
          }}
          onClick={() => setOutageDialogOpen(true)}
        >
          <Box sx={{ pointerEvents: 'none' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666' }}>
              Total Outage Duration
            </Typography>
            <MuiTooltip title="Based on Outage due to Major Incident P1/P2" arrow placement="top">
              <InfoOutlined sx={{ fontSize: '12px', color: '#999' }} />
            </MuiTooltip>
          </Box>
          <Typography variant="h4" sx={{ 
            fontSize: '1.5rem', 
            fontWeight: 'bold',
            color: totalOutageDuration > 0 ? '#e74c3c' : '#27ae60'
          }}>
            {formatOutageDuration(totalOutageDuration)}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#999' }}>
            P1/P2 Outage Time
          </Typography>
          
          <Box 
            sx={{ 
              mt: 1, 
              pt: 1, 
              borderTop: '1px solid #e8e8e8', 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.5
            }}
          >
            {trendPeriod === 'week' ? (
              outageLastWeekDuration < outagePrevWeekDuration ? (
                <>
                  <TrendingDown sx={{ fontSize: '12px', color: '#27ae60' }} />
                  <Typography variant="caption" sx={{ fontSize: '9px', color: '#27ae60', fontWeight: 600 }}>
                    {formatOutageDuration(outagePrevWeekDuration - outageLastWeekDuration)} less vs prev week
                  </Typography>
                </>
              ) : outageLastWeekDuration > outagePrevWeekDuration ? (
                <>
                  <TrendingUp sx={{ fontSize: '12px', color: '#e74c3c' }} />
                  <Typography variant="caption" sx={{ fontSize: '9px', color: '#e74c3c', fontWeight: 600 }}>
                    {formatOutageDuration(outageLastWeekDuration - outagePrevWeekDuration)} more vs prev week
                  </Typography>
                </>
              ) : (
                <Typography variant="caption" sx={{ fontSize: '9px', color: '#888', fontWeight: 600 }}>
                  No change
                </Typography>
              )
            ) : (
              outageLastMonthDuration < outagePrevMonthDuration ? (
                <>
                  <TrendingDown sx={{ fontSize: '12px', color: '#27ae60' }} />
                  <Typography variant="caption" sx={{ fontSize: '9px', color: '#27ae60', fontWeight: 600 }}>
                    {formatOutageDuration(outagePrevMonthDuration - outageLastMonthDuration)} less vs prev month
                  </Typography>
                </>
              ) : outageLastMonthDuration > outagePrevMonthDuration ? (
                <>
                  <TrendingUp sx={{ fontSize: '12px', color: '#e74c3c' }} />
                  <Typography variant="caption" sx={{ fontSize: '9px', color: '#e74c3c', fontWeight: 600 }}>
                    {formatOutageDuration(outageLastMonthDuration - outagePrevMonthDuration)} more vs prev month
                  </Typography>
                </>
              ) : (
                <Typography variant="caption" sx={{ fontSize: '9px', color: '#888', fontWeight: 600 }}>
                  No change
                </Typography>
              )
            )}
          </Box>
          </Box>
        </Box> */}
        {/* P1/P2 Critical & High Priority Section */}
        <Box sx={{ 
          flex: 1,
          minWidth: '500px',
          p: 2, 
          bgcolor: '#fff', 
          border: '1px solid #ffcdd2',
          borderLeft: '4px solid #d32f2f',
          borderRadius: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            {/* Section Header with View Mode Toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ErrorIcon sx={{ color: '#d32f2f', fontSize: 18 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#d32f2f', fontSize: '0.9rem' }}>
                  P1/P2 - Critical & High Priority
                </Typography>
              </Box>
              
              {/* View Mode Toggle */}
              <Box sx={{ display: 'flex', gap: 0.5, bgcolor: '#f5f5f5', borderRadius: 1, p: 0.5 }}>
                <Button
                  size="small"
                  variant={p1p2ViewMode === 'caused' ? 'contained' : 'text'}
                  onClick={() => setP1P2ViewMode('caused')}
                  sx={{
                    fontSize: '0.7rem',
                    px: 1.5,
                    py: 0.5,
                    minWidth: 'auto',
                    textTransform: 'none',
                    bgcolor: p1p2ViewMode === 'caused' ? '#d32f2f' : 'transparent',
                    color: p1p2ViewMode === 'caused' ? '#fff' : '#666',
                    '&:hover': {
                      bgcolor: p1p2ViewMode === 'caused' ? '#b71c1c' : '#e0e0e0'
                    }
                  }}
                >
                  Caused by App
                </Button>
                <Button
                  size="small"
                  variant={p1p2ViewMode === 'impacted' ? 'contained' : 'text'}
                  onClick={() => setP1P2ViewMode('impacted')}
                  sx={{
                    fontSize: '0.7rem',
                    px: 1.5,
                    py: 0.5,
                    minWidth: 'auto',
                    textTransform: 'none',
                    bgcolor: p1p2ViewMode === 'impacted' ? '#d32f2f' : 'transparent',
                    color: p1p2ViewMode === 'impacted' ? '#fff' : '#666',
                    '&:hover': {
                      bgcolor: p1p2ViewMode === 'impacted' ? '#b71c1c' : '#e0e0e0'
                    }
                  }}
                >
                  Affected App
                </Button>
              </Box>
            </Box>
          </Box>
            
          {p1p2SectionLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                <CircularProgress size={40} />
              </Box>
            ) : (
              <>
                {p1p2Error && (
                  <Alert
                    severity="warning"
                    action={
                      <Button color="inherit" size="small" onClick={fetchP1P2IncidentsWithTrends}>
                        Retry
                      </Button>
                    }
                    sx={{ width: '100%', mb: 2 }}
                  >
                    {p1p2Error}
                  </Alert>
                )}

                {/* Metrics in a row */}
                <Box sx={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                  {/* Total Incidents */}
                  <Box 
                    sx={{ 
                      textAlign: 'center',
                      cursor: 'pointer',
                      p: 1,
                      borderRadius: 1,
                      '&:hover': {
                        backgroundColor: '#ffebee'
                      }
                    }}
                    onClick={() => setP1P2IncidentDialogOpen(true)}
                  >
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666', display: 'block', mb: 0.5 }}>
                      Total Incidents
                    </Typography>
                    <Typography variant="h4" sx={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#d32f2f' }}>
                      {totalP1P2FromAPI}
                    </Typography>
                  </Box>

                  {/* % Alert */}
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666', display: 'block', mb: 0.5 }}>
                      % Captured by Alert
                    </Typography>
                    <Typography variant="h4" sx={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#d32f2f' }}>
                      {(() => {
                        const filtered = incidentData.filter(inc => {
                          const priorityMatch = inc.priority === '1' || inc.priority === '2';
                          const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || 
                            (p1p2ViewMode === 'impacted' 
                              ? inc.portfolio_name === selectedPortfolio
                              : inc.caused_by_portfolio === selectedPortfolio);
                          const platformMatch = !selectedPlatform || selectedPlatform === 'all' || 
                            (p1p2ViewMode === 'impacted'
                              ? inc.platform_name === selectedPlatform
                              : inc.caused_by_platform === selectedPlatform);
                          return priorityMatch && portfolioMatch && platformMatch;
                        });
                        return filtered.length > 0 ? `${Math.round((filtered.filter(inc => inc.contact_type_display_value === 'Alert').length / filtered.length) * 100)}%` : 'N/A';
                      })()}
                    </Typography>
                  </Box>

                  {/* % Caused by Change */}
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666', display: 'block', mb: 0.5 }}>
                      % Caused by Change
                    </Typography>
                    <Typography variant="h4" sx={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#d32f2f' }}>
                      {(() => {
                        const filtered = incidentData.filter(inc => {
                          const priorityMatch = inc.priority === '1' || inc.priority === '2';
                          const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || 
                            (p1p2ViewMode === 'impacted' 
                              ? inc.portfolio_name === selectedPortfolio
                              : inc.caused_by_portfolio === selectedPortfolio);
                          const platformMatch = !selectedPlatform || selectedPlatform === 'all' || 
                            (p1p2ViewMode === 'impacted'
                              ? inc.platform_name === selectedPlatform
                              : inc.caused_by_platform === selectedPlatform);
                          return priorityMatch && portfolioMatch && platformMatch;
                        });
                        return filtered.length > 0 ? `${Math.round((filtered.filter(inc => inc.caused_by_display_value && inc.caused_by_display_value.includes('CHG')).length / filtered.length) * 100)}%` : 'N/A';
                      })()}
                    </Typography>
                  </Box>

                  {/* MTTD */}
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666', display: 'block', mb: 0.5 }}>
                      MTTD(Avg)
                    </Typography>
                    <Typography variant="h4" sx={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#d32f2f' }}>
                      {(() => {
                        const filtered = incidentData.filter(inc => {
                          const priorityMatch = inc.priority === '1' || inc.priority === '2';
                          const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || 
                            (p1p2ViewMode === 'impacted' 
                              ? inc.portfolio_name === selectedPortfolio
                              : inc.caused_by_portfolio === selectedPortfolio);
                          const platformMatch = !selectedPlatform || selectedPlatform === 'all' || 
                            (p1p2ViewMode === 'impacted'
                              ? inc.platform_name === selectedPlatform
                              : inc.caused_by_platform === selectedPlatform);
                          return priorityMatch && portfolioMatch && platformMatch && mttdData.has(inc.number);
                        });
                        if (filtered.length === 0) return 'N/A';
                        const totalMTTD = filtered.reduce((sum, inc) => sum + getMTTDMinutes(inc.number), 0);
                        const avgMTTD = Math.round(totalMTTD / filtered.length);
                        const hours = Math.floor(avgMTTD / 60);
                        const minutes = avgMTTD % 60;
                        return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
                      })()}
                    </Typography>
                  </Box>

                  {/* MTTR */}
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666', display: 'block', mb: 0.5 }}>
                      MTTR(Avg)
                    </Typography>
                    <Typography variant="h4" sx={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#d32f2f' }}>
                      {(() => {
                        const filtered = incidentData.filter(inc => {
                          const priorityMatch = inc.priority === '1' || inc.priority === '2';
                          const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || 
                            (p1p2ViewMode === 'impacted' 
                              ? inc.portfolio_name === selectedPortfolio
                              : inc.caused_by_portfolio === selectedPortfolio);
                          const platformMatch = !selectedPlatform || selectedPlatform === 'all' || 
                            (p1p2ViewMode === 'impacted'
                              ? inc.platform_name === selectedPlatform
                              : inc.caused_by_platform === selectedPlatform);
                          return priorityMatch && portfolioMatch && platformMatch && mttrData.has(inc.number);
                        });
                        if (filtered.length === 0) return 'N/A';
                        const totalMTTR = filtered.reduce((sum, inc) => sum + getMTTRMinutes(inc.number), 0);
                        const avgMTTR = Math.round(totalMTTR / filtered.length);
                        const hours = Math.floor(avgMTTR / 60);
                        const minutes = avgMTTR % 60;
                        return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
                      })()}
                    </Typography>
                  </Box>

                  {/* Top Contributor */}
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666', display: 'block', mb: 0.5 }}>
                      Top Incident Contributor
                    </Typography>
                    <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#d32f2f' }}>
                      {(() => {
                        const filtered = incidentData.filter(inc => {
                          const priorityMatch = inc.priority === '1' || inc.priority === '2';
                          const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || 
                            (p1p2ViewMode === 'impacted' 
                              ? inc.portfolio_name === selectedPortfolio
                              : inc.caused_by_portfolio === selectedPortfolio);
                          const platformMatch = !selectedPlatform || selectedPlatform === 'all' || 
                            (p1p2ViewMode === 'impacted'
                              ? inc.platform_name === selectedPlatform
                              : inc.caused_by_platform === selectedPlatform);
                          return priorityMatch && portfolioMatch && platformMatch;
                        });
                        const ciCounts: { [key: string]: number } = {};
                        filtered.forEach(inc => {
                          const ci = inc.cmdb_ci_display_value || 'Unknown';
                          ciCounts[ci] = (ciCounts[ci] || 0) + 1;
                        });
                        const topCI = Object.entries(ciCounts).sort((a, b) => b[1] - a[1])[0];
                        return topCI ? topCI[0] : 'N/A';
                      })()}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#999' }}>
                      {(() => {
                        const filtered = incidentData.filter(inc => {
                          const priorityMatch = inc.priority === '1' || inc.priority === '2';
                          const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || 
                            (p1p2ViewMode === 'impacted' 
                              ? inc.portfolio_name === selectedPortfolio
                              : inc.caused_by_portfolio === selectedPortfolio);
                          const platformMatch = !selectedPlatform || selectedPlatform === 'all' || 
                            (p1p2ViewMode === 'impacted'
                              ? inc.platform_name === selectedPlatform
                              : inc.caused_by_platform === selectedPlatform);
                          return priorityMatch && portfolioMatch && platformMatch;
                        });
                        const ciCounts: { [key: string]: number } = {};
                        filtered.forEach(inc => {
                          const ci = inc.cmdb_ci_display_value || 'Unknown';
                          ciCounts[ci] = (ciCounts[ci] || 0) + 1;
                        });
                        const topCI = Object.entries(ciCounts).sort((a, b) => b[1] - a[1])[0];
                        return topCI ? `${topCI[1]} incidents` : '0 incidents';
                      })()}
                    </Typography>
                  </Box>
                </Box>
              
                {/* Trend Display */}
                <Box 
                  sx={{ 
                    mt: 2, 
                    pt: 1.5, 
                    borderTop: '1px solid #e8e8e8', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1
                  }}
                >
                  {trendPeriod === 'week' ? (
                    p1p2LastWeekCount < p1p2PrevWeekCount ? (
                      <>
                        <TrendingDown sx={{ fontSize: '16px', color: '#27ae60' }} />
                        <Typography variant="body2" sx={{ fontSize: '11px', color: '#27ae60', fontWeight: 600 }}>
                          {p1p2PrevWeekCount - p1p2LastWeekCount} fewer incidents vs previous week
                        </Typography>
                      </>
                    ) : p1p2LastWeekCount > p1p2PrevWeekCount ? (
                      <>
                        <TrendingUp sx={{ fontSize: '16px', color: '#e74c3c' }} />
                        <Typography variant="body2" sx={{ fontSize: '11px', color: '#e74c3c', fontWeight: 600 }}>
                          {p1p2LastWeekCount - p1p2PrevWeekCount} more incidents vs previous week
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" sx={{ fontSize: '11px', color: '#888', fontWeight: 600 }}>
                        No change vs previous week
                      </Typography>
                    )
                  ) : (
                    p1p2LastMonthCount < p1p2PrevMonthCount ? (
                      <>
                        <TrendingDown sx={{ fontSize: '16px', color: '#27ae60' }} />
                        <Typography variant="body2" sx={{ fontSize: '11px', color: '#27ae60', fontWeight: 600 }}>
                          {p1p2PrevMonthCount - p1p2LastMonthCount} fewer incidents vs previous month
                        </Typography>
                      </>
                    ) : p1p2LastMonthCount > p1p2PrevMonthCount ? (
                      <>
                        <TrendingUp sx={{ fontSize: '16px', color: '#e74c3c' }} />
                        <Typography variant="body2" sx={{ fontSize: '11px', color: '#e74c3c', fontWeight: 600 }}>
                          {p1p2LastMonthCount - p1p2PrevMonthCount} more incidents vs previous month
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" sx={{ fontSize: '11px', color: '#888', fontWeight: 600 }}>
                        No change vs previous month
                      </Typography>
                    )
                  )}
                </Box>
              </>
            )}
        </Box>

        {/* P3/P4 Incidents Section */}
        <Box sx={{ 
          flex: 1,
          minWidth: '500px',
          p: 2, 
          bgcolor: '#fff', 
          border: '1px solid #c5e1a5',
          borderLeft: '4px solid #7cb342',
          borderRadius: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            {/* Section Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Warning sx={{ color: '#7cb342', fontSize: 18 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#7cb342', fontSize: '0.9rem' }}>
                P3/P4 - Medium & Low Priority
              </Typography>
            </Box>
          </Box>

          {/* Metrics Section */}
          {p3p4SectionLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
              <CircularProgress size={40} />
            </Box>
          ) : (
            <>
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                {/* Metrics in a row */}
                <Box sx={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                  {/* Total Incidents */}
                  <Box 
                    sx={{ 
                      textAlign: 'center',
                      cursor: 'pointer',
                      p: 1,
                      borderRadius: 1,
                      '&:hover': {
                        backgroundColor: '#f1f8e9'
                      }
                    }}
                    onClick={() => setP3P4IncidentDialogOpen(true)}
                  >
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666', display: 'block', mb: 0.5 }}>
                      Total Incidents
                    </Typography>
                    <Typography variant="h4" sx={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#7cb342' }}>
                      {totalP3P4FromAPI}
                    </Typography>
                  </Box>

                  {/* % Alert */}
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666', display: 'block', mb: 0.5 }}>
                      % Captured by Alert
                    </Typography>
                    <Typography variant="h4" sx={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#7cb342' }}>
                      {(() => {
                        const filtered = p3p4IncidentData.filter(inc => {
                          const priorityMatch = inc.priority === '3' || inc.priority === '4';
                          const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || inc.portfolio_name === selectedPortfolio;
                          const platformMatch = !selectedPlatform || selectedPlatform === 'all' || inc.platform_name === selectedPlatform;
                          return priorityMatch && portfolioMatch && platformMatch;
                        });
                        return filtered.length > 0 ? `${Math.round((filtered.filter(inc => inc.contact_type_display_value === 'Alert').length / filtered.length) * 100)}%` : 'N/A';
                      })()}
                    </Typography>
                  </Box>

                  {/* % Caused by Change */}
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666', display: 'block', mb: 0.5 }}>
                      % Caused by Change
                    </Typography>
                    <Typography variant="h4" sx={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#7cb342' }}>
                      {(() => {
                        const filtered = p3p4IncidentData.filter(inc => {
                          const priorityMatch = inc.priority === '3' || inc.priority === '4';
                          const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || inc.portfolio_name === selectedPortfolio;
                          const platformMatch = !selectedPlatform || selectedPlatform === 'all' || inc.platform_name === selectedPlatform;
                          return priorityMatch && portfolioMatch && platformMatch;
                        });
                        return filtered.length > 0 ? `${Math.round((filtered.filter(inc => inc.caused_by_display_value && inc.caused_by_display_value.includes('CHG')).length / filtered.length) * 100)}%` : 'N/A';
                      })()}
                    </Typography>
                  </Box>

              {/* MTTR */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666', display: 'block', mb: 0.5 }}>
                  MTTR(Avg)
                </Typography>
                <Typography variant="h4" sx={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#7cb342' }}>
                  {(() => {
                    const filtered = p3p4IncidentData.filter(inc => {
                      const priorityMatch = inc.priority === '3' || inc.priority === '4';
                      const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || inc.portfolio_name === selectedPortfolio;
                      const platformMatch = !selectedPlatform || selectedPlatform === 'all' || inc.platform_name === selectedPlatform;
                      return priorityMatch && portfolioMatch && platformMatch && mttrData.has(inc.number);
                    });
                    if (filtered.length === 0) return 'N/A';
                    const totalMTTR = filtered.reduce((sum, inc) => sum + getMTTRMinutes(inc.number), 0);
                    const avgMTTR = Math.round(totalMTTR / filtered.length);
                    const hours = Math.floor(avgMTTR / 60);
                    const minutes = avgMTTR % 60;
                    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
                  })()}
                </Typography>
              </Box>

                  {/* Top Contributor */}
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666', display: 'block', mb: 0.5 }}>
                      Top Incident Contributor
                    </Typography>
                    <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#7cb342' }}>
                      {(() => {
                        // Filter incidents based on portfolio and platform selection
                        const filteredIncidents = p3p4IncidentData.filter(inc => {
                          const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || inc.portfolio_name === selectedPortfolio;
                          const platformMatch = !selectedPlatform || selectedPlatform === 'all' || inc.platform_name === selectedPlatform;
                          return portfolioMatch && platformMatch;
                        });
                        const ciCounts: { [key: string]: number } = {};
                        filteredIncidents.forEach(inc => {
                          const ci = inc.cmdb_ci_display_value || 'Unknown';
                          ciCounts[ci] = (ciCounts[ci] || 0) + 1;
                        });
                        const topCI = Object.entries(ciCounts).sort((a, b) => b[1] - a[1])[0];
                        return topCI ? topCI[0] : 'N/A';
                      })()}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#999' }}>
                      {(() => {
                        // Filter incidents based on portfolio and platform selection
                        const filteredIncidents = p3p4IncidentData.filter(inc => {
                          const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || inc.portfolio_name === selectedPortfolio;
                          const platformMatch = !selectedPlatform || selectedPlatform === 'all' || inc.platform_name === selectedPlatform;
                          return portfolioMatch && platformMatch;
                        });
                        const ciCounts: { [key: string]: number } = {};
                        filteredIncidents.forEach(inc => {
                          const ci = inc.cmdb_ci_display_value || 'Unknown';
                          ciCounts[ci] = (ciCounts[ci] || 0) + 1;
                        });
                        const topCI = Object.entries(ciCounts).sort((a, b) => b[1] - a[1])[0];
                        return topCI ? `${topCI[1]} incidents` : '0 incidents';
                      })()}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              
              {/* Trend Display */}
              <Box 
                sx={{ 
                  mt: 2, 
                  pt: 1.5, 
                  borderTop: '1px solid #e8e8e8', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1
                }}
              >
                {trendPeriod === 'week' ? (
                  p3p4LastWeekCount < p3p4PrevWeekCount ? (
                    <>
                      <TrendingDown sx={{ fontSize: '16px', color: '#27ae60' }} />
                      <Typography variant="body2" sx={{ fontSize: '11px', color: '#27ae60', fontWeight: 600 }}>
                        {p3p4PrevWeekCount - p3p4LastWeekCount} fewer incidents vs previous week
                      </Typography>
                    </>
                  ) : p3p4LastWeekCount > p3p4PrevWeekCount ? (
                    <>
                      <TrendingUp sx={{ fontSize: '16px', color: '#e74c3c' }} />
                      <Typography variant="body2" sx={{ fontSize: '11px', color: '#e74c3c', fontWeight: 600 }}>
                        {p3p4LastWeekCount - p3p4PrevWeekCount} more incidents vs previous week
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2" sx={{ fontSize: '11px', color: '#888', fontWeight: 600 }}>
                      No change vs previous week
                    </Typography>
                  )
                ) : (
                  p3p4LastMonthCount < p3p4PrevMonthCount ? (
                    <>
                      <TrendingDown sx={{ fontSize: '16px', color: '#27ae60' }} />
                      <Typography variant="body2" sx={{ fontSize: '11px', color: '#27ae60', fontWeight: 600 }}>
                        {p3p4PrevMonthCount - p3p4LastMonthCount} fewer incidents vs previous month
                      </Typography>
                    </>
                  ) : p3p4LastMonthCount > p3p4PrevMonthCount ? (
                    <>
                      <TrendingUp sx={{ fontSize: '16px', color: '#e74c3c' }} />
                      <Typography variant="body2" sx={{ fontSize: '11px', color: '#e74c3c', fontWeight: 600 }}>
                        {p3p4LastMonthCount - p3p4PrevMonthCount} more incidents vs previous month
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2" sx={{ fontSize: '11px', color: '#888', fontWeight: 600 }}>
                      No change vs previous month
                    </Typography>
                  )
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* Change and Asset related metrics Section */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box sx={{
          flex: 1,
          minWidth: '500px',
          p: 2, 
          bgcolor: '#fff', 
          border: '1px solid #b3e5fc',
          borderLeft: '4px solid #0288d1',
          borderRadius: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            {/* Section Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DashboardIcon sx={{ color: '#0288d1', fontSize: 18 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0288d1', fontSize: '0.9rem' }}>
                Change and Asset related metrics
              </Typography>
            </Box>
          </Box>

          {/* Metrics Section */}
          {changeOnboardingLoading || certificationLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
              <CircularProgress size={40} />
            </Box>
          ) : (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              {/* Metrics in a row */}
              <Box sx={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                {/* % Onboarded MVCs */}
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666', display: 'block', mb: 0.5 }}>
                    % MVCs Onboarded for Change
                  </Typography>
                  <Typography variant="h4" sx={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#0288d1' }}>
                    {mvcOnboardingMetrics.percentageOnboarded}%
                  </Typography>
              
                </Box>

                {/* Red Channel Apps */}
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666', display: 'block', mb: 0.5 }}>
                    Red Channel Apps
                  </Typography>
                  <Typography variant="h4" sx={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#0288d1' }}>
                    {redChannelAppsCount}
                  </Typography>
                </Box>

                {/* MVC Certification % */}
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666', display: 'block', mb: 0.5 }}>
                    MVC Certification %
                  </Typography>
                  <Typography variant="h4" sx={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#0288d1' }}>
                    {mvcCertificationPercentage}%
                  </Typography>
                </Box>

                {/* Overall Certification % */}
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666', display: 'block', mb: 0.5 }}>
                    Overall Certification %
                  </Typography>
                  <Typography variant="h4" sx={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#0288d1' }}>
                    {overallCertificationPercentage}%
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Reliability Assessment Card */}


      {/* AI Insights Section */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SmartToy color="primary" />
          Health Insights {selectedPortfolio && `- ${selectedPortfolio}`}
        </Typography>
        <Grid container spacing={2}>
          {aiInsights.map((insight, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Alert 
                severity={getInsightColor(insight.type) as any}
                icon={getInsightIcon(insight.type)}
                sx={{ height: '100%' }}
              >
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  {insight.title}
                </Typography>
                {insight.title === 'Postmortem Insights' && postmortemLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" color="textSecondary">
                      Loading postmortem data...
                    </Typography>
                  </Box>
                ) : insight.title === 'Reliability Assessment Summary' && fullStackProgress ? (
                  <Box>
                    <Typography variant="body2" gutterBottom sx={{ whiteSpace: 'pre-line' }}>
                      {insight.description.split('**').map((part, i) => 
                        i % 2 === 1 ? <strong key={i}>{part}</strong> : part
                      )}
                    </Typography>
                    
                    {/* Trend Line */}
                    {fullStackProgress.improvements && (() => {
                      const vsData = trendPeriod === 'week' 
                        ? fullStackProgress.improvements.vs_last_week 
                        : fullStackProgress.improvements.vs_last_month;
                      
                      if (!vsData) return null;

                      const trends = [];
                      let hasPositive = false;
                      let hasNegative = false;
                      
                      if (vsData.crawl_percentage) {
                        const val = vsData.crawl_percentage.absolute;
                        if (val > 0) hasPositive = true;
                        if (val < 0) hasNegative = true;
                        trends.push({ label: 'Crawl', val });
                      }
                      if (vsData.walk_percentage) {
                        const val = vsData.walk_percentage.absolute;
                        if (val > 0) hasPositive = true;
                        if (val < 0) hasNegative = true;
                        trends.push({ label: 'Walk', val });
                      }
                      if (vsData.run_percentage) {
                        const val = vsData.run_percentage.absolute;
                        if (val > 0) hasPositive = true;
                        if (val < 0) hasNegative = true;
                        trends.push({ label: 'Run', val });
                      }

                      if (trends.length === 0) return null;

                      const overallColor = hasPositive && !hasNegative ? '#27ae60' : hasNegative && !hasPositive ? '#e74c3c' : '#888';
                      const TrendIcon = hasPositive && !hasNegative ? TrendingUp : hasNegative && !hasPositive ? TrendingDown : TrendingUp;

                      return (
                        <Box 
                          sx={{ 
                            mt: 2, 
                            pt: 1.5, 
                            borderTop: '1px solid #e8e8e8', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                          }}
                        >
                          <TrendIcon sx={{ fontSize: '16px', color: overallColor }} />
                          <Typography variant="body2" sx={{ fontSize: '11px', color: overallColor, fontWeight: 600 }}>
                            {trends.map(t => `${t.label} ${t.val > 0 ? '↑' : t.val < 0 ? '↓' : '→'}${Math.abs(t.val).toFixed(1)}%`).join(', ')} vs {trendPeriod === 'week' ? 'previous week' : 'previous month'}
                          </Typography>
                        </Box>
                      );
                    })()}
                  </Box>
                ) : insight.title === 'Resiliency Assessment Summary' && fullStackResiliencyProgress ? (
                  <Box>
                    <Typography variant="body2" gutterBottom sx={{ whiteSpace: 'pre-line' }}>
                      {insight.description.split('**').map((part, i) => 
                        i % 2 === 1 ? <strong key={i}>{part}</strong> : part
                      )}
                    </Typography>
                    
                    {/* Trend Line */}
                    {fullStackResiliencyProgress.improvements && (() => {
                      const vsData = trendPeriod === 'week' 
                        ? fullStackResiliencyProgress.improvements.vs_last_week 
                        : fullStackResiliencyProgress.improvements.vs_last_month;
                      
                      if (!vsData) return null;

                      const trends = [];
                      let hasPositive = false;
                      let hasNegative = false;
                      
                      if (vsData.trp_available_percentage) {
                        const val = vsData.trp_available_percentage.absolute;
                        if (val > 0) hasPositive = true;
                        if (val < 0) hasNegative = true;
                        trends.push({ label: 'TRP', val });
                      }
                      if (vsData.backup_available_percentage) {
                        const val = vsData.backup_available_percentage.absolute;
                        if (val > 0) hasPositive = true;
                        if (val < 0) hasNegative = true;
                        trends.push({ label: 'Backup Available', val });
                      }
                      if (vsData.failover_exists_percentage) {
                        const val = vsData.failover_exists_percentage.absolute;
                        if (val > 0) hasPositive = true;
                        if (val < 0) hasNegative = true;
                        trends.push({ label: 'Failover Exists', val });
                      }
                      if (vsData.restore_tested_percentage) {
                        const val = vsData.restore_tested_percentage.absolute;
                        if (val > 0) hasPositive = true;
                        if (val < 0) hasNegative = true;
                        trends.push({ label: 'Restore Tested', val });
                      }
                      if (vsData.failover_tested_percentage) {
                        const val = vsData.failover_tested_percentage.absolute;
                        if (val > 0) hasPositive = true;
                        if (val < 0) hasNegative = true;
                        trends.push({ label: 'Failover Tested', val });
                      }

                      if (trends.length === 0) return null;

                      const overallColor = hasPositive && !hasNegative ? '#27ae60' : hasNegative && !hasPositive ? '#e74c3c' : '#888';
                      const TrendIcon = hasPositive && !hasNegative ? TrendingUp : hasNegative && !hasPositive ? TrendingDown : TrendingUp;

                      return (
                        <Box 
                          sx={{ 
                            mt: 2, 
                            pt: 1.5, 
                            borderTop: '1px solid #e8e8e8', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                          }}
                        >
                          <TrendIcon sx={{ fontSize: '16px', color: overallColor }} />
                          <Typography variant="body2" sx={{ fontSize: '11px', color: overallColor, fontWeight: 600 }}>
                            {trends.map(t => `${t.label} ${t.val > 0 ? '↑' : t.val < 0 ? '↓' : '→'}${Math.abs(t.val).toFixed(2)}%`).join(', ')} vs {trendPeriod === 'week' ? 'previous week' : 'previous month'}
                          </Typography>
                        </Box>
                      );
                    })()}
                  </Box>
                ) : insight.title === 'Resiliency Assessment Summary' && !fullStackResiliencyProgress ? (
                  <Typography variant="body2" gutterBottom sx={{ fontStyle: 'italic' }}>
                    {insight.description}
                  </Typography>
                ) : insight.title === 'Top Signal-to-Noise Insights (Last 30 Days – Hedwig Alert Analysis)' && snrData.length > 0 ? (
                  <Box>
                    {snrData.map((app: any, idx: number) => {
                      const alertCount = parseInt(app.snr_alert_count);
                      const quickResolved = parseInt(app.snr_quick_resolved_alerts);
                      const snr = parseFloat(app.calculated_snr).toFixed(1);
                      const snrLastWeek = app.snr_last_week ? parseFloat(app.snr_last_week).toFixed(1) : null;
                      const snrLastMonth = app.snr_last_month ? parseFloat(app.snr_last_month).toFixed(1) : null;
                      
                      let trendText = '';
                      if (trendPeriod === 'week' && snrLastWeek) {
                        const diff = parseFloat(snr) - parseFloat(snrLastWeek);
                        trendText = ` (${diff > 0 ? '↑' : diff < 0 ? '↓' : '→'} ${Math.abs(diff).toFixed(1)}% vs last week)`;
                      } else if (trendPeriod === 'month' && snrLastMonth) {
                        const diff = parseFloat(snr) - parseFloat(snrLastMonth);
                        trendText = ` (${diff > 0 ? '↑' : diff < 0 ? '↓' : '→'} ${Math.abs(diff).toFixed(1)}% vs last month)`;
                      }
                      
                      return (
                        <Typography key={idx} variant="body2" gutterBottom sx={{ mb: 1 }}>
                          • <strong>
                            {app.snr_link ? (
                              <a 
                                href={app.snr_link} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{ color: '#1976d2', textDecoration: 'none' }}
                                onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                                onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                              >
                                {app.application}
                              </a>
                            ) : (
                              app.application
                            )}
                          </strong> – {alertCount} alerts: {quickResolved > 0 ? `${quickResolved} resolved in 15 minutes (potential noise)` : 'no quick resolutions'}; SNR ≈ {snr}%{trendText}
                        </Typography>
                      );
                    })}
                  </Box>
                ) : (
                  <Typography variant="body2" gutterBottom sx={{ whiteSpace: 'pre-line' }}>
                    {insight.description.split('**').map((part, i) => 
                      i % 2 === 1 ? <strong key={i}>{part}</strong> : part
                    )}
                  </Typography>
                )}
                {insight.title !== 'Reliability Assessment Summary' && 
                 insight.title !== 'Resiliency Assessment Summary' && 
                 insight.title !== 'Postmortem Completion Status' && 
                 insight.title !== 'Postmortem Action Items' && 
                 insight.title !== 'Postmortem Insights' && 
                 insight.title !== 'Top Signal-to-Noise Insights (Last 30 Days – Hedwig Alert Analysis)' && (
                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                    <strong>% Captured by Alert:</strong> {`${insight.confidence}%`}
                  </Typography>
                )}
                {insight.causedByChangePercentage !== undefined && (
                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                    <strong>% Caused by Change:</strong> {`${insight.causedByChangePercentage}%`}
                  </Typography>
                )}
                {insight.action && (
                  <Typography variant="caption" display="block" sx={{ mt: 1, fontStyle: 'italic' }}>
                    Action: {insight.action}
                  </Typography>
                )}
              </Alert>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Health Trends Chart */}
      {/* <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Portfolio Health Trends {selectedPortfolio && `- ${selectedPortfolio}`}
        </Typography>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={healthTrends}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis yAxisId="left" domain={[99, 100]} />
            <YAxis yAxisId="right" orientation="right" />
            <Tooltip />
            <Legend />
            <Line 
              yAxisId="left" 
              type="monotone" 
              dataKey="availability" 
              stroke="#1976d2" 
              strokeWidth={2}
              name="Availability (%)"
            />
            <Line 
              yAxisId="right" 
              type="monotone" 
              dataKey="incidents" 
              stroke="#f44336" 
              strokeWidth={2}
              name="Incidents"
            />
          </LineChart>
        </ResponsiveContainer>
      </Paper> */}

      {/* Health Summary Tabs Section */}
      <Paper elevation={2} sx={{ mb: 3 }}>
        <Tabs 
          value={healthSummaryTab} 
          onChange={(e, newValue) => setHealthSummaryTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 1 }}
        >
          <Tab label="MVC App Health Summary" />
          <Tab label="Outage Analytics" />
          <Tab label="Reliability Actions" />
          <Tab label="Resilience Actions" />
          <Tab label="Postmortem Open Actions" />
          {/* <Tab label="Jira Actions" /> */}
          {/* <Tab label="Decommissioning Status" /> */}{/* MOVED to standalone page under Reliability & Resilience */}
          <Tab label="Application Certification" />
          <Tab label="Change Onboarding" />
        </Tabs>

        {/* Tab 1: MVC App Health Summary */}
        {healthSummaryTab === 0 && (
          <Box>
            {/* Portfolio Health Table */}
            <Box sx={{ p: 2, borderBottom: '1px solid #e1e5e9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                MVC Application Health Details {selectedPortfolio && `- ${selectedPortfolio}`}
              </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<GetApp />}
              onClick={handleExportToCSV}
              sx={{
                borderColor: '#00A8CC',
                color: '#00A8CC',
                '&:hover': {
                  borderColor: '#0067b3',
                  backgroundColor: 'rgba(0, 168, 204, 0.04)',
                },
                fontSize: '12px',
                textTransform: 'none',
              }}
            >
              Export CSV
            </Button>
          </Box>
        </Box>
        
        {/* Search Bar */}
        <Box sx={{ p: 2, borderBottom: '1px solid #e1e5e9' }}>
          <TextField
            size="small"
            placeholder="Search applications by name, App ID, portfolio, or platform..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(0); // Reset to first page when searching
            }}
            InputProps={{
              startAdornment: <Search sx={{ color: '#6c757d', mr: 1 }} />,
            }}
            sx={{
              width: '400px',
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f8f9fa',
                '& fieldset': {
                  borderColor: '#e1e5e9',
                },
                '&:hover fieldset': {
                  borderColor: '#00A8CC',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#00A8CC',
                },
              },
            }}
          />
        </Box>

        {hedwigDataLoading && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1.5,
              borderBottom: '1px solid #e1e5e9',
              backgroundColor: '#fcfcfc'
            }}
          >
            <CircularProgress size={16} />
            <Typography variant="caption" sx={{ color: '#6c757d', fontWeight: 600 }}>
              Loading Hedwig on-call coverage...
            </Typography>
          </Box>
        )}

        {hedwigDataError && (
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" onClick={reloadHedwigData}>
                Retry
              </Button>
            }
            sx={{ mx: 2, mt: hedwigDataLoading ? 0 : 2, mb: 2 }}
          >
            Unable to load Hedwig team data. The Hedwig column will show placeholders until the data loads.
          </Alert>
        )}
        
        {sortedAndFilteredApps.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <DashboardIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="textSecondary" gutterBottom>
              No Applications Found
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {!selectedPortfolio 
                ? 'No applications available in the system.' 
                : `No applications found in ${selectedPortfolio} portfolio.`
              }
            </Typography>
          </Box>
        ) : (
        <Paper sx={{ 
          border: '1px solid #e1e5e9',
          borderRadius: '8px',
          overflow: 'hidden',
          backgroundColor: '#ffffff',
          boxShadow: 'none',
        }}>
          <TableContainer data-testid="portfolio-table-container">
            <Table stickyHeader sx={{ tableLayout: 'fixed', minWidth: '1400px' }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    fontSize: '13px',
                    color: '#2c3e50',
                    backgroundColor: '#f8f9fa',
                    borderBottom: '1px solid #e1e5e9',
                    py: 2,
                    minWidth: '100px',
                    textAlign: 'left',
                  }}>App ID</TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    fontSize: '13px',
                    color: '#2c3e50',
                    backgroundColor: '#f8f9fa',
                    borderBottom: '1px solid #e1e5e9',
                    py: 2,
                    minWidth: '150px',
                    textAlign: 'left',
                  }}>Platform Portfolio</TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    fontSize: '13px',
                    color: '#2c3e50',
                    backgroundColor: '#f8f9fa',
                    borderBottom: '1px solid #e1e5e9',
                    py: 2,
                    minWidth: '120px',
                    textAlign: 'left',
                  }}>Platform</TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    fontSize: '13px',
                    color: '#2c3e50',
                    backgroundColor: '#f8f9fa',
                    borderBottom: '1px solid #e1e5e9',
                    py: 2,
                    minWidth: '250px',
                    maxWidth: '250px',
                    textAlign: 'left',
                  }}>Application</TableCell>
                  {/* <TableCell sx={{ 
                    fontWeight: 600, 
                    fontSize: '13px',
                    color: '#2c3e50',
                    backgroundColor: '#f8f9fa',
                    borderBottom: '1px solid #e1e5e9',
                    py: 2,
                  }}>Health Score</TableCell> */}
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    fontSize: '13px',
                    color: '#2c3e50',
                    backgroundColor: '#f8f9fa',
                    borderBottom: '1px solid #e1e5e9',
                    py: 2,
                    cursor: 'pointer',
                    minWidth: '80px',
                    textAlign: 'center',
                    '&:hover': { backgroundColor: '#e9ecef' }
                  }} onClick={() => handleSort('p1p2')}>
                    P1/P2 {getSortIcon('p1p2')}
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    fontSize: '13px',
                    color: '#2c3e50',
                    backgroundColor: '#f8f9fa',
                    borderBottom: '1px solid #e1e5e9',
                    py: 2,
                    cursor: 'pointer',
                    minWidth: '90px',
                    textAlign: 'center',
                    '&:hover': { backgroundColor: '#e9ecef' }
                  }} onClick={() => handleSort('p3p4mi')}>
                    P3/P4(MI) {getSortIcon('p3p4mi')}
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    fontSize: '13px',
                    color: '#2c3e50',
                    backgroundColor: '#f8f9fa',
                    borderBottom: '1px solid #e1e5e9',
                    py: 2,
                    cursor: 'pointer',
                    minWidth: '160px',
                    textAlign: 'center',
                    '&:hover': { backgroundColor: '#e9ecef' }
                  }} onClick={() => handleSort('incidentsCausedByChange')}>
                    Incident Caused by Change {getSortIcon('incidentsCausedByChange')}
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    fontSize: '13px',
                    color: '#2c3e50',
                    backgroundColor: '#f8f9fa',
                    borderBottom: '1px solid #e1e5e9',
                    py: 2,
                    cursor: 'pointer',
                    minWidth: '110px',
                    textAlign: 'center',
                    '&:hover': { backgroundColor: '#e9ecef' }
                  }} onClick={() => handleSort('goldenSignalScore')}>
                    <MuiTooltip title="Golden Signal monitoring score (0-100) based on incident capture rate" arrow placement="top">
                      <span>Golden Signal {getSortIcon('goldenSignalScore')}</span>
                    </MuiTooltip>
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    fontSize: '13px',
                    color: '#2c3e50',
                    backgroundColor: '#f8f9fa',
                    borderBottom: '1px solid #e1e5e9',
                    py: 2,
                    cursor: 'pointer',
                    minWidth: '130px',
                    textAlign: 'center',
                    '&:hover': { backgroundColor: '#e9ecef' }
                  }} onClick={() => handleSort('nfr')}>
                    <MuiTooltip title="Non-Functional Requirements (NFR) test certification status from iPace/Loadstar" arrow placement="top">
                      <span>NFR Certification {getSortIcon('nfr')}</span>
                    </MuiTooltip>
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    fontSize: '13px',
                    color: '#2c3e50',
                    backgroundColor: '#f8f9fa',
                    borderBottom: '1px solid #e1e5e9',
                    py: 2,
                    cursor: 'pointer',
                    minWidth: '120px',
                    textAlign: 'center',
                    '&:hover': { backgroundColor: '#e9ecef' }
                  }} onClick={() => handleSort('onCallRotaStatus')}>
                    <MuiTooltip title="On-call rotation status from Hedwig - shows if team has active on-call coverage" arrow placement="top">
                      <span>On Call Rota {getSortIcon('onCallRotaStatus')}</span>
                    </MuiTooltip>
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    fontSize: '13px',
                    color: '#2c3e50',
                    backgroundColor: '#f8f9fa',
                    borderBottom: '1px solid #e1e5e9',
                    py: 2,
                    cursor: 'pointer',
                    minWidth: '120px',
                    textAlign: 'center',
                    '&:hover': { backgroundColor: '#e9ecef' }
                  }} onClick={() => handleSort('calculatedSnr')}>
                    <MuiTooltip title="Signal-to-Noise Ratio - measures alert quality (lower is better)" arrow placement="top">
                      <span>Signal to Noise {getSortIcon('calculatedSnr')}</span>
                    </MuiTooltip>
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    fontSize: '13px',
                    color: '#2c3e50',
                    backgroundColor: '#f8f9fa',
                    borderBottom: '1px solid #e1e5e9',
                    py: 2,
                    cursor: 'pointer',
                    minWidth: '130px',
                    textAlign: 'center',
                    '&:hover': { backgroundColor: '#e9ecef' }
                  }} onClick={() => handleSort('sloAvailabilityMtd')}>
                    <MuiTooltip title="Service Level Objective Availability - percentage uptime for the current month" arrow placement="top">
                      <span>SLO Availability {getSortIcon('sloAvailabilityMtd')}</span>
                    </MuiTooltip>
                  </TableCell>
                </TableRow>
              </TableHead>
            <TableBody>
              {paginatedApps.map((app, index) => {
                const healthScore = getHealthScore(app);
                return (
                  <TableRow 
                    key={index} 
                    sx={{ 
                      '&:hover': { backgroundColor: '#f8f9fa' },
                      borderBottom: '1px solid #e1e5e9',
                    }}
                  >
                    <TableCell sx={{ 
                      fontSize: '13px',
                      py: 2,
                      borderBottom: '1px solid #e1e5e9',
                      minWidth: '100px',
                      textAlign: 'left',
                    }}>
                      <Button
                        variant="text"
                        onClick={() => setSelectedApp(selectedApp === app.appId ? null : app.appId)}
                        sx={{ 
                          fontWeight: 600,
                          fontSize: '13px',
                          color: '#0067b3',
                          textTransform: 'none',
                          padding: '4px 8px',
                          minWidth: 'auto',
                        }}
                      >
                        {app.appId}
                      </Button>
                    </TableCell>
                    <TableCell sx={{ 
                      fontSize: '13px',
                      py: 2,
                      borderBottom: '1px solid #e1e5e9',
                      minWidth: '150px',
                      textAlign: 'left',
                    }}>{app.platformPortfolio}</TableCell>
                    <TableCell sx={{ 
                      fontSize: '13px',
                      py: 2,
                      borderBottom: '1px solid #e1e5e9',
                      minWidth: '120px',
                      textAlign: 'left',
                    }}>{app.platform}</TableCell>
                    <TableCell sx={{ 
                      fontSize: '13px',
                      py: 2,
                      borderBottom: '1px solid #e1e5e9',
                      minWidth: '250px',
                      maxWidth: '250px',
                      textAlign: 'left',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      <MuiTooltip title={app.application} placement="top">
                        <span>{app.application}</span>
                      </MuiTooltip>
                    </TableCell>
                    {/* <TableCell sx={{ 
                      fontSize: '13px',
                      py: 2,
                      borderBottom: '1px solid #e1e5e9',
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={healthScore}
                          sx={{ 
                            width: 60, 
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: '#e1e5e9',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: getHealthColor(healthScore),
                              borderRadius: 3,
                            }
                          }}
                        />
                        <Typography variant="body2" sx={{ 
                          fontWeight: 600, 
                          color: getHealthColor(healthScore),
                          fontSize: '12px',
                        }}>
                          {healthScore}
                        </Typography>
                      </Box>
                    </TableCell> */}
                    {/* P1/P2 Cell */}
                    <TableCell sx={{ 
                      fontSize: '13px',
                      py: 2,
                      borderBottom: '1px solid #e1e5e9',
                      minWidth: '80px',
                      textAlign: 'center',
                    }}>
                      {(() => {
                        // Calculate alert capture percentage for this app's P1/P2 incidents
                        const appP1P2Incidents = incidentData.filter(inc => 
                          inc.cmdb_ci_u_application_id === app.appId && 
                          (inc.priority === '1' || inc.priority === '2')
                        );
                        const alertCapturedCount = appP1P2Incidents.filter(inc => 
                          inc.contact_type_display_value && 
                          inc.contact_type_display_value.toLowerCase() === 'alert'
                        ).length;
                        const alertCapturePercentage = appP1P2Incidents.length > 0 
                          ? Math.round((alertCapturedCount / appP1P2Incidents.length) * 100) 
                          : 100;
                        
                        // Show orange if there are P1/P2 incidents but low alert capture (< 50%)
                        const hasLowAlertCapture = app.p1p2 > 0 && alertCapturePercentage < 50;
                        
                        return (
                          <MuiTooltip 
                            title={
                              app.p1p2 > 0 
                                ? hasLowAlertCapture 
                                  ? (
                                    <Box sx={{ p: 1 }}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.9rem', color: '#fff' }}>
                                        <Warning sx={{ fontSize: '16px', color: '#ffb74d' }} /> Low Alert Coverage
                                      </Typography>
                                      <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.8rem', color: '#fff' }}>
                                        {alertCapturePercentage}% alert-based detection for P1/P2 incidents.
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: '#ffcc80', fontStyle: 'italic', fontSize: '0.75rem' }}>
                                        {app.fullStackMonitoring === 4 
                                          ? '💡 Enable Hedwig–ServiceNow MIM integration for critical alerts.'
                                          : '💡 Improve monitoring coverage.'}
                                      </Typography>
                                    </Box>
                                  )
                                  : `✓ ${alertCapturePercentage}% alert coverage - Excellent monitoring!`
                                : 'No P1/P2 incidents'
                            }
                            arrow
                            placement="top"
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  bgcolor: hasLowAlertCapture ? '#424242' : 'rgba(97, 97, 97, 0.92)',
                                  fontSize: '0.875rem',
                                  maxWidth: 320,
                                  '& .MuiTooltip-arrow': {
                                    color: hasLowAlertCapture ? '#424242' : 'rgba(97, 97, 97, 0.92)',
                                  },
                                }
                              }
                            }}
                          >
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center', 
                              gap: 1
                            }}>
                              <Chip
                                label={app.p1p2}
                                color={app.p1p2 === 0 ? 'success' : hasLowAlertCapture ? 'warning' : alertCapturePercentage === 100 ? 'success' : 'error'}
                                size="small"
                                sx={{ 
                                  fontSize: '11px',
                                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                  ...(hasLowAlertCapture && {
                                    background: 'linear-gradient(135deg, #ff9800 0%, #fb8c00 100%)',
                                    color: '#fff',
                                    fontWeight: 700,
                                    boxShadow: '0 2px 8px rgba(255, 152, 0, 0.35)',
                                    border: '1px solid rgba(245, 124, 0, 0.5)',
                                    '&:hover': {
                                      boxShadow: '0 4px 12px rgba(255, 152, 0, 0.45)',
                                      transform: 'translateY(-2px)'
                                    }
                                  }),
                                  ...(app.p1p2 > 0 && alertCapturePercentage === 100 && {
                                    background: 'linear-gradient(135deg, #27ae60 0%, #229954 100%)',
                                    color: '#fff',
                                    fontWeight: 700,
                                    boxShadow: '0 2px 8px rgba(39, 174, 96, 0.35)',
                                    border: '1px solid rgba(34, 153, 84, 0.5)',
                                    '&:hover': {
                                      boxShadow: '0 4px 12px rgba(39, 174, 96, 0.45)',
                                      transform: 'translateY(-2px)'
                                    }
                                  }),
                                  ...(!hasLowAlertCapture && app.p1p2 > 0 && alertCapturePercentage !== 100 && {
                                    '&:hover': {
                                      transform: 'translateY(-1px)',
                                      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)'
                                    }
                                  })
                                }}
                              />
                            </Box>
                          </MuiTooltip>
                        );
                      })()}
                    </TableCell>
                    {/* P3/P4(MI) Cell */}
                    <TableCell sx={{ 
                      fontSize: '13px',
                      py: 2,
                      borderBottom: '1px solid #e1e5e9',
                      minWidth: '90px',
                      textAlign: 'center',
                    }}>
                      {(() => {
                        // Calculate alert capture percentage for this app's P3/P4 incidents
                        const appP3P4Incidents = p3p4IncidentData.filter(inc => 
                          inc.cmdb_ci_u_application_id === app.appId && 
                          (inc.priority === '3' || inc.priority === '4')
                        );
                        const alertCapturedCount = appP3P4Incidents.filter(inc => 
                          inc.contact_type_display_value && 
                          inc.contact_type_display_value.toLowerCase() === 'alert'
                        ).length;
                        const alertCapturePercentage = appP3P4Incidents.length > 0 
                          ? Math.round((alertCapturedCount / appP3P4Incidents.length) * 100) 
                          : 100;
                        
                        // Show orange if there are P3/P4 incidents but low alert capture (< 80%)
                        const hasLowAlertCapture = app.p3p4mi > 0 && alertCapturePercentage < 80;
                        
                        return (
                          <MuiTooltip 
                            title={
                              app.p3p4mi > 0 
                                ? hasLowAlertCapture 
                                  ? (
                                    <Box sx={{ p: 1 }}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.9rem', color: '#fff' }}>
                                        <Warning sx={{ fontSize: '16px', color: '#ffb74d' }} /> Low Alert Coverage
                                      </Typography>
                                      <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.8rem', color: '#fff' }}>
                                        {alertCapturePercentage}% alert-based detection for P3/P4 incidents.
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: '#ffcc80', fontStyle: 'italic', fontSize: '0.75rem' }}>
                                        {app.fullStackMonitoring === 4 
                                          ? '💡 Enable Hedwig–ServiceNow MIM integration for critical alerts.'
                                          : '💡 Improve monitoring coverage.'}
                                      </Typography>
                                    </Box>
                                  )
                                  : `✓ ${alertCapturePercentage}% alert coverage - Excellent monitoring!`
                                : 'No P3/P4 incidents'
                            }
                            arrow
                            placement="top"
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  bgcolor: hasLowAlertCapture ? '#424242' : 'rgba(97, 97, 97, 0.92)',
                                  fontSize: '0.875rem',
                                  maxWidth: 320,
                                  '& .MuiTooltip-arrow': {
                                    color: hasLowAlertCapture ? '#424242' : 'rgba(97, 97, 97, 0.92)',
                                  },
                                }
                              }
                            }}
                          >
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center', 
                              gap: 1
                            }}>
                              <Chip
                                label={app.p3p4mi}
                                color={app.p3p4mi === 0 ? 'success' : hasLowAlertCapture ? 'warning' : alertCapturePercentage >= 80 ? 'success' : 'error'}
                                size="small"
                                sx={{ 
                                  fontSize: '11px',
                                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                  ...(hasLowAlertCapture && {
                                    background: 'linear-gradient(135deg, #ff9800 0%, #fb8c00 100%)',
                                    color: '#fff',
                                    fontWeight: 700,
                                    boxShadow: '0 2px 8px rgba(255, 152, 0, 0.35)',
                                    border: '1px solid rgba(245, 124, 0, 0.5)',
                                    '&:hover': {
                                      boxShadow: '0 4px 12px rgba(255, 152, 0, 0.45)',
                                      transform: 'translateY(-2px)'
                                    }
                                  }),
                                  ...(app.p3p4mi > 0 && alertCapturePercentage >= 80 && {
                                    background: 'linear-gradient(135deg, #27ae60 0%, #229954 100%)',
                                    color: '#fff',
                                    fontWeight: 700,
                                    boxShadow: '0 2px 8px rgba(39, 174, 96, 0.35)',
                                    border: '1px solid rgba(34, 153, 84, 0.5)',
                                    '&:hover': {
                                      boxShadow: '0 4px 12px rgba(39, 174, 96, 0.45)',
                                      transform: 'translateY(-2px)'
                                    }
                                  }),
                                  ...(!hasLowAlertCapture && app.p3p4mi > 0 && alertCapturePercentage < 80 && {
                                    '&:hover': {
                                      transform: 'translateY(-1px)',
                                      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)'
                                    }
                                  })
                                }}
                              />
                            </Box>
                          </MuiTooltip>
                        );
                      })()}
                    </TableCell>
                    {/* Incident Caused by Change Cell */}
                    <TableCell sx={{ 
                      fontSize: '13px',
                      py: 2,
                      borderBottom: '1px solid #e1e5e9',
                      minWidth: '160px',
                      textAlign: 'center',
                    }}>
                      <MuiTooltip 
                        title={
                          parseInt(app.incidentsCausedByChange) > 0 
                            ? `Incident IDs: ${getIncidentIdsForChangeApp(app.appId).join(', ') || 'N/A'}` 
                            : 'No incidents caused by change'
                        }
                        arrow
                        placement="top"
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                          <Chip
                            label={app.incidentsCausedByChange}
                            color={parseInt(app.incidentsCausedByChange) === 0 ? 'success' : 'warning'}
                            size="small"
                            sx={{ 
                              fontSize: '11px',
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              ...(parseInt(app.incidentsCausedByChange) > 0 && {
                                background: 'linear-gradient(135deg, #ff9800 0%, #fb8c00 100%)',
                                color: '#fff',
                                fontWeight: 700,
                                boxShadow: '0 2px 8px rgba(255, 152, 0, 0.35)',
                                border: '1px solid rgba(245, 124, 0, 0.5)',
                                '&:hover': {
                                  boxShadow: '0 4px 12px rgba(255, 152, 0, 0.45)',
                                  transform: 'translateY(-2px)'
                                }
                              })
                            }}
                          />
                        </Box>
                      </MuiTooltip>
                    </TableCell>
                    {/* Golden Signal Cell */}
                    <TableCell sx={{ 
                      fontSize: '13px',
                      py: 2,
                      borderBottom: '1px solid #e1e5e9',
                      minWidth: '120px',
                      textAlign: 'center',
                    }}>
                      <MuiTooltip 
                        title={app.goldenSignalScore !== null 
                          ? `Score: ${app.goldenSignalScore}/100\nIncidents: ${app.goldenSignalTotalIncidents}\nCapture Rate: ${app.goldenSignalCapturePercentage}%` 
                          : 'No data available'}
                        arrow
                        placement="top"
                      >
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                          {app.goldenSignalScore !== null ? (
                            <Chip
                              label={`${app.goldenSignalScore}`}
                              color={app.goldenSignalScore === 100 ? 'success' : app.goldenSignalScore >= 50 ? 'warning' : 'error'}
                              size="small"
                              sx={{ 
                                fontSize: '11px',
                                fontWeight: 700,
                                ...(app.goldenSignalScore === 100 && {
                                  background: 'linear-gradient(135deg, #27ae60 0%, #229954 100%)',
                                  color: '#fff',
                                  boxShadow: '0 2px 8px rgba(39, 174, 96, 0.35)',
                                  border: '1px solid rgba(34, 153, 84, 0.5)',
                                }),
                                ...(app.goldenSignalScore >= 50 && app.goldenSignalScore < 100 && {
                                  background: 'linear-gradient(135deg, #ff9800 0%, #fb8c00 100%)',
                                  color: '#fff',
                                  boxShadow: '0 2px 8px rgba(255, 152, 0, 0.35)',
                                  border: '1px solid rgba(245, 124, 0, 0.5)',
                                }),
                                ...(app.goldenSignalScore < 50 && {
                                  background: 'linear-gradient(135deg, #d32f2f 0%, #c62828 100%)',
                                  color: '#fff',
                                  boxShadow: '0 2px 8px rgba(211, 47, 47, 0.35)',
                                  border: '1px solid rgba(198, 40, 40, 0.5)',
                                })
                              }}
                            />
                          ) : (
                            <Chip
                              label="NA"
                              size="small"
                              sx={{ 
                                fontSize: '11px',
                                backgroundColor: '#e0e0e0',
                                color: '#666',
                                fontWeight: 600
                              }}
                            />
                          )}
                        </Box>
                      </MuiTooltip>
                    </TableCell>
                    {/* NFR Certification Cell */}
                    <TableCell sx={{ 
                      fontSize: '13px',
                      py: 2,
                      borderBottom: '1px solid #e1e5e9',
                      minWidth: '170px',
                      maxWidth: '220px',
                      textAlign: 'center',
                    }}>
                      {(() => {
                        // Find NFR test insights matching this app's macId (appId)
                        const appNfrRecords = nfrData.filter(nfr => 
                          nfr.macId.includes(app.appId)
                        );
                        
                        if (nfrLoading) {
                          return (
                            <MuiTooltip 
                              title="Loading NFR test data..."
                              arrow
                              placement="top"
                            >
                              <Box sx={{ display: 'inline-flex' }}>
                                <Chip
                                  label="Loading..."
                                  size="small"
                                  sx={{ 
                                    fontSize: '11px',
                                    backgroundColor: '#e0e0e0',
                                    color: '#666',
                                    fontWeight: 600
                                  }}
                                />
                              </Box>
                            </MuiTooltip>
                          );
                        }
                        
                        if (nfrError || appNfrRecords.length === 0) {
                          return (
                            <MuiTooltip 
                              title="No NFR test data available for this application"
                              arrow
                              placement="top"
                            >
                              <Box sx={{ display: 'inline-flex' }}>
                                <Chip
                                  label="No data"
                                  size="small"
                                  sx={{ 
                                    fontSize: '11px',
                                    backgroundColor: '#e0e0e0',
                                    color: '#666',
                                    fontWeight: 600
                                  }}
                                />
                              </Box>
                            </MuiTooltip>
                          );
                        }
                        
                        // Aggregate all nfrStatus values from all records
                        const allStatuses = appNfrRecords.flatMap(record => record.nfrStatus);
                        
                        // Display all unique nfrStatus values
                        const uniqueStatuses = Array.from(new Set(allStatuses));
                        let statusLabel = uniqueStatuses.length > 0 ? uniqueStatuses.join(', ') : 'No status';
                        
                        // Determine color based on status keywords
                        let chipColor: 'success' | 'error' | 'warning' = 'warning';
                        let bgGradient = '';
                        
                        const statusLower = statusLabel.toLowerCase();
                        if (statusLower.includes('passed') && !statusLower.includes('failed')) {
                          chipColor = 'success';
                          bgGradient = 'linear-gradient(135deg, #27ae60 0%, #229954 100%)';
                        } else if (statusLower.includes('failed')) {
                          chipColor = 'error';
                          bgGradient = 'linear-gradient(135deg, #d32f2f 0%, #c62828 100%)';
                        } else {
                          chipColor = 'warning';
                          bgGradient = 'linear-gradient(135deg, #ff9800 0%, #fb8c00 100%)';
                        }
                        
                        const tooltipText = appNfrRecords.map((record, idx) => 
                          `Test ${idx + 1}:\n` +
                          `  Status: ${record.nfrStatus.join(', ')}\n` +
                          `  Type: ${record.testType.join(', ')}\n` +
                          `  Date: ${record.lastTestExecutionDate}`
                        ).join('\n\n');
                        
                        return (
                          <MuiTooltip 
                            title={tooltipText}
                            arrow
                            placement="top"
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                              <Chip
                                label={statusLabel}
                                color={chipColor}
                                size="small"
                                sx={{ 
                                  fontSize: '11px',
                                  fontWeight: 700,
                                  height: 'auto',
                                  maxWidth: '100%',
                                  '& .MuiChip-label': {
                                    display: 'block',
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                    textAlign: 'center',
                                    lineHeight: 1.2,
                                    py: 0.5,
                                  },
                                  ...(bgGradient && {
                                    background: bgGradient,
                                    color: '#fff',
                                    boxShadow: `0 2px 8px ${
                                      chipColor === 'success' ? 'rgba(39, 174, 96, 0.35)' :
                                      chipColor === 'error' ? 'rgba(211, 47, 47, 0.35)' :
                                      'rgba(255, 152, 0, 0.35)'
                                    }`,
                                    border: `1px solid ${
                                      chipColor === 'success' ? 'rgba(34, 153, 84, 0.5)' :
                                      chipColor === 'error' ? 'rgba(198, 40, 40, 0.5)' :
                                      'rgba(245, 124, 0, 0.5)'
                                    }`,
                                  })
                                }}
                              />
                            </Box>
                          </MuiTooltip>
                        );
                      })()}
                    </TableCell>
                    <TableCell sx={{ 
                      fontSize: '13px',
                      py: 2,
                      borderBottom: '1px solid #e1e5e9',
                      minWidth: '120px',
                      textAlign: 'center',
                    }}>
                      <MuiTooltip 
                        title={app.onCallRotaStatus === 'Yes' 
                          ? `On-Call Users: ${app.uniqueOnCallUsers}\n${app.onCallUserNames ? app.onCallUserNames.substring(0, 100) + (app.onCallUserNames.length > 100 ? '...' : '') : ''}` 
                          : app.onCallRotaStatus === 'No Service'
                          ? 'Team is onboarded but no service configured'
                          : 'No Hedwig team onboarded'}
                        arrow
                        placement="top"
                      >
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Chip
                            label={app.onCallRotaStatus}
                            color={app.onCallRotaStatus === 'Yes' ? 'success' : app.onCallRotaStatus === 'No' ? 'error' : 'warning'}
                            size="small"
                            sx={{ 
                              fontSize: '11px',
                              fontWeight: 700,
                              ...(app.onCallRotaStatus === 'Yes' && {
                                background: 'linear-gradient(135deg, #27ae60 0%, #229954 100%)',
                                color: '#fff',
                                boxShadow: '0 2px 8px rgba(39, 174, 96, 0.35)',
                                border: '1px solid rgba(34, 153, 84, 0.5)',
                              }),
                              ...(app.onCallRotaStatus === 'No' && {
                                background: 'linear-gradient(135deg, #d32f2f 0%, #c62828 100%)',
                                color: '#fff',
                                boxShadow: '0 2px 8px rgba(211, 47, 47, 0.35)',
                                border: '1px solid rgba(198, 40, 40, 0.5)',
                              }),
                              ...(app.onCallRotaStatus !== 'Yes' && app.onCallRotaStatus !== 'No' && {
                                background: 'linear-gradient(135deg, #ff9800 0%, #fb8c00 100%)',
                                color: '#fff',
                                boxShadow: '0 2px 8px rgba(255, 152, 0, 0.35)',
                                border: '1px solid rgba(245, 124, 0, 0.5)',
                              })
                            }}
                          />
                        </Box>
                      </MuiTooltip>
                    </TableCell>
                    {/* Signal to Noise Cell - MOVED BEFORE SLO Availability */}
                    <TableCell sx={{ 
                      fontSize: '13px',
                      py: 2,
                      borderBottom: '1px solid #e1e5e9',
                      minWidth: '120px',
                      textAlign: 'center',
                    }}>
                      <MuiTooltip title={app.snrLink ? `${getSnrDisplayInfo(app).tooltip} - Click to view detailed SNR report` : getSnrDisplayInfo(app).tooltip}>
                        <Box
                          component={app.snrLink ? 'a' : 'div'}
                          href={app.snrLink || undefined}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            display: 'inline-block',
                            textDecoration: 'none',
                            cursor: app.snrLink ? 'pointer' : 'default',
                            '&:hover .MuiChip-root': app.snrLink ? {
                              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                              transform: 'translateY(-1px)',
                              transition: 'all 0.2s ease'
                            } : {}
                          }}
                        >
                          <Chip
                            label={getSnrDisplayInfo(app).label}
                            color={getSnrDisplayInfo(app).color}
                            size="small"
                            icon={app.snrLink ? <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z"/></svg> : undefined}
                            sx={{
                              fontSize: '11px',
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              ...(getSnrDisplayInfo(app).color === 'success' && {
                                background: 'linear-gradient(135deg, #27ae60 0%, #229954 100%)',
                                color: '#fff',
                                fontWeight: 700,
                                boxShadow: '0 2px 8px rgba(39, 174, 96, 0.35)',
                                border: '1px solid rgba(34, 153, 84, 0.5)',
                                '&:hover': {
                                  boxShadow: '0 4px 12px rgba(39, 174, 96, 0.45)',
                                  transform: 'translateY(-2px)'
                                }
                              }),
                              ...(getSnrDisplayInfo(app).color === 'warning' && {
                                background: 'linear-gradient(135deg, #ff9800 0%, #fb8c00 100%)',
                                color: '#fff',
                                fontWeight: 700,
                                boxShadow: '0 2px 8px rgba(255, 152, 0, 0.35)',
                                border: '1px solid rgba(245, 124, 0, 0.5)',
                                '&:hover': {
                                  boxShadow: '0 4px 12px rgba(255, 152, 0, 0.45)',
                                  transform: 'translateY(-2px)'
                                }
                              })
                            }}
                          />
                        </Box>
                      </MuiTooltip>
                    </TableCell>
                    {/* SLO Availability Cell - MOVED AFTER Signal to Noise */}
                    <TableCell sx={{ 
                      fontSize: '13px',
                      py: 2,
                      borderBottom: '1px solid #e1e5e9',
                      minWidth: '130px',
                      textAlign: 'center',
                    }}>
                      <MuiTooltip 
                        title={app.sloAvailabilityMtd > 0 
                          ? `SLO Availability: ${app.sloAvailabilityMtd.toFixed(2)}%` 
                          : 'No SLO data available'}
                        arrow
                        placement="top"
                      >
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Chip
                            label={app.sloAvailabilityMtd > 0 ? `${app.sloAvailabilityMtd.toFixed(2)}%` : 'NA'}
                            color={app.sloAvailabilityMtd >= 99 ? 'success' : app.sloAvailabilityMtd >= 95 ? 'warning' : app.sloAvailabilityMtd > 0 ? 'error' : 'default'}
                            size="small"
                            sx={{ 
                              fontSize: '11px',
                              fontWeight: 700,
                              ...(app.sloAvailabilityMtd >= 99 && {
                                background: 'linear-gradient(135deg, #27ae60 0%, #229954 100%)',
                                color: '#fff',
                                boxShadow: '0 2px 8px rgba(39, 174, 96, 0.35)',
                                border: '1px solid rgba(34, 153, 84, 0.5)',
                              }),
                              ...(app.sloAvailabilityMtd >= 95 && app.sloAvailabilityMtd < 99 && {
                                background: 'linear-gradient(135deg, #ff9800 0%, #fb8c00 100%)',
                                color: '#fff',
                                boxShadow: '0 2px 8px rgba(255, 152, 0, 0.35)',
                                border: '1px solid rgba(245, 124, 0, 0.5)',
                              }),
                              ...(app.sloAvailabilityMtd > 0 && app.sloAvailabilityMtd < 95 && {
                                background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                                color: '#fff',
                                boxShadow: '0 2px 8px rgba(231, 76, 60, 0.35)',
                                border: '1px solid rgba(192, 57, 43, 0.5)',
                              })
                            }}
                          />
                        </Box>
                      </MuiTooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
              
              {/* Total Row */}
              <TableRow sx={{ 
                backgroundColor: '#e3f2fd',
                borderTop: '2px solid #1976d2',
                '& td': { fontWeight: 600, fontSize: '14px', borderBottom: 'none' }
              }}>
                <TableCell sx={{ 
                  fontWeight: 700,
                  fontSize: '14px',
                  color: '#1976d2',
                  py: 2,
                  minWidth: '100px',
                  textAlign: 'left',
                }}>
                  TOTAL
                </TableCell>
                <TableCell sx={{ minWidth: '150px', textAlign: 'left' }}>-</TableCell>
                <TableCell sx={{ minWidth: '120px', textAlign: 'left' }}>-</TableCell>
                <TableCell sx={{ minWidth: '180px', textAlign: 'left' }}>-</TableCell>
                <TableCell sx={{ color: '#1976d2', minWidth: '80px', textAlign: 'center' }}>
                  {portfolioTotals.totalP1 + portfolioTotals.totalP2}
                </TableCell>
                <TableCell sx={{ color: '#1976d2', minWidth: '90px', textAlign: 'center' }}>
                  {portfolioTotals.totalP3P4}
                </TableCell>
                <TableCell sx={{ color: '#1976d2', minWidth: '160px', textAlign: 'center' }}>
                  {portfolioTotals.totalIncidentViaChange}
                </TableCell>
                <TableCell sx={{ minWidth: '110px', textAlign: 'center' }}>-</TableCell>
                <TableCell sx={{ minWidth: '130px', textAlign: 'center' }}>-</TableCell>
                <TableCell sx={{ minWidth: '120px', textAlign: 'center' }}>-</TableCell>
                <TableCell sx={{ minWidth: '120px', textAlign: 'center' }}>-</TableCell>
                <TableCell sx={{ minWidth: '130px', textAlign: 'center' }}>-</TableCell>
              </TableRow>
            </TableBody>
          </Table>
          </TableContainer>
          
          {/* Pagination */}
          <TablePagination
            component="div"
            count={sortedAndFilteredApps.length}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              borderTop: '1px solid #e1e5e9',
              backgroundColor: '#f8f9fa',
              '& .MuiTablePagination-toolbar': {
                minHeight: '52px',
              },
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                fontSize: '13px',
                color: '#6c757d',
              },
            }}
          />
            </Paper>
            )}
          </Box>
        )}

        {/* Tab 4: Postmortem Open Action Items */}
        {healthSummaryTab === 4 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  {postmortemView === 'actions' ? 'Open Postmortem Status' : 'Postmortem Trends'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Data Source: <Link href="https://sre-postmortem-prod.maersk-digital.net/" target="_blank" rel="noopener noreferrer" sx={{ color: '#1976d2', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Postmortem</Link>
                </Typography>
              </Box>
              
              {/* Toggle Button */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant={postmortemView === 'actions' ? 'contained' : 'outlined'}
                  onClick={() => setPostmortemView('actions')}
                  disabled={postmortemLoading}
                  sx={{
                    textTransform: 'none',
                    bgcolor: postmortemView === 'actions' ? '#1976d2' : 'transparent',
                    color: postmortemView === 'actions' ? 'white' : '#1976d2',
                    borderColor: '#1976d2',
                    '&:hover': {
                      bgcolor: postmortemView === 'actions' ? '#1565c0' : 'rgba(25, 118, 210, 0.04)',
                      borderColor: '#1976d2'
                    }
                  }}
                >
                  Open Actions
                </Button>
                <Button
                  variant={postmortemView === 'trends' ? 'contained' : 'outlined'}
                  onClick={() => setPostmortemView('trends')}
                  disabled={postmortemLoading}
                  sx={{
                    textTransform: 'none',
                    bgcolor: postmortemView === 'trends' ? '#1976d2' : 'transparent',
                    color: postmortemView === 'trends' ? 'white' : '#1976d2',
                    borderColor: '#1976d2',
                    '&:hover': {
                      bgcolor: postmortemView === 'trends' ? '#1565c0' : 'rgba(25, 118, 210, 0.04)',
                      borderColor: '#1976d2'
                    }
                  }}
                >
                  Trends
                </Button>
              </Box>
            </Box>
            
            {/* Loading State */}
            {postmortemLoading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: 400, gap: 2 }}>
                <CircularProgress size={60} />
                <Typography variant="h6" color="textSecondary">
                  Loading postmortem and action item data...
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  This may take a moment
                </Typography>
              </Box>
            ) : (
              <>
            {/* Conditional Rendering based on view */}
            {postmortemView === 'trends' ? (
              <>
                <PostmortemTrends 
                  selectedPortfolio={selectedPortfolio}
                  selectedPlatform={selectedPlatform}
                />
                <PostmortemActionStatus 
                  selectedPortfolio={selectedPortfolio}
                  selectedPlatform={selectedPlatform}
                  postmortemDetailsData={postmortemDetailsData}
                />
              </>
            ) : (
              <>
            {/* Postmortem Status Filter Chips */}
            {(() => {
              // Calculate counts for each status - exclude DONE, INCOMPLETE, and LTREVIEW
              // Also apply portfolio and platform filters to match the table
              let openPostmortems = postmortemDetailsData.filter((pm: any) => 
                pm.incidentStatus && 
                pm.incidentStatus.toUpperCase() !== 'DONE' &&
                pm.incidentStatus.toUpperCase() !== 'INCOMPLETE' &&
                pm.incidentStatus.toUpperCase() !== 'LTREVIEW'
              );
              
              // Apply portfolio filter (by causedBy_portfolio)
              if (selectedPortfolio && selectedPortfolio !== 'all') {
                openPostmortems = openPostmortems.filter((pm: any) => 
                  pm.causedBy_portfolio === selectedPortfolio
                );
              }
              
              // Apply platform filter (by causedBy_platform)
              if (selectedPlatform && selectedPlatform !== 'all') {
                openPostmortems = openPostmortems.filter((pm: any) => 
                  pm.causedBy_platform === selectedPlatform
                );
              }
              
              const statusCounts: { [key: string]: number } = {};
              openPostmortems.forEach((pm: any) => {
                const status = pm.incidentStatus || 'Unknown';
                statusCounts[status] = (statusCounts[status] || 0) + 1;
              });
              
              const totalCount = openPostmortems.length;
              
              // Define status labels with proper formatting
              // Flow order: New → Progress → SRE Review (excluding LT Review)
              const statusLabels: { [key: string]: string } = {
                'NEW': 'New',
                'PROGRESS': 'Progress',
                'SREREVIEW': 'SRE Review'
              };
              
              return (
                <Box sx={{ 
                  mb: 2, 
                  display: 'flex', 
                  gap: 2, 
                  alignItems: 'center',
                  flexWrap: 'wrap'
                }}>
                  {/* Status Filter Chips */}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Chip
                      label={`All (${totalCount})`}
                      onClick={() => setPostmortemStatusFilter('all')}
                      variant={postmortemStatusFilter === 'all' ? 'filled' : 'outlined'}
                      sx={{ 
                        cursor: 'pointer',
                        fontWeight: 600,
                        bgcolor: postmortemStatusFilter === 'all' ? '#1976d2 !important' : 'transparent',
                        color: postmortemStatusFilter === 'all' ? '#fff !important' : '#666',
                        borderColor: postmortemStatusFilter === 'all' ? '#1976d2' : '#ccc',
                        '&:hover': {
                          bgcolor: postmortemStatusFilter === 'all' ? '#1565c0 !important' : '#f5f5f5'
                        }
                      }}
                    />
                    {Object.keys(statusLabels).map((statusKey) => {
                      const count = statusCounts[statusKey] || 0;
                      if (count > 0) {
                        const isSelected = postmortemStatusFilter === statusKey;
                        // LT Review is progressive (green), others are orange
                        const isProgressive = statusKey === 'LTREVIEW';
                        const chipColor = isProgressive ? '#27ae60' : '#ff9800';
                        const chipHoverColor = isProgressive ? '#229954' : '#f57c00';
                        
                        return (
                          <Chip
                            key={statusKey}
                            label={`${statusLabels[statusKey]} (${count})`}
                            onClick={() => setPostmortemStatusFilter(statusKey)}
                            variant={isSelected ? 'filled' : 'outlined'}
                            sx={{ 
                              cursor: 'pointer',
                              fontWeight: 600,
                              bgcolor: isSelected ? `${chipColor} !important` : 'transparent',
                              color: isSelected ? '#fff !important' : '#666',
                              borderColor: isSelected ? chipColor : '#ccc',
                              '&:hover': {
                                bgcolor: isSelected ? `${chipHoverColor} !important` : '#f5f5f5'
                              }
                            }}
                          />
                        );
                      }
                      return null;
                    })}
                  </Box>
                  
                  {/* Search Bar */}
                  <TextField
                    size="small"
                    placeholder="Search by Incident ID, Impacted App, Causing Portfolio, App, or Platform..."
                    value={postmortemSearchQuery}
                    onChange={(e) => setPostmortemSearchQuery(e.target.value)}
                    sx={{ flexGrow: 1, minWidth: '300px', maxWidth: '500px' }}
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="11" cy="11" r="8"></circle>
                            <path d="m21 21-4.35-4.35"></path>
                          </svg>
                        </Box>
                      ),
                    }}
                  />
                  
                  {/* Export Button */}
                  <Button
                    variant="contained"
                    onClick={handleExportPostmortemToCSV}
                    sx={{
                      textTransform: 'none',
                      bgcolor: '#1976d2',
                      '&:hover': { bgcolor: '#1565c0' },
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Export to CSV
                  </Button>
                </Box>
              );
            })()}
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={postmortemSortConfig?.key === 'incidentID'}
                        direction={postmortemSortConfig?.key === 'incidentID' ? postmortemSortConfig.direction : 'asc'}
                        onClick={() => {
                          const isAsc = postmortemSortConfig?.key === 'incidentID' && postmortemSortConfig.direction === 'asc';
                          setPostmortemSortConfig({ key: 'incidentID', direction: isAsc ? 'desc' : 'asc' });
                        }}
                      >
                        <strong>Incident ID</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={postmortemSortConfig?.key === 'priority'}
                        direction={postmortemSortConfig?.key === 'priority' ? postmortemSortConfig.direction : 'asc'}
                        onClick={() => {
                          const isAsc = postmortemSortConfig?.key === 'priority' && postmortemSortConfig.direction === 'asc';
                          setPostmortemSortConfig({ key: 'priority', direction: isAsc ? 'desc' : 'asc' });
                        }}
                      >
                        <strong>Priority</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={postmortemSortConfig?.key === 'createdOn'}
                        direction={postmortemSortConfig?.key === 'createdOn' ? postmortemSortConfig.direction : 'asc'}
                        onClick={() => {
                          const isAsc = postmortemSortConfig?.key === 'createdOn' && postmortemSortConfig.direction === 'asc';
                          setPostmortemSortConfig({ key: 'createdOn', direction: isAsc ? 'desc' : 'asc' });
                        }}
                      >
                        <strong>Created On</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={postmortemSortConfig?.key === 'assignedTo'}
                        direction={postmortemSortConfig?.key === 'assignedTo' ? postmortemSortConfig.direction : 'asc'}
                        onClick={() => {
                          const isAsc = postmortemSortConfig?.key === 'assignedTo' && postmortemSortConfig.direction === 'asc';
                          setPostmortemSortConfig({ key: 'assignedTo', direction: isAsc ? 'desc' : 'asc' });
                        }}
                      >
                        <strong>Postmortem Owner</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={postmortemSortConfig?.key === 'cmdbCI'}
                        direction={postmortemSortConfig?.key === 'cmdbCI' ? postmortemSortConfig.direction : 'asc'}
                        onClick={() => {
                          const isAsc = postmortemSortConfig?.key === 'cmdbCI' && postmortemSortConfig.direction === 'asc';
                          setPostmortemSortConfig({ key: 'cmdbCI', direction: isAsc ? 'desc' : 'asc' });
                        }}
                      >
                        <strong>Impacted App</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={postmortemSortConfig?.key === 'application'}
                        direction={postmortemSortConfig?.key === 'application' ? postmortemSortConfig.direction : 'asc'}
                        onClick={() => {
                          const isAsc = postmortemSortConfig?.key === 'application' && postmortemSortConfig.direction === 'asc';
                          setPostmortemSortConfig({ key: 'application', direction: isAsc ? 'desc' : 'asc' });
                        }}
                      >
                        <strong>Causing App</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={postmortemSortConfig?.key === 'platform'}
                        direction={postmortemSortConfig?.key === 'platform' ? postmortemSortConfig.direction : 'asc'}
                        onClick={() => {
                          const isAsc = postmortemSortConfig?.key === 'platform' && postmortemSortConfig.direction === 'asc';
                          setPostmortemSortConfig({ key: 'platform', direction: isAsc ? 'desc' : 'asc' });
                        }}
                      >
                        <strong>Causing Platform</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={postmortemSortConfig?.key === 'portfolio'}
                        direction={postmortemSortConfig?.key === 'portfolio' ? postmortemSortConfig.direction : 'asc'}
                        onClick={() => {
                          const isAsc = postmortemSortConfig?.key === 'portfolio' && postmortemSortConfig.direction === 'asc';
                          setPostmortemSortConfig({ key: 'portfolio', direction: isAsc ? 'desc' : 'asc' });
                        }}
                      >
                        <strong>Causing Portfolio</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={postmortemSortConfig?.key === 'description'}
                        direction={postmortemSortConfig?.key === 'description' ? postmortemSortConfig.direction : 'asc'}
                        onClick={() => {
                          const isAsc = postmortemSortConfig?.key === 'description' && postmortemSortConfig.direction === 'asc';
                          setPostmortemSortConfig({ key: 'description', direction: isAsc ? 'desc' : 'asc' });
                        }}
                      >
                        <strong>Description</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={postmortemSortConfig?.key === 'status'}
                        direction={postmortemSortConfig?.key === 'status' ? postmortemSortConfig.direction : 'asc'}
                        onClick={() => {
                          const isAsc = postmortemSortConfig?.key === 'status' && postmortemSortConfig.direction === 'asc';
                          setPostmortemSortConfig({ key: 'status', direction: isAsc ? 'desc' : 'asc' });
                        }}
                      >
                        <strong>Status</strong>
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    // Filter to show only open postmortems (status not DONE, not INCOMPLETE, and not LTREVIEW)
                    let openPostmortems = postmortemDetailsData.filter((pm: any) => 
                      pm.incidentStatus && 
                      pm.incidentStatus.toUpperCase() !== 'DONE' &&
                      pm.incidentStatus.toUpperCase() !== 'INCOMPLETE' &&
                      pm.incidentStatus.toUpperCase() !== 'LTREVIEW'
                    );
                    
                    // Apply portfolio filter for postmortem table (by causedBy_portfolio)
                    if (selectedPortfolio && selectedPortfolio !== 'all') {
                      openPostmortems = openPostmortems.filter((pm: any) => 
                        pm.causedBy_portfolio === selectedPortfolio
                      );
                    }
                    
                    // Apply platform filter for postmortem table (by causedBy_platform)
                    if (selectedPlatform && selectedPlatform !== 'all') {
                      openPostmortems = openPostmortems.filter((pm: any) => 
                        pm.causedBy_platform === selectedPlatform
                      );
                    }
                    
                    // Apply status filter
                    let filteredPostmortems = openPostmortems;
                    if (postmortemStatusFilter !== 'all') {
                      filteredPostmortems = openPostmortems.filter((pm: any) => 
                        pm.incidentStatus === postmortemStatusFilter
                      );
                    }
                    
                    // Apply search filter
                    if (postmortemSearchQuery) {
                      const query = postmortemSearchQuery.toLowerCase();
                      filteredPostmortems = filteredPostmortems.filter((pm: any) => {
                        const incidentNumber = pm.incidentID?.toLowerCase() || '';
                        const impactedApp = pm.cmdb_ci?.toLowerCase() || '';
                        const causingPortfolio = pm.causedBy_portfolio?.toLowerCase() || '';
                        const causingApp = pm.causedBy_application?.toLowerCase() || '';
                        const causingPlatform = pm.causedBy_platform?.toLowerCase() || '';
                        
                        return incidentNumber.includes(query) ||
                               impactedApp.includes(query) ||
                               causingPortfolio.includes(query) ||
                               causingApp.includes(query) ||
                               causingPlatform.includes(query);
                      });
                    }
                    
                    // Apply sorting if configured
                    let sortedPostmortems = [...filteredPostmortems];
                    if (postmortemSortConfig) {
                      sortedPostmortems.sort((a, b) => {
                        let aValue: any;
                        let bValue: any;
                        
                        switch (postmortemSortConfig.key) {
                          case 'incidentID':
                            aValue = a.incidentID || '';
                            bValue = b.incidentID || '';
                            break;
                          case 'createdOn':
                            aValue = a.createdOn ? new Date(a.createdOn).getTime() : 0;
                            bValue = b.createdOn ? new Date(b.createdOn).getTime() : 0;
                            break;
                          case 'assignedTo':
                            aValue = a.assigned_to || '';
                            bValue = b.assigned_to || '';
                            break;
                          case 'cmdbCI':
                            aValue = a.cmdb_ci || '';
                            bValue = b.cmdb_ci || '';
                            break;
                          case 'portfolio':
                            aValue = a.causedBy_portfolio || '';
                            bValue = b.causedBy_portfolio || '';
                            break;
                          case 'application':
                            aValue = a.causedBy_application || '';
                            bValue = b.causedBy_application || '';
                            break;
                          case 'platform':
                            aValue = a.causedBy_platform || '';
                            bValue = b.causedBy_platform || '';
                            break;
                          case 'description':
                            aValue = a.description || '';
                            bValue = b.description || '';
                            break;
                          case 'priority':
                            aValue = a.priority || '';
                            bValue = b.priority || '';
                            break;
                          case 'status':
                            aValue = a.incidentStatus || '';
                            bValue = b.incidentStatus || '';
                            break;
                          default:
                            return 0;
                        }
                        
                        if (aValue < bValue) {
                          return postmortemSortConfig.direction === 'asc' ? -1 : 1;
                        }
                        if (aValue > bValue) {
                          return postmortemSortConfig.direction === 'asc' ? 1 : -1;
                        }
                        return 0;
                      });
                    }
                    
                    // Apply pagination
                    const paginatedPostmortems = sortedPostmortems.slice(
                      postmortemPage * postmortemRowsPerPage,
                      postmortemPage * postmortemRowsPerPage + postmortemRowsPerPage
                    );
                    
                    if (sortedPostmortems.length === 0) {
                      return (
                        <TableRow>
                          <TableCell colSpan={9} align="center">
                            <Typography variant="body2" color="text.secondary">
                              No open postmortems found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    }
                    
                    return paginatedPostmortems.map((pm: any, index: number) => {
                      // Extract incident number and URL
                      const getIncidentInfo = (idField: string) => {
                        if (!idField) return { number: null, url: null };
                        
                        // Check if it's a URL
                        if (idField.includes('service-now.com')) {
                          const match = idField.match(/number=([A-Z0-9]+)/);
                          return {
                            number: match ? match[1] : null,
                            url: idField
                          };
                        }
                        
                        // If it's just an incident number (like INC1234567)
                        if (idField.match(/^[A-Z]{3}\d+$/)) {
                          return {
                            number: idField,
                            url: `https://maersk.service-now.com/incident.do?sysparm_query=number=${idField}`
                          };
                        }
                        
                        return { number: idField, url: null };
                      };
                      
                      // Extract incident number from various formats
                      const extractIncidentNumber = (idField: string) => {
                        if (!idField) return null;
                        
                        // If it's a URL, extract the incident number
                        if (idField.includes('service-now.com') || idField.includes('incidentID=')) {
                          const match = idField.match(/(?:number=|incidentID=)([A-Z0-9]+)/);
                          return match ? match[1] : idField;
                        }
                        
                        // If it's just an incident number
                        if (idField.match(/^[A-Z]{3}\d+$/)) {
                          return idField;
                        }
                        
                        return idField;
                      };
                      
                      const incidentNumber = extractIncidentNumber(pm.incidentID);
                      const postmortemLink = pm.customID || null;
                      
                      return (
                        <TableRow key={index} hover>
                          <TableCell>
                            {incidentNumber && postmortemLink ? (
                              <Link
                                href={postmortemLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ 
                                  color: '#00A8CC',
                                  textDecoration: 'none',
                                  '&:hover': {
                                    textDecoration: 'underline'
                                  }
                                }}
                              >
                                {incidentNumber}
                              </Link>
                            ) : (incidentNumber || 'N/A')}
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={`P${pm.priority}`} 
                              color={pm.priority === '1' ? 'error' : 'warning'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{pm.createdOn ? new Date(pm.createdOn).toLocaleDateString() : 'N/A'}</TableCell>
                          <TableCell>{pm.assigned_to || 'N/A'}</TableCell>
                          <TableCell>{pm.cmdb_ci || 'N/A'}</TableCell>
                          <TableCell>{pm.causedBy_application || 'N/A'}</TableCell>
                          <TableCell>{pm.causedBy_platform || 'N/A'}</TableCell>
                          <TableCell>{pm.causedBy_portfolio || 'N/A'}</TableCell>
                          <TableCell sx={{ maxWidth: 300 }}>
                            {pm.description ? (
                              <MuiTooltip title={pm.description} arrow>
                                <Typography variant="body2" noWrap>
                                  {pm.description}
                                </Typography>
                              </MuiTooltip>
                            ) : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const status = pm.incidentStatus?.toLowerCase() || 'n/a';
                              
                              // Define RAG status colors
                              let bgcolor = '#f5f5f5'; // Default gray
                              let textColor = '#666';
                              
                              // Green - Completed/Done or LT Review (progressive)
                              if (status === 'done' || status === 'completed' || status === 'closed' || status === 'ltreview') {
                                bgcolor = '#d4edda';
                                textColor = '#155724';
                              }
                              // Amber/Yellow - In Progress/SRE Review
                              else if (status.includes('progress') || status === 'srereview' || 
                                       (status.includes('review') && status !== 'ltreview')) {
                                bgcolor = '#fff3cd';
                                textColor = '#856404';
                              }
                              // Red - Incomplete/Blocked
                              else if (status === 'incomplete' || status.includes('blocked') || 
                                       status.includes('stuck')) {
                                bgcolor = '#f8d7da';
                                textColor = '#721c24';
                              }
                              
                              return (
                                <Chip 
                                  label={pm.incidentStatus || 'N/A'} 
                                  size="small"
                                  sx={{
                                    backgroundColor: `${bgcolor} !important`,
                                    color: `${textColor} !important`,
                                    fontWeight: 600,
                                    '& .MuiChip-label': {
                                      color: `${textColor} !important`
                                    }
                                  }}
                                />
                              );
                            })()}
                          </TableCell>
                        </TableRow>
                      );
                    });
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Pagination for Postmortem Table */}
            {(() => {
              let openPostmortems = postmortemDetailsData.filter((pm: any) => 
                pm.incidentStatus && 
                pm.incidentStatus.toUpperCase() !== 'DONE' &&
                pm.incidentStatus.toUpperCase() !== 'LTREVIEW'
              );
              
              // Apply portfolio filter (by causedBy_portfolio)
              if (selectedPortfolio && selectedPortfolio !== 'all') {
                openPostmortems = openPostmortems.filter((pm: any) => 
                  pm.causedBy_portfolio === selectedPortfolio
                );
              }
              
              // Apply platform filter (by causedBy_platform)
              if (selectedPlatform && selectedPlatform !== 'all') {
                openPostmortems = openPostmortems.filter((pm: any) => 
                  pm.causedBy_platform === selectedPlatform
                );
              }
              
              return openPostmortems.length > 0 ? (
                <TablePagination
                  component="div"
                  count={openPostmortems.length}
                  page={postmortemPage}
                  onPageChange={(event, newPage) => setPostmortemPage(newPage)}
                  rowsPerPage={postmortemRowsPerPage}
                  onRowsPerPageChange={(event) => {
                    setPostmortemRowsPerPage(parseInt(event.target.value, 10));
                    setPostmortemPage(0);
                  }}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  sx={{
                    borderTop: '1px solid #e1e5e9',
                    backgroundColor: '#f8f9fa',
                    '& .MuiTablePagination-toolbar': {
                      minHeight: '52px',
                    },
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                      fontSize: '13px',
                      color: '#6c757d',
                    },
                  }}
                />
              ) : null;
            })()}

            {/* Open Action Items Table */}
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom>
                Open Action Item Status
              </Typography>
              
              {/* Action Items Filter Chips */}
              {(() => {
                // Calculate counts for each category
                const today = new Date();
                const fifteenDaysFromNow = new Date();
                fifteenDaysFromNow.setDate(today.getDate() + 15);
                
                let within15Count = 0;
                let after15Count = 0;
                let pastDueCount = 0;
                let totalCount = 0;
                
                postmortemDetailsData.forEach((pm: any) => {
                  if (pm.actionDetails && Array.isArray(pm.actionDetails)) {
                    pm.actionDetails.forEach((action: any) => {
                      // Case-insensitive portfolio comparison
                      const actionPortfolio = (action.portfolio || '').toLowerCase();
                      const selectedPortfolioLower = (selectedPortfolio || '').toLowerCase();
                      if (!selectedPortfolio || selectedPortfolio === 'all' || actionPortfolio === selectedPortfolioLower) {
                        const actionStatus = action.status ? action.status.toLowerCase() : '';
                        if (actionStatus !== 'done' && actionStatus !== 'closed' && actionStatus !== '3') {
                          totalCount++;
                          
                          if (action.dueDate && action.dueDate !== '') {
                            try {
                              const dateStr = action.dueDate.trim();
                              let dueDate: Date | null = null;
                              
                              // Parse date based on format
                              if (dateStr.includes(' ') || dateStr.split('-')[0].length <= 2) {
                                // PTASK format: DD-MM-YYYY HH:mm:ss
                                const dateTimeParts = dateStr.split(' ');
                                const datePart = dateTimeParts[0];
                                const parts = datePart.split('-');
                                if (parts.length === 3) {
                                  const day = parts[0];
                                  const month = parts[1];
                                  const year = parts[2];
                                  dueDate = new Date(`${year}-${month}-${day}`);
                                }
                              } else {
                                // JIRA format: YYYY-MM-DD
                                dueDate = new Date(dateStr);
                              }
                              
                              if (dueDate && !isNaN(dueDate.getTime())) {
                                if (dueDate < today) {
                                  pastDueCount++;
                                } else if (dueDate <= fifteenDaysFromNow) {
                                  within15Count++;
                                } else {
                                  after15Count++;
                                }
                              }
                            } catch (error) {
                              // Skip invalid dates
                            }
                          }
                        }
                      }
                    });
                  }
                });
                
                return (
                  <Box sx={{ 
                    mb: 2, 
                    display: 'flex', 
                    gap: 2, 
                    alignItems: 'center',
                    flexWrap: 'wrap'
                  }}>
                    {/* Due Date Filter Chips */}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Chip
                        label={`All (${totalCount})`}
                        onClick={() => setActionItemsDueDateFilter('all')}
                        variant={actionItemsDueDateFilter === 'all' ? 'filled' : 'outlined'}
                        sx={{ 
                          cursor: 'pointer',
                          fontWeight: 600,
                          bgcolor: actionItemsDueDateFilter === 'all' ? '#1976d2 !important' : 'transparent',
                          color: actionItemsDueDateFilter === 'all' ? '#fff !important' : '#666',
                          borderColor: actionItemsDueDateFilter === 'all' ? '#1976d2' : '#ccc',
                          '&:hover': {
                            bgcolor: actionItemsDueDateFilter === 'all' ? '#1565c0 !important' : '#f5f5f5'
                          }
                        }}
                      />
                      <Chip
                        label={`Open actions due within 15 days (${within15Count})`}
                        onClick={() => setActionItemsDueDateFilter('within15')}
                        variant={actionItemsDueDateFilter === 'within15' ? 'filled' : 'outlined'}
                        sx={{ 
                          cursor: 'pointer',
                          fontWeight: 600,
                          bgcolor: actionItemsDueDateFilter === 'within15' ? '#ff9800 !important' : 'transparent',
                          color: actionItemsDueDateFilter === 'within15' ? '#fff !important' : '#666',
                          borderColor: actionItemsDueDateFilter === 'within15' ? '#ff9800' : '#ccc',
                          '&:hover': {
                            bgcolor: actionItemsDueDateFilter === 'within15' ? '#f57c00 !important' : '#f5f5f5'
                          }
                        }}
                      />
                      <Chip
                        label={`Open actions due after 15 days (${after15Count})`}
                        onClick={() => setActionItemsDueDateFilter('after15')}
                        variant={actionItemsDueDateFilter === 'after15' ? 'filled' : 'outlined'}
                        sx={{ 
                          cursor: 'pointer',
                          fontWeight: 600,
                          bgcolor: actionItemsDueDateFilter === 'after15' ? '#0288d1 !important' : 'transparent',
                          color: actionItemsDueDateFilter === 'after15' ? '#fff !important' : '#666',
                          borderColor: actionItemsDueDateFilter === 'after15' ? '#0288d1' : '#ccc',
                          '&:hover': {
                            bgcolor: actionItemsDueDateFilter === 'after15' ? '#01579b !important' : '#f5f5f5'
                          }
                        }}
                      />
                      <Chip
                        label={`Open Action Past due date (${pastDueCount})`}
                        onClick={() => setActionItemsDueDateFilter('pastdue')}
                        variant={actionItemsDueDateFilter === 'pastdue' ? 'filled' : 'outlined'}
                        sx={{ 
                          cursor: 'pointer',
                          fontWeight: 600,
                          bgcolor: actionItemsDueDateFilter === 'pastdue' ? '#d32f2f !important' : 'transparent',
                          color: actionItemsDueDateFilter === 'pastdue' ? '#fff !important' : '#666',
                          borderColor: actionItemsDueDateFilter === 'pastdue' ? '#d32f2f' : '#ccc',
                          '&:hover': {
                            bgcolor: actionItemsDueDateFilter === 'pastdue' ? '#c62828 !important' : '#f5f5f5'
                          }
                        }}
                      />
                    </Box>
                    
                    {/* Category Filter Chips */}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', width: '100%' }}>
                      {(() => {
                        // Calculate category counts
                        const categoryCounts: Record<string, number> = {
                          'all': 0,
                          'Monitoring, Alerting & Observability': 0,
                          'Database Performance & Optimization': 0,
                          'Automation & Infrastructure Resilience': 0,
                          'Change Management, Testing & Governance': 0,
                          'Documentation, Training & Process Improvement': 0,
                          'Uncategorized': 0
                        };
                        
                        if (postmortemDetailsData && Array.isArray(postmortemDetailsData)) {
                          postmortemDetailsData.forEach((pm: any) => {
                            if (pm.actionDetails && Array.isArray(pm.actionDetails)) {
                              pm.actionDetails.forEach((action: any) => {
                                const portfolio = action.portfolio || '';
                                const portfolioLower = portfolio.toLowerCase();
                                const selectedPortfolioLower = (selectedPortfolio || '').toLowerCase();
                                const status = (action.status || '').toLowerCase();
                                const isOpen = status !== 'done' && status !== 'closed' && status !== '3';
                                
                                // Filter by portfolio if selected (and not 'all'), otherwise include all (case-insensitive)
                                const portfolioMatches = !selectedPortfolio || selectedPortfolio === 'all' || portfolioLower === selectedPortfolioLower;
                                
                                if (portfolioMatches && isOpen) {
                                  categoryCounts['all']++;
                                  const { category } = categorizeAction(action.description || '');
                                  if (categoryCounts[category] !== undefined) {
                                    categoryCounts[category]++;
                                  }
                                }
                              });
                            }
                          });
                        }
                        
                        return (
                          <>
                            <Chip
                              label={`All Categories (${categoryCounts['all']})`}
                              onClick={() => setActionItemsCategoryFilter('all')}
                              variant={actionItemsCategoryFilter === 'all' ? 'filled' : 'outlined'}
                              sx={{ 
                                cursor: 'pointer',
                                fontWeight: 600,
                                bgcolor: actionItemsCategoryFilter === 'all' ? '#607d8b !important' : 'transparent',
                                color: actionItemsCategoryFilter === 'all' ? '#fff !important' : '#666',
                                borderColor: actionItemsCategoryFilter === 'all' ? '#607d8b' : '#ccc',
                                '&:hover': {
                                  bgcolor: actionItemsCategoryFilter === 'all' ? '#546e7a !important' : '#f5f5f5'
                                }
                              }}
                            />
                            <Chip
                              label={`Monitoring (${categoryCounts['Monitoring, Alerting & Observability']})`}
                              onClick={() => setActionItemsCategoryFilter('Monitoring, Alerting & Observability')}
                              variant={actionItemsCategoryFilter === 'Monitoring, Alerting & Observability' ? 'filled' : 'outlined'}
                              sx={{ 
                                cursor: 'pointer',
                                fontWeight: 600,
                                bgcolor: actionItemsCategoryFilter === 'Monitoring, Alerting & Observability' ? '#1976d2 !important' : 'transparent',
                                color: actionItemsCategoryFilter === 'Monitoring, Alerting & Observability' ? '#fff !important' : '#666',
                                borderColor: actionItemsCategoryFilter === 'Monitoring, Alerting & Observability' ? '#1976d2' : '#ccc',
                                '&:hover': {
                                  bgcolor: actionItemsCategoryFilter === 'Monitoring, Alerting & Observability' ? '#1565c0 !important' : '#f5f5f5'
                                }
                              }}
                            />
                            <Chip
                              label={`Database (${categoryCounts['Database Performance & Optimization']})`}
                              onClick={() => setActionItemsCategoryFilter('Database Performance & Optimization')}
                              variant={actionItemsCategoryFilter === 'Database Performance & Optimization' ? 'filled' : 'outlined'}
                              sx={{ 
                                cursor: 'pointer',
                                fontWeight: 600,
                                bgcolor: actionItemsCategoryFilter === 'Database Performance & Optimization' ? '#7b1fa2 !important' : 'transparent',
                                color: actionItemsCategoryFilter === 'Database Performance & Optimization' ? '#fff !important' : '#666',
                                borderColor: actionItemsCategoryFilter === 'Database Performance & Optimization' ? '#7b1fa2' : '#ccc',
                                '&:hover': {
                                  bgcolor: actionItemsCategoryFilter === 'Database Performance & Optimization' ? '#6a1b9a !important' : '#f5f5f5'
                                }
                              }}
                            />
                            <Chip
                              label={`Automation (${categoryCounts['Automation & Infrastructure Resilience']})`}
                              onClick={() => setActionItemsCategoryFilter('Automation & Infrastructure Resilience')}
                              variant={actionItemsCategoryFilter === 'Automation & Infrastructure Resilience' ? 'filled' : 'outlined'}
                              sx={{ 
                                cursor: 'pointer',
                                fontWeight: 600,
                                bgcolor: actionItemsCategoryFilter === 'Automation & Infrastructure Resilience' ? '#388e3c !important' : 'transparent',
                                color: actionItemsCategoryFilter === 'Automation & Infrastructure Resilience' ? '#fff !important' : '#666',
                                borderColor: actionItemsCategoryFilter === 'Automation & Infrastructure Resilience' ? '#388e3c' : '#ccc',
                                '&:hover': {
                                  bgcolor: actionItemsCategoryFilter === 'Automation & Infrastructure Resilience' ? '#2e7d32 !important' : '#f5f5f5'
                                }
                              }}
                            />
                            <Chip
                              label={`Change Mgmt (${categoryCounts['Change Management, Testing & Governance']})`}
                              onClick={() => setActionItemsCategoryFilter('Change Management, Testing & Governance')}
                              variant={actionItemsCategoryFilter === 'Change Management, Testing & Governance' ? 'filled' : 'outlined'}
                              sx={{ 
                                cursor: 'pointer',
                                fontWeight: 600,
                                bgcolor: actionItemsCategoryFilter === 'Change Management, Testing & Governance' ? '#f57c00 !important' : 'transparent',
                                color: actionItemsCategoryFilter === 'Change Management, Testing & Governance' ? '#fff !important' : '#666',
                                borderColor: actionItemsCategoryFilter === 'Change Management, Testing & Governance' ? '#f57c00' : '#ccc',
                                '&:hover': {
                                  bgcolor: actionItemsCategoryFilter === 'Change Management, Testing & Governance' ? '#ef6c00 !important' : '#f5f5f5'
                                }
                              }}
                            />
                            <Chip
                              label={`Documentation (${categoryCounts['Documentation, Training & Process Improvement']})`}
                              onClick={() => setActionItemsCategoryFilter('Documentation, Training & Process Improvement')}
                              variant={actionItemsCategoryFilter === 'Documentation, Training & Process Improvement' ? 'filled' : 'outlined'}
                              sx={{ 
                                cursor: 'pointer',
                                fontWeight: 600,
                                bgcolor: actionItemsCategoryFilter === 'Documentation, Training & Process Improvement' ? '#c2185b !important' : 'transparent',
                                color: actionItemsCategoryFilter === 'Documentation, Training & Process Improvement' ? '#fff !important' : '#666',
                                borderColor: actionItemsCategoryFilter === 'Documentation, Training & Process Improvement' ? '#c2185b' : '#ccc',
                                '&:hover': {
                                  bgcolor: actionItemsCategoryFilter === 'Documentation, Training & Process Improvement' ? '#ad1457 !important' : '#f5f5f5'
                                }
                              }}
                            />
                            <Chip
                              label={`Uncategorized (${categoryCounts['Uncategorized']})`}
                              onClick={() => setActionItemsCategoryFilter('Uncategorized')}
                              variant={actionItemsCategoryFilter === 'Uncategorized' ? 'filled' : 'outlined'}
                              sx={{ 
                                cursor: 'pointer',
                                fontWeight: 600,
                                bgcolor: actionItemsCategoryFilter === 'Uncategorized' ? '#757575 !important' : 'transparent',
                                color: actionItemsCategoryFilter === 'Uncategorized' ? '#fff !important' : '#666',
                                borderColor: actionItemsCategoryFilter === 'Uncategorized' ? '#757575' : '#ccc',
                                '&:hover': {
                                  bgcolor: actionItemsCategoryFilter === 'Uncategorized' ? '#616161 !important' : '#f5f5f5'
                                }
                              }}
                            />
                          </>
                        );
                      })()}
                    </Box>
                    
                    {/* Search Bar */}
                    <TextField
                      size="small"
                      placeholder="Search by Incident ID, Action ID, or Portfolio..."
                      value={actionItemsSearchQuery}
                      onChange={(e) => setActionItemsSearchQuery(e.target.value)}
                      sx={{ flexGrow: 1, minWidth: '300px', maxWidth: '500px' }}
                      InputProps={{
                        startAdornment: (
                          <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="11" cy="11" r="8"></circle>
                              <path d="m21 21-4.35-4.35"></path>
                            </svg>
                          </Box>
                        ),
                      }}
                    />
                    
                    {/* Export Button */}
                    <Button
                      variant="contained"
                      onClick={handleExportActionItemsToCSV}
                      sx={{
                        textTransform: 'none',
                        bgcolor: '#1976d2',
                        '&:hover': { bgcolor: '#1565c0' },
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Export to CSV
                    </Button>
                  </Box>
                );
              })()}
              
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={actionItemsSortConfig?.key === 'incidentID'}
                          direction={actionItemsSortConfig?.key === 'incidentID' ? actionItemsSortConfig.direction : 'asc'}
                          onClick={() => {
                            const isAsc = actionItemsSortConfig?.key === 'incidentID' && actionItemsSortConfig.direction === 'asc';
                            setActionItemsSortConfig({ key: 'incidentID', direction: isAsc ? 'desc' : 'asc' });
                          }}
                        >
                          <strong>Incident ID</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={actionItemsSortConfig?.key === 'actionID'}
                          direction={actionItemsSortConfig?.key === 'actionID' ? actionItemsSortConfig.direction : 'asc'}
                          onClick={() => {
                            const isAsc = actionItemsSortConfig?.key === 'actionID' && actionItemsSortConfig.direction === 'asc';
                            setActionItemsSortConfig({ key: 'actionID', direction: isAsc ? 'desc' : 'asc' });
                          }}
                        >
                          <strong>Action ID</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={actionItemsSortConfig?.key === 'assignee'}
                          direction={actionItemsSortConfig?.key === 'assignee' ? actionItemsSortConfig.direction : 'asc'}
                          onClick={() => {
                            const isAsc = actionItemsSortConfig?.key === 'assignee' && actionItemsSortConfig.direction === 'asc';
                            setActionItemsSortConfig({ key: 'assignee', direction: isAsc ? 'desc' : 'asc' });
                          }}
                        >
                          <strong>Assigned To</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={actionItemsSortConfig?.key === 'portfolio'}
                          direction={actionItemsSortConfig?.key === 'portfolio' ? actionItemsSortConfig.direction : 'asc'}
                          onClick={() => {
                            const isAsc = actionItemsSortConfig?.key === 'portfolio' && actionItemsSortConfig.direction === 'asc';
                            setActionItemsSortConfig({ key: 'portfolio', direction: isAsc ? 'desc' : 'asc' });
                          }}
                        >
                          <strong>Portfolio</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={actionItemsSortConfig?.key === 'causedBy_application'}
                          direction={actionItemsSortConfig?.key === 'causedBy_application' ? actionItemsSortConfig.direction : 'asc'}
                          onClick={() => {
                            const isAsc = actionItemsSortConfig?.key === 'causedBy_application' && actionItemsSortConfig.direction === 'asc';
                            setActionItemsSortConfig({ key: 'causedBy_application', direction: isAsc ? 'desc' : 'asc' });
                          }}
                        >
                          <strong>Caused By Application</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={actionItemsSortConfig?.key === 'description'}
                          direction={actionItemsSortConfig?.key === 'description' ? actionItemsSortConfig.direction : 'asc'}
                          onClick={() => {
                            const isAsc = actionItemsSortConfig?.key === 'description' && actionItemsSortConfig.direction === 'asc';
                            setActionItemsSortConfig({ key: 'description', direction: isAsc ? 'desc' : 'asc' });
                          }}
                        >
                          <strong>Description</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={actionItemsSortConfig?.key === 'category'}
                          direction={actionItemsSortConfig?.key === 'category' ? actionItemsSortConfig.direction : 'asc'}
                          onClick={() => {
                            const isAsc = actionItemsSortConfig?.key === 'category' && actionItemsSortConfig.direction === 'asc';
                            setActionItemsSortConfig({ key: 'category', direction: isAsc ? 'desc' : 'asc' });
                          }}
                        >
                          <strong>Category</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={actionItemsSortConfig?.key === 'dueDate'}
                          direction={actionItemsSortConfig?.key === 'dueDate' ? actionItemsSortConfig.direction : 'asc'}
                          onClick={() => {
                            const isAsc = actionItemsSortConfig?.key === 'dueDate' && actionItemsSortConfig.direction === 'asc';
                            setActionItemsSortConfig({ key: 'dueDate', direction: isAsc ? 'desc' : 'asc' });
                          }}
                        >
                          <strong>Due Date</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={actionItemsSortConfig?.key === 'status'}
                          direction={actionItemsSortConfig?.key === 'status' ? actionItemsSortConfig.direction : 'asc'}
                          onClick={() => {
                            const isAsc = actionItemsSortConfig?.key === 'status' && actionItemsSortConfig.direction === 'asc';
                            setActionItemsSortConfig({ key: 'status', direction: isAsc ? 'desc' : 'asc' });
                          }}
                        >
                          <strong>Status</strong>
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      // Get action items from ALL postmortems (both open and closed)
                      // But only show OPEN action items (matching the card logic)
                      
                      // Flatten action items from all postmortems and filter by portfolio
                      const allActionItems: any[] = [];
                      postmortemDetailsData.forEach((pm: any) => {
                        if (pm.actionDetails && Array.isArray(pm.actionDetails)) {
                          pm.actionDetails.forEach((action: any) => {
                            // Filter by portfolio if selected (case-insensitive)
                            const actionPortfolio = (action.portfolio || '').toLowerCase();
                            const selectedPortfolioLower = (selectedPortfolio || '').toLowerCase();
                            if (!selectedPortfolio || selectedPortfolio === 'all' || actionPortfolio === selectedPortfolioLower) {
                              // Only include OPEN action items (status not "done", not "closed", and not "3")
                              const actionStatus = action.status ? action.status.toLowerCase() : '';
                              if (actionStatus !== 'done' && actionStatus !== 'closed' && actionStatus !== '3') {
                                allActionItems.push({
                                  ...action,
                                  incidentID: pm.incidentID,
                                  incidentNumber: pm.incidentID,
                                  postmortemLink: pm.customID,
                                  causedBy_application: pm.causedBy_application || ''
                                });
                              }
                            }
                          });
                        }
                      });
                      
                      // Apply search filter
                      let searchFilteredItems = allActionItems;
                      if (actionItemsSearchQuery) {
                        const query = actionItemsSearchQuery.toLowerCase();
                        searchFilteredItems = allActionItems.filter((action: any) => {
                          const incidentID = action.incidentID?.toLowerCase() || '';
                          const actionID = Array.isArray(action.id) ? action.id.join(', ').toLowerCase() : (action.id?.toLowerCase() || '');
                          const portfolio = action.portfolio?.toLowerCase() || '';
                          
                          return incidentID.includes(query) ||
                                 actionID.includes(query) ||
                                 portfolio.includes(query);
                        });
                      }
                      
                      // Apply due date filter
                      let filteredByDueDate = searchFilteredItems;
                      if (actionItemsDueDateFilter !== 'all') {
                        const today = new Date();
                        const fifteenDaysFromNow = new Date();
                        fifteenDaysFromNow.setDate(today.getDate() + 15);
                        
                        filteredByDueDate = searchFilteredItems.filter((action: any) => {
                          if (!action.dueDate || action.dueDate === '') return false;
                          
                          try {
                            const dateStr = action.dueDate.trim();
                            let dueDate: Date | null = null;
                            
                            // Parse date based on format
                            if (dateStr.includes(' ') || dateStr.split('-')[0].length <= 2) {
                              // PTASK format: DD-MM-YYYY HH:mm:ss
                              const dateTimeParts = dateStr.split(' ');
                              const datePart = dateTimeParts[0];
                              const parts = datePart.split('-');
                              if (parts.length === 3) {
                                const day = parts[0];
                                const month = parts[1];
                                const year = parts[2];
                                dueDate = new Date(`${year}-${month}-${day}`);
                              }
                            } else {
                              // JIRA format: YYYY-MM-DD
                              dueDate = new Date(dateStr);
                            }
                            
                            if (dueDate && !isNaN(dueDate.getTime())) {
                              if (actionItemsDueDateFilter === 'within15') {
                                return dueDate >= today && dueDate <= fifteenDaysFromNow;
                              } else if (actionItemsDueDateFilter === 'after15') {
                                return dueDate > fifteenDaysFromNow;
                              } else if (actionItemsDueDateFilter === 'pastdue') {
                                return dueDate < today;
                              }
                            }
                          } catch (error) {
                            return false;
                          }
                          
                          return false;
                        });
                      }
                      
                      // Apply category filter
                      let filteredByCategory = filteredByDueDate;
                      if (actionItemsCategoryFilter !== 'all') {
                        filteredByCategory = filteredByDueDate.filter((action: any) => {
                          const { category } = categorizeAction(action.description || '');
                          return category === actionItemsCategoryFilter;
                        });
                      }
                      
                      // Apply sorting if configured
                      let sortedActionItems = [...filteredByCategory];
                      if (actionItemsSortConfig) {
                        sortedActionItems.sort((a, b) => {
                          let aValue: any;
                          let bValue: any;
                          
                          switch (actionItemsSortConfig.key) {
                            case 'incidentID':
                              aValue = a.incidentID || '';
                              bValue = b.incidentID || '';
                              break;
                            case 'actionID':
                              aValue = Array.isArray(a.id) ? a.id.join(', ') : (a.id || '');
                              bValue = Array.isArray(b.id) ? b.id.join(', ') : (b.id || '');
                              break;
                            case 'assignee':
                              aValue = a.assignee || '';
                              bValue = b.assignee || '';
                              break;
                            case 'portfolio':
                              aValue = a.portfolio || '';
                              bValue = b.portfolio || '';
                              break;
                            case 'causedBy_application':
                              aValue = a.causedBy_application || '';
                              bValue = b.causedBy_application || '';
                              break;
                            case 'description':
                              aValue = a.description || '';
                              bValue = b.description || '';
                              break;
                            case 'category':
                              aValue = categorizeAction(a.description || '').category;
                              bValue = categorizeAction(b.description || '').category;
                              break;
                            case 'dueDate':
                              aValue = a.dueDate ? new Date(a.dueDate).getTime() : 0;
                              bValue = b.dueDate ? new Date(b.dueDate).getTime() : 0;
                              break;
                            case 'status':
                              aValue = a.status || '';
                              bValue = b.status || '';
                              break;
                            default:
                              return 0;
                          }
                          
                          if (aValue < bValue) {
                            return actionItemsSortConfig.direction === 'asc' ? -1 : 1;
                          }
                          if (aValue > bValue) {
                            return actionItemsSortConfig.direction === 'asc' ? 1 : -1;
                          }
                          return 0;
                        });
                      }
                      
                      // Apply pagination
                      const paginatedActionItems = sortedActionItems.slice(
                        actionItemsPage * actionItemsRowsPerPage,
                        actionItemsPage * actionItemsRowsPerPage + actionItemsRowsPerPage
                      );
                      
                      if (sortedActionItems.length === 0) {
                        return (
                          <TableRow>
                            <TableCell colSpan={9} align="center">
                              <Typography variant="body2" color="text.secondary">
                                No open action items found
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      }
                      
                      return paginatedActionItems.map((action: any, index: number) => {
                        // Extract incident number and URL
                        const getIncidentInfo = (idField: string) => {
                          if (!idField) return { number: null };
                          
                          if (idField.includes('service-now.com')) {
                            const match = idField.match(/number=([A-Z0-9]+)/);
                            return {
                              number: match ? match[1] : null
                            };
                          }
                          
                          if (idField.match(/^[A-Z]{3}\d+$/)) {
                            return {
                              number: idField
                            };
                          }
                          
                          return { number: idField };
                        };
                        
                        const incidentInfo = getIncidentInfo(action.incidentID);
                        const postmortemUrl = action.postmortemLink || null;
                        
                        return (
                          <TableRow key={index} hover>
                            <TableCell>
                              {incidentInfo.number && postmortemUrl ? (
                                <Link
                                  href={postmortemUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{ 
                                    color: '#00A8CC',
                                    textDecoration: 'none',
                                    '&:hover': {
                                      textDecoration: 'underline'
                                    }
                                  }}
                                >
                                  {incidentInfo.number}
                                </Link>
                              ) : (incidentInfo.number || 'N/A')}
                            </TableCell>
                            <TableCell>
                              {(() => {
                                // Handle both array and string action IDs
                                const actionIds = Array.isArray(action.id) ? action.id : [action.id].filter(Boolean);
                                
                                if (actionIds.length === 0) return 'N/A';
                                
                                return actionIds.map((id: string, idx: number) => {
                                  const trimmedId = id.trim();
                                  
                                  // Check if it's a PTASK (ServiceNow) or JIRA task
                                  const isPTask = trimmedId.startsWith('PTASK');
                                  
                                  let linkUrl = '';
                                  if (isPTask) {
                                    // Create ServiceNow PTASK link
                                    linkUrl = `https://maersk.service-now.com/nav_to.do?uri=task.do?sysparm_query=number=${trimmedId}`;
                                  } else {
                                    // Create JIRA link
                                    linkUrl = `https://maersk-tools.atlassian.net/browse/${trimmedId}`;
                                  }
                                  
                                  return (
                                    <React.Fragment key={idx}>
                                      {idx > 0 && ', '}
                                      <Link
                                        href={linkUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{ 
                                          color: '#00A8CC',
                                          textDecoration: 'none',
                                          '&:hover': {
                                            textDecoration: 'underline'
                                          }
                                        }}
                                      >
                                        {trimmedId}
                                      </Link>
                                    </React.Fragment>
                                  );
                                });
                              })()}
                            </TableCell>
                            <TableCell>
                              {action.assignee || 'N/A'}
                            </TableCell>
                            <TableCell>
                              {action.portfolio || 'N/A'}
                            </TableCell>
                            <TableCell>
                              {action.causedBy_application || ''}
                            </TableCell>
                            <TableCell sx={{ maxWidth: 400 }}>
                              {action.description && action.description !== 'null' ? (
                                <MuiTooltip title={action.description} arrow>
                                  <Typography variant="body2" noWrap>
                                    {action.description}
                                  </Typography>
                                </MuiTooltip>
                              ) : 'N/A'}
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const { category, color } = categorizeAction(action.description || '');
                                return (
                                  <Chip
                                    label={category}
                                    size="small"
                                    sx={{
                                      backgroundColor: `${color} !important`,
                                      color: '#fff !important',
                                      fontWeight: 600,
                                      fontSize: '0.75rem'
                                    }}
                                  />
                                );
                              })()}
                            </TableCell>
                            <TableCell>
                              {(() => {
                                if (!action.dueDate || action.dueDate === '') return 'N/A';
                                
                                try {
                                  const dateStr = action.dueDate.trim();
                                  
                                  // Check if it's PTASK format (DD-MM-YYYY HH:mm:ss)
                                  // This format has spaces and day comes first
                                  if (dateStr.includes(' ') || dateStr.split('-')[0].length <= 2) {
                                    // Handle DD-MM-YYYY HH:mm:ss format (PTASK)
                                    const dateTimeParts = dateStr.split(' ');
                                    const datePart = dateTimeParts[0];
                                    const parts = datePart.split('-');
                                    
                                    if (parts.length === 3) {
                                      // Convert DD-MM-YYYY to YYYY-MM-DD for proper parsing
                                      const day = parts[0];
                                      const month = parts[1];
                                      const year = parts[2];
                                      const formattedDate = `${year}-${month}-${day}`;
                                      const parsedDate = new Date(formattedDate);
                                      
                                      // Check if date is valid
                                      if (!isNaN(parsedDate.getTime())) {
                                        return parsedDate.toLocaleDateString();
                                      }
                                    }
                                  } else {
                                    // Handle YYYY-MM-DD format (JIRA/ISO format)
                                    const parsedDate = new Date(dateStr);
                                    
                                    // Check if date is valid
                                    if (!isNaN(parsedDate.getTime())) {
                                      return parsedDate.toLocaleDateString();
                                    }
                                  }
                                  
                                  return 'Invalid Date';
                                } catch (error) {
                                  return 'Invalid Date';
                                }
                              })()}
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const status = action.status?.toLowerCase() || 'n/a';
                                
                                // Parse due date and check if past due
                                let isPastDue = false;
                                let tooltipMessage = action.status || 'N/A';
                                
                                if (action.dueDate && action.dueDate !== '') {
                                  try {
                                    const dateStr = action.dueDate.trim();
                                    let dueDate: Date | null = null;
                                    const today = new Date();
                                    today.setHours(0, 0, 0, 0);
                                    
                                    // Parse date based on format
                                    if (dateStr.includes(' ') || dateStr.split('-')[0].length <= 2) {
                                      // PTASK format: DD-MM-YYYY HH:mm:ss
                                      const dateTimeParts = dateStr.split(' ');
                                      const datePart = dateTimeParts[0];
                                      const parts = datePart.split('-');
                                      if (parts.length === 3) {
                                        const day = parts[0];
                                        const month = parts[1];
                                        const year = parts[2];
                                        dueDate = new Date(`${year}-${month}-${day}`);
                                      }
                                    } else {
                                      // JIRA format: YYYY-MM-DD
                                      dueDate = new Date(dateStr);
                                    }
                                    
                                    if (dueDate && !isNaN(dueDate.getTime())) {
                                      dueDate.setHours(0, 0, 0, 0);
                                      if (dueDate < today) {
                                        isPastDue = true;
                                        const daysOverdue = Math.floor((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));
                                        tooltipMessage = `Past Due: ${daysOverdue} day${daysOverdue !== 1 ? 's' : ''} overdue`;
                                      } else {
                                        const daysRemaining = Math.floor((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                                        tooltipMessage = `${action.status || 'N/A'} - Due in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}`;
                                      }
                                    }
                                  } catch (error) {
                                    // Keep default tooltip
                                  }
                                }
                                
                                // Define RAG status colors based on due date
                                let bgcolor = '#f5f5f5'; // Default gray
                                let textColor = '#666';
                                
                                // Red - Past Due
                                if (isPastDue) {
                                  bgcolor = '#f8d7da';
                                  textColor = '#721c24';
                                }
                                // Green - Completed statuses
                                else if (status === 'done' || status === 'closed' || status === 'completed') {
                                  bgcolor = '#d4edda';
                                  textColor = '#155724';
                                }
                                // Amber/Yellow - In Progress, To Do, Assess statuses
                                else if (status.includes('progress') || status.includes('inprogress') || 
                                         status === 'in progress' || status === 'open' || status === 'new' ||
                                         status === 'to do' || status === 'todo' || status === 'assess') {
                                  bgcolor = '#fff3cd';
                                  textColor = '#856404';
                                }
                                // Red - Blocked/High Priority statuses
                                else if (status.includes('blocked') || status.includes('stuck') || 
                                         status.includes('issue') || status.includes('problem')) {
                                  bgcolor = '#f8d7da';
                                  textColor = '#721c24';
                                }
                                
                                return (
                                  <MuiTooltip title={tooltipMessage} arrow>
                                    <Chip 
                                      label={action.status || 'N/A'} 
                                      size="small"
                                      sx={{
                                        backgroundColor: `${bgcolor} !important`,
                                        color: `${textColor} !important`,
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        '& .MuiChip-label': {
                                          color: `${textColor} !important`
                                        }
                                      }}
                                    />
                                  </MuiTooltip>
                                );
                              })()}
                            </TableCell>
                          </TableRow>
                        );
                      });
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Pagination for Action Items Table */}
              {(() => {
                // Calculate total count for OPEN action items only (matching card logic)
                const allActionItems: any[] = [];
                postmortemDetailsData.forEach((pm: any) => {
                  if (pm.actionDetails && Array.isArray(pm.actionDetails)) {
                    pm.actionDetails.forEach((action: any) => {
                      // Case-insensitive portfolio comparison
                      const actionPortfolio = (action.portfolio || '').toLowerCase();
                      const selectedPortfolioLower = (selectedPortfolio || '').toLowerCase();
                      if (!selectedPortfolio || selectedPortfolio === 'all' || actionPortfolio === selectedPortfolioLower) {
                        // Only include OPEN action items (status not "done" and not "closed")
                        const actionStatus = action.status ? action.status.toLowerCase() : '';
                        if (actionStatus !== 'done' && actionStatus !== 'closed' && actionStatus !== '3') {
                          allActionItems.push(action);
                        }
                      }
                    });
                  }
                });
                
                return allActionItems.length > 0 ? (
                  <TablePagination
                    component="div"
                    count={allActionItems.length}
                    page={actionItemsPage}
                    onPageChange={(event, newPage) => setActionItemsPage(newPage)}
                    rowsPerPage={actionItemsRowsPerPage}
                    onRowsPerPageChange={(event) => {
                      setActionItemsRowsPerPage(parseInt(event.target.value, 10));
                      setActionItemsPage(0);
                    }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    sx={{
                      borderTop: '1px solid #e1e5e9',
                      backgroundColor: '#f8f9fa',
                      '& .MuiTablePagination-toolbar': {
                        minHeight: '52px',
                      },
                      '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                        fontSize: '13px',
                        color: '#6c757d',
                      },
                    }}
                  />
                ) : null;
              })()}
            </Box>
            </>
            )}
            </>
            )}
          </Box>
        )}

        {/* Tab 2: Reliability Actions */}
        {healthSummaryTab === 2 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6">
                  Reliability Actions - Pending Items
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                  Data Source: <Link 
                    href="/reliability-assessment" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    sx={{ color: '#0067b3', textDecoration: 'underline', cursor: 'pointer' }}
                  >
                    Reliability
                  </Link> | Last Refresh: Daily at 4:30 AM UTC
                </Typography>
              </Box>
              
              {/* View Toggle Button */}
              <Box sx={{ display: 'flex', gap: 1, bgcolor: '#f0f0f0', borderRadius: 1, p: 0.5 }}>
                <Button
                  size="small"
                  variant={reliabilityActionsView === 'application' ? 'contained' : 'text'}
                  onClick={() => {
                    setReliabilityActionsView('application');
                    setSelectedReliabilityPlatform('');
                  }}
                  sx={{
                    bgcolor: reliabilityActionsView === 'application' ? '#0067b3' : 'transparent',
                    color: reliabilityActionsView === 'application' ? 'white' : '#666',
                    '&:hover': {
                      bgcolor: reliabilityActionsView === 'application' ? '#005a9e' : 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  Application View
                </Button>
                <Button
                  size="small"
                  variant={reliabilityActionsView === 'platform' ? 'contained' : 'text'}
                  onClick={() => setReliabilityActionsView('platform')}
                  sx={{
                    bgcolor: reliabilityActionsView === 'platform' ? '#0067b3' : 'transparent',
                    color: reliabilityActionsView === 'platform' ? 'white' : '#666',
                    '&:hover': {
                      bgcolor: reliabilityActionsView === 'platform' ? '#005a9e' : 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  Platform View
                </Button>
              </Box>
            </Box>

            {(!selectedPortfolio || selectedPortfolio === 'all') && (!selectedPlatform || selectedPlatform === 'all') ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                Please select at least a portfolio to view reliability action items
              </Alert>
            ) : reliabilityActionsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : reliabilityActionsError ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                {reliabilityActionsError}
              </Alert>
            ) : reliabilityActionsData ? (
              <Box>
                {/* Summary Header */}
                <Box sx={{ mb: 3, p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="textSecondary">Portfolio</Typography>
                      <Typography variant="h6">{reliabilityActionsData.portfolio_name}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="textSecondary">Total Apps</Typography>
                      <Typography variant="h6">{reliabilityActionsData.total_apps}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="textSecondary">Total Platforms</Typography>
                      <Typography variant="h6">{reliabilityActionsData.total_platforms}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="textSecondary">Overall Progress</Typography>
                      <Typography variant="h6">
                        {reliabilityActionsData.overall_percentages?.overall_percentage != null 
                          ? `${parseFloat(reliabilityActionsData.overall_percentages.overall_percentage).toFixed(1)}%` 
                          : 'N/A'}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                        <Chip 
                          label={`Crawl: ${parseFloat(reliabilityActionsData.overall_percentages?.crawl_percentage || 0).toFixed(1)}%`}
                          size="small"
                          sx={{ bgcolor: '#6f42c1', color: 'white', fontSize: '10px', height: '20px' }}
                        />
                        <Chip 
                          label={`Walk: ${parseFloat(reliabilityActionsData.overall_percentages?.walk_percentage || 0).toFixed(1)}%`}
                          size="small"
                          sx={{ bgcolor: '#28a745', color: 'white', fontSize: '10px', height: '20px' }}
                        />
                        <Chip 
                          label={`Run: ${parseFloat(reliabilityActionsData.overall_percentages?.run_percentage || 0).toFixed(1)}%`}
                          size="small"
                          sx={{ bgcolor: '#9c27b0', color: 'white', fontSize: '10px', height: '20px' }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                {/* Application View - shows pending items by platform and app */}
                {reliabilityActionsView === 'application' && reliabilityActionsData.platforms && reliabilityActionsData.platforms.length > 0 && (
                  reliabilityActionsData.platforms.map((platform: any) => (
                    <Box key={platform.platform_name} sx={{ mb: 4 }}>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {platform.platform_name}
                        <Chip 
                          label={`${platform.total_apps} apps`} 
                          size="small" 
                          color="primary"
                        />
                        <Chip 
                          label={`Crawl: ${parseFloat(platform.platform_percentages?.crawl_percentage || 0).toFixed(1)}%`} 
                          size="small" 
                          sx={{ bgcolor: '#6f42c1', color: 'white' }}
                        />
                        <Chip 
                          label={`Walk: ${parseFloat(platform.platform_percentages?.walk_percentage || 0).toFixed(1)}%`} 
                          size="small" 
                          sx={{ bgcolor: '#28a745', color: 'white' }}
                        />
                        <Chip 
                          label={`Run: ${parseFloat(platform.platform_percentages?.run_percentage || 0).toFixed(1)}%`} 
                          size="small" 
                          sx={{ bgcolor: '#9c27b0', color: 'white' }}
                        />
                      </Typography>

                      <TableContainer component={Paper} sx={{ mb: 2 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                              <TableCell sx={{ fontWeight: 600 }}>App ID</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Application</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Crawl %</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Walk %</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Run %</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Pending Crawl</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Pending Walk</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Pending Run</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {platform.applications && platform.applications.map((app: any) => {
                              const crawlCount = app.pending_items?.crawl?.count || 0;
                              const walkCount = app.pending_items?.walk?.count || 0;
                              const runCount = app.pending_items?.run?.count || 0;
                              const totalPending = crawlCount + walkCount + runCount;

                              // Only show apps with pending items
                              if (totalPending === 0) return null;

                              return (
                                <TableRow key={app.application} hover>
                                  <TableCell>
                                    <a
                                      href={`https://mac.maersk.io/application/${app.app_id}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ color: '#0067b3', textDecoration: 'underline' }}
                                    >
                                      {app.app_id}
                                    </a>
                                  </TableCell>
                                  <TableCell>{app.application}</TableCell>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      {parseFloat(app.completion_percentages?.crawl_percentage || 0).toFixed(1)}%
                                      <LinearProgress 
                                        variant="determinate" 
                                        value={parseFloat(app.completion_percentages?.crawl_percentage || 0)}
                                        sx={{ 
                                          width: 60, 
                                          height: 6,
                                          borderRadius: 1,
                                          bgcolor: '#e0e0e0',
                                          '& .MuiLinearProgress-bar': {
                                            bgcolor: '#6f42c1'
                                          }
                                        }}
                                      />
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      {parseFloat(app.completion_percentages?.walk_percentage || 0).toFixed(1)}%
                                      <LinearProgress 
                                        variant="determinate" 
                                        value={parseFloat(app.completion_percentages?.walk_percentage || 0)}
                                        sx={{ 
                                          width: 60, 
                                          height: 6,
                                          borderRadius: 1,
                                          bgcolor: '#e0e0e0',
                                          '& .MuiLinearProgress-bar': {
                                            bgcolor: '#28a745'
                                          }
                                        }}
                                      />
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      {parseFloat(app.completion_percentages?.run_percentage || 0).toFixed(1)}%
                                      <LinearProgress 
                                        variant="determinate" 
                                        value={parseFloat(app.completion_percentages?.run_percentage || 0)}
                                        sx={{ 
                                          width: 60, 
                                          height: 6,
                                          borderRadius: 1,
                                          bgcolor: '#e0e0e0',
                                          '& .MuiLinearProgress-bar': {
                                            bgcolor: '#9c27b0'
                                          }
                                        }}
                                      />
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    {crawlCount > 0 ? (
                                      <MuiTooltip 
                                        title={
                                          <Box>
                                            {app.pending_items.crawl.items.slice(0, 5).map((item: string, idx: number) => (
                                              <Typography key={idx} variant="caption" display="block">
                                                • {item}
                                              </Typography>
                                            ))}
                                            {app.pending_items.crawl.items.length > 5 && (
                                              <Typography variant="caption" display="block" sx={{ fontStyle: 'italic' }}>
                                                ...and {app.pending_items.crawl.items.length - 5} more
                                              </Typography>
                                            )}
                                          </Box>
                                        }
                                        arrow
                                      >
                                        <Chip 
                                          label={crawlCount} 
                                          size="small" 
                                          color="warning"
                                          sx={{ cursor: 'help' }}
                                        />
                                      </MuiTooltip>
                                    ) : (
                                      <Chip label="0" size="small" color="success" />
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {walkCount > 0 ? (
                                      <MuiTooltip 
                                        title={
                                          <Box>
                                            {app.pending_items.walk.items.slice(0, 5).map((item: string, idx: number) => (
                                              <Typography key={idx} variant="caption" display="block">
                                                • {item}
                                              </Typography>
                                            ))}
                                            {app.pending_items.walk.items.length > 5 && (
                                              <Typography variant="caption" display="block" sx={{ fontStyle: 'italic' }}>
                                                ...and {app.pending_items.walk.items.length - 5} more
                                              </Typography>
                                            )}
                                          </Box>
                                        }
                                        arrow
                                      >
                                        <Chip 
                                          label={walkCount} 
                                          size="small" 
                                          color="warning"
                                          sx={{ cursor: 'help' }}
                                        />
                                      </MuiTooltip>
                                    ) : (
                                      <Chip label="0" size="small" color="success" />
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {runCount > 0 ? (
                                      <MuiTooltip 
                                        title={
                                          <Box>
                                            {app.pending_items.run.items.slice(0, 5).map((item: string, idx: number) => (
                                              <Typography key={idx} variant="caption" display="block">
                                                • {item}
                                              </Typography>
                                            ))}
                                            {app.pending_items.run.items.length > 5 && (
                                              <Typography variant="caption" display="block" sx={{ fontStyle: 'italic' }}>
                                                ...and {app.pending_items.run.items.length - 5} more
                                              </Typography>
                                            )}
                                          </Box>
                                        }
                                        arrow
                                      >
                                        <Chip 
                                          label={runCount} 
                                          size="small" 
                                          color="warning"
                                          sx={{ cursor: 'help' }}
                                        />
                                      </MuiTooltip>
                                    ) : (
                                      <Chip label="0" size="small" color="success" />
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  ))
                )}

                {/* Platform View - shows platform summary and detail table */}
                {reliabilityActionsView === 'platform' && (
                  <Box sx={{ mt: 3 }}>
                    {!selectedReliabilityPlatform ? (
                      <>
                        {/* Export Button */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<FileDownload />}
                            onClick={() => {
                              try {
                                const portfolioName = selectedPortfolio || 'All Portfolios';
                                const csvHeaders = ['Platform', 'App Count', 'Crawl %', 'Walk %', 'Run %', 'Summary'];
                                
                                const csvData = reliabilityActionsData.platforms.map((platform: any) => {
                                  const crawlPercentage = parseFloat(platform.platform_percentages?.crawl_percentage) || 0;
                                  const walkPercentage = parseFloat(platform.platform_percentages?.walk_percentage) || 0;
                                  const runPercentage = parseFloat(platform.platform_percentages?.run_percentage) || 0;
                                  
                                  // Build summary text
                                  const parameterSummary: { [key: string]: { phase: string; apps: string[] } } = {};
                                  platform.applications.forEach((app: any) => {
                                    ['crawl', 'walk', 'run'].forEach((phase) => {
                                      const pendingItems = app.pending_items?.[phase]?.items || [];
                                      pendingItems.forEach((param: string) => {
                                        if (!parameterSummary[param]) {
                                          parameterSummary[param] = { 
                                            phase: phase.charAt(0).toUpperCase() + phase.slice(1),
                                            apps: [] 
                                          };
                                        }
                                        if (!parameterSummary[param].apps.includes(app.application)) {
                                          parameterSummary[param].apps.push(app.application);
                                        }
                                      });
                                    });
                                  });
                                  
                                  let summaryText = '';
                                  ['Crawl', 'Walk', 'Run'].forEach((phase) => {
                                    const phaseParams = Object.entries(parameterSummary).filter(
                                      ([, data]) => data.phase === phase
                                    );
                                    if (phaseParams.length > 0) {
                                      summaryText += `${phase}: `;
                                      phaseParams.forEach(([parameter, data]) => {
                                        summaryText += `${parameter} (${data.apps.join('; ')}); `;
                                      });
                                    }
                                  });
                                  
                                  return [
                                    platform.platform_name,
                                    platform.total_apps,
                                    crawlPercentage.toFixed(1) + '%',
                                    walkPercentage.toFixed(1) + '%',
                                    runPercentage.toFixed(1) + '%',
                                    summaryText || 'All parameters completed'
                                  ];
                                });

                                const csvContent = [
                                  csvHeaders.join(','),
                                  ...csvData.map((row: any[]) => 
                                    row.map((field: any) => 
                                      typeof field === 'string' && (field.includes(',') || field.includes('"') || field.includes('\n'))
                                        ? `"${field.replace(/"/g, '""')}"` 
                                        : field
                                    ).join(',')
                                  )
                                ].join('\n');

                                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                                const link = document.createElement('a');
                                const url = URL.createObjectURL(blob);
                                link.setAttribute('href', url);
                                link.setAttribute('download', `reliability-actions-platform-summary-${portfolioName.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`);
                                link.style.visibility = 'hidden';
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                URL.revokeObjectURL(url);
                              } catch (error) {
                                console.error('Error exporting to CSV:', error);
                              }
                            }}
                          >
                            Export to CSV
                          </Button>
                        </Box>
                        
                        {/* Platform Summary Table */}
                        <TableContainer component={Paper}>
                          <Table>
                            <TableHead>
                              <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                                <TableCell><strong>Platform</strong></TableCell>
                                <TableCell align="center"><strong>App Count</strong></TableCell>
                                <TableCell align="center"><strong>Crawl %</strong></TableCell>
                                <TableCell align="center"><strong>Walk %</strong></TableCell>
                                <TableCell align="center"><strong>Run %</strong></TableCell>
                                <TableCell><strong>Summary</strong></TableCell>
                              </TableRow>
                            </TableHead>
                          <TableBody>
                            {reliabilityActionsData.platforms.map((platform: any) => {
                              const crawlPercentage = parseFloat(platform.platform_percentages?.crawl_percentage) || 0;
                              const walkPercentage = parseFloat(platform.platform_percentages?.walk_percentage) || 0;
                              const runPercentage = parseFloat(platform.platform_percentages?.run_percentage) || 0;
                              const overallPercentage = ((crawlPercentage + walkPercentage + runPercentage) / 3).toFixed(1);

                              // Build summary of pending items grouped by parameter
                              const parameterSummary: { [key: string]: { phase: string; apps: string[] } } = {};
                              
                              platform.applications.forEach((app: any) => {
                                ['crawl', 'walk', 'run'].forEach((phase) => {
                                  const pendingItems = app.pending_items?.[phase]?.items || [];
                                  pendingItems.forEach((param: string) => {
                                    if (!parameterSummary[param]) {
                                      parameterSummary[param] = { 
                                        phase: phase.charAt(0).toUpperCase() + phase.slice(1),
                                        apps: [] 
                                      };
                                    }
                                    if (!parameterSummary[param].apps.includes(app.application)) {
                                      parameterSummary[param].apps.push(app.application);
                                    }
                                  });
                                });
                              });

                              return (
                                <TableRow 
                                  key={platform.platform_name}
                                  hover
                                  sx={{ 
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: '#f5f5f5' }
                                  }}
                                  onClick={() => setSelectedReliabilityPlatform(platform.platform_name)}
                                >
                                  <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {platform.platform_name}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Chip label={platform.total_apps} size="small" color="primary" />
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography variant="body2">{crawlPercentage.toFixed(1)}%</Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography variant="body2">{walkPercentage.toFixed(1)}%</Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography variant="body2">{runPercentage.toFixed(1)}%</Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Box sx={{ maxHeight: 150, overflowY: 'auto', fontSize: '12px' }}>
                                      {Object.keys(parameterSummary).length > 0 ? (
                                        <>
                                          {/* Group by phase */}
                                          {['Crawl', 'Walk', 'Run'].map((phase) => {
                                            const phaseParams = Object.entries(parameterSummary).filter(
                                              ([, data]) => data.phase === phase
                                            );
                                            
                                            if (phaseParams.length === 0) return null;
                                            
                                            return (
                                              <Box key={phase} sx={{ mb: 1.5 }}>
                                                <Typography 
                                                  variant="caption" 
                                                  sx={{ 
                                                    fontWeight: 'bold', 
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.5,
                                                    mb: 0.5,
                                                    pb: 0.5,
                                                    borderBottom: '2px solid',
                                                    borderColor: phase === 'Crawl' ? '#6f42c1' : phase === 'Walk' ? '#28a745' : '#9c27b0',
                                                  }}
                                                >
                                                  <Chip 
                                                    label={phase} 
                                                    size="small" 
                                                    sx={{ 
                                                      height: 18,
                                                      fontSize: '10px',
                                                      bgcolor: phase === 'Crawl' ? '#6f42c1' : phase === 'Walk' ? '#28a745' : '#9c27b0',
                                                      color: 'white'
                                                    }} 
                                                  />
                                                  ({phaseParams.length} items)
                                                </Typography>
                                                {phaseParams
                                                  .sort(([, a], [, b]) => b.apps.length - a.apps.length)
                                                  .map(([parameter, data]) => (
                                                    <Box key={parameter} sx={{ ml: 1, mb: 0.5 }}>
                                                      <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                                                        • {parameter}
                                                      </Typography>
                                                      <Typography variant="caption" sx={{ color: '#666', display: 'block', ml: 1.5, fontSize: '10px' }}>
                                                        {data.apps.join(', ')}
                                                      </Typography>
                                                    </Box>
                                                  ))}
                                              </Box>
                                            );
                                          })}
                                        </>
                                      ) : (
                                        <Typography variant="caption" sx={{ color: '#666', fontStyle: 'italic' }}>
                                          All parameters completed ✓
                                        </Typography>
                                      )}
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      </>
                    ) : (
                      // Platform Detail Table - showing applications with pending items
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Button
                              size="small"
                              startIcon={<KeyboardArrowUp sx={{ transform: 'rotate(-90deg)' }} />}
                              onClick={() => setSelectedReliabilityPlatform('')}
                            >
                              Back to Platform Summary
                            </Button>
                            <Typography variant="h6">{selectedReliabilityPlatform}</Typography>
                          </Box>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<FileDownload />}
                            onClick={() => {
                              const selectedPlatformData = reliabilityActionsData.platforms.find(
                                (p: any) => p.platform_name === selectedReliabilityPlatform
                              );
                              if (selectedPlatformData) {
                                const appsWithPendingItems = selectedPlatformData.applications.filter((app: any) => {
                                  const crawlCount = app.pending_items?.crawl?.count || 0;
                                  const walkCount = app.pending_items?.walk?.count || 0;
                                  const runCount = app.pending_items?.run?.count || 0;
                                  return (crawlCount + walkCount + runCount) > 0;
                                });

                                const csvHeaders = ['Application', 'Crawl Pending Items', 'Walk Pending Items', 'Run Pending Items', 'Total Pending Count'];
                                const csvData = appsWithPendingItems.map((app: any) => {
                                  const crawlItems = app.pending_items?.crawl?.items || [];
                                  const walkItems = app.pending_items?.walk?.items || [];
                                  const runItems = app.pending_items?.run?.items || [];
                                  const totalPending = crawlItems.length + walkItems.length + runItems.length;

                                  return [
                                    app.application,
                                    crawlItems.join('; ') || 'None',
                                    walkItems.join('; ') || 'None',
                                    runItems.join('; ') || 'None',
                                    totalPending.toString()
                                  ];
                                });

                                const csvContent = [
                                  csvHeaders.join(','),
                                  ...csvData.map((row: any[]) => 
                                    row.map((field: any) => 
                                      typeof field === 'string' && (field.includes(',') || field.includes('"') || field.includes('\n'))
                                        ? `"${field.replace(/"/g, '""')}"` 
                                        : field
                                    ).join(',')
                                  )
                                ].join('\n');

                                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                                const link = document.createElement('a');
                                const url = URL.createObjectURL(blob);
                                link.setAttribute('href', url);
                                link.setAttribute('download', `reliability-actions-${selectedReliabilityPlatform.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`);
                                link.style.visibility = 'hidden';
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                URL.revokeObjectURL(url);
                              }
                            }}
                          >
                            Export to CSV
                          </Button>
                        </Box>

                        {(() => {
                          const selectedPlatformData = reliabilityActionsData.platforms.find(
                            (p: any) => p.platform_name === selectedReliabilityPlatform
                          );

                          if (!selectedPlatformData) {
                            return (
                              <Alert severity="info">
                                Platform data not found
                              </Alert>
                            );
                          }

                          // Filter applications with pending items
                          const appsWithPendingItems = selectedPlatformData.applications.filter((app: any) => {
                            const crawlCount = app.pending_items?.crawl?.count || 0;
                            const walkCount = app.pending_items?.walk?.count || 0;
                            const runCount = app.pending_items?.run?.count || 0;
                            return (crawlCount + walkCount + runCount) > 0;
                          });

                          if (appsWithPendingItems.length === 0) {
                            return (
                              <Alert severity="success">
                                All applications in this platform have completed all reliability parameters!
                              </Alert>
                            );
                          }

                          return (
                            <TableContainer component={Paper}>
                              <Table>
                                <TableHead>
                                  <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                                    <TableCell><strong>Application</strong></TableCell>
                                    <TableCell><strong>Crawl Pending</strong></TableCell>
                                    <TableCell><strong>Walk Pending</strong></TableCell>
                                    <TableCell><strong>Run Pending</strong></TableCell>
                                    <TableCell align="center"><strong>Total Pending</strong></TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {appsWithPendingItems.map((app: any, idx: number) => {
                                    const crawlItems = app.pending_items?.crawl?.items || [];
                                    const walkItems = app.pending_items?.walk?.items || [];
                                    const runItems = app.pending_items?.run?.items || [];
                                    const totalPending = crawlItems.length + walkItems.length + runItems.length;

                                    return (
                                      <TableRow key={idx}>
                                        <TableCell>
                                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {app.application}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          {crawlItems.length > 0 ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                              <Chip 
                                                label={`${crawlItems.length} items`}
                                                size="small" 
                                                sx={{ 
                                                  bgcolor: '#6f42c1', 
                                                  color: 'white',
                                                  width: 'fit-content',
                                                  mb: 1
                                                }}
                                              />
                                              <Box sx={{ fontSize: '11px', color: '#666' }}>
                                                {crawlItems.map((item: string, i: number) => (
                                                  <Typography key={i} variant="caption" display="block" sx={{ mb: 0.3 }}>
                                                    • {item.replace(/_/g, ' ')}
                                                  </Typography>
                                                ))}
                                              </Box>
                                            </Box>
                                          ) : (
                                            <Chip label="✓ Complete" size="small" color="success" />
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          {walkItems.length > 0 ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                              <Chip 
                                                label={`${walkItems.length} items`}
                                                size="small" 
                                                sx={{ 
                                                  bgcolor: '#28a745', 
                                                  color: 'white',
                                                  width: 'fit-content',
                                                  mb: 1
                                                }}
                                              />
                                              <Box sx={{ fontSize: '11px', color: '#666' }}>
                                                {walkItems.map((item: string, i: number) => (
                                                  <Typography key={i} variant="caption" display="block" sx={{ mb: 0.3 }}>
                                                    • {item.replace(/_/g, ' ')}
                                                  </Typography>
                                                ))}
                                              </Box>
                                            </Box>
                                          ) : (
                                            <Chip label="✓ Complete" size="small" color="success" />
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          {runItems.length > 0 ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                              <Chip 
                                                label={`${runItems.length} items`}
                                                size="small" 
                                                sx={{ 
                                                  bgcolor: '#9c27b0', 
                                                  color: 'white',
                                                  width: 'fit-content',
                                                  mb: 1
                                                }}
                                              />
                                              <Box sx={{ fontSize: '11px', color: '#666' }}>
                                                {runItems.map((item: string, i: number) => (
                                                  <Typography key={i} variant="caption" display="block" sx={{ mb: 0.3 }}>
                                                    • {item.replace(/_/g, ' ')}
                                                  </Typography>
                                                ))}
                                              </Box>
                                            </Box>
                                          ) : (
                                            <Chip label="✓ Complete" size="small" color="success" />
                                          )}
                                        </TableCell>
                                        <TableCell align="center">
                                          <Chip 
                                            label={totalPending}
                                            size="small"
                                            color={totalPending >= 10 ? 'error' : totalPending >= 5 ? 'warning' : 'info'}
                                            sx={{ fontWeight: 'bold' }}
                                          />
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          );
                        })()}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            ) : (
              <Alert severity="info" sx={{ mt: 2 }}>
                No data available
              </Alert>
            )}
          </Box>
        )}

        {/* Tab 5: Jira Actions - DISABLED */}
        {/* {healthSummaryTab === 5 && (
          <JiraActions />
        )} */}

        {/* Tab 6: Decommissioning Status — MOVED to standalone /decommissioning-status page under Reliability & Resilience */}
        {false && healthSummaryTab === 6 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Decommissioning Status
              </Typography>
              <Typography variant="caption" sx={{ color: '#666', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Application Data Source: 
                <Link 
                  href="https://mac.maersk.io/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  sx={{ 
                    color: '#0067b3',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  MAC
                </Link>
                <Box component="span" sx={{ mx: 1, color: '#ccc' }}>|</Box>
                Refresh Frequency - Daily 23:00 UTC
              </Typography>
              <Typography variant="caption" sx={{ color: '#666', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Cost Data Source: 
                <Link 
                  href="https://app.powerbi.com/groups/me/apps/1161082c-c885-4a6f-b5df-043860c73439/reports/786adebb-a253-4ebd-a30c-d29fd8ad4070/ReportSection94ba8c76798120510b97?ctid=05d75c05-fa1a-42e7-9cf1-eb416c396f2d&experience=power-bi" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  sx={{ 
                    color: '#0067b3',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  ITFM DCO
                </Link>
                <Box component="span" sx={{ mx: 1, color: '#ccc' }}>|</Box>
                Refresh Frequency - Once in a Quarter
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, bgcolor: '#f0f0f0', borderRadius: 1, p: 0.5, mb: 2 }}>
              <Button
                size="small"
                variant={decomView === 'table' ? 'contained' : 'text'}
                onClick={() => setDecomView('table')}
                sx={{
                  bgcolor: decomView === 'table' ? '#0067b3' : 'transparent',
                  color: decomView === 'table' ? 'white' : '#666',
                  '&:hover': { bgcolor: decomView === 'table' ? '#005a9e' : 'rgba(0,0,0,0.04)' }
                }}
              >
                Table View
              </Button>
              <Button
                size="small"
                variant={decomView === 'trends' ? 'contained' : 'text'}
                onClick={() => setDecomView('trends')}
                sx={{
                  bgcolor: decomView === 'trends' ? '#0067b3' : 'transparent',
                  color: decomView === 'trends' ? 'white' : '#666',
                  '&:hover': { bgcolor: decomView === 'trends' ? '#005a9e' : 'rgba(0,0,0,0.04)' }
                }}
              >
                Trends
              </Button>
            </Box>

            {/* Tracking Status Filter Chips, Search, and Export Button */}
            {decomView === 'trends' ? (
              // Pass raw decommissioningData and do NOT apply UI filters here
              <DecommissionTrends decommissioningData={decommissioningData} />
            ) : (() => {
              const handleExportCSV = () => {
                try {
                  // Filter data based on current filters
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                    
                    let filteredData = decommissioningData.filter((item: DecommissioningItem) =>
                      item.target_date_decom !== null && item.target_date_decom !== undefined
                    );

                    // Filter out unwanted MAC statuses (only show Active and Inactive)
                    filteredData = filteredData.filter((item: DecommissioningItem) =>
                      item.status === 'Active' || item.status === 'Inactive'
                    );
                    
                    // Apply year filter
                    if (decomYearFilter === 'all') {
                      if (selectedYears.length > 0) {
                        filteredData = filteredData.filter((item: DecommissioningItem) => {
                          if (!item.target_date_decom) return false;
                          const itemYear = new Date(item.target_date_decom).getFullYear();
                          return selectedYears.includes(itemYear.toString());
                        });
                      }
                      // If no specific years selected in 'all' mode, show all
                    } else if (decomYearFilter === 'custom') {
                      // Custom date range filter
                      if (customRangeStart && customRangeEnd) {
                        filteredData = filteredData.filter((item: DecommissioningItem) => {
                          if (!item.target_date_decom) return false;
                          const itemDate = new Date(item.target_date_decom);
                          const startDate = new Date(customRangeStart);
                          const endDate = new Date(customRangeEnd);
                          return itemDate >= startDate && itemDate <= endDate;
                        });
                      }
                    } else if (decomYearFilter !== 'all') {
                      // For specific year selection
                      filteredData = filteredData.filter((item: DecommissioningItem) => {
                        if (!item.target_date_decom) return false;
                        const itemYear = new Date(item.target_date_decom).getFullYear();
                        return itemYear.toString() === decomYearFilter;
                      });
                    }
                    
                    // Apply tracking status filter
                    if (decomTrackingFilter !== 'all') {
                      filteredData = filteredData.filter((item: DecommissioningItem) => {
                        const status = calculateDecommissioningStatus(item);
                        if (decomTrackingFilter === 'completed') {
                          return status === 'Decommissioned';
                        } else if (decomTrackingFilter === 'delayed-overdue') {
                          return status === 'Delayed - Overdue';
                        } else if (decomTrackingFilter === 'delayed-repeatedly') {
                          return status === 'Delayed - Repeatedly';
                        } else if (decomTrackingFilter === 'delayed-extended') {
                          return status === 'Delayed - Date Extended';
                        } else if (decomTrackingFilter === 'ontrack') {
                          return status === 'To be Decommissioned';
                        } else if (decomTrackingFilter === 'cancelled') {
                          return status === 'Cancelled';
                        }
                        return true;
                      });
                    }
                    
                    // Apply search filter
                    if (decomSearchQuery) {
                      const query = decomSearchQuery.toLowerCase();
                      filteredData = filteredData.filter((item: DecommissioningItem) =>
                        item.portfolio?.toLowerCase().includes(query) ||
                        item.platform?.toLowerCase().includes(query) ||
                        item.app_id?.toLowerCase().includes(query) ||
                        item.app_name?.toLowerCase().includes(query)
                      );
                    }
                    
                    // Create CSV content based on visible columns
                    const headers: string[] = [];
                    if (decomColumnVisibility.portfolio) headers.push('Portfolio');
                    if (decomColumnVisibility.platform) headers.push('Platform');
                    if (decomColumnVisibility.app_id) headers.push('App ID');
                    if (decomColumnVisibility.app_name) headers.push('App Name');
                    if (decomColumnVisibility.mvc_group) headers.push('MVC Group');
                    if (decomColumnVisibility.owners) headers.push('Owner Name', 'Owner Email');
                    if (decomColumnVisibility.target_date) headers.push('Target Decom Date');
                    if (decomColumnVisibility.prev_target_date) headers.push('Prev Target Date');
                    if (decomColumnVisibility.decom_date) headers.push('Decom Date');
                    if (decomColumnVisibility.delayed_comments) headers.push('Delayed Comments from Mac');
                    if (decomColumnVisibility.strategic_intent) headers.push('Strategic Intent');
                    if (decomColumnVisibility.backup_tested) headers.push('Restore from Backup Tested (Last 12 Months)');
                    if (decomColumnVisibility.failover_tested) headers.push('Failover Tested Date (Last 12 Months)');
                    if (decomColumnVisibility.status) headers.push('Status');
                    if (decomColumnVisibility.decom_status) headers.push('Decom Status', 'Change Description');
                    if (decomColumnVisibility.application_labels) headers.push('Application Labels');
                    if (decomColumnVisibility.p1p2) headers.push('P1/P2');
                    if (decomColumnVisibility.p3p4mi) headers.push('P3/P4(MI)');
                    if (decomColumnVisibility.total_cost) headers.push('Total Approx cost (Last 12 months)');
                    if (decomColumnVisibility.bia_status) headers.push('BIA Status');
                    
                    const csvRows = [headers.join(',')];
                    
                    filteredData.forEach((item: DecommissioningItem) => {
                      // Use the calculated status from the item
                      const decomStatus = calculateDecommissioningStatus(item);
                      
                      const row: string[] = [];
                      if (decomColumnVisibility.portfolio) row.push(item.portfolio || 'N/A');
                      if (decomColumnVisibility.platform) row.push(item.platform || 'N/A');
                      if (decomColumnVisibility.app_id) row.push(item.app_id || 'N/A');
                      if (decomColumnVisibility.app_name) row.push(item.app_name || 'N/A');
                      if (decomColumnVisibility.mvc_group) row.push(item.mvc_group || 'N/A');
                      if (decomColumnVisibility.owners) {
                        row.push(item.owner_names || 'N/A');
                        row.push(item.owners || 'N/A');
                      }
                      if (decomColumnVisibility.target_date) row.push(item.target_date_decom ? new Date(item.target_date_decom).toLocaleDateString() : 'N/A');
                      
                      // Get previous target date from revisions
                      if (decomColumnVisibility.prev_target_date) {
                        const oldestPrevDate = getOldestPrevTargetDate(item);
                        row.push(oldestPrevDate ? oldestPrevDate.toLocaleDateString() : 'N/A');
                      }
                      
                      if (decomColumnVisibility.decom_date) row.push(item.decom_date ? new Date(item.decom_date).toLocaleDateString() : 'N/A');
                      if (decomColumnVisibility.delayed_comments) row.push(item.decom_extension_reason || 'N/A');
                      if (decomColumnVisibility.strategic_intent) row.push(item.strategic_intent?.intent || 'N/A');
                      if (decomColumnVisibility.backup_tested) row.push(item.backup_tested_last_12_months === 'Y' ? 'Yes' : item.backup_tested_last_12_months === 'N' ? 'No' : 'N/A');
                      if (decomColumnVisibility.failover_tested) row.push(item.failover_tested_date_last_12_months ? new Date(item.failover_tested_date_last_12_months).toLocaleDateString() : 'N/A');
                      if (decomColumnVisibility.status) row.push(item.status || 'N/A');
                      if (decomColumnVisibility.decom_status) {
                        row.push(decomStatus);
                        row.push(item.decom_change_description || 'N/A');
                      }
                      if (decomColumnVisibility.application_labels) {
                        const value = Array.isArray(item.application_labels) && item.application_labels.length > 0
                          ? item.application_labels.map((label: any) => label.name).join(', ')
                          : item.application_labels && typeof item.application_labels === 'string'
                          ? item.application_labels
                          : 'N/A';
                        row.push(value);
                      }
                      if (decomColumnVisibility.p1p2) {
                        const counts = getIncidentCounts(item.app_id);
                        row.push((counts.p1 + counts.p2).toString());
                      }
                      if (decomColumnVisibility.p3p4mi) {
                        const counts = getIncidentCounts(item.app_id);
                        row.push(counts.p3p4mi.toString());
                      }
                      if (decomColumnVisibility.total_cost) {
                        if (item.total_cost !== null && item.total_cost !== undefined) {
                          const costValue = typeof item.total_cost === 'string' ? parseFloat(item.total_cost) : item.total_cost;
                          if (!isNaN(costValue)) {
                            row.push(`$${costValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
                          } else {
                            row.push('Not Available');
                          }
                        } else {
                          row.push('Not Available');
                        }
                      }
                      if (decomColumnVisibility.bia_status) {
                        row.push(item.bia_status === true ? 'BIA Valid' : item.bia_status === false ? 'BIA Expired' : 'BIA - To be assessed');
                      }
                      
                      csvRows.push(row.map(cell => `"${cell}"`).join(','));
                    });
                    
                    // Download CSV
                    const csvContent = csvRows.join('\n');
                    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                    const link = document.createElement('a');
                    const url = URL.createObjectURL(blob);
                    
                    link.setAttribute('href', url);
                    link.setAttribute('download', `decommissioning_status_${new Date().toISOString().split('T')[0]}.csv`);
                    link.style.visibility = 'hidden';
                    
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                } catch (error) {
                  console.error('Error exporting decommissioning data:', error);
                }
              };

              const handleLoadDcoCsv = async (event: React.ChangeEvent<HTMLInputElement>) => {
                const file = event.target.files?.[0];
                if (!file) return;

                setDcoCsvLoading(true);
                try {
                  const controller = new AbortController();
                  dcoCsvAbortControllerRef.current = controller;
                  const formData = new FormData();
                  formData.append('file', file);

                  const response = await authFetch(DCO.LOAD_CSV, {
                    method: 'POST',
                    body: formData,
                    signal: controller.signal,
                  });

                  if (!response.ok) {
                    const errorText = await response.text().catch(() => '');
                    throw new Error(`Failed to load DCO CSV (${response.status}): ${errorText}`);
                  }

                  let result: any = null;
                  try {
                    result = await response.json();
                  } catch {
                    // If response is not JSON, ignore
                  }
                  console.log('✅ DCO CSV load result:', result);

                  setTagSuccessMessage('DCO cost analysis CSV loaded successfully');
                  await fetchDecommissioningData();
                } catch (error: any) {
                  if (error?.name === 'AbortError') {
                    console.warn('⏹️ DCO CSV load cancelled by user');
                  } else {
                    console.error('❌ Error loading DCO CSV:', error);
                    alert(error?.message || 'Failed to load DCO CSV');
                  }
                } finally {
                  setDcoCsvLoading(false);
                  dcoCsvAbortControllerRef.current = null;
                  // Allow re-upload of same file
                  event.target.value = '';
                }
              };

              // Filter to only records with target_date_decom
              const validData = decommissioningData.filter((item: DecommissioningItem) =>
                item.target_date_decom !== null && item.target_date_decom !== undefined
              );

              // Filter out unwanted MAC statuses (only show Active and Inactive)
              let validDataFiltered = validData.filter((item: DecommissioningItem) =>
                item.status === 'Active' || item.status === 'Inactive'
              );

              // Apply year filter for counts
              if (decomYearFilter === 'all') {
                if (selectedYears.length > 0) {
                  validDataFiltered = validDataFiltered.filter((item: DecommissioningItem) => {
                    if (!item.target_date_decom) return false;
                    const itemYear = new Date(item.target_date_decom).getFullYear();
                    return selectedYears.includes(itemYear.toString());
                  });
                }
                // If no specific years selected in 'all' mode, show all
              } else if (decomYearFilter === 'custom') {
                // Custom date range filter
                if (customRangeStart && customRangeEnd) {
                  validDataFiltered = validDataFiltered.filter((item: DecommissioningItem) => {
                    if (!item.target_date_decom) return false;
                    const itemDate = new Date(item.target_date_decom);
                    const startDate = new Date(customRangeStart);
                    const endDate = new Date(customRangeEnd);
                    return itemDate >= startDate && itemDate <= endDate;
                  });
                }
              } else if (decomYearFilter !== 'all') {
                // For specific year selection
                validDataFiltered = validDataFiltered.filter((item: DecommissioningItem) => {
                  if (!item.target_date_decom) return false;
                  const itemYear = new Date(item.target_date_decom).getFullYear();
                  return itemYear.toString() === decomYearFilter;
                });
              }

              // Use all decommissioning data for CSV export
              //const validData = decommissioningData;
              
              const today = new Date();
              today.setHours(0, 0, 0, 0);

              // Calculate counts using new status logic
              const retiredInactiveCount = validDataFiltered.filter(item => calculateDecommissioningStatus(item) === 'Decommissioned').length;
              const toBeDecommissionedCount = validDataFiltered.filter(item => calculateDecommissioningStatus(item) === 'To be Decommissioned').length;
              const delayedOverdueCount = validDataFiltered.filter(item => calculateDecommissioningStatus(item) === 'Delayed - Overdue').length;
              const delayedRepeatedlyCount = validDataFiltered.filter(item => calculateDecommissioningStatus(item) === 'Delayed - Repeatedly').length;
              const delayedDateExtendedCount = validDataFiltered.filter(item => calculateDecommissioningStatus(item) === 'Delayed - Date Extended').length;
              const cancelledCount = validDataFiltered.filter(item => calculateDecommissioningStatus(item) === 'Cancelled').length;

              return (
                <Box sx={{ 
                  mb: 2, 
                  display: 'flex', 
                  gap: 1, 
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  justifyContent: 'space-between'
                }}>
                  {/* Status Filter Chips */}
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center', flex: 1 }}>
                    <Chip
                      label={`All (${validDataFiltered.length})`}
                      onClick={() => {
                        setDecomTrackingFilter('all');
                        setDecomPage(0);
                      }}
                      variant={decomTrackingFilter === 'all' ? 'filled' : 'outlined'}
                      sx={{
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '0.77rem',
                        height: '30px',
                        bgcolor: decomTrackingFilter === 'all' ? '#1976d2 !important' : 'transparent',
                        color: decomTrackingFilter === 'all' ? '#fff !important' : '#666',
                        borderColor: decomTrackingFilter === 'all' ? '#1976d2' : '#ccc',
                        '&:hover': {
                          bgcolor: decomTrackingFilter === 'all' ? '#1565c0 !important' : '#f5f5f5'
                        }
                      }}
                    />
                    <Chip
                      label={`Decommissioned (${retiredInactiveCount})`}
                      onClick={() => {
                        setDecomTrackingFilter('completed');
                        setDecomPage(0);
                      }}
                      variant={decomTrackingFilter === 'completed' ? 'filled' : 'outlined'}
                      sx={{
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '0.77rem',
                        height: '30px',
                        bgcolor: decomTrackingFilter === 'completed' ? '#2e7d32 !important' : 'transparent',
                        color: decomTrackingFilter === 'completed' ? '#fff !important' : '#2e7d32',
                        borderColor: '#2e7d32',
                        '&:hover': {
                          bgcolor: decomTrackingFilter === 'completed' ? '#1b5e20 !important' : 'rgba(46, 125, 50, 0.08)'
                        }
                      }}
                    />
                    <Chip
                      label={`To be Decommissioned (${toBeDecommissionedCount})`}
                      onClick={() => {
                        setDecomTrackingFilter('ontrack');
                        setDecomPage(0);
                      }}
                      variant={decomTrackingFilter === 'ontrack' ? 'filled' : 'outlined'}
                      sx={{
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '0.77rem',
                        height: '30px',
                        bgcolor: decomTrackingFilter === 'ontrack' ? '#1976d2 !important' : 'transparent',
                        color: decomTrackingFilter === 'ontrack' ? '#fff !important' : '#1976d2',
                        borderColor: '#1976d2',
                        '&:hover': {
                          bgcolor: decomTrackingFilter === 'ontrack' ? '#1565c0 !important' : 'rgba(25, 118, 210, 0.08)'
                        }
                      }}
                    />
                    <Chip
                      label={`Delayed - Overdue (${delayedOverdueCount})`}
                      onClick={() => {
                        setDecomTrackingFilter('delayed-overdue');
                        setDecomPage(0);
                      }}
                      variant={decomTrackingFilter === 'delayed-overdue' ? 'filled' : 'outlined'}
                      sx={{
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '0.77rem',
                        height: '30px',
                        bgcolor: decomTrackingFilter === 'delayed-overdue' ? '#d32f2f !important' : 'transparent',
                        color: decomTrackingFilter === 'delayed-overdue' ? '#fff !important' : '#d32f2f',
                        borderColor: '#d32f2f',
                        '&:hover': {
                          bgcolor: decomTrackingFilter === 'delayed-overdue' ? '#c62828 !important' : 'rgba(211, 47, 47, 0.08)'
                        }
                      }}
                    />
                    <Chip
                      label={`Delayed - Repeatedly (${delayedRepeatedlyCount})`}
                      onClick={() => {
                        setDecomTrackingFilter('delayed-repeatedly');
                        setDecomPage(0);
                      }}
                      variant={decomTrackingFilter === 'delayed-repeatedly' ? 'filled' : 'outlined'}
                      sx={{
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '0.77rem',
                        height: '30px',
                        bgcolor: decomTrackingFilter === 'delayed-repeatedly' ? '#ed6c02 !important' : 'transparent',
                        color: decomTrackingFilter === 'delayed-repeatedly' ? '#fff !important' : '#ed6c02',
                        borderColor: '#ed6c02',
                        '&:hover': {
                          bgcolor: decomTrackingFilter === 'delayed-repeatedly' ? '#e65100 !important' : 'rgba(237, 108, 2, 0.08)'
                        }
                      }}
                    />
                    <Chip
                      label={`Delayed - Date Extended (${delayedDateExtendedCount})`}
                      onClick={() => {
                        setDecomTrackingFilter('delayed-extended');
                        setDecomPage(0);
                      }}
                      variant={decomTrackingFilter === 'delayed-extended' ? 'filled' : 'outlined'}
                      sx={{
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '0.77rem',
                        height: '30px',
                        bgcolor: decomTrackingFilter === 'delayed-extended' ? '#ffeb3b !important' : 'transparent',
                        color: decomTrackingFilter === 'delayed-extended' ? '#000 !important' : '#ffeb3b',
                        borderColor: '#ffeb3b',
                        '&:hover': {
                          bgcolor: decomTrackingFilter === 'delayed-extended' ? '#fdd835 !important' : 'rgba(255, 235, 59, 0.08)'
                        }
                      }}
                    />
                    <Chip
                      label={`Cancelled (${cancelledCount})`}
                      onClick={() => {
                        setDecomTrackingFilter('cancelled');
                        setDecomPage(0);
                      }}
                      variant={decomTrackingFilter === 'cancelled' ? 'filled' : 'outlined'}
                      sx={{
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '0.77rem',
                        height: '30px',
                        bgcolor: decomTrackingFilter === 'cancelled' ? '#666 !important' : 'transparent',
                        color: decomTrackingFilter === 'cancelled' ? '#fff !important' : '#666',
                        borderColor: '#666',
                        '&:hover': {
                          bgcolor: decomTrackingFilter === 'cancelled' ? '#555 !important' : 'rgba(102, 102, 102, 0.08)'
                        }
                      }}
                    />
                  </Box>

                  {/* Search Bar, Column Settings, and Export Button Container */}
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 0.5, 
                    alignItems: 'center',
                    flexShrink: 0,
                    minWidth: 'fit-content'
                  }}>
                    {/* Search Bar */}
                    <TextField
                      size="small"
                      placeholder="Search by Portfolio, Platform, App ID, or App Name..."
                      value={decomSearchQuery}
                      onChange={(e) => {
                        setDecomSearchQuery(e.target.value);
                        setDecomPage(0);
                      }}
                      sx={{ 
                        width: '290px',
                        '& .MuiInputBase-root': {
                          height: '36px',
                          fontSize: '0.87rem'
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <Box sx={{ mr: 0.5, display: 'flex', alignItems: 'center' }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="11" cy="11" r="8"></circle>
                              <path d="m21 21-4.35-4.35"></path>
                            </svg>
                          </Box>
                        ),
                      }}
                    />

                    {/* Column Settings Button */}
                    <IconButton
                      onClick={() => setDecomColumnDialogOpen(true)}
                      sx={{
                        bgcolor: '#f5f5f5',
                        '&:hover': { bgcolor: '#e0e0e0' },
                        border: '1px solid #ccc',
                        borderRadius: 1,
                        width: '34px',
                        height: '34px',
                        minWidth: '34px'
                      }}
                      title="Column Settings"
                    >
                      <Settings sx={{ color: '#666', fontSize: '17px' }} />
                    </IconButton>

                    {/* Load CSV Button (also used to cancel while loading) - only for allowed users */}
                    {canManageDcoCsv && (
                    <MuiTooltip
                      title={dcoCsvLoading ? 'Click to cancel DCO CSV load' : 'Load DCO cost analysis CSV'}
                      arrow
                    >
                      <Button
                        variant="contained"
                        color={dcoCsvLoading ? 'success' : 'primary'}
                        component="label"
                        onClick={async (event) => {
                          if (dcoCsvLoading) {
                            // Prevent the label from triggering the file input when we're cancelling
                            event.preventDefault();
                            event.stopPropagation();

                            if (dcoCsvAbortControllerRef.current) {
                              const confirmCancel = window.confirm('Do you want to cancel the DCO CSV load?');
                              if (confirmCancel) {
                                try {
                                  dcoCsvAbortControllerRef.current.abort();
                                } catch (e) {
                                  console.error('Error aborting DCO CSV load:', e);
                                }

                                // Also tell the backend to cooperatively stop the loader
                                try {
                                  await authFetch(DCO.CANCEL_LOAD, { method: 'POST' });
                                } catch (e) {
                                  console.error('Error calling DCO cancel endpoint:', e);
                                }
                              }
                            }
                          }
                        }}
                        sx={{
                          textTransform: 'none',
                          whiteSpace: 'nowrap',
                          height: '34px',
                          px: 1.5,
                          fontSize: '0.85rem',
                          // Let the Button's color prop control base color; only tweak hover
                          '&:hover': {
                            opacity: 0.9
                          }
                        }}
                      >
                        {dcoCsvLoading ? 'Loading CSV...' : 'Load CSV'}
                        {!dcoCsvLoading && (
                          <input
                            type="file"
                            accept=".csv,text/csv"
                            hidden
                            onChange={handleLoadDcoCsv}
                          />
                        )}
                      </Button>
                    </MuiTooltip>
                    )}

                    {/* Export Button */}
                    <Button
                      variant="contained"
                      onClick={handleExportCSV}
                      sx={{
                        textTransform: 'none',
                        bgcolor: '#1976d2',
                        '&:hover': { bgcolor: '#1565c0' },
                        whiteSpace: 'nowrap',
                        height: '34px',
                        px: 1.5,
                        fontSize: '0.85rem'
                      }}
                    >
                      Export CSV
                    </Button>
                  </Box>
                </Box>
              );
            })()}

            {decomView !== 'trends' && (decommissioningLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : decommissioningError ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                {decommissioningError}
              </Alert>
            ) : decommissioningData.length > 0 ? (
              <Box>

                {/* Decommissioning Data Table */}
                <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
                  <Table>
                    <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                      <TableRow>
                        {decomColumnVisibility.portfolio && (
                          <TableCell>
                            <strong>Portfolio</strong>
                          </TableCell>
                        )}
                        {decomColumnVisibility.platform && (
                          <TableCell>
                            <strong>Platform</strong>
                          </TableCell>
                        )}
                        {decomColumnVisibility.app_id && <TableCell><strong>App ID</strong></TableCell>}
                        {decomColumnVisibility.app_name && <TableCell><strong>App Name</strong></TableCell>}
                        {decomColumnVisibility.mvc_group && (
                          <TableCell>
                            <TableSortLabel
                              active={decomSortConfig?.key === 'mvc_group'}
                              direction={decomSortConfig?.key === 'mvc_group' ? decomSortConfig.direction : 'asc'}
                              onClick={() => {
                                const isAsc = decomSortConfig?.key === 'mvc_group' && decomSortConfig.direction === 'asc';
                                setDecomSortConfig({ key: 'mvc_group', direction: isAsc ? 'desc' : 'asc' });
                              }}
                            >
                              <strong>MVC Group</strong>
                            </TableSortLabel>
                          </TableCell>
                        )}
                        {decomColumnVisibility.owners && (
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <strong>Owners</strong>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDecomOwnersFilterAnchor(e.currentTarget);
                                  setDecomOwnersTempFilter(decomOwnersFilter);
                                }}
                                sx={{ 
                                  padding: '2px',
                                  color: decomOwnersFilter ? '#1976d2' : '#666'
                                }}
                              >
                                {decomOwnersFilter ? (
                                  <FilterAlt sx={{ fontSize: 16, color: '#1976d2' }} />
                                ) : (
                                  <FilterAltOutlined sx={{ fontSize: 16, color: '#666' }} />
                                )}
                              </IconButton>
                            </Box>
                          </TableCell>
                        )}
                        {decomColumnVisibility.target_date && (
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <TableSortLabel
                                active={decomSortConfig?.key === 'target_date_decom'}
                                direction={decomSortConfig?.key === 'target_date_decom' ? decomSortConfig.direction : 'asc'}
                                onClick={() => {
                                  const isAsc = decomSortConfig?.key === 'target_date_decom' && decomSortConfig.direction === 'asc';
                                  setDecomSortConfig({ key: 'target_date_decom', direction: isAsc ? 'desc' : 'asc' });
                                }}
                              >
                                <strong>Target Decom Date</strong>
                              </TableSortLabel>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDecomYearFilterAnchor(e.currentTarget);
                                }}
                                sx={{
                                  padding: '2px',
                                  '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
                                }}
                              >
                                {(decomYearFilter === previousYear.toString() || decomYearFilter === nextYear.toString() || (decomYearFilter === 'all' && selectedYears.length > 0) || decomYearFilter === currentYear.toString() || decomYearFilter === 'custom') ? (
                                  <FilterAlt
                                    sx={{
                                      fontSize: 16,
                                      color: '#1976d2'
                                    }}
                                  />
                                ) : (
                                  <FilterAltOutlined
                                    sx={{
                                      fontSize: 16,
                                      color: '#666'
                                    }}
                                  />
                                )}
                              </IconButton>
                            </Box>
                          </TableCell>
                        )}
                        {decomColumnVisibility.prev_target_date && (
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <TableSortLabel
                                active={decomSortConfig?.key === 'prev_target_date'}
                                direction={decomSortConfig?.key === 'prev_target_date' ? decomSortConfig.direction : 'asc'}
                                onClick={() => {
                                  const isAsc = decomSortConfig?.key === 'prev_target_date' && decomSortConfig.direction === 'asc';
                                  setDecomSortConfig({ key: 'prev_target_date', direction: isAsc ? 'desc' : 'asc' });
                                }}
                              >
                                <strong>Prev Target Date</strong>
                              </TableSortLabel>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDecomPrevTargetDateFilterAnchor(e.currentTarget);
                                  setDecomPrevTargetDateTempFilter(decomPrevTargetDateFilter);
                                  setDecomPrevTargetDateTempOperator(decomPrevTargetDateTempOperator);
                                  setDecomPrevTargetDateTempCustomStart(decomPrevTargetDateTempCustomStart);
                                  setDecomPrevTargetDateTempCustomEnd(decomPrevTargetDateTempCustomEnd);
                                }}
                                sx={{ 
                                  padding: '2px',
                                  '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
                                }}
                              >
                                {decomPrevTargetDateFilter ? (
                                  <FilterAlt 
                                    sx={{ 
                                      fontSize: 16, 
                                      color: '#1976d2'
                                    }} 
                                  />
                                ) : (
                                  <FilterAltOutlined 
                                    sx={{ 
                                      fontSize: 16, 
                                      color: '#666'
                                    }} 
                                  />
                                )}
                              </IconButton>
                            </Box>
                          </TableCell>
                        )}
                        {decomColumnVisibility.decom_date && (
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <TableSortLabel
                                active={decomSortConfig?.key === 'decom_date'}
                                direction={decomSortConfig?.key === 'decom_date' ? decomSortConfig.direction : 'asc'}
                                onClick={() => {
                                  const isAsc = decomSortConfig?.key === 'decom_date' && decomSortConfig.direction === 'asc';
                                  setDecomSortConfig({ key: 'decom_date', direction: isAsc ? 'desc' : 'asc' });
                                }}
                              >
                                <strong>Decom Date</strong>
                              </TableSortLabel>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDecomDecomDateFilterAnchor(e.currentTarget);
                                  setDecomDecomDateTempFilter(decomDecomDateFilter);
                                  setDecomDecomDateTempOperator(decomDecomDateOperator);
                                  setDecomDecomDateTempCustomStart(decomDecomDateCustomStart);
                                  setDecomDecomDateTempCustomEnd(decomDecomDateCustomEnd);
                                }}
                                sx={{ 
                                  padding: '2px',
                                  '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
                                }}
                              >
                                {decomDecomDateFilter ? (
                                  <FilterAlt 
                                    sx={{ 
                                      fontSize: 16, 
                                      color: '#1976d2'
                                    }} 
                                  />
                                ) : (
                                  <FilterAltOutlined 
                                    sx={{ 
                                      fontSize: 16, 
                                      color: '#666'
                                    }} 
                                  />
                                )}
                              </IconButton>
                            </Box>
                          </TableCell>
                        )}
                        {decomColumnVisibility.delayed_comments && (
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <strong>Delayed Comments from Mac</strong>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDecomDelayedCommentsFilterAnchor(e.currentTarget);
                                  setDecomDelayedCommentsTempFilter(decomDelayedCommentsFilter);
                                }}
                                sx={{ 
                                  padding: '2px',
                                  '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
                                }}
                              >
                                {decomDelayedCommentsFilter ? (
                                  <FilterAlt 
                                    sx={{ 
                                      fontSize: 16, 
                                      color: '#1976d2'
                                    }} 
                                  />
                                ) : (
                                  <FilterAltOutlined 
                                    sx={{ 
                                      fontSize: 16, 
                                      color: '#666'
                                    }} 
                                  />
                                )}
                              </IconButton>
                            </Box>
                          </TableCell>
                        )}
                        {decomColumnVisibility.strategic_intent && (
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <strong>Strategic Intent</strong>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDecomStrategicIntentFilterAnchor(e.currentTarget);
                                  setDecomStrategicIntentTempFilter(decomStrategicIntentFilter);
                                }}
                                sx={{ 
                                  padding: '2px',
                                  color: decomStrategicIntentFilter.length > 0 ? '#1976d2' : '#666'
                                }}
                              >
                                {decomStrategicIntentFilter.length > 0 ? (
                                  <FilterAlt sx={{ fontSize: 16, color: '#1976d2' }} />
                                ) : (
                                  <FilterAltOutlined sx={{ fontSize: 16, color: '#666' }} />
                                )}
                              </IconButton>
                            </Box>
                          </TableCell>
                        )}
                        {decomColumnVisibility.backup_tested && (
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <strong>Restore from Backup Tested (Last 12 Months)</strong>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDecomBackupTestedDateFilterAnchor(e.currentTarget);
                                  setDecomBackupTestedDateTempFilter(decomBackupTestedDateFilter);
                                  setDecomBackupTestedDateTempOperator(decomBackupTestedDateOperator);
                                  setDecomBackupTestedDateTempCustomStart(decomBackupTestedDateCustomStart);
                                  setDecomBackupTestedDateTempCustomEnd(decomBackupTestedDateCustomEnd);
                                }}
                                sx={{ 
                                  padding: '2px',
                                  '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
                                }}
                              >
                                {decomBackupTestedDateFilter || (decomBackupTestedDateOperator === 'custom' && (decomBackupTestedDateCustomStart || decomBackupTestedDateCustomEnd)) ? (
                                  <FilterAlt sx={{ fontSize: 16, color: '#1976d2' }} />
                                ) : (
                                  <FilterAltOutlined sx={{ fontSize: 16, color: '#666' }} />
                                )}
                              </IconButton>
                            </Box>
                          </TableCell>
                        )}
                        {decomColumnVisibility.failover_tested && (
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <strong>Failover Tested Date (Last 12 Months)</strong>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDecomFailoverTestedDateFilterAnchor(e.currentTarget);
                                  setDecomFailoverTestedDateTempFilter(decomFailoverTestedDateFilter);
                                  setDecomFailoverTestedDateTempOperator(decomFailoverTestedDateOperator);
                                  setDecomFailoverTestedDateTempCustomStart(decomFailoverTestedDateCustomStart);
                                  setDecomFailoverTestedDateTempCustomEnd(decomFailoverTestedDateCustomEnd);
                                }}
                                sx={{ 
                                  padding: '2px',
                                  '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
                                }}
                              >
                                {decomFailoverTestedDateFilter ? (
                                  <FilterAlt 
                                    sx={{ 
                                      fontSize: 16, 
                                      color: '#1976d2'
                                    }} 
                                  />
                                ) : (
                                  <FilterAltOutlined 
                                    sx={{ 
                                      fontSize: 16, 
                                      color: '#666'
                                    }} 
                                  />
                                )}
                              </IconButton>
                            </Box>
                          </TableCell>
                        )}
                        {decomColumnVisibility.status && (
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <strong>MAC Status</strong>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDecomStatusFilterAnchor(e.currentTarget);
                                  setDecomStatusTempFilter(decomStatusFilter);
                                }}
                                sx={{ 
                                  padding: '2px',
                                  color: decomStatusFilter.length > 0 ? '#1976d2' : '#666'
                                }}
                              >
                                {decomStatusFilter.length > 0 ? (
                                  <FilterAlt sx={{ fontSize: 16, color: '#1976d2' }} />
                                ) : (
                                  <FilterAltOutlined sx={{ fontSize: 16, color: '#666' }} />
                                )}
                              </IconButton>
                            </Box>
                          </TableCell>
                        )}
                        {decomColumnVisibility.decom_status && (
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <TableSortLabel
                                active={decomSortConfig?.key === 'decom_status'}
                                direction={decomSortConfig?.key === 'decom_status' ? decomSortConfig.direction : 'asc'}
                                onClick={() => {
                                  const isAsc = decomSortConfig?.key === 'decom_status' && decomSortConfig.direction === 'asc';
                                  setDecomSortConfig({ key: 'decom_status', direction: isAsc ? 'desc' : 'asc' });
                                }}
                              >
                                <strong>Decom Status</strong>
                              </TableSortLabel>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDecomDecomStatusFilterAnchor(e.currentTarget);
                                  setDecomDecomStatusTempFilter(decomDecomStatusFilter);
                                }}
                                sx={{ 
                                  padding: '2px',
                                  color: decomDecomStatusFilter.length > 0 ? '#1976d2' : '#666'
                                }}
                              >
                                {decomDecomStatusFilter.length > 0 ? (
                                  <FilterAlt sx={{ fontSize: 16, color: '#1976d2' }} />
                                ) : (
                                  <FilterAltOutlined sx={{ fontSize: 16, color: '#666' }} />
                                )}
                              </IconButton>
                            </Box>
                          </TableCell>
                        )}
                        {decomColumnVisibility.application_labels && (
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <TableSortLabel
                                active={decomSortConfig?.key === 'application_labels'}
                                direction={decomSortConfig?.key === 'application_labels' ? decomSortConfig.direction : 'asc'}
                                onClick={() => {
                                  const isAsc = decomSortConfig?.key === 'application_labels' && decomSortConfig.direction === 'asc';
                                  setDecomSortConfig({ key: 'application_labels', direction: isAsc ? 'desc' : 'asc' });
                                }}
                              >
                                <strong>Application Labels</strong>
                              </TableSortLabel>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDecomApplicationLabelsFilterAnchor(e.currentTarget);
                                  setDecomApplicationLabelsTempFilter(decomApplicationLabelsFilter);
                                  setDecomApplicationLabelsSearchText('');
                                }}
                                sx={{ 
                                  padding: '2px',
                                  color: decomApplicationLabelsFilter.length > 0 ? '#1976d2' : '#666'
                                }}
                              >
                                {decomApplicationLabelsFilter.length > 0 ? (
                                  <FilterAlt sx={{ fontSize: 16, color: '#1976d2' }} />
                                ) : (
                                  <FilterAltOutlined sx={{ fontSize: 16, color: '#666' }} />
                                )}
                              </IconButton>
                            </Box>
                          </TableCell>
                        )}
                        {decomColumnVisibility.tag && (
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <TableSortLabel
                                active={decomSortConfig?.key === 'tag'}
                                direction={decomSortConfig?.key === 'tag' ? decomSortConfig.direction : 'asc'}
                                onClick={() => {
                                  const isAsc = decomSortConfig?.key === 'tag' && decomSortConfig.direction === 'asc';
                                  setDecomSortConfig({ key: 'tag', direction: isAsc ? 'desc' : 'asc' });
                                }}
                              >
                                <strong>Tag</strong>
                              </TableSortLabel>
                            </Box>
                          </TableCell>
                        )}
                        {decomColumnVisibility.p1p2 && (
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <TableSortLabel
                                active={decomSortConfig?.key === 'p1p2'}
                                direction={decomSortConfig?.key === 'p1p2' ? decomSortConfig.direction : 'asc'}
                                onClick={() => {
                                  const isAsc = decomSortConfig?.key === 'p1p2' && decomSortConfig.direction === 'asc';
                                  setDecomSortConfig({ key: 'p1p2', direction: isAsc ? 'desc' : 'asc' });
                                }}
                              >
                                <strong>P1/P2</strong>
                              </TableSortLabel>
                            </Box>
                          </TableCell>
                        )}
                        {decomColumnVisibility.p3p4mi && (
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <TableSortLabel
                                active={decomSortConfig?.key === 'p3p4mi'}
                                direction={decomSortConfig?.key === 'p3p4mi' ? decomSortConfig.direction : 'asc'}
                                onClick={() => {
                                  const isAsc = decomSortConfig?.key === 'p3p4mi' && decomSortConfig.direction === 'asc';
                                  setDecomSortConfig({ key: 'p3p4mi', direction: isAsc ? 'desc' : 'asc' });
                                }}
                              >
                                <strong>P3/P4(MI)</strong>
                              </TableSortLabel>
                            </Box>
                          </TableCell>
                        )}
                        {decomColumnVisibility.total_cost && (
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <TableSortLabel
                                active={decomSortConfig?.key === 'total_cost'}
                                direction={decomSortConfig?.key === 'total_cost' ? decomSortConfig.direction : 'asc'}
                                onClick={() => {
                                  const isAsc = decomSortConfig?.key === 'total_cost' && decomSortConfig.direction === 'asc';
                                  setDecomSortConfig({ key: 'total_cost', direction: isAsc ? 'desc' : 'asc' });
                                }}
                              >
                                <strong>
                                  Total Approx cost (Last 12 months)
                                </strong>
                              </TableSortLabel>
                            </Box>
                          </TableCell>
                        )}
                        {decomColumnVisibility.bia_status && (
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <TableSortLabel
                                active={decomSortConfig?.key === 'bia_status'}
                                direction={decomSortConfig?.key === 'bia_status' ? decomSortConfig.direction : 'asc'}
                                onClick={() => {
                                  const isAsc = decomSortConfig?.key === 'bia_status' && decomSortConfig.direction === 'asc';
                                  setDecomSortConfig({ key: 'bia_status', direction: isAsc ? 'desc' : 'asc' });
                                }}
                              >
                                <strong>BIA Status</strong>
                              </TableSortLabel>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDecomBiaStatusFilterAnchor(e.currentTarget);
                                  setDecomBiaStatusTempFilter(decomBiaStatusFilter);
                                }}
                                sx={{ padding: '2px', color: decomBiaStatusFilter.length > 0 ? '#1976d2' : '#666' }}
                              >
                                {decomBiaStatusFilter.length > 0 ? (
                                  <FilterAlt sx={{ fontSize: 16, color: '#1976d2' }} />
                                ) : (
                                  <FilterAltOutlined sx={{ fontSize: 16, color: '#666' }} />
                                )}
                              </IconButton>
                            </Box>
                          </TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(() => {
                        // Start with all decommissioning data (don't filter out records without target_date_decom)
                        //let filteredData = decommissioningData;

                          let filteredData = decommissioningData.filter((item: DecommissioningItem) =>
                          item.target_date_decom !== null && item.target_date_decom !== undefined
                        );

                        // Filter out unwanted MAC statuses (only show Active and Inactive)
                        filteredData = filteredData.filter((item: DecommissioningItem) =>
                          item.status === 'Active' || item.status === 'Inactive'
                        );
 
                        
                        // Apply portfolio filter
                        if (decomPortfolioFilter.length > 0) {
                          filteredData = filteredData.filter((item: DecommissioningItem) =>
                            decomPortfolioFilter.includes(item.portfolio)
                          );
                        }
                        
                        // Apply platform filter
                        if (decomPlatformFilter.length > 0) {
                          filteredData = filteredData.filter((item: DecommissioningItem) =>
                            decomPlatformFilter.includes(item.platform)
                          );
                        }
                        
                        // Apply strategic intent filter
                        if (decomStrategicIntentFilter.length > 0) {
                          filteredData = filteredData.filter((item: DecommissioningItem) =>
                            decomStrategicIntentFilter.includes(item.strategic_intent?.intent || 'N/A')
                          );
                        }
                        
                        // Apply status filter
                        if (decomStatusFilter.length > 0) {
                          filteredData = filteredData.filter((item: DecommissioningItem) =>
                            decomStatusFilter.includes(item.status || 'N/A')
                          );
                        }
                        
                        // Apply decom status filter
                        if (decomDecomStatusFilter.length > 0) {
                          filteredData = filteredData.filter((item: DecommissioningItem) => {
                            return decomDecomStatusFilter.includes(calculateDecommissioningStatus(item));
                          });
                        }
                        
                        // Apply application labels filter
                        if (decomApplicationLabelsFilter.length > 0) {
                          filteredData = filteredData.filter((item: DecommissioningItem) => {
                            if (Array.isArray(item.application_labels) && item.application_labels.length > 0) {
                              // Check if any of the item's labels match any of the selected filters
                              return item.application_labels.some((label: any) => 
                                decomApplicationLabelsFilter.includes(label.name)
                              );
                            }
                            return decomApplicationLabelsFilter.includes('N/A');
                          });
                        }
                        
                        // Apply owners filter
                        if (decomOwnersFilter) {
                          filteredData = filteredData.filter((item: DecommissioningItem) => {
                            const ownerNames = item.owner_names?.toLowerCase() || '';
                            const owners = item.owners?.toLowerCase() || '';
                            const searchTerm = decomOwnersFilter.toLowerCase();
                            return ownerNames.includes(searchTerm) || owners.includes(searchTerm);
                          });
                        }
                        
                        // Apply delayed comments filter
                        if (decomDelayedCommentsFilter) {
                          filteredData = filteredData.filter((item: DecommissioningItem) => {
                            const delayedComments = item.decom_extension_reason?.toLowerCase() || '';
                            const searchTerm = decomDelayedCommentsFilter.toLowerCase();
                            return delayedComments.includes(searchTerm);
                          });
                        }

                        // Apply prev target date filter
                        if (decomPrevTargetDateFilter || (decomPrevTargetDateOperator === 'custom' && (decomPrevTargetDateCustomStart || decomPrevTargetDateCustomEnd))) {
                          filteredData = filteredData.filter((item: DecommissioningItem) => {
                            const prevTargetDate = getOldestPrevTargetDate(item);
                            if (!prevTargetDate) return false;
                            const itemDate = new Date(prevTargetDate);
                            itemDate.setHours(0, 0, 0, 0);
                            if (decomPrevTargetDateOperator === 'custom') {
                              if (decomPrevTargetDateCustomStart && decomPrevTargetDateCustomEnd) {
                                const startDate = new Date(decomPrevTargetDateCustomStart);
                                const endDate = new Date(decomPrevTargetDateCustomEnd);
                                startDate.setHours(0, 0, 0, 0);
                                endDate.setHours(23, 59, 59, 999);
                                return itemDate.getTime() >= startDate.getTime() && itemDate.getTime() <= endDate.getTime();
                              } else if (decomPrevTargetDateCustomStart) {
                                const startDate = new Date(decomPrevTargetDateCustomStart);
                                startDate.setHours(0, 0, 0, 0);
                                return itemDate.getTime() >= startDate.getTime();
                              } else if (decomPrevTargetDateCustomEnd) {
                                const endDate = new Date(decomPrevTargetDateCustomEnd);
                                endDate.setHours(0, 0, 0, 0);
                                return itemDate.getTime() <= endDate.getTime();
                              }
                              return true;
                            } else {
                              const filterDate = new Date(decomPrevTargetDateFilter);
                              filterDate.setHours(0, 0, 0, 0);
                              if (decomPrevTargetDateOperator === 'equals') {
                                return itemDate.getTime() === filterDate.getTime();
                              } else if (decomPrevTargetDateOperator === 'after') {
                                return itemDate.getTime() > filterDate.getTime();
                              } else if (decomPrevTargetDateOperator === 'before') {
                                return itemDate.getTime() < filterDate.getTime();
                              }
                            }
                            return false;
                          });
                        }

                        // Apply BIA status filter
                        if (decomBiaStatusFilter.length > 0) {
                          filteredData = filteredData.filter((item: DecommissioningItem) => {
                            const biaLabel = item.bia_status === true ? 'BIA Valid' : item.bia_status === false ? 'BIA Expired' : 'BIA - To be assessed';
                            return decomBiaStatusFilter.includes(biaLabel);
                          });
                        }
                        
                        // Apply decom date filter
                        if (decomDecomDateFilter || (decomDecomDateOperator === 'custom' && (decomDecomDateCustomStart || decomDecomDateCustomEnd))) {
                          filteredData = filteredData.filter((item: DecommissioningItem) => {
                            if (!item.decom_date) return false;
                            const itemDate = new Date(item.decom_date);
                            itemDate.setHours(0, 0, 0, 0);
                            
                            if (decomDecomDateOperator === 'custom') {
                              // Custom range logic
                              if (decomDecomDateCustomStart && decomDecomDateCustomEnd) {
                                const startDate = new Date(decomDecomDateCustomStart);
                                startDate.setHours(0, 0, 0, 0);
                                const endDate = new Date(decomDecomDateCustomEnd);
                                endDate.setHours(0, 0, 0, 0);
                                return itemDate.getTime() >= startDate.getTime() && itemDate.getTime() <= endDate.getTime();
                              } else if (decomDecomDateCustomStart) {
                                const startDate = new Date(decomDecomDateCustomStart);
                                startDate.setHours(0, 0, 0, 0);
                                return itemDate.getTime() >= startDate.getTime();
                              } else if (decomDecomDateCustomEnd) {
                                const endDate = new Date(decomDecomDateCustomEnd);
                                endDate.setHours(0, 0, 0, 0);
                                return itemDate.getTime() <= endDate.getTime();
                              }
                              return true;
                            } else {
                              // Single date operators
                              const filterDate = new Date(decomDecomDateFilter);
                              filterDate.setHours(0, 0, 0, 0);
                              
                              if (decomDecomDateOperator === 'equals') {
                                return itemDate.getTime() === filterDate.getTime();
                              } else if (decomDecomDateOperator === 'after') {
                                return itemDate.getTime() > filterDate.getTime();
                              } else if (decomDecomDateOperator === 'before') {
                                return itemDate.getTime() < filterDate.getTime();
                              }
                            }
                            return false;
                          });
                        }
                        
                        // Apply failover tested date filter
                        if (decomFailoverTestedDateFilter || (decomFailoverTestedDateOperator === 'custom' && (decomFailoverTestedDateCustomStart || decomFailoverTestedDateCustomEnd))) {
                          filteredData = filteredData.filter((item: DecommissioningItem) => {
                            if (!item.failover_tested_date_last_12_months) return false;
                            const itemDate = new Date(item.failover_tested_date_last_12_months);
                            itemDate.setHours(0, 0, 0, 0);
                            
                            if (decomFailoverTestedDateOperator === 'custom') {
                              // Custom range logic
                              if (decomFailoverTestedDateCustomStart && decomFailoverTestedDateCustomEnd) {
                                const startDate = new Date(decomFailoverTestedDateCustomStart);
                                startDate.setHours(0, 0, 0, 0);
                                const endDate = new Date(decomFailoverTestedDateCustomEnd);
                                endDate.setHours(0, 0, 0, 0);
                                return itemDate.getTime() >= startDate.getTime() && itemDate.getTime() <= endDate.getTime();
                              } else if (decomFailoverTestedDateCustomStart) {
                                const startDate = new Date(decomFailoverTestedDateCustomStart);
                                startDate.setHours(0, 0, 0, 0);
                                return itemDate.getTime() >= startDate.getTime();
                              } else if (decomFailoverTestedDateCustomEnd) {
                                const endDate = new Date(decomFailoverTestedDateCustomEnd);
                                endDate.setHours(0, 0, 0, 0);
                                return itemDate.getTime() <= endDate.getTime();
                              }
                              return true;
                            } else {
                              // Single date operators
                              const filterDate = new Date(decomFailoverTestedDateFilter);
                              filterDate.setHours(0, 0, 0, 0);
                              
                              if (decomFailoverTestedDateOperator === 'equals') {
                                return itemDate.getTime() === filterDate.getTime();
                              } else if (decomFailoverTestedDateOperator === 'after') {
                                return itemDate.getTime() > filterDate.getTime();
                              } else if (decomFailoverTestedDateOperator === 'before') {
                                return itemDate.getTime() < filterDate.getTime();
                              }
                            }
                            return false;
                          });
                        }
                        
                        // Apply backup tested date filter
                        if (decomBackupTestedDateFilter || (decomBackupTestedDateOperator === 'custom' && (decomBackupTestedDateCustomStart || decomBackupTestedDateCustomEnd))) {
                          filteredData = filteredData.filter((item: DecommissioningItem) => {
                            if (!item.backup_tested_last_12_months) return false;
                            const itemDate = new Date(item.backup_tested_last_12_months);
                            itemDate.setHours(0, 0, 0, 0);
                            
                            if (decomBackupTestedDateOperator === 'custom') {
                              // Custom range logic
                              if (decomBackupTestedDateCustomStart && decomBackupTestedDateCustomEnd) {
                                const startDate = new Date(decomBackupTestedDateCustomStart);
                                startDate.setHours(0, 0, 0, 0);
                                const endDate = new Date(decomBackupTestedDateCustomEnd);
                                endDate.setHours(0, 0, 0, 0);
                                return itemDate.getTime() >= startDate.getTime() && itemDate.getTime() <= endDate.getTime();
                              } else if (decomBackupTestedDateCustomStart) {
                                const startDate = new Date(decomBackupTestedDateCustomStart);
                                startDate.setHours(0, 0, 0, 0);
                                return itemDate.getTime() >= startDate.getTime();
                              } else if (decomBackupTestedDateCustomEnd) {
                                const endDate = new Date(decomBackupTestedDateCustomEnd);
                                endDate.setHours(0, 0, 0, 0);
                                return itemDate.getTime() <= endDate.getTime();
                              }
                              return true;
                            } else {
                              // Single date operators
                              const filterDate = new Date(decomBackupTestedDateFilter);
                              filterDate.setHours(0, 0, 0, 0);
                              
                              if (decomBackupTestedDateOperator === 'equals') {
                                return itemDate.getTime() === filterDate.getTime();
                              } else if (decomBackupTestedDateOperator === 'after') {
                                return itemDate.getTime() > filterDate.getTime();
                              } else if (decomBackupTestedDateOperator === 'before') {
                                return itemDate.getTime() < filterDate.getTime();
                              }
                            }
                            return false;
                          });
                        }
                        
                        // Apply year filter
                        if (decomYearFilter === 'all') {
                          if (selectedYears.length > 0) {
                            filteredData = filteredData.filter((item: DecommissioningItem) => {
                              if (!item.target_date_decom) return false;
                              const itemYear = new Date(item.target_date_decom).getFullYear();
                              return selectedYears.includes(itemYear.toString());
                            });
                          }
                          // If no specific years selected in 'all' mode, show all
                        } else if (decomYearFilter === 'custom') {
                          // Custom date range filter
                          if (customRangeStart && customRangeEnd) {
                            filteredData = filteredData.filter((item: DecommissioningItem) => {
                              if (!item.target_date_decom) return false;
                              const itemDate = new Date(item.target_date_decom);
                              const startDate = new Date(customRangeStart);
                              const endDate = new Date(customRangeEnd);
                              return itemDate >= startDate && itemDate <= endDate;
                            });
                          }
                        } else if (decomYearFilter !== 'all') {
                          // For specific year selection
                          filteredData = filteredData.filter((item: DecommissioningItem) => {
                            if (!item.target_date_decom) return false;
                            const itemYear = new Date(item.target_date_decom).getFullYear();
                            return itemYear.toString() === decomYearFilter;
                          });
                        }
                        
                        // Apply tracking status filter
                        if (decomTrackingFilter !== 'all') {
                          filteredData = filteredData.filter((item: DecommissioningItem) => {
                            const status = calculateDecommissioningStatus(item);
                            if (decomTrackingFilter === 'completed') {
                              return status === 'Decommissioned';
                            } else if (decomTrackingFilter === 'delayed-overdue') {
                              return status === 'Delayed - Overdue';
                            } else if (decomTrackingFilter === 'delayed-repeatedly') {
                              return status === 'Delayed - Repeatedly';
                            } else if (decomTrackingFilter === 'delayed-extended') {
                              return status === 'Delayed - Date Extended';
                            } else if (decomTrackingFilter === 'ontrack') {
                              return status === 'To be Decommissioned';
                            } else if (decomTrackingFilter === 'cancelled') {
                              return status === 'Cancelled';
                            }
                            return true;
                          });
                        }
                        
                        // Apply search filter
                        if (decomSearchQuery) {
                          const query = decomSearchQuery.toLowerCase();
                          filteredData = filteredData.filter((item: DecommissioningItem) =>
                            item.portfolio?.toLowerCase().includes(query) ||
                            item.platform?.toLowerCase().includes(query) ||
                            item.app_id?.toLowerCase().includes(query) ||
                            item.app_name?.toLowerCase().includes(query)
                          );
                        }

                        // Apply sorting
                        if (decomSortConfig) {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          
                          filteredData = [...filteredData].sort((a: any, b: any) => {
                            let aValue: any;
                            let bValue: any;
                            
                            // Special handling for different column types
                            if (decomSortConfig.key === 'target_date_decom' || decomSortConfig.key === 'decom_date') {
                              // Date sorting - dates before 2026 first, then 2026 onwards
                              const aDate = a[decomSortConfig.key] ? new Date(a[decomSortConfig.key]) : null;
                              const bDate = b[decomSortConfig.key] ? new Date(b[decomSortConfig.key]) : null;
                              
                              // Handle null dates (put them at the end)
                              if (!aDate && !bDate) {
                                aValue = 0;
                                bValue = 0;
                              } else if (!aDate) {
                                aValue = 1; // null dates at end
                                bValue = 0;
                              } else if (!bDate) {
                                aValue = 0;
                                bValue = 1; // null dates at end
                              } else {
                                const year2026 = new Date(2026, 0, 1); // January 1, 2026
                                
                                const aYear = aDate.getFullYear();
                                const bYear = bDate.getFullYear();
                                const aIsBefore2026 = aYear < 2026;
                                const bIsBefore2026 = bYear < 2026;
                                
                                if (aIsBefore2026 && !bIsBefore2026) {
                                  // a is before 2026, b is 2026 or later - a should come first
                                  aValue = 0;
                                  bValue = 1;
                                } else if (!aIsBefore2026 && bIsBefore2026) {
                                  // a is 2026 or later, b is before 2026 - b should come first
                                  aValue = 1;
                                  bValue = 0;
                                } else {
                                  // Both in same group (both before 2026 or both 2026+) - sort chronologically
                                  aValue = aDate.getTime();
                                  bValue = bDate.getTime();
                                }
                              }
                            } else if (decomSortConfig.key === 'prev_target_date') {
                              // Prev Target Date sorting - get oldest prev target date from all revisions
                              const oldestPrevDateA = getOldestPrevTargetDate(a);
                              const oldestPrevDateB = getOldestPrevTargetDate(b);
                              aValue = oldestPrevDateA ? oldestPrevDateA.getTime() : 0;
                              bValue = oldestPrevDateB ? oldestPrevDateB.getTime() : 0;
                            } else if (decomSortConfig.key === 'decom_status') {
                              // Decom Status sorting - use the calculated status
                              aValue = calculateDecommissioningStatus(a);
                              bValue = calculateDecommissioningStatus(b);
                            } else if (decomSortConfig.key === 'mvc_group') {
                              // MVC Group sorting - simple string comparison, put empty at end
                              aValue = (a.mvc_group || '\uffff');
                              bValue = (b.mvc_group || '\uffff');
                            } else if (decomSortConfig.key === 'p1p2') {
                              // P1/P2 sorting - get incident counts
                              const countsA = getIncidentCounts(a.app_id);
                              const countsB = getIncidentCounts(b.app_id);
                              aValue = countsA.p1 + countsA.p2;
                              bValue = countsB.p1 + countsB.p2;
                            } else if (decomSortConfig.key === 'p3p4mi') {
                              // P3/P4(MI) sorting - get incident counts
                              const countsA = getIncidentCounts(a.app_id);
                              const countsB = getIncidentCounts(b.app_id);
                              aValue = countsA.p3p4mi;
                              bValue = countsB.p3p4mi;
                            } else if (decomSortConfig.key === 'total_cost') {
                              // Total Cost sorting - normalize to numeric and handle null/NaN (put at end)
                              const parseCost = (value: any) => {
                                if (value === null || value === undefined) return -Infinity;
                                if (typeof value === 'string') {
                                  const parsed = parseFloat(value);
                                  return isNaN(parsed) ? -Infinity : parsed;
                                }
                                if (typeof value === 'number') {
                                  return value;
                                }
                                return -Infinity;
                              };
                              aValue = parseCost(a.total_cost);
                              bValue = parseCost(b.total_cost);
                            } else if (decomSortConfig.key === 'bia_status') {
                              // BIA Status sorting: true(1) > false(0) > null(-1)
                              const biaRank = (v: boolean | null | undefined) => v === true ? 1 : v === false ? 0 : -1;
                              aValue = biaRank(a.bia_status);
                              bValue = biaRank(b.bia_status);
                            } else {
                              aValue = a[decomSortConfig.key] || '';
                              bValue = b[decomSortConfig.key] || '';
                            }
                            
                            if (decomSortConfig.direction === 'asc') {
                              return aValue > bValue ? 1 : -1;
                            } else {
                              return aValue < bValue ? 1 : -1;
                            }
                          });
                        }

                        // Apply pagination
                        const paginatedData = filteredData.slice(
                          decomPage * decomRowsPerPage,
                          decomPage * decomRowsPerPage + decomRowsPerPage
                        );

                        return paginatedData.map((item: DecommissioningItem, index: number) => {
                          // Calculate tracking status for display
                          const getTrackingStatus = () => {
                            const status = calculateDecommissioningStatus(item);
                            switch (status) {
                              case 'Decommissioned':
                                return {
                                  label: 'Decommissioned',
                                  bgcolor: '#2e7d32',
                                  color: '#fff'
                                };
                              case 'To be Decommissioned':
                                return {
                                  label: 'To be Decommissioned',
                                  bgcolor: '#1976d2',
                                  color: '#fff'
                                };
                              case 'Delayed - Overdue':
                                return {
                                  label: 'Delayed - Overdue',
                                  bgcolor: '#d32f2f',
                                  color: '#fff'
                                };
                              case 'Delayed - Date Extended':
                                return {
                                  label: 'Delayed - Date Extended',
                                  bgcolor: '#ffeb3b',
                                  color: '#000'
                                };
                              case 'Delayed - Repeatedly':
                                return {
                                  label: 'Delayed - Repeatedly',
                                  bgcolor: '#ed6c02',
                                  color: '#fff'
                                };
                              case 'Cancelled':
                                return {
                                  label: 'Cancelled',
                                  bgcolor: '#666',
                                  color: '#fff'
                                };
                              default:
                                return {
                                  label: status || 'Unknown',
                                  bgcolor: '#666',
                                  color: '#fff'
                                };
                            }
                          };
                          
                          const trackingStatus = getTrackingStatus();
                          
                          // Helper function to get status tooltip text
                          const getStatusTooltip = (status: string) => {
                            switch (status) {
                              case 'Decommissioned':
                                return 'Application is fully turned off';
                              case 'To be Decommissioned':
                                return 'Planned, on track, no delay yet';
                              case 'Delayed - Date Extended':
                                return 'First short delay agreed';
                              case 'Delayed - Repeatedly':
                                return 'Multiple delays over time';
                              case 'Delayed - Overdue':
                                return 'Missed target with no valid extension';
                              case 'Cancelled':
                                return 'No longer planned for decommission';
                              default:
                                return 'Unknown status';
                            }
                          };
                          
                          const statusTooltip = getStatusTooltip(trackingStatus.label);
                          
                          return (
                            <TableRow key={index} hover>
                              {decomColumnVisibility.portfolio && <TableCell>{item.portfolio || 'N/A'}</TableCell>}
                              {decomColumnVisibility.platform && <TableCell>{item.platform || 'N/A'}</TableCell>}
                              {decomColumnVisibility.app_id && (
                                <TableCell>
                                  {item.app_id ? (
                                    <Link
                                      href={`https://mac.maersk.io/application/${item.app_id}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      sx={{
                                        color: '#1976d2',
                                        textDecoration: 'none',
                                        '&:hover': {
                                          textDecoration: 'underline'
                                        }
                                      }}
                                    >
                                      {item.app_id}
                                    </Link>
                                  ) : (
                                    'N/A'
                                  )}
                                </TableCell>
                              )}
                              {decomColumnVisibility.app_name && <TableCell>{item.app_name || 'N/A'}</TableCell>}
                              {decomColumnVisibility.mvc_group && (
                                <TableCell>
                                  {item.mvc_group || 'N/A'}
                                </TableCell>
                              )}
                              {decomColumnVisibility.owners && (
                                <TableCell>
                                  {item.owner_names && item.owners ? (
                                    <MuiTooltip 
                                      title={item.owners}
                                      arrow
                                      placement="top"
                                    >
                                      <Box component="span" sx={{ cursor: 'help' }}>
                                        {item.owner_names}
                                      </Box>
                                    </MuiTooltip>
                                  ) : (
                                    item.owner_names || item.owners || 'N/A'
                                  )}
                                </TableCell>
                              )}
                              {decomColumnVisibility.target_date && (
                                <TableCell>
                                  {item.target_date_decom 
                                    ? new Date(item.target_date_decom).toLocaleDateString() 
                                    : 'N/A'}
                                </TableCell>
                              )}
                              {decomColumnVisibility.prev_target_date && (
                                <TableCell>
                                  {(() => {
                                    const oldestPrevDate = getOldestPrevTargetDate(item);
                                    return oldestPrevDate ? oldestPrevDate.toLocaleDateString() : 'N/A';
                                  })()}
                                </TableCell>
                              )}
                              {decomColumnVisibility.decom_date && (
                                <TableCell>
                                  {item.decom_date 
                                    ? new Date(item.decom_date).toLocaleDateString() 
                                    : 'N/A'}
                                </TableCell>
                              )}
                              {decomColumnVisibility.delayed_comments && <TableCell>{item.decom_extension_reason || 'N/A'}</TableCell>}
                              {decomColumnVisibility.strategic_intent && <TableCell>{item.strategic_intent?.intent || 'N/A'}</TableCell>}
                              {decomColumnVisibility.backup_tested && (
                                <TableCell>
                                  <Chip 
                                    label={item.backup_tested_last_12_months === 'Y' ? 'Yes' : item.backup_tested_last_12_months === 'N' ? 'No' : 'N/A'}
                                    size="small"
                                    sx={{
                                      bgcolor: item.backup_tested_last_12_months === 'Y' ? '#4caf50 !important' : item.backup_tested_last_12_months === 'N' ? '#f44336 !important' : '#9e9e9e !important',
                                      color: '#fff !important',
                                      fontWeight: 600,
                                      '& .MuiChip-label': {
                                        color: '#fff !important'
                                      }
                                    }}
                                  />
                                </TableCell>
                              )}
                              {decomColumnVisibility.failover_tested && (
                                <TableCell>
                                  {item.failover_tested_date_last_12_months 
                                    ? new Date(item.failover_tested_date_last_12_months).toLocaleDateString() 
                                    : 'N/A'}
                                </TableCell>
                              )}
                              {decomColumnVisibility.status && (
                                <TableCell>
                                  <Chip 
                                    label={item.status || 'N/A'}
                                    size="small"
                                    sx={{
                                      bgcolor: item.status === 'Active' ? '#4caf50 !important' : '#9e9e9e !important',
                                      color: '#fff !important',
                                      fontWeight: 600,
                                      '& .MuiChip-label': {
                                        color: '#fff !important'
                                      }
                                    }}
                                  />
                                </TableCell>
                              )}
                              {decomColumnVisibility.decom_status && (
                                <TableCell>
                                  {item.decom_change_description ? (
                                    <MuiTooltip 
                                      title={`${statusTooltip}${item.decom_change_description ? `\n\nChange Description: ${item.decom_change_description}` : ''}`}
                                      arrow
                                      placement="top"
                                    >
                                      <Chip 
                                        label={trackingStatus.label}
                                        size="small"
                                        sx={{
                                          bgcolor: `${trackingStatus.bgcolor} !important`,
                                          color: `${trackingStatus.color} !important`,
                                          fontWeight: 600,
                                          cursor: 'help',
                                          '& .MuiChip-label': {
                                            color: `${trackingStatus.color} !important`
                                          }
                                        }}
                                      />
                                    </MuiTooltip>
                                  ) : (
                                    <MuiTooltip 
                                      title={statusTooltip}
                                      arrow
                                      placement="top"
                                    >
                                      <Chip 
                                        label={trackingStatus.label}
                                        size="small"
                                        sx={{
                                          bgcolor: `${trackingStatus.bgcolor} !important`,
                                          color: `${trackingStatus.color} !important`,
                                          fontWeight: 600,
                                          cursor: 'help',
                                          '& .MuiChip-label': {
                                            color: `${trackingStatus.color} !important`
                                          }
                                        }}
                                      />
                                    </MuiTooltip>
                                  )}
                                </TableCell>
                              )}
                              {decomColumnVisibility.application_labels && (
                                <TableCell>
                                  {Array.isArray(item.application_labels) && item.application_labels.length > 0
                                    ? item.application_labels.map((label: any) => label.name).join(', ')
                                    : item.application_labels && typeof item.application_labels === 'string'
                                    ? item.application_labels
                                    : 'N/A'}
                                </TableCell>
                              )}
                              {decomColumnVisibility.tag && (
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                      {item.tag?.decomm_tag && (
                                        <MuiTooltip 
                                          title={item.tag.remarks || 'No remarks available'} 
                                          arrow
                                          placement="top"
                                        >
                                          <Chip
                                            label={item.tag.decomm_tag}
                                            size="small"
                                            sx={{
                                              bgcolor: '#1976d2 !important',
                                              color: '#fff !important',
                                              fontSize: '0.75rem',
                                              height: '20px',
                                              cursor: 'pointer',
                                              '& .MuiChip-label': {
                                                color: '#fff !important',
                                                px: 1
                                              }
                                            }}
                                          />
                                        </MuiTooltip>
                                      )}
                                    </Box>
                                    <Box>
                                      {item.tag ? (
                                        <IconButton
                                          size="small"
                                          sx={{ padding: '2px' }}
                                          title="Edit Tag"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditTag(item);
                                          }}
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                      ) : (
                                        <IconButton
                                          size="small"
                                          sx={{ padding: '2px' }}
                                          title="Add Tag"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleAddTag(item);
                                          }}
                                        >
                                          <AddIcon fontSize="small" />
                                        </IconButton>
                                      )}
                                    </Box>
                                  </Box>
                                </TableCell>
                              )}
                              {decomColumnVisibility.p1p2 && (
                                <TableCell>
                                  {(() => {
                                    const counts = getIncidentCounts(item.app_id);
                                    const p1p2Total = counts.p1 + counts.p2;
                                    return (
                                      <Chip
                                        label={p1p2Total}
                                        size="small"
                                        color={p1p2Total === 0 ? 'success' : 'error'}
                                        sx={{ fontSize: '11px' }}
                                      />
                                    );
                                  })()}
                                </TableCell>
                              )}
                              {decomColumnVisibility.p3p4mi && (
                                <TableCell>
                                  {(() => {
                                    const counts = getIncidentCounts(item.app_id);
                                    return (
                                      <Chip
                                        label={counts.p3p4mi}
                                        size="small"
                                        color={counts.p3p4mi === 0 ? 'success' : 'warning'}
                                        sx={{ fontSize: '11px' }}
                                      />
                                    );
                                  })()}
                                </TableCell>
                              )}
                              {decomColumnVisibility.total_cost && (
                                <TableCell>
                                  {(() => {
                                    if (item.total_cost !== null && item.total_cost !== undefined) {
                                      const costValue = typeof item.total_cost === 'string' ? parseFloat(item.total_cost) : item.total_cost;
                                      if (!isNaN(costValue)) {
                                        return `$${costValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                                      }
                                    }
                                    return 'Not Available';
                                  })()} 
                                </TableCell>
                              )}
                              {decomColumnVisibility.bia_status && (
                                <TableCell>
                                  {item.bia_status === true ? (
                                    <Chip label="BIA Valid" size="small" sx={{ fontSize: '11px', backgroundColor: '#d4edda !important', color: '#155724 !important', fontWeight: 500, border: '1px solid #c3e6cb' }} />
                                  ) : item.bia_status === false ? (
                                    <Chip label="BIA Expired" size="small" sx={{ fontSize: '11px', backgroundColor: '#f8d7da !important', color: '#721c24 !important', fontWeight: 500, border: '1px solid #f5c6cb' }} />
                                  ) : (
                                    <Chip label="BIA - To be assessed" size="small" sx={{ fontSize: '11px', backgroundColor: '#fff3cd !important', color: '#856404 !important', fontWeight: 500, border: '1px solid #ffeeba' }} />
                                  )}
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        });
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination */}
                <TablePagination
                  component="div"
                  count={(() => {
                    // Filter to only show records where target_date_decom is NOT null
                    let filteredData = decommissioningData.filter((item: DecommissioningItem) =>
                    item.target_date_decom !== null && item.target_date_decom !== undefined
                        );

                    // Filter out unwanted MAC statuses (only show Active and Inactive)
                    filteredData = filteredData.filter((item: DecommissioningItem) =>
                      item.status === 'Active' || item.status === 'Inactive'
                    );

                    // Start with all decommissioning data (matching the main table body logic)
                    //let filteredData = decommissioningData;
                    
                    // Apply portfolio filter
                    if (decomPortfolioFilter.length > 0) {
                      filteredData = filteredData.filter((item: DecommissioningItem) =>
                        decomPortfolioFilter.includes(item.portfolio)
                      );
                    }
                    
                    // Apply platform filter
                    if (decomPlatformFilter.length > 0) {
                      filteredData = filteredData.filter((item: DecommissioningItem) =>
                        decomPlatformFilter.includes(item.platform)
                      );
                    }
                    
                    // Apply strategic intent filter
                    if (decomStrategicIntentFilter.length > 0) {
                      filteredData = filteredData.filter((item: DecommissioningItem) =>
                        decomStrategicIntentFilter.includes(item.strategic_intent?.intent || 'N/A')
                      );
                    }
                    
                    // Apply status filter
                    if (decomStatusFilter.length > 0) {
                      filteredData = filteredData.filter((item: DecommissioningItem) =>
                        decomStatusFilter.includes(item.status || 'N/A')
                      );
                    }
                    
                    // Apply decom status filter
                    if (decomDecomStatusFilter.length > 0) {
                      filteredData = filteredData.filter((item: DecommissioningItem) => {
                        return decomDecomStatusFilter.includes(calculateDecommissioningStatus(item));
                      });
                    }
                    
                    // Apply owners filter
                    if (decomOwnersFilter) {
                      filteredData = filteredData.filter((item: DecommissioningItem) => {
                        const ownerNames = item.owner_names?.toLowerCase() || '';
                        const owners = item.owners?.toLowerCase() || '';
                        const searchTerm = decomOwnersFilter.toLowerCase();
                        return ownerNames.includes(searchTerm) || owners.includes(searchTerm);
                      });
                    }
                    
                    // Apply delayed comments filter
                    if (decomDelayedCommentsFilter) {
                      filteredData = filteredData.filter((item: DecommissioningItem) => {
                        const delayedComments = item.decom_extension_reason?.toLowerCase() || '';
                        const searchTerm = decomDelayedCommentsFilter.toLowerCase();
                        return delayedComments.includes(searchTerm);
                      });
                    }
                    
                    // Apply prev target date filter
                    if (decomPrevTargetDateFilter || (decomPrevTargetDateOperator === 'custom' && (decomPrevTargetDateCustomStart || decomPrevTargetDateCustomEnd))) {
                      filteredData = filteredData.filter((item: DecommissioningItem) => {
                        // Get prev target date from revisions (oldest date)
                        const prevTargetDate = getOldestPrevTargetDate(item);
                        
                        if (!prevTargetDate) return false;
                        
                        const itemDate = new Date(prevTargetDate);
                        itemDate.setHours(0, 0, 0, 0);
                        
                        if (decomPrevTargetDateOperator === 'custom') {
                          if (decomPrevTargetDateCustomStart && decomPrevTargetDateCustomEnd) {
                            const startDate = new Date(decomPrevTargetDateCustomStart);
                            const endDate = new Date(decomPrevTargetDateCustomEnd);
                            startDate.setHours(0, 0, 0, 0);
                            endDate.setHours(23, 59, 59, 999);
                            return itemDate.getTime() >= startDate.getTime() && itemDate.getTime() <= endDate.getTime();
                          } else if (decomPrevTargetDateCustomStart) {
                            const startDate = new Date(decomPrevTargetDateCustomStart);
                            startDate.setHours(0, 0, 0, 0);
                            return itemDate.getTime() >= startDate.getTime();
                          } else if (decomPrevTargetDateCustomEnd) {
                            const endDate = new Date(decomPrevTargetDateCustomEnd);
                            endDate.setHours(0, 0, 0, 0);
                            return itemDate.getTime() <= endDate.getTime();
                          }
                          return true;
                        } else {
                          // Single date operators
                          const filterDate = new Date(decomPrevTargetDateFilter);
                          filterDate.setHours(0, 0, 0, 0);
                          
                          if (decomPrevTargetDateOperator === 'equals') {
                            return itemDate.getTime() === filterDate.getTime();
                          } else if (decomPrevTargetDateOperator === 'after') {
                            return itemDate.getTime() > filterDate.getTime();
                          } else if (decomPrevTargetDateOperator === 'before') {
                            return itemDate.getTime() < filterDate.getTime();
                          }
                        }
                        return false;
                      });
                    }
                    
                    // Apply decom date filter
                    if (decomDecomDateFilter || (decomDecomDateOperator === 'custom' && (decomDecomDateCustomStart || decomDecomDateCustomEnd))) {
                      filteredData = filteredData.filter((item: DecommissioningItem) => {
                        if (!item.decom_date) return false;
                        const itemDate = new Date(item.decom_date);
                        itemDate.setHours(0, 0, 0, 0);
                        
                        if (decomDecomDateOperator === 'custom') {
                          // Custom range logic
                          if (decomDecomDateCustomStart && decomDecomDateCustomEnd) {
                            const startDate = new Date(decomDecomDateCustomStart);
                            startDate.setHours(0, 0, 0, 0);
                            const endDate = new Date(decomDecomDateCustomEnd);
                            endDate.setHours(0, 0, 0, 0);
                            return itemDate.getTime() >= startDate.getTime() && itemDate.getTime() <= endDate.getTime();
                          } else if (decomDecomDateCustomStart) {
                            const startDate = new Date(decomDecomDateCustomStart);
                            startDate.setHours(0, 0, 0, 0);
                            return itemDate.getTime() >= startDate.getTime();
                          } else if (decomDecomDateCustomEnd) {
                            const endDate = new Date(decomDecomDateCustomEnd);
                            endDate.setHours(0, 0, 0, 0);
                            return itemDate.getTime() <= endDate.getTime();
                          }
                          return true;
                        } else {
                          // Single date operators
                          const filterDate = new Date(decomDecomDateFilter);
                          filterDate.setHours(0, 0, 0, 0);
                          
                          if (decomDecomDateOperator === 'equals') {
                            return itemDate.getTime() === filterDate.getTime();
                          } else if (decomDecomDateOperator === 'after') {
                            return itemDate.getTime() > filterDate.getTime();
                          } else if (decomDecomDateOperator === 'before') {
                            return itemDate.getTime() < filterDate.getTime();
                          }
                        }
                        return false;
                      });
                    }
                    
                    // Apply failover tested date filter
                    if (decomFailoverTestedDateFilter || (decomFailoverTestedDateOperator === 'custom' && (decomFailoverTestedDateCustomStart || decomFailoverTestedDateCustomEnd))) {
                      filteredData = filteredData.filter((item: DecommissioningItem) => {
                        if (!item.failover_tested_date_last_12_months) return false;
                        const itemDate = new Date(item.failover_tested_date_last_12_months);
                        itemDate.setHours(0, 0, 0, 0);
                        
                        if (decomFailoverTestedDateOperator === 'custom') {
                          // Custom range logic
                          if (decomFailoverTestedDateCustomStart && decomFailoverTestedDateCustomEnd) {
                            const startDate = new Date(decomFailoverTestedDateCustomStart);
                            startDate.setHours(0, 0, 0, 0);
                            const endDate = new Date(decomFailoverTestedDateCustomEnd);
                            endDate.setHours(0, 0, 0, 0);
                            return itemDate.getTime() >= startDate.getTime() && itemDate.getTime() <= endDate.getTime();
                          } else if (decomFailoverTestedDateCustomStart) {
                            const startDate = new Date(decomFailoverTestedDateCustomStart);
                            startDate.setHours(0, 0, 0, 0);
                            return itemDate.getTime() >= startDate.getTime();
                          } else if (decomFailoverTestedDateCustomEnd) {
                            const endDate = new Date(decomFailoverTestedDateCustomEnd);
                            endDate.setHours(0, 0, 0, 0);
                            return itemDate.getTime() <= endDate.getTime();
                          }
                          return true;
                        } else {
                          // Single date operators
                          const filterDate = new Date(decomFailoverTestedDateFilter);
                          filterDate.setHours(0, 0, 0, 0);
                          
                          if (decomFailoverTestedDateOperator === 'equals') {
                            return itemDate.getTime() === filterDate.getTime();
                          } else if (decomFailoverTestedDateOperator === 'after') {
                            return itemDate.getTime() > filterDate.getTime();
                          } else if (decomFailoverTestedDateOperator === 'before') {
                            return itemDate.getTime() < filterDate.getTime();
                          }
                        }
                        return false;
                      });
                    }
                    
                    // Apply backup tested date filter
                    if (decomBackupTestedDateFilter || (decomBackupTestedDateOperator === 'custom' && (decomBackupTestedDateCustomStart || decomBackupTestedDateCustomEnd))) {
                      filteredData = filteredData.filter((item: DecommissioningItem) => {
                        if (!item.backup_tested_last_12_months) return false;
                        const itemDate = new Date(item.backup_tested_last_12_months);
                        itemDate.setHours(0, 0, 0, 0);
                        
                        if (decomBackupTestedDateOperator === 'custom') {
                          // Custom range logic
                          if (decomBackupTestedDateCustomStart && decomBackupTestedDateCustomEnd) {
                            const startDate = new Date(decomBackupTestedDateCustomStart);
                            startDate.setHours(0, 0, 0, 0);
                            const endDate = new Date(decomBackupTestedDateCustomEnd);
                            endDate.setHours(0, 0, 0, 0);
                            return itemDate.getTime() >= startDate.getTime() && itemDate.getTime() <= endDate.getTime();
                          } else if (decomBackupTestedDateCustomStart) {
                            const startDate = new Date(decomBackupTestedDateCustomStart);
                            startDate.setHours(0, 0, 0, 0);
                            return itemDate.getTime() >= startDate.getTime();
                          } else if (decomBackupTestedDateCustomEnd) {
                            const endDate = new Date(decomBackupTestedDateCustomEnd);
                            endDate.setHours(0, 0, 0, 0);
                            return itemDate.getTime() <= endDate.getTime();
                          }
                          return true;
                        } else {
                          // Single date operators
                          const filterDate = new Date(decomBackupTestedDateFilter);
                          filterDate.setHours(0, 0, 0, 0);
                          
                          if (decomBackupTestedDateOperator === 'equals') {
                            return itemDate.getTime() === filterDate.getTime();
                          } else if (decomBackupTestedDateOperator === 'after') {
                            return itemDate.getTime() > filterDate.getTime();
                          } else if (decomBackupTestedDateOperator === 'before') {
                            return itemDate.getTime() < filterDate.getTime();
                          }
                        }
                        return false;
                      });
                    }
                    
                    // Apply tracking status filter
                    if (decomTrackingFilter !== 'all') {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      
                      filteredData = filteredData.filter((item: DecommissioningItem) => {
                        const status = calculateDecommissioningStatus(item);
                        if (decomTrackingFilter === 'completed') {
                          return status === 'Decommissioned';
                        } else if (decomTrackingFilter === 'delayed-overdue') {
                          return status === 'Delayed - Overdue';
                        } else if (decomTrackingFilter === 'delayed-repeatedly') {
                          return status === 'Delayed - Repeatedly';
                        } else if (decomTrackingFilter === 'delayed-extended') {
                          return status === 'Delayed - Date Extended';
                        } else if (decomTrackingFilter === 'ontrack') {
                          return status === 'To be Decommissioned';
                        } else if (decomTrackingFilter === 'cancelled') {
                          return status === 'Cancelled';
                        }
                        return true;
                      });
                    }
                    
                    // Apply search filter
                    if (decomSearchQuery) {
                      const query = decomSearchQuery.toLowerCase();
                      filteredData = filteredData.filter((item: DecommissioningItem) =>
                        item.portfolio?.toLowerCase().includes(query) ||
                        item.platform?.toLowerCase().includes(query) ||
                        item.app_id?.toLowerCase().includes(query) ||
                        item.app_name?.toLowerCase().includes(query)
                      );
                    }
                    return filteredData.length;
                  })()}
                  page={decomPage}
                  onPageChange={(e, newPage) => setDecomPage(newPage)}
                  rowsPerPage={decomRowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setDecomRowsPerPage(parseInt(e.target.value, 10));
                    setDecomPage(0);
                  }}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                />
              </Box>
            ) : (
              <Alert severity="info" sx={{ mt: 2 }}>
                No decommissioning data available
              </Alert>
            ))}
          </Box>
        )}

        {/* Portfolio Filter Popover */}
        <Popover
          open={Boolean(decomPortfolioFilterAnchor)}
          anchorEl={decomPortfolioFilterAnchor}
          onClose={() => {
            setDecomPortfolioFilterAnchor(null);
            setDecomPortfolioSearchText('');
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              width: 280,
              maxHeight: 400,
              border: '1px solid #e0e0e0',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }
          }}
        >
          <Box sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#333' }}>
              Select Portfolio
            </Typography>
            
            <TextField
              fullWidth
              size="small"
              placeholder="Search"
              value={decomPortfolioSearchText}
              onChange={(e) => setDecomPortfolioSearchText(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ fontSize: 18, color: '#666', mr: 0.5 }} />,
                endAdornment: decomPortfolioSearchText && (
                  <IconButton
                    size="small"
                    onClick={() => setDecomPortfolioSearchText('')}
                    sx={{ padding: 0 }}
                  >
                    <Close sx={{ fontSize: 16 }} />
                  </IconButton>
                ),
              }}
              sx={{
                mb: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '4px',
                  bgcolor: '#fff',
                }
              }}
            />

            <Box sx={{ 
              maxHeight: 240, 
              overflowY: 'auto',
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
              bgcolor: '#fff',
              mb: 1
            }}>
              {(() => {
                const uniquePortfolios = Array.from(new Set(decommissioningData.map(item => item.portfolio))).sort();
                const filteredPortfolios = uniquePortfolios.filter(portfolio =>
                  portfolio.toLowerCase().includes(decomPortfolioSearchText.toLowerCase())
                );
                
                return filteredPortfolios.map((portfolio) => (
                  <Box
                    key={portfolio}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 1,
                      py: 0.5,
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: '#f5f5f5'
                      }
                    }}
                    onClick={() => {
                      setDecomPortfolioTempFilter(prev =>
                        prev.includes(portfolio)
                          ? prev.filter(p => p !== portfolio)
                          : [...prev, portfolio]
                      );
                    }}
                  >
                    <Checkbox
                      checked={decomPortfolioTempFilter.includes(portfolio)}
                      size="small"
                      sx={{ padding: '4px' }}
                    />
                    <Typography variant="body2">{portfolio}</Typography>
                  </Box>
                ));
              })()}
            </Box>

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                size="small"
                onClick={() => {
                  setDecomPortfolioTempFilter([]);
                }}
                sx={{
                  textTransform: 'none',
                  color: '#666',
                  border: '1px solid #e0e0e0',
                  bgcolor: '#fff',
                  '&:hover': {
                    bgcolor: '#f5f5f5'
                  }
                }}
              >
                Clear
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  setDecomPortfolioFilter(decomPortfolioTempFilter);
                  setDecomPortfolioFilterAnchor(null);
                  setDecomPortfolioSearchText('');
                  setDecomPage(0);
                }}
                sx={{
                  textTransform: 'none',
                  bgcolor: '#0a3055',
                  '&:hover': {
                    bgcolor: '#082742'
                  }
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Popover>

        {/* Platform Filter Popover */}
        <Popover
          open={Boolean(decomPlatformFilterAnchor)}
          anchorEl={decomPlatformFilterAnchor}
          onClose={() => {
            setDecomPlatformFilterAnchor(null);
            setDecomPlatformSearchText('');
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              width: 280,
              maxHeight: 400,
              border: '1px solid #e0e0e0',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }
          }}
        >
          <Box sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#333' }}>
              Select Platform
            </Typography>
            
            {/* Search Box */}
            <TextField
              fullWidth
              size="small"
              placeholder="Search"
              value={decomPlatformSearchText}
              onChange={(e) => setDecomPlatformSearchText(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ fontSize: 18, color: '#666', mr: 0.5 }} />,
                endAdornment: decomPlatformSearchText && (
                  <IconButton
                    size="small"
                    onClick={() => setDecomPlatformSearchText('')}
                    sx={{ padding: 0 }}
                  >
                    <Close sx={{ fontSize: 16 }} />
                  </IconButton>
                ),
              }}
              sx={{
                mb: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '4px',
                  bgcolor: '#fff',
                }
              }}
            />

            {/* Options List */}
            <Box sx={{ 
              maxHeight: 240, 
              overflowY: 'auto',
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
              bgcolor: '#fff',
              mb: 1
            }}>
              {(() => {
                const uniquePlatforms = Array.from(new Set(decommissioningData.map(item => item.platform))).sort();
                const filteredPlatforms = uniquePlatforms.filter(platform =>
                  platform.toLowerCase().includes(decomPlatformSearchText.toLowerCase())
                );
                
                return filteredPlatforms.map((platform) => (
                  <Box
                    key={platform}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 1,
                      py: 0.5,
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: '#f5f5f5'
                      }
                    }}
                    onClick={() => {
                      setDecomPlatformTempFilter(prev =>
                        prev.includes(platform)
                          ? prev.filter(p => p !== platform)
                          : [...prev, platform]
                      );
                    }}
                  >
                    <Checkbox
                      checked={decomPlatformTempFilter.includes(platform)}
                      size="small"
                      sx={{ padding: '4px' }}
                    />
                    <Typography variant="body2">{platform}</Typography>
                  </Box>
                ));
              })()}
            </Box>

            {/* Buttons */}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                size="small"
                onClick={() => {
                  setDecomPlatformTempFilter([]);
                }}
                sx={{
                  textTransform: 'none',
                  color: '#666',
                  border: '1px solid #e0e0e0',
                  bgcolor: '#fff',
                  '&:hover': {
                    bgcolor: '#f5f5f5'
                  }
                }}
              >
                Clear
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  setDecomPlatformFilter(decomPlatformTempFilter);
                  setDecomPlatformFilterAnchor(null);
                  setDecomPlatformSearchText('');
                  setDecomPage(0);
                }}
                sx={{
                  textTransform: 'none',
                  bgcolor: '#0a3055',
                  '&:hover': {
                    bgcolor: '#082742'
                  }
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Popover>

        {/* Strategic Intent Filter Popover */}
        <Popover
          open={Boolean(decomStrategicIntentFilterAnchor)}
          anchorEl={decomStrategicIntentFilterAnchor}
          onClose={() => {
            setDecomStrategicIntentFilterAnchor(null);
            setDecomStrategicIntentSearchText('');
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              width: 280,
              maxHeight: 400,
              border: '1px solid #e0e0e0',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }
          }}
        >
          <Box sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#333' }}>
              Select Strategic Intent
            </Typography>
            
            <TextField
              fullWidth
              size="small"
              placeholder="Search"
              value={decomStrategicIntentSearchText}
              onChange={(e) => setDecomStrategicIntentSearchText(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ fontSize: 18, color: '#666', mr: 0.5 }} />,
                endAdornment: decomStrategicIntentSearchText && (
                  <IconButton
                    size="small"
                    onClick={() => setDecomStrategicIntentSearchText('')}
                    sx={{ padding: 0 }}
                  >
                    <Close sx={{ fontSize: 16 }} />
                  </IconButton>
                ),
              }}
              sx={{
                mb: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '4px',
                  bgcolor: '#fff',
                }
              }}
            />

            <Box sx={{ 
              maxHeight: 240, 
              overflowY: 'auto',
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
              bgcolor: '#fff',
              mb: 1
            }}>
              {(() => {
                const uniqueIntents = Array.from(new Set(decommissioningData.map(item => item.strategic_intent?.intent || 'N/A'))).sort();
                const filteredIntents = uniqueIntents.filter(intent =>
                  intent.toLowerCase().includes(decomStrategicIntentSearchText.toLowerCase())
                );
                
                return filteredIntents.map((intent) => (
                  <Box
                    key={intent}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 1,
                      py: 0.5,
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: '#f5f5f5'
                      }
                    }}
                    onClick={() => {
                      setDecomStrategicIntentTempFilter(prev =>
                        prev.includes(intent)
                          ? prev.filter(p => p !== intent)
                          : [...prev, intent]
                      );
                    }}
                  >
                    <Checkbox
                      checked={decomStrategicIntentTempFilter.includes(intent)}
                      size="small"
                      sx={{ padding: '4px' }}
                    />
                    <Typography variant="body2">{intent}</Typography>
                  </Box>
                ));
              })()}
            </Box>

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                size="small"
                onClick={() => {
                  setDecomStrategicIntentTempFilter([]);
                }}
                sx={{
                  textTransform: 'none',
                  color: '#666',
                  border: '1px solid #e0e0e0',
                  bgcolor: '#fff',
                  '&:hover': {
                    bgcolor: '#f5f5f5'
                  }
                }}
              >
                Clear
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  setDecomStrategicIntentFilter(decomStrategicIntentTempFilter);
                  setDecomStrategicIntentFilterAnchor(null);
                  setDecomStrategicIntentSearchText('');
                  setDecomPage(0);
                }}
                sx={{
                  textTransform: 'none',
                  bgcolor: '#0a3055',
                  '&:hover': {
                    bgcolor: '#082742'
                  }
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Popover>

        {/* Application Labels Filter Popover */}
        <Popover
          open={Boolean(decomApplicationLabelsFilterAnchor)}
          anchorEl={decomApplicationLabelsFilterAnchor}
          onClose={() => {
            setDecomApplicationLabelsFilterAnchor(null);
            setDecomApplicationLabelsSearchText('');
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              width: 280,
              maxHeight: 400,
              border: '1px solid #e0e0e0',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }
          }}
        >
          <Box sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#333' }}>
              Select Application Labels
            </Typography>
            
            <TextField
              fullWidth
              size="small"
              placeholder="Search"
              value={decomApplicationLabelsSearchText}
              onChange={(e) => setDecomApplicationLabelsSearchText(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ fontSize: 18, color: '#666', mr: 0.5 }} />,
                endAdornment: decomApplicationLabelsSearchText && (
                  <IconButton
                    size="small"
                    onClick={() => setDecomApplicationLabelsSearchText('')}
                    sx={{ padding: 0 }}
                  >
                    <Close sx={{ fontSize: 16 }} />
                  </IconButton>
                ),
              }}
              sx={{
                mb: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '4px',
                  bgcolor: '#fff',
                }
              }}
            />

            <Box sx={{ 
              maxHeight: 240, 
              overflowY: 'auto',
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
              bgcolor: '#fff',
              mb: 1
            }}>
              {(() => {
                // Get all unique label names from all items
                const allLabels = new Set<string>();
                decommissioningData.forEach(item => {
                  if (Array.isArray(item.application_labels) && item.application_labels.length > 0) {
                    item.application_labels.forEach((label: any) => {
                      allLabels.add(label.name);
                    });
                  }
                });
                // Add N/A option if some items have no labels
                if (decommissioningData.some(item => !item.application_labels || item.application_labels.length === 0)) {
                  allLabels.add('N/A');
                }
                
                const uniqueLabels = Array.from(allLabels).sort();
                const filteredLabels = uniqueLabels.filter(label =>
                  label.toLowerCase().includes(decomApplicationLabelsSearchText.toLowerCase())
                );
                
                return filteredLabels.map((label) => (
                  <Box
                    key={label}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 1,
                      py: 0.5,
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: '#f5f5f5'
                      }
                    }}
                    onClick={() => {
                      setDecomApplicationLabelsTempFilter(prev =>
                        prev.includes(label)
                          ? prev.filter(p => p !== label)
                          : [...prev, label]
                      );
                    }}
                  >
                    <Checkbox
                      checked={decomApplicationLabelsTempFilter.includes(label)}
                      size="small"
                      sx={{ padding: '4px' }}
                    />
                    <Typography variant="body2">{label}</Typography>
                  </Box>
                ));
              })()}
            </Box>

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                size="small"
                onClick={() => {
                  setDecomApplicationLabelsTempFilter([]);
                }}
                sx={{
                  textTransform: 'none',
                  color: '#666',
                  border: '1px solid #e0e0e0',
                  bgcolor: '#fff',
                  '&:hover': {
                    bgcolor: '#f5f5f5'
                  }
                }}
              >
                Clear
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  setDecomApplicationLabelsFilter(decomApplicationLabelsTempFilter);
                  setDecomApplicationLabelsFilterAnchor(null);
                  setDecomApplicationLabelsSearchText('');
                  setDecomPage(0);
                }}
                sx={{
                  textTransform: 'none',
                  bgcolor: '#0a3055',
                  '&:hover': {
                    bgcolor: '#082742'
                  }
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Popover>

        {/* Status Filter Popover */}
        <Popover
          open={Boolean(decomStatusFilterAnchor)}
          anchorEl={decomStatusFilterAnchor}
          onClose={() => {
            setDecomStatusFilterAnchor(null);
            setDecomStatusSearchText('');
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              width: 280,
              maxHeight: 400,
              border: '1px solid #e0e0e0',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }
          }}
        >
          <Box sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#333' }}>
              Select Status
            </Typography>
            
            <TextField
              fullWidth
              size="small"
              placeholder="Search"
              value={decomStatusSearchText}
              onChange={(e) => setDecomStatusSearchText(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ fontSize: 18, color: '#666', mr: 0.5 }} />,
                endAdornment: decomStatusSearchText && (
                  <IconButton
                    size="small"
                    onClick={() => setDecomStatusSearchText('')}
                    sx={{ padding: 0 }}
                  >
                    <Close sx={{ fontSize: 16 }} />
                  </IconButton>
                ),
              }}
              sx={{
                mb: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '4px',
                  bgcolor: '#fff',
                }
              }}
            />

            <Box sx={{ 
              maxHeight: 240, 
              overflowY: 'auto',
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
              bgcolor: '#fff',
              mb: 1
            }}>
              {(() => {
                const uniqueStatuses = Array.from(new Set(decommissioningData.map(item => item.status || 'N/A'))).sort();
                const filteredStatuses = uniqueStatuses.filter(status =>
                  status.toLowerCase().includes(decomStatusSearchText.toLowerCase())
                );
                
                return filteredStatuses.map((status) => (
                  <Box
                    key={status}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 1,
                      py: 0.5,
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: '#f5f5f5'
                      }
                    }}
                    onClick={() => {
                      setDecomStatusTempFilter(prev =>
                        prev.includes(status)
                          ? prev.filter(p => p !== status)
                          : [...prev, status]
                      );
                    }}
                  >
                    <Checkbox
                      checked={decomStatusTempFilter.includes(status)}
                      size="small"
                      sx={{ padding: '4px' }}
                    />
                    <Typography variant="body2">{status}</Typography>
                  </Box>
                ));
              })()}
            </Box>

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                size="small"
                onClick={() => {
                  setDecomStatusTempFilter([]);
                }}
                sx={{
                  textTransform: 'none',
                  color: '#666',
                  border: '1px solid #e0e0e0',
                  bgcolor: '#fff',
                  '&:hover': {
                    bgcolor: '#f5f5f5'
                  }
                }}
              >
                Clear
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  setDecomStatusFilter(decomStatusTempFilter);
                  setDecomStatusFilterAnchor(null);
                  setDecomStatusSearchText('');
                  setDecomPage(0);
                }}
                sx={{
                  textTransform: 'none',
                  bgcolor: '#0a3055',
                  '&:hover': {
                    bgcolor: '#082742'
                  }
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Popover>

        {/* Decom Status Filter Popover */}
        <Popover
          open={Boolean(decomDecomStatusFilterAnchor)}
          anchorEl={decomDecomStatusFilterAnchor}
          onClose={() => {
            setDecomDecomStatusFilterAnchor(null);
            setDecomDecomStatusSearchText('');
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              width: 280,
              maxHeight: 400,
              border: '1px solid #e0e0e0',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }
          }}
        >
          <Box sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#333' }}>
              Select Decom Status
            </Typography>
            
            <TextField
              fullWidth
              size="small"
              placeholder="Search"
              value={decomDecomStatusSearchText}
              onChange={(e) => setDecomDecomStatusSearchText(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ fontSize: 18, color: '#666', mr: 0.5 }} />,
                endAdornment: decomDecomStatusSearchText && (
                  <IconButton
                    size="small"
                    onClick={() => setDecomDecomStatusSearchText('')}
                    sx={{ padding: 0 }}
                  >
                    <Close sx={{ fontSize: 16 }} />
                  </IconButton>
                ),
              }}
              sx={{
                mb: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '4px',
                  bgcolor: '#fff',
                }
              }}
            />

            <Box sx={{ 
              maxHeight: 240, 
              overflowY: 'auto',
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
              bgcolor: '#fff',
              mb: 1
            }}>
              {(() => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                
                // Generate unique decom statuses
                const statusOptions = ['Decommissioned', 'To be Decommissioned', 'Delayed - Overdue', 'Delayed - Date Extended', 'Delayed - Repeatedly', 'Cancelled'];
                const filteredStatuses = statusOptions.filter(status =>
                  status.toLowerCase().includes(decomDecomStatusSearchText.toLowerCase())
                );
                
                return filteredStatuses.map((status) => (
                  <Box
                    key={status}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 1,
                      py: 0.5,
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: '#f5f5f5'
                      }
                    }}
                    onClick={() => {
                      setDecomDecomStatusTempFilter(prev =>
                        prev.includes(status)
                          ? prev.filter(p => p !== status)
                          : [...prev, status]
                      );
                    }}
                  >
                    <Checkbox
                      checked={decomDecomStatusTempFilter.includes(status)}
                      size="small"
                      sx={{ padding: '4px' }}
                    />
                    <Typography variant="body2">{status}</Typography>
                  </Box>
                ));
              })()}
            </Box>

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                size="small"
                onClick={() => {
                  setDecomDecomStatusTempFilter([]);
                }}
                sx={{
                  textTransform: 'none',
                  color: '#666',
                  border: '1px solid #e0e0e0',
                  bgcolor: '#fff',
                  '&:hover': {
                    bgcolor: '#f5f5f5'
                  }
                }}
              >
                Clear
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  setDecomDecomStatusFilter(decomDecomStatusTempFilter);
                  setDecomDecomStatusFilterAnchor(null);
                  setDecomDecomStatusSearchText('');
                  setDecomPage(0);
                }}
                sx={{
                  textTransform: 'none',
                  bgcolor: '#0a3055',
                  '&:hover': {
                    bgcolor: '#082742'
                  }
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Popover>

        {/* Owners Filter Popover */}
        <Popover
          open={Boolean(decomOwnersFilterAnchor)}
          anchorEl={decomOwnersFilterAnchor}
          onClose={() => {
            setDecomOwnersFilterAnchor(null);
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              width: 280,
              maxHeight: 400,
              border: '1px solid #e0e0e0',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }
          }}
        >
          <Box sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#333' }}>
              Owners
            </Typography>
            
            {/* Text Input Field */}
            <TextField
              fullWidth
              size="small"
              placeholder=""
              value={decomOwnersTempFilter}
              onChange={(e) => setDecomOwnersTempFilter(e.target.value)}
              sx={{
                mb: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '4px',
                  bgcolor: '#fff',
                  border: '1px solid #1976d2',
                  '&:hover': {
                    borderColor: '#1976d2',
                  },
                  '&.Mui-focused': {
                    borderColor: '#1976d2',
                  }
                }
              }}
            />

            {/* Buttons */}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                size="small"
                onClick={() => {
                  setDecomOwnersTempFilter('');
                }}
                sx={{
                  textTransform: 'none',
                  color: '#666',
                  border: '1px solid #e0e0e0',
                  bgcolor: '#fff',
                  '&:hover': {
                    bgcolor: '#f5f5f5'
                  }
                }}
              >
                Clear
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  setDecomOwnersFilter(decomOwnersTempFilter);
                  setDecomOwnersFilterAnchor(null);
                  setDecomPage(0);
                }}
                sx={{
                  textTransform: 'none',
                  bgcolor: '#0a3055',
                  '&:hover': {
                    bgcolor: '#082742'
                  }
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Popover>

        {/* Delayed Comments Filter Popover */}
        <Popover
          open={Boolean(decomDelayedCommentsFilterAnchor)}
          anchorEl={decomDelayedCommentsFilterAnchor}
          onClose={() => {
            setDecomDelayedCommentsFilterAnchor(null);
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              width: 280,
              maxHeight: 400,
              border: '1px solid #e0e0e0',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }
          }}
        >
          <Box sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#333' }}>
              Delayed Comments from Mac
            </Typography>
            
            {/* Text Input Field */}
            <TextField
              fullWidth
              size="small"
              placeholder=""
              value={decomDelayedCommentsTempFilter}
              onChange={(e) => setDecomDelayedCommentsTempFilter(e.target.value)}
              sx={{
                mb: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '4px',
                  bgcolor: '#fff',
                  border: '1px solid #1976d2',
                  '&:hover': {
                    borderColor: '#1976d2',
                  },
                  '&.Mui-focused': {
                    borderColor: '#1976d2',
                  }
                }
              }}
            />

            {/* Buttons */}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                size="small"
                onClick={() => {
                  setDecomDelayedCommentsTempFilter('');
                }}
                sx={{
                  textTransform: 'none',
                  color: '#666',
                  border: '1px solid #e0e0e0',
                  bgcolor: '#fff',
                  '&:hover': {
                    bgcolor: '#f5f5f5'
                  }
                }}
              >
                Clear
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  setDecomDelayedCommentsFilter(decomDelayedCommentsTempFilter);
                  setDecomDelayedCommentsFilterAnchor(null);
                  setDecomPage(0);
                }}
                sx={{
                  textTransform: 'none',
                  bgcolor: '#0a3055',
                  '&:hover': {
                    bgcolor: '#082742'
                  }
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Popover>

        {/* Year Filter Popover */}
        <Popover
          open={Boolean(decomYearFilterAnchor)}
          anchorEl={decomYearFilterAnchor}
          onClose={() => {
            setDecomYearFilterAnchor(null);
            setAllYearsExpanded(false);
            setCustomRangeExpanded(false);
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              width: 220,
              border: '1px solid #e0e0e0',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              maxHeight: 450,
              overflow: 'hidden'
            }
          }}
        >
          <Box sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#333' }}>
              Filter by Year
            </Typography>

            {/* Show Back button when All Years or Custom Range is expanded */}
            {(allYearsExpanded || customRangeExpanded) && (
              <Button
                size="small"
                onClick={() => {
                  setAllYearsExpanded(false);
                  setCustomRangeExpanded(false);
                }}
                sx={{
                  textTransform: 'none',
                  color: '#666',
                  mb: 2,
                  justifyContent: 'flex-start',
                  fontSize: '0.9rem'
                }}
                startIcon={<KeyboardArrowUp sx={{ transform: 'rotate(-90deg)' }} />}
              >
                Back to Year Options
              </Button>
            )}

            {!allYearsExpanded && !customRangeExpanded ? (
              /* Main year options */
              <Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    variant={decomYearFilter === previousYear.toString() ? 'contained' : 'text'}
                    size="medium"
                    fullWidth
                    onClick={() => {
                      setDecomYearFilter(previousYear.toString());
                      setDecomPage(0);
                      setDecomYearFilterAnchor(null);
                    }}
                    sx={{
                      textTransform: 'none',
                      justifyContent: 'flex-start',
                      bgcolor: decomYearFilter === previousYear.toString() ? '#1976d2' : 'transparent',
                      color: decomYearFilter === previousYear.toString() ? '#fff' : '#333',
                      '&:hover': {
                        bgcolor: decomYearFilter === previousYear.toString() ? '#1565c0' : '#f5f5f5'
                      }
                    }}
                  >
                    Previous Year ({previousYear})
                  </Button>
                  <Button
                    variant={decomYearFilter === currentYear.toString() ? 'contained' : 'text'}
                    size="medium"
                    fullWidth
                    onClick={() => {
                      setDecomYearFilter(currentYear.toString());
                      setDecomPage(0);
                      setDecomYearFilterAnchor(null);
                    }}
                    sx={{
                      textTransform: 'none',
                      justifyContent: 'flex-start',
                      bgcolor: decomYearFilter === currentYear.toString() ? '#1976d2' : 'transparent',
                      color: decomYearFilter === currentYear.toString() ? '#fff' : '#333',
                      '&:hover': {
                        bgcolor: decomYearFilter === currentYear.toString() ? '#1565c0' : '#f5f5f5'
                      }
                    }}
                  >
                    Current Year ({currentYear})
                  </Button>
                  <Button
                    variant={decomYearFilter === nextYear.toString() ? 'contained' : 'text'}
                    size="medium"
                    fullWidth
                    onClick={() => {
                      setDecomYearFilter(nextYear.toString());
                      setDecomPage(0);
                      setDecomYearFilterAnchor(null);
                    }}
                    sx={{
                      textTransform: 'none',
                      justifyContent: 'flex-start',
                      bgcolor: decomYearFilter === nextYear.toString() ? '#1976d2' : 'transparent',
                      color: decomYearFilter === nextYear.toString() ? '#fff' : '#333',
                      '&:hover': {
                        bgcolor: decomYearFilter === nextYear.toString() ? '#1565c0' : '#f5f5f5'
                      }
                    }}
                  >
                    Next Year ({nextYear})
                  </Button>
                  <Button
                    variant={decomYearFilter === 'custom' ? 'contained' : 'text'}
                    size="medium"
                    fullWidth
                    onClick={() => {
                      setCustomRangeExpanded(true);
                    }}
                    sx={{
                      textTransform: 'none',
                      justifyContent: 'space-between',
                      bgcolor: decomYearFilter === 'custom' ? '#1976d2' : 'transparent',
                      color: decomYearFilter === 'custom' ? '#fff' : '#333',
                      border: decomYearFilter === 'custom' ? 'none' : '1px solid #e0e0e0',
                      '&:hover': {
                        bgcolor: decomYearFilter === 'custom' ? '#1565c0' : '#f5f5f5'
                      }
                    }}
                    endIcon={<KeyboardArrowUp sx={{ transform: 'rotate(90deg)' }} />}
                  >
                    Custom Range
                  </Button>
                  <Button
                    variant={decomYearFilter === 'all' ? 'contained' : 'text'}
                    size="medium"
                    fullWidth
                    onClick={() => {
                      setAllYearsExpanded(true);
                      if (decomYearFilter !== 'all') {
                        // Reset the selected years only when switching TO All Years mode from another filter
                        setSelectedYears([]);
                      }
                    }}
                    sx={{
                      textTransform: 'none',
                      justifyContent: 'space-between',
                      bgcolor: decomYearFilter === 'all' ? '#1976d2' : 'transparent',
                      color: decomYearFilter === 'all' ? '#fff' : '#333',
                      border: decomYearFilter === 'all' ? 'none' : '1px solid #e0e0e0',
                      '&:hover': {
                        bgcolor: decomYearFilter === 'all' ? '#1565c0' : '#f5f5f5'
                      }
                    }}
                    endIcon={<KeyboardArrowUp sx={{ transform: 'rotate(90deg)' }} />}
                  >
                    All Years
                  </Button>
                </Box>
              </Box>
            ) : customRangeExpanded ? (
              /* Custom Range expanded view */
              <Box>
                <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                  Select a custom date range:
                </Typography>
                
                <TextField
                  label="Start Date"
                  type="date"
                  size="small"
                  fullWidth
                  value={customRangeStart}
                  onChange={(e) => setCustomRangeStart(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ mb: 2 }}
                />
                
                <TextField
                  label="End Date"
                  type="date"
                  size="small"
                  fullWidth
                  value={customRangeEnd}
                  onChange={(e) => setCustomRangeEnd(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ mb: 2 }}
                />

                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setCustomRangeStart('');
                      setCustomRangeEnd('');
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    Clear
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => {
                      if (customRangeStart && customRangeEnd) {
                        setDecomYearFilter('custom');
                        setDecomPage(0);
                        setDecomYearFilterAnchor(null);
                        setCustomRangeExpanded(false);
                      }
                    }}
                    disabled={!customRangeStart || !customRangeEnd}
                    sx={{ textTransform: 'none' }}
                  >
                    Apply
                  </Button>
                </Box>
              </Box>
            ) : allYearsExpanded ? (
              /* All Years expanded view */
              <Box>
                <TextField
                  size="small"
                  placeholder="Search years..."
                  value={yearSearchText}
                  onChange={(e) => setYearSearchText(e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: <Search sx={{ color: '#666', mr: 1 }} />
                  }}
                />

                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setSelectedYears(getAvailableYears.map((year: number) => year.toString()))}
                    sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                  >
                    Select All
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setSelectedYears([])}
                    sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                  >
                    Clear All
                  </Button>
                </Box>

                <Box sx={{ maxHeight: 150, overflowY: 'auto', mb: 1 }}>
                  {getAvailableYears
                    .filter((year: number) => year.toString().includes(yearSearchText))
                    .map((year: number) => (
                      <FormControlLabel
                        key={year}
                        control={
                          <Checkbox
                            checked={selectedYears.includes(year.toString())}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedYears(prev => [...prev, year.toString()]);
                              } else {
                                setSelectedYears(prev => prev.filter(y => y !== year.toString()));
                              }
                            }}
                            size="small"
                          />
                        }
                        label={year.toString()}
                        sx={{ display: 'block', mb: 0.5 }}
                      />
                    ))}
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => {
                      setDecomYearFilter('all');
                      setDecomPage(0);
                      setDecomYearFilterAnchor(null);
                      setAllYearsExpanded(false);
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    Apply
                  </Button>
                </Box>
              </Box>
            ) : null}
          </Box>
        </Popover>

        {/* Prev Target Date Filter Popover */}
        <Popover
          open={Boolean(decomPrevTargetDateFilterAnchor)}
          anchorEl={decomPrevTargetDateFilterAnchor}
          onClose={() => {
            setDecomPrevTargetDateFilterAnchor(null);
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              width: 320,
              border: '1px solid #e0e0e0',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }
          }}
        >
          <Box sx={{ p: 2 }}>
            {/* Comparison Operator Dropdown */}
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <Select
                value={decomPrevTargetDateTempOperator}
                onChange={(e) => setDecomPrevTargetDateTempOperator(e.target.value as 'equals' | 'after' | 'before' | 'custom')}
                sx={{
                  borderRadius: '4px',
                  bgcolor: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1976d2',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1976d2',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1976d2',
                  }
                }}
              >
                <MenuItem value="equals">Equals</MenuItem>
                <MenuItem value="after">After</MenuItem>
                <MenuItem value="before">Before</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>

            {/* Conditional Date Input - Single Date or Range */}
            {decomPrevTargetDateTempOperator === 'custom' ? (
              <Box>
                {/* Start Date */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#666', fontWeight: 500 }}>
                    Start Date
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    placeholder="YYYY-MM-DD"
                    value={decomPrevTargetDateTempCustomStart}
                    onChange={(e) => setDecomPrevTargetDateTempCustomStart(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '4px',
                        bgcolor: '#fff',
                        '& fieldset': {
                          borderColor: '#1976d2',
                        },
                        '&:hover fieldset': {
                          borderColor: '#1976d2',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1976d2',
                        }
                      }
                    }}
                  />
                </Box>
                
                {/* End Date */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#666', fontWeight: 500 }}>
                    End Date
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    placeholder="YYYY-MM-DD"
                    value={decomPrevTargetDateTempCustomEnd}
                    onChange={(e) => setDecomPrevTargetDateTempCustomEnd(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '4px',
                        bgcolor: '#fff',
                        '& fieldset': {
                          borderColor: '#1976d2',
                        },
                        '&:hover fieldset': {
                          borderColor: '#1976d2',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1976d2',
                        }
                      }
                    }}
                  />
                </Box>
              </Box>
            ) : (
              <TextField
                fullWidth
                size="small"
                type="date"
                placeholder="YYYY-MM-DD"
                value={decomPrevTargetDateTempFilter}
                onChange={(e) => setDecomPrevTargetDateTempFilter(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '4px',
                    bgcolor: '#fff',
                    '& fieldset': {
                      borderColor: '#1976d2',
                    },
                    '&:hover fieldset': {
                      borderColor: '#1976d2',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1976d2',
                    }
                  }
                }}
              />
            )}

            {/* Buttons */}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                size="small"
                onClick={() => {
                  setDecomPrevTargetDateTempFilter('');
                  setDecomPrevTargetDateTempCustomStart('');
                  setDecomPrevTargetDateTempCustomEnd('');
                }}
                sx={{
                  textTransform: 'none',
                  color: '#666',
                  border: '1px solid #e0e0e0',
                  bgcolor: '#fff',
                  '&:hover': {
                    bgcolor: '#f5f5f5'
                  }
                }}
              >
                Clear
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  setDecomPrevTargetDateFilter(decomPrevTargetDateTempFilter);
                  setDecomPrevTargetDateOperator(decomPrevTargetDateTempOperator);
                  setDecomPrevTargetDateCustomStart(decomPrevTargetDateTempCustomStart);
                  setDecomPrevTargetDateCustomEnd(decomPrevTargetDateTempCustomEnd);
                  setDecomPrevTargetDateFilterAnchor(null);
                  setDecomPage(0);
                }}
                sx={{
                  textTransform: 'none',
                  bgcolor: '#0a3055',
                  '&:hover': {
                    bgcolor: '#082742'
                  }
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Popover>

        {/* Decom Date Filter Popover */}
        <Popover
          open={Boolean(decomDecomDateFilterAnchor)}
          anchorEl={decomDecomDateFilterAnchor}
          onClose={() => {
            setDecomDecomDateFilterAnchor(null);
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              width: 320,
              border: '1px solid #e0e0e0',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }
          }}
        >
          <Box sx={{ p: 2 }}>
            {/* Comparison Operator Dropdown */}
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <Select
                value={decomDecomDateTempOperator}
                onChange={(e) => setDecomDecomDateTempOperator(e.target.value as 'equals' | 'after' | 'before' | 'custom')}
                sx={{
                  borderRadius: '4px',
                  bgcolor: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1976d2',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1976d2',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1976d2',
                  }
                }}
              >
                <MenuItem value="equals">Equals</MenuItem>
                <MenuItem value="after">After</MenuItem>
                <MenuItem value="before">Before</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>

            {/* Conditional Date Input - Single Date or Range */}
            {decomDecomDateTempOperator === 'custom' ? (
              <Box>
                {/* Start Date */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#666', fontWeight: 500 }}>
                    Start Date
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    placeholder="YYYY-MM-DD"
                    value={decomDecomDateTempCustomStart}
                    onChange={(e) => setDecomDecomDateTempCustomStart(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '4px',
                        bgcolor: '#fff',
                        '& fieldset': {
                          borderColor: '#1976d2',
                        },
                        '&:hover fieldset': {
                          borderColor: '#1976d2',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1976d2',
                        }
                      }
                    }}
                  />
                </Box>
                
                {/* End Date */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#666', fontWeight: 500 }}>
                    End Date
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    placeholder="YYYY-MM-DD"
                    value={decomDecomDateTempCustomEnd}
                    onChange={(e) => setDecomDecomDateTempCustomEnd(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '4px',
                        bgcolor: '#fff',
                        '& fieldset': {
                          borderColor: '#1976d2',
                        },
                        '&:hover fieldset': {
                          borderColor: '#1976d2',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1976d2',
                        }
                      }
                    }}
                  />
                </Box>
              </Box>
            ) : (
              <TextField
                fullWidth
                size="small"
                type="date"
                placeholder="YYYY-MM-DD"
                value={decomDecomDateTempFilter}
                onChange={(e) => setDecomDecomDateTempFilter(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '4px',
                    bgcolor: '#fff',
                    '& fieldset': {
                      borderColor: '#1976d2',
                    },
                    '&:hover fieldset': {
                      borderColor: '#1976d2',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1976d2',
                    }
                  }
                }}
              />
            )}

            {/* Buttons */}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                size="small"
                onClick={() => {
                  setDecomDecomDateTempFilter('');
                  setDecomDecomDateTempCustomStart('');
                  setDecomDecomDateTempCustomEnd('');
                }}
                sx={{
                  textTransform: 'none',
                  color: '#666',
                  border: '1px solid #e0e0e0',
                  bgcolor: '#fff',
                  '&:hover': {
                    bgcolor: '#f5f5f5'
                  }
                }}
              >
                Clear
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  setDecomDecomDateFilter(decomDecomDateTempFilter);
                  setDecomDecomDateOperator(decomDecomDateTempOperator);
                  setDecomDecomDateCustomStart(decomDecomDateTempCustomStart);
                  setDecomDecomDateCustomEnd(decomDecomDateTempCustomEnd);
                  setDecomDecomDateFilterAnchor(null);
                  setDecomPage(0);
                }}
                sx={{
                  textTransform: 'none',
                  bgcolor: '#0a3055',
                  '&:hover': {
                    bgcolor: '#082742'
                  }
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Popover>

        {/* Failover Tested Date Filter Popover */}
        <Popover
          open={Boolean(decomFailoverTestedDateFilterAnchor)}
          anchorEl={decomFailoverTestedDateFilterAnchor}
          onClose={() => {
            setDecomFailoverTestedDateFilterAnchor(null);
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              width: 320,
              border: '1px solid #e0e0e0',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }
          }}
        >
          <Box sx={{ p: 2 }}>
            {/* Comparison Operator Dropdown */}
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <Select
                value={decomFailoverTestedDateTempOperator}
                onChange={(e) => setDecomFailoverTestedDateTempOperator(e.target.value as 'equals' | 'after' | 'before' | 'custom')}
                sx={{
                  borderRadius: '4px',
                  bgcolor: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1976d2',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1976d2',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1976d2',
                  }
                }}
              >
                <MenuItem value="equals">Equals</MenuItem>
                <MenuItem value="after">After</MenuItem>
                <MenuItem value="before">Before</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>

            {/* Conditional Date Input - Single Date or Range */}
            {decomFailoverTestedDateTempOperator === 'custom' ? (
              <Box>
                {/* Start Date */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#666', fontWeight: 500 }}>
                    Start Date
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    placeholder="YYYY-MM-DD"
                    value={decomFailoverTestedDateTempCustomStart}
                    onChange={(e) => setDecomFailoverTestedDateTempCustomStart(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '4px',
                        bgcolor: '#fff',
                        '& fieldset': {
                          borderColor: '#1976d2',
                        },
                        '&:hover fieldset': {
                          borderColor: '#1976d2',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1976d2',
                        }
                      }
                    }}
                  />
                </Box>
                
                {/* End Date */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#666', fontWeight: 500 }}>
                    End Date
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    placeholder="YYYY-MM-DD"
                    value={decomFailoverTestedDateTempCustomEnd}
                    onChange={(e) => setDecomFailoverTestedDateTempCustomEnd(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '4px',
                        bgcolor: '#fff',
                        '& fieldset': {
                          borderColor: '#1976d2',
                        },
                        '&:hover fieldset': {
                          borderColor: '#1976d2',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1976d2',
                        }
                      }
                    }}
                  />
                </Box>
              </Box>
            ) : (
              <TextField
                fullWidth
                size="small"
                type="date"
                placeholder="YYYY-MM-DD"
                value={decomFailoverTestedDateTempFilter}
                onChange={(e) => setDecomFailoverTestedDateTempFilter(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '4px',
                    bgcolor: '#fff',
                    '& fieldset': {
                      borderColor: '#1976d2',
                    },
                    '&:hover fieldset': {
                      borderColor: '#1976d2',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1976d2',
                    }
                  }
                }}
              />
            )}

            {/* Buttons */}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                size="small"
                onClick={() => {
                  setDecomFailoverTestedDateTempFilter('');
                  setDecomFailoverTestedDateTempCustomStart('');
                  setDecomFailoverTestedDateTempCustomEnd('');
                }}
                sx={{
                  textTransform: 'none',
                  color: '#666',
                  border: '1px solid #e0e0e0',
                  bgcolor: '#fff',
                  '&:hover': {
                    bgcolor: '#f5f5f5'
                  }
                }}
              >
                Clear
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  setDecomFailoverTestedDateFilter(decomFailoverTestedDateTempFilter);
                  setDecomFailoverTestedDateOperator(decomFailoverTestedDateTempOperator);
                  setDecomFailoverTestedDateCustomStart(decomFailoverTestedDateTempCustomStart);
                  setDecomFailoverTestedDateCustomEnd(decomFailoverTestedDateTempCustomEnd);
                  setDecomFailoverTestedDateFilterAnchor(null);
                  setDecomPage(0);
                }}
                sx={{
                  textTransform: 'none',
                  bgcolor: '#0a3055',
                  '&:hover': {
                    bgcolor: '#082742'
                  }
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Popover>

        {/* Backup Tested Date Filter Popover */}
        <Popover
          open={Boolean(decomBackupTestedDateFilterAnchor)}
          anchorEl={decomBackupTestedDateFilterAnchor}
          onClose={() => setDecomBackupTestedDateFilterAnchor(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              mt: 1,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              minWidth: '320px'
            }
          }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#333' }}>
              Filter Backup Tested Date
            </Typography>

            {/* Operator Dropdown */}
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <Select
                value={decomBackupTestedDateTempOperator}
                onChange={(e) => setDecomBackupTestedDateTempOperator(e.target.value as 'equals' | 'after' | 'before' | 'custom')}
                sx={{
                  borderRadius: '4px',
                  bgcolor: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1976d2',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1976d2',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1976d2',
                  }
                }}
              >
                <MenuItem value="equals">Equals</MenuItem>
                <MenuItem value="after">After</MenuItem>
                <MenuItem value="before">Before</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>

            {/* Conditional Date Input(s) */}
            {decomBackupTestedDateTempOperator === 'custom' ? (
              <Box>
                {/* Start Date */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#666', fontWeight: 500 }}>
                    Start Date
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    placeholder="YYYY-MM-DD"
                    value={decomBackupTestedDateTempCustomStart}
                    onChange={(e) => setDecomBackupTestedDateTempCustomStart(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '4px',
                        bgcolor: '#fff',
                        '& fieldset': {
                          borderColor: '#1976d2',
                        },
                        '&:hover fieldset': {
                          borderColor: '#1976d2',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1976d2',
                        }
                      }
                    }}
                  />
                </Box>
                
                {/* End Date */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#666', fontWeight: 500 }}>
                    End Date
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    placeholder="YYYY-MM-DD"
                    value={decomBackupTestedDateTempCustomEnd}
                    onChange={(e) => setDecomBackupTestedDateTempCustomEnd(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '4px',
                        bgcolor: '#fff',
                        '& fieldset': {
                          borderColor: '#1976d2',
                        },
                        '&:hover fieldset': {
                          borderColor: '#1976d2',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1976d2',
                        }
                      }
                    }}
                  />
                </Box>
              </Box>
            ) : (
              <TextField
                fullWidth
                size="small"
                type="date"
                placeholder="YYYY-MM-DD"
                value={decomBackupTestedDateTempFilter}
                onChange={(e) => setDecomBackupTestedDateTempFilter(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '4px',
                    bgcolor: '#fff',
                    '& fieldset': {
                      borderColor: '#1976d2',
                    },
                    '&:hover fieldset': {
                      borderColor: '#1976d2',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1976d2',
                    }
                  }
                }}
              />
            )}

            {/* Buttons */}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                size="small"
                onClick={() => {
                  setDecomBackupTestedDateTempFilter('');
                  setDecomBackupTestedDateTempCustomStart('');
                  setDecomBackupTestedDateTempCustomEnd('');
                }}
                sx={{
                  textTransform: 'none',
                  color: '#666',
                  border: '1px solid #e0e0e0',
                  bgcolor: '#fff',
                  '&:hover': {
                    bgcolor: '#f5f5f5'
                  }
                }}
              >
                Clear
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  setDecomBackupTestedDateFilter(decomBackupTestedDateTempFilter);
                  setDecomBackupTestedDateOperator(decomBackupTestedDateTempOperator);
                  setDecomBackupTestedDateCustomStart(decomBackupTestedDateTempCustomStart);
                  setDecomBackupTestedDateCustomEnd(decomBackupTestedDateTempCustomEnd);
                  setDecomBackupTestedDateFilterAnchor(null);
                  setDecomPage(0);
                }}
                sx={{
                  textTransform: 'none',
                  bgcolor: '#0a3055',
                  '&:hover': {
                    bgcolor: '#082742'
                  }
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Popover>

        {/* BIA Status Filter Popover */}
        <Popover
          open={Boolean(decomBiaStatusFilterAnchor)}
          anchorEl={decomBiaStatusFilterAnchor}
          onClose={() => setDecomBiaStatusFilterAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{
            sx: {
              width: 260,
              maxHeight: 400,
              border: '1px solid #e0e0e0',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }
          }}
        >
          <Box sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#333' }}>
              Filter BIA Status
            </Typography>
            <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '4px', bgcolor: '#fff', mb: 1 }}>
              {(['BIA Valid', 'BIA Expired', 'BIA - To be assessed'] as string[]).map((option) => (
                <Box
                  key={option}
                  sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5, cursor: 'pointer', '&:hover': { bgcolor: '#f5f5f5' } }}
                  onClick={() => {
                    setDecomBiaStatusTempFilter(prev =>
                      prev.includes(option) ? prev.filter(p => p !== option) : [...prev, option]
                    );
                  }}
                >
                  <Checkbox checked={decomBiaStatusTempFilter.includes(option)} size="small" sx={{ padding: '4px' }} />
                  <Typography variant="body2">{option}</Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                size="small"
                onClick={() => setDecomBiaStatusTempFilter([])}
                sx={{ textTransform: 'none', color: '#666', border: '1px solid #e0e0e0', bgcolor: '#fff', '&:hover': { bgcolor: '#f5f5f5' } }}
              >
                Clear
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  setDecomBiaStatusFilter(decomBiaStatusTempFilter);
                  setDecomBiaStatusFilterAnchor(null);
                  setDecomPage(0);
                }}
                sx={{ textTransform: 'none', bgcolor: '#0a3055', '&:hover': { bgcolor: '#082742' } }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Popover>

        {/* Column Selection Dialog for Decommissioning Tab */}
        <Dialog
          open={decomColumnDialogOpen}
          onClose={() => setDecomColumnDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Settings sx={{ color: '#666' }} />
            Column Settings
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
              Select which columns to display in the decommissioning status table:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={decomColumnVisibility.portfolio}
                    onChange={(e) => setDecomColumnVisibility(prev => ({ ...prev, portfolio: e.target.checked }))}
                  />
                }
                label="Portfolio"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={decomColumnVisibility.platform}
                    onChange={(e) => setDecomColumnVisibility(prev => ({ ...prev, platform: e.target.checked }))}
                  />
                }
                label="Platform"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={decomColumnVisibility.app_id}
                    onChange={(e) => setDecomColumnVisibility(prev => ({ ...prev, app_id: e.target.checked }))}
                  />
                }
                label="App ID"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={decomColumnVisibility.app_name}
                    onChange={(e) => setDecomColumnVisibility(prev => ({ ...prev, app_name: e.target.checked }))}
                  />
                }
                label="App Name"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={decomColumnVisibility.mvc_group}
                    onChange={(e) => setDecomColumnVisibility(prev => ({ ...prev, mvc_group: e.target.checked }))}
                  />
                }
                label="MVC Group"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={decomColumnVisibility.owners}
                    onChange={(e) => setDecomColumnVisibility(prev => ({ ...prev, owners: e.target.checked }))}
                  />
                }
                label="Owners"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={decomColumnVisibility.target_date}
                    onChange={(e) => setDecomColumnVisibility(prev => ({ ...prev, target_date: e.target.checked }))}
                  />
                }
                label="Target Date"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={decomColumnVisibility.prev_target_date}
                    onChange={(e) => setDecomColumnVisibility(prev => ({ ...prev, prev_target_date: e.target.checked }))}
                  />
                }
                label="Prev Target Date"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={decomColumnVisibility.decom_date}
                    onChange={(e) => setDecomColumnVisibility(prev => ({ ...prev, decom_date: e.target.checked }))}
                  />
                }
                label="Decom Date"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={decomColumnVisibility.delayed_comments}
                    onChange={(e) => setDecomColumnVisibility(prev => ({ ...prev, delayed_comments: e.target.checked }))}
                  />
                }
                label="Delayed Comments from Mac"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={decomColumnVisibility.strategic_intent}
                    onChange={(e) => setDecomColumnVisibility(prev => ({ ...prev, strategic_intent: e.target.checked }))}
                  />
                }
                label="Strategic Intent"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={decomColumnVisibility.backup_tested}
                    onChange={(e) => setDecomColumnVisibility(prev => ({ ...prev, backup_tested: e.target.checked }))}
                  />
                }
                label="Restore from Backup Tested (Last 12 Months)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={decomColumnVisibility.failover_tested}
                    onChange={(e) => setDecomColumnVisibility(prev => ({ ...prev, failover_tested: e.target.checked }))}
                  />
                }
                label="Failover Tested Date (Last 12 Months)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={decomColumnVisibility.status}
                    onChange={(e) => setDecomColumnVisibility(prev => ({ ...prev, status: e.target.checked }))}
                  />
                }
                label="Status"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={decomColumnVisibility.decom_status}
                    onChange={(e) => setDecomColumnVisibility(prev => ({ ...prev, decom_status: e.target.checked }))}
                  />
                }
                label="Decom Status"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={decomColumnVisibility.tag}
                    onChange={(e) => setDecomColumnVisibility(prev => ({ ...prev, tag: e.target.checked }))}
                  />
                }
                label="Tag"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={decomColumnVisibility.application_labels}
                    onChange={(e) => setDecomColumnVisibility(prev => ({ ...prev, application_labels: e.target.checked }))}
                  />
                }
                label="Application Labels"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={decomColumnVisibility.p1p2}
                    onChange={(e) => setDecomColumnVisibility(prev => ({ ...prev, p1p2: e.target.checked }))}
                  />
                }
                label="P1/P2"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={decomColumnVisibility.p3p4mi}
                    onChange={(e) => setDecomColumnVisibility(prev => ({ ...prev, p3p4mi: e.target.checked }))}
                  />
                }
                label="P3/P4(MI)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={decomColumnVisibility.total_cost}
                    onChange={(e) => setDecomColumnVisibility(prev => ({ ...prev, total_cost: e.target.checked }))}
                  />
                }
                label="Total Approx cost (Last 12 months)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={decomColumnVisibility.bia_status}
                    onChange={(e) => setDecomColumnVisibility(prev => ({ ...prev, bia_status: e.target.checked }))}
                  />
                }
                label="BIA Status"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDecomColumnDialogOpen(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Tab 1: Outage Analytics */}
        {healthSummaryTab === 1 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Outage Analytics
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px', display: 'block', mb: 2 }}>
              Data Source: <a 
                href="https://maersk.service-now.com" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ color: '#1976d2', textDecoration: 'none' }}
              >
                Major Incident P1/P2 (based on Outage)
              </a>
            </Typography>
            
            {/* View Mode Selector and Controls */}
            <Box sx={{ 
              mb: 2, 
              display: 'flex', 
              gap: 2, 
              alignItems: 'center',
              flexWrap: 'wrap',
              justifyContent: 'space-between'
            }}>
              {/* View Mode Chips */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Chip
                  label="Daily"
                  onClick={() => {
                    setOutageViewMode('daily');
                    setOutagePage(0);
                  }}
                  variant={outageViewMode === 'daily' ? 'filled' : 'outlined'}
                  sx={{
                    cursor: 'pointer',
                    fontWeight: 600,
                    bgcolor: outageViewMode === 'daily' ? '#1976d2 !important' : 'transparent',
                    color: outageViewMode === 'daily' ? '#fff !important' : '#666',
                    borderColor: outageViewMode === 'daily' ? '#1976d2' : '#ccc',
                    '&:hover': {
                      bgcolor: outageViewMode === 'daily' ? '#1565c0 !important' : '#f5f5f5'
                    }
                  }}
                />
                <Chip
                  label="Weekly"
                  onClick={() => {
                    setOutageViewMode('weekly');
                    setOutagePage(0);
                  }}
                  variant={outageViewMode === 'weekly' ? 'filled' : 'outlined'}
                  sx={{
                    cursor: 'pointer',
                    fontWeight: 600,
                    bgcolor: outageViewMode === 'weekly' ? '#1976d2 !important' : 'transparent',
                    color: outageViewMode === 'weekly' ? '#fff !important' : '#666',
                    borderColor: outageViewMode === 'weekly' ? '#1976d2' : '#ccc',
                    '&:hover': {
                      bgcolor: outageViewMode === 'weekly' ? '#1565c0 !important' : '#f5f5f5'
                    }
                  }}
                />
                <Chip
                  label="Monthly"
                  onClick={() => {
                    setOutageViewMode('monthly');
                    setOutagePage(0);
                  }}
                  variant={outageViewMode === 'monthly' ? 'filled' : 'outlined'}
                  sx={{
                    cursor: 'pointer',
                    fontWeight: 600,
                    bgcolor: outageViewMode === 'monthly' ? '#1976d2 !important' : 'transparent',
                    color: outageViewMode === 'monthly' ? '#fff !important' : '#666',
                    borderColor: outageViewMode === 'monthly' ? '#1976d2' : '#ccc',
                    '&:hover': {
                      bgcolor: outageViewMode === 'monthly' ? '#1565c0 !important' : '#f5f5f5'
                    }
                  }}
                />
                
                {/* Separator */}
                <Box sx={{ width: '1px', height: '24px', bgcolor: '#ddd', mx: 0.5 }} />
                
                {/* App Type Filter Toggles - Simple Switch Style */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      cursor: 'pointer',
                      '&:hover': { opacity: 0.8 }
                    }}
                    onClick={() => {
                      setShowMvcApps(!showMvcApps);
                      setOutagePage(0);
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 20,
                        borderRadius: '10px',
                        bgcolor: showMvcApps ? '#2e7d32' : '#ccc',
                        position: 'relative',
                        transition: 'all 0.3s',
                        mr: 1
                      }}
                    >
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          bgcolor: 'white',
                          position: 'absolute',
                          top: 2,
                          left: showMvcApps ? 22 : 2,
                          transition: 'all 0.3s',
                          boxShadow: 1
                        }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 500 }}>
                      MVC
                    </Typography>
                  </Box>
                  
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      cursor: 'pointer',
                      ml: 2,
                      '&:hover': { opacity: 0.8 }
                    }}
                    onClick={() => {
                      setShowOtherApps(!showOtherApps);
                      setOutagePage(0);
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 20,
                        borderRadius: '10px',
                        bgcolor: showOtherApps ? '#ed6c02' : '#ccc',
                        position: 'relative',
                        transition: 'all 0.3s',
                        mr: 1
                      }}
                    >
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          bgcolor: 'white',
                          position: 'absolute',
                          top: 2,
                          left: showOtherApps ? 22 : 2,
                          transition: 'all 0.3s',
                          boxShadow: 1
                        }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 500 }}>
                      Other Apps
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Search and Export on same line */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  placeholder="Search by CI or App..."
                  value={outageSearchQuery}
                  onChange={(e) => setOutageSearchQuery(e.target.value)}
                  sx={{ minWidth: '250px' }}
                  InputProps={{
                    startAdornment: (
                      <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                        <Search />
                      </Box>
                    ),
                  }}
                />
                <Button
                  variant="contained"
                  onClick={() => {
                    try {
                      // Process the data inline for export with date breakdown
                      const filteredIncidents = incidentData.filter(inc => {
                        const priorityMatch = inc.priority === '1' || inc.priority === '2';
                        const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || inc.portfolio_name === selectedPortfolio;
                        const platformMatch = !selectedPlatform || selectedPlatform === 'all' || inc.platform_name === selectedPlatform;
                        const hasOutage = mttrData.has(inc.number) && getMTTRMinutes(inc.number) > 0;
                        return priorityMatch && portfolioMatch && platformMatch && hasOutage;
                      });

                      // Group by CI and Date
                      const ciDateMap = new Map<string, Map<string, { duration: number; incidentCount: number; incidents: string[] }>>();
                      
                      filteredIncidents.forEach(incident => {
                        const ci = incident.cmdb_ci_display_value || 'Unknown CI';
                        const duration = getMTTRMinutes(incident.number);
                        
                        if (!incident.sys_created_on_value) return;
                        
                        const date = new Date(incident.sys_created_on_value);
                        let dateKey = '';
                        
                        if (outageViewMode === 'daily') {
                          dateKey = date.toISOString().split('T')[0];
                        } else if (outageViewMode === 'weekly') {
                          const weekStart = new Date(date);
                          weekStart.setDate(date.getDate() - date.getDay());
                          dateKey = weekStart.toISOString().split('T')[0];
                        } else {
                          dateKey = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
                        }
                        
                        if (!ciDateMap.has(ci)) {
                          ciDateMap.set(ci, new Map());
                        }
                        
                        const dateMap = ciDateMap.get(ci)!;
                        if (!dateMap.has(dateKey)) {
                          dateMap.set(dateKey, { duration: 0, incidentCount: 0, incidents: [] });
                        }
                        
                        const dateData = dateMap.get(dateKey)!;
                        dateData.duration += duration;
                        dateData.incidentCount += 1;
                        dateData.incidents.push(incident.number);
                      });

                      const exportData: any[] = [];
                      ciDateMap.forEach((dateMap, ci) => {
                        dateMap.forEach((data, dateKey) => {
                          let displayDate = dateKey;
                          
                          if (outageViewMode === 'weekly') {
                            const weekStart = new Date(dateKey);
                            const weekEnd = new Date(weekStart);
                            weekEnd.setDate(weekEnd.getDate() + 6);
                            displayDate = `Week of ${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
                          } else if (outageViewMode === 'monthly') {
                            const [year, month] = dateKey.split('-');
                            const monthDate = new Date(parseInt(year), parseInt(month) - 1);
                            displayDate = monthDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                          } else {
                            displayDate = new Date(dateKey).toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'short', 
                              day: 'numeric' 
                            });
                          }
                          
                          if (!outageSearchQuery || ci.toLowerCase().includes(outageSearchQuery.toLowerCase()) || displayDate.toLowerCase().includes(outageSearchQuery.toLowerCase())) {
                            exportData.push({
                              ci,
                              date: displayDate,
                              incidentCount: data.incidentCount,
                              duration: data.duration,
                              incidents: data.incidents.join('; ')
                            });
                          }
                        });
                      });

                      const csvHeaders = ['CI / App', outageViewMode === 'daily' ? 'Date' : outageViewMode === 'weekly' ? 'Week' : 'Month', 'Incidents', 'Downtime (minutes)', 'Incident Numbers'];
                      const csvRows = [csvHeaders.join(',')];
                      
                      exportData.forEach((row: any) => {
                        csvRows.push([
                          `"${row.ci}"`,
                          `"${row.date}"`,
                          row.incidentCount,
                          row.duration,
                          `"${row.incidents}"`
                        ].join(','));
                      });
                      
                      const csvContent = csvRows.join('\n');
                      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                      const link = document.createElement('a');
                      const url = URL.createObjectURL(blob);
                      link.setAttribute('href', url);
                      link.setAttribute('download', `outage-analytics-${outageViewMode}-${new Date().toISOString().split('T')[0]}.csv`);
                      link.style.visibility = 'hidden';
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    } catch (error) {
                      console.error('Error exporting outage analytics:', error);
                    }
                  }}
                  sx={{
                    textTransform: 'none',
                    bgcolor: '#1976d2',
                    '&:hover': { bgcolor: '#1565c0' },
                    whiteSpace: 'nowrap'
                  }}
                >
                  Export to CSV
                </Button>
              </Box>
            </Box>

            {/* Quick Stats */}
            {(() => {
              const dateRange = getDateRange(selectedTimeRange);
              const startDate = new Date(dateRange.start_time);
              const endDate = new Date(dateRange.end_time);
              
              const filteredIncidents = incidentData.filter(inc => {
                const priorityMatch = inc.priority === '1' || inc.priority === '2';
                const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || inc.portfolio_name === selectedPortfolio;
                const platformMatch = !selectedPlatform || selectedPlatform === 'all' || inc.platform_name === selectedPlatform;
                return priorityMatch && portfolioMatch && platformMatch;
              });
              
              // Calculate total apps based on current filter toggles
              let mvcFilteredApps: any[] = [];
              let otherFilteredApps: any[] = [];
              
              if (showMvcApps) {
                // MVC Apps count: all portfolioApps (all are MVC)
                mvcFilteredApps = portfolioApps.filter(app => {
                  const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || app.platformPortfolio === selectedPortfolio;
                  const platformMatch = !selectedPlatform || selectedPlatform === 'all' || app.platform === selectedPlatform;
                  return portfolioMatch && platformMatch;
                });
              }
              
              if (showOtherApps) {
                // Other Apps count: apps with incidents that are not in portfolioApps
                const mvcAppIds = new Set(portfolioApps.map(app => app.appId?.toLowerCase()));
                const mvcAppNames = new Set(portfolioApps.map(app => app.application?.toLowerCase()));
                
                const otherAppsSet = new Set<string>();
                filteredIncidents.forEach(inc => {
                  const appId = inc.cmdb_ci_u_application_id;
                  const appName = inc.cmdb_ci_display_value;
                  
                  // Skip if this is an MVC app (case-insensitive comparison)
                  if ((appId && mvcAppIds.has(appId.toLowerCase())) || (appName && mvcAppNames.has(appName.toLowerCase()))) {
                    return;
                  }
                  
                  // Use appId as primary identifier (lowercase), fallback to appName
                  const key = appId ? appId.toLowerCase() : (appName ? appName.toLowerCase() : 'Unknown');
                  otherAppsSet.add(key);
                });
                
                otherFilteredApps = Array.from(otherAppsSet).map(key => ({ key }));
              }
                            const allFilteredApps = [...mvcFilteredApps, ...otherFilteredApps];
              
              // Calculate impacted apps - apps that have incidents
              const impactedMvcAppIds = new Set<string>();
              const impactedOtherAppIds = new Set<string>();
              const mvcAppIds = new Set(portfolioApps.map(app => app.appId?.toLowerCase()));
              const mvcAppNames = new Set(portfolioApps.map(app => app.application?.toLowerCase()));
              
              filteredIncidents.forEach(inc => {
                const appId = inc.cmdb_ci_u_application_id;
                const appName = inc.cmdb_ci_display_value;
                
                if ((appId && mvcAppIds.has(appId.toLowerCase())) || (appName && mvcAppNames.has(appName.toLowerCase()))) {
                  // MVC app with incident
                  if (showMvcApps) {
                    const key = appId ? appId.toLowerCase() : (appName ? appName.toLowerCase() : 'Unknown');
                    impactedMvcAppIds.add(key);
                  }
                } else {
                  // Other app with incident
                  if (showOtherApps) {
                    const key = appId ? appId.toLowerCase() : (appName ? appName.toLowerCase() : 'Unknown');
                    impactedOtherAppIds.add(key);
                  }
                }
              });
              
              const totalImpacted = impactedMvcAppIds.size + impactedOtherAppIds.size;
              
              // Calculate total duration and incidents only for selected app types
              let relevantIncidents = filteredIncidents.filter(inc => {
                const appId = inc.cmdb_ci_u_application_id;
                const appName = inc.cmdb_ci_display_value;
                const isMvcApp = (appId && mvcAppIds.has(appId.toLowerCase())) || (appName && mvcAppNames.has(appName.toLowerCase()));
                
                if (showMvcApps && showOtherApps) {
                  // Show all incidents
                  return true;
                } else if (showMvcApps) {
                  // Show only MVC app incidents
                  return isMvcApp;
                } else if (showOtherApps) {
                  // Show only Other app incidents
                  return !isMvcApp;
                } else {
                  // Show none
                  return false;
                }
              });
              
              const totalDuration = relevantIncidents.reduce((sum, inc) => sum + getOutageDurationMinutes(inc), 0);
              const totalIncidents = relevantIncidents.length;
              
              // Build label for app count
              let appCountLabel = '';
              if (showMvcApps && showOtherApps) {
                appCountLabel = 'All Apps (Total / Impacted)';
              } else if (showMvcApps) {
                appCountLabel = 'MVC Apps (Total / Impacted)';
              } else if (showOtherApps) {
                appCountLabel = 'Other Apps (Total / Impacted)';
              } else {
                appCountLabel = 'Apps (Total / Impacted)';
              }
              
              return (
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                  <Paper sx={{ px: 2, py: 1, backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', flex: '1 1 auto', minWidth: '150px' }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {appCountLabel}
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color="#2563eb">
                      {allFilteredApps.length} / {totalImpacted}
                    </Typography>
                  </Paper>
                  <Paper sx={{ px: 2, py: 1, backgroundColor: '#fef2f2', border: '1px solid #fecaca', flex: '1 1 auto', minWidth: '150px' }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Impacted Outage Time
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color="#dc2626">
                      {formatOutageDuration(totalDuration)}
                    </Typography>
                  </Paper>
                  <Paper sx={{ px: 2, py: 1, backgroundColor: '#fffbeb', border: '1px solid #fed7aa', flex: '1 1 auto', minWidth: '150px' }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Impacted Incidents
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color="#d97706">
                      {totalIncidents}
                    </Typography>
                  </Paper>
                </Box>
              );
            })()}

            {/* Outage Analytics Calendar View */}
            {(() => {
              // Handler for sorting
              const handleOutageSort = (key: string) => {
                setOutageSortConfig(prevConfig => {
                  if (prevConfig?.key === key) {
                    return {
                      key,
                      direction: prevConfig.direction === 'asc' ? 'desc' : 'asc'
                    };
                  }
                  return { key, direction: 'desc' };
                });
              };

              // Calculate availability and outages for each app across time periods
              const processCalendarData = () => {
                const dateRange = getDateRange(selectedTimeRange);
                const startDate = new Date(dateRange.start_time);
                const endDate = new Date(dateRange.end_time);
                
                // Filter incidents
                const filteredIncidents = incidentData.filter(inc => {
                  const priorityMatch = inc.priority === '1' || inc.priority === '2';
                  const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || inc.portfolio_name === selectedPortfolio;
                  const platformMatch = !selectedPlatform || selectedPlatform === 'all' || inc.platform_name === selectedPlatform;
                  return priorityMatch && portfolioMatch && platformMatch;
                });

                let allApps: any[] = [];
                let mvcApps: any[] = [];
                let otherApps: any[] = [];
                
                // Get MVC apps if toggle is enabled
                if (showMvcApps) {
                  mvcApps = portfolioApps.filter(app => {
                    const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || app.platformPortfolio === selectedPortfolio;
                    const platformMatch = !selectedPlatform || selectedPlatform === 'all' || app.platform === selectedPlatform;
                    const searchMatch = !outageSearchQuery || 
                      app.application.toLowerCase().includes(outageSearchQuery.toLowerCase()) ||
                      app.appId.toLowerCase().includes(outageSearchQuery.toLowerCase()) ||
                      app.platformPortfolio.toLowerCase().includes(outageSearchQuery.toLowerCase()) ||
                      app.platform.toLowerCase().includes(outageSearchQuery.toLowerCase());
                    
                    return portfolioMatch && platformMatch && searchMatch;
                  });
                }
                
                // Get Other apps if toggle is enabled
                if (showOtherApps) {
                  // Get all MVC app IDs and names from portfolioApps
                  const mvcAppIds = new Set(portfolioApps.map(app => app.appId?.toLowerCase()));
                  const mvcAppNames = new Set(portfolioApps.map(app => app.application?.toLowerCase()));
                  
                  // Get unique apps from incidents that are not MVC
                  const otherAppsMap = new Map<string, any>();
                  
                  filteredIncidents.forEach(inc => {
                    const appId = inc.cmdb_ci_u_application_id;
                    const appName = inc.cmdb_ci_display_value;
                    const portfolio = inc.portfolio_name;
                    const platform = inc.platform_name;
                    
                    // Skip if this app is in portfolioApps (MVC) - case-insensitive comparison
                    if ((appId && mvcAppIds.has(appId.toLowerCase())) || (appName && mvcAppNames.has(appName.toLowerCase()))) {
                      return;
                    }
                    
                    // Use combination of appId and appName as key to ensure uniqueness
                    // If appId exists, use it as primary identifier, otherwise use appName
                    const key = appId ? appId.toLowerCase() : (appName ? appName.toLowerCase() : 'Unknown');
                    
                    if (!otherAppsMap.has(key)) {
                      // Create app structure from incident data (not in portfolioApps)
                      otherAppsMap.set(key, {
                        appId: appId || key,
                        application: appName || 'Unknown App',
                        platformPortfolio: portfolio || 'Unknown Portfolio',
                        platform: platform || 'Unknown Platform'
                      });
                    }
                  });
                  
                  // Convert to array and apply search filter
                  otherApps = Array.from(otherAppsMap.values()).filter(app => {
                    const searchMatch = !outageSearchQuery || 
                      app.application.toLowerCase().includes(outageSearchQuery.toLowerCase()) ||
                      app.appId.toLowerCase().includes(outageSearchQuery.toLowerCase()) ||
                      app.platformPortfolio.toLowerCase().includes(outageSearchQuery.toLowerCase()) ||
                      app.platform.toLowerCase().includes(outageSearchQuery.toLowerCase());
                    return searchMatch;
                  });
                }
                
                // Combine MVC and Other apps based on toggles
                allApps = [...mvcApps, ...otherApps];

                // Generate time periods based on view mode
                const timePeriods: Array<{ key: string; label: string; shortLabel: string; start: Date; end: Date }> = [];
                
                if (outageViewMode === 'daily') {
                  let currentDate = new Date(startDate);
                  while (currentDate <= endDate) {
                    const dateKey = currentDate.toISOString().split('T')[0];
                    const dayStart = new Date(currentDate);
                    dayStart.setHours(0, 0, 0, 0);
                    const dayEnd = new Date(currentDate);
                    dayEnd.setHours(23, 59, 59, 999);
                    
                    timePeriods.push({
                      key: dateKey,
                      label: currentDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
                      shortLabel: `${currentDate.getDate()}\n${currentDate.toLocaleDateString('en-US', { weekday: 'short' })}`,
                      start: dayStart,
                      end: dayEnd
                    });
                    currentDate.setDate(currentDate.getDate() + 1);
                  }
                } else if (outageViewMode === 'weekly') {
                  // Helper function to get ISO week number
                  const getISOWeek = (date: Date): number => {
                    const target = new Date(date.valueOf());
                    const dayNumber = (date.getDay() + 6) % 7;
                    target.setDate(target.getDate() - dayNumber + 3);
                    const firstThursday = target.valueOf();
                    target.setMonth(0, 1);
                    if (target.getDay() !== 4) {
                      target.setMonth(0, 1 + ((4 - target.getDay()) + 7) % 7);
                    }
                    return 1 + Math.ceil((firstThursday - target.valueOf()) / 604800000);
                  };
                  
                  let currentDate = new Date(startDate);
                  // Align to week start (Monday for ISO week)
                  const dayOfWeek = currentDate.getDay();
                  const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
                  currentDate.setDate(currentDate.getDate() + daysToMonday);
                  currentDate.setHours(0, 0, 0, 0);
                  
                  while (currentDate <= endDate) {
                    const weekEnd = new Date(currentDate);
                    weekEnd.setDate(weekEnd.getDate() + 6);
                    weekEnd.setHours(23, 59, 59, 999);
                    const dateKey = currentDate.toISOString().split('T')[0];
                    const isoWeek = getISOWeek(currentDate);
                    
                    timePeriods.push({
                      key: dateKey,
                      label: `Week ${isoWeek}\n${currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
                      shortLabel: `Week ${isoWeek}\n${currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
                      start: new Date(currentDate),
                      end: weekEnd
                    });
                    currentDate.setDate(currentDate.getDate() + 7);
                  }
                } else { // monthly
                  let currentDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
                  currentDate.setHours(0, 0, 0, 0);
                  const lastMonth = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
                  
                  while (currentDate <= lastMonth) {
                    const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
                    monthEnd.setHours(23, 59, 59, 999);
                    const dateKey = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1).toString().padStart(2, '0')}`;
                    
                    timePeriods.push({
                      key: dateKey,
                      label: currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
                      shortLabel: currentDate.toLocaleDateString('en-US', { month: 'short' }),
                      start: new Date(currentDate),
                      end: monthEnd
                    });
                    currentDate.setMonth(currentDate.getMonth() + 1);
                  }
                }

                // Calculate outages for each app and time period
                const calendarData = allApps.map(app => {
                  const appIncidents = filteredIncidents.filter(inc => {
                    // Match by App ID (if both exist)
                    if (app.appId && inc.cmdb_ci_u_application_id) {
                      if (app.appId.toLowerCase() === inc.cmdb_ci_u_application_id.toLowerCase()) {
                        return true;
                      }
                    }
                    // Match by Application Name (if both exist)
                    if (app.application && inc.cmdb_ci_display_value) {
                      if (app.application.toLowerCase() === inc.cmdb_ci_display_value.toLowerCase()) {
                        return true;
                      }
                    }
                    return false;
                  });
                  
                  const periodData = timePeriods.map(period => {
                    const periodIncidents = appIncidents.filter(inc => {
                      if (!inc.sys_created_on_value) return false;
                      const incDate = new Date(inc.sys_created_on_value);
                      return incDate >= period.start && incDate <= period.end;
                    });
                    
                    const totalDuration = periodIncidents.reduce((sum, inc) => 
                      sum + getOutageDurationMinutes(inc), 0
                    );
                    
                    // Calculate actual minutes in this specific period
                    const totalMinutesInPeriod = Math.round((period.end.getTime() - period.start.getTime()) / (1000 * 60));
                    const availability = totalMinutesInPeriod > 0 
                      ? ((totalMinutesInPeriod - totalDuration) / totalMinutesInPeriod) * 100 
                      : 100;
                    
                    return {
                      duration: totalDuration,
                      incidentCount: periodIncidents.length,
                      incidents: periodIncidents.map(inc => inc.number),
                      availability: Math.max(0, Math.min(100, availability))
                    };
                  });
                  
                  const totalDuration = periodData.reduce((sum, p) => sum + p.duration, 0);
                  const totalIncidents = periodData.reduce((sum, p) => sum + p.incidentCount, 0);
                  
                  // Calculate overall availability based on total selected date range
                  const totalMinutesInDateRange = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60));
                  const avgAvailability = totalMinutesInDateRange > 0
                    ? ((totalMinutesInDateRange - totalDuration) / totalMinutesInDateRange) * 100
                    : 100;
                  
                  return {
                    appId: app.appId,
                    appName: app.application,
                    portfolio: app.platformPortfolio,
                    platform: app.platform,
                    periods: periodData,
                    totalDuration,
                    totalIncidents,
                    avgAvailability
                  };
                });

                return { calendarData, timePeriods };
              };

              const { calendarData, timePeriods } = processCalendarData();

              // Sort calendar data based on outageSortConfig
              const sortedCalendarData = [...calendarData].sort((a, b) => {
                if (!outageSortConfig) return 0;
                
                let aValue: number = 0;
                let bValue: number = 0;
                
                if (outageSortConfig.key === 'avgAvailability') {
                  aValue = a.avgAvailability;
                  bValue = b.avgAvailability;
                } else if (outageSortConfig.key === 'totalDuration') {
                  aValue = a.totalDuration;
                  bValue = b.totalDuration;
                } else if (outageSortConfig.key === 'totalIncidents') {
                  aValue = a.totalIncidents;
                  bValue = b.totalIncidents;
                }
                
                if (outageSortConfig.direction === 'asc') {
                  return aValue - bValue;
                } else {
                  return bValue - aValue;
                }
              });

              // Pagination for calendar data
              const paginatedCalendarData = sortedCalendarData.slice(
                outagePage * outageRowsPerPage,
                outagePage * outageRowsPerPage + outageRowsPerPage
              );

              // Get availability color
              const getAvailabilityColor = (availability: number) => {
                if (availability >= 99.9) return '#22c55e'; // Excellent - green
                if (availability >= 99.5) return '#4ade80'; // Great - light green
                if (availability >= 99) return '#fbbf24'; // Good - yellow
                if (availability >= 95) return '#fb923c'; // Fair - orange
                return '#ef4444'; // Poor - red
              };

              return (
                <>
                  {/* Calendar Grid */}
                  {calendarData.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center', backgroundColor: '#f9fafb', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        No outage data available for the selected filters
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                      <Box sx={{ minWidth: 800 }}>
                        {/* Header Row */}
                        <Box sx={{ display: 'flex', borderBottom: '2px solid #e5e7eb', pb: 1, mb: 1 }}>
                          <Box sx={{ width: 200, flexShrink: 0, pr: 2 }}>
                            <Typography variant="caption" fontWeight="bold" color="text.secondary">
                              Application
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5, flex: 1 }}>
                            {timePeriods.map((period, idx) => (
                              <Box 
                                key={period.key} 
                                sx={{ 
                                  minWidth: outageViewMode === 'daily' ? 35 : outageViewMode === 'weekly' ? 100 : 80,
                                  textAlign: 'center' 
                                }}
                              >
                                <Typography variant="caption" sx={{ fontSize: '9px', whiteSpace: 'pre-line', lineHeight: 1.2 }}>
                                  {period.shortLabel}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                          <Box sx={{ width: 100, flexShrink: 0, textAlign: 'center', pl: 2 }}>
                            <Box 
                              sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: '#f3f4f6', borderRadius: 0.5 }
                              }}
                              onClick={() => handleOutageSort('avgAvailability')}
                            >
                              <Typography variant="caption" fontWeight="bold" color="text.secondary">
                                Avg
                              </Typography>
                              {outageSortConfig?.key === 'avgAvailability' && (
                                <Box component="span" sx={{ ml: 0.5, fontSize: '10px' }}>
                                  {outageSortConfig.direction === 'asc' ? '▲' : '▼'}
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </Box>

                        {/* Data Rows */}
                        {paginatedCalendarData.map((appData, appIdx) => (
                          <Box 
                            key={appData.appId} 
                            sx={{ 
                              display: 'flex',
                              mb: 0.5,
                              py: 0.5,
                              backgroundColor: appIdx % 2 === 0 ? 'white' : '#f9fafb',
                              borderRadius: 0.5,
                              '&:hover': { backgroundColor: '#f3f4f6' }
                            }}
                          >
                            <Box sx={{ width: 200, flexShrink: 0, pr: 2, display: 'flex', alignItems: 'center' }}>
                              <Box>
                                <Typography variant="body2" sx={{ fontSize: '11px', fontWeight: 600 }}>
                                  {appData.appName}
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '9px', color: 'text.secondary' }}>
                                  {appData.appId}
                                </Typography>
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.5, flex: 1 }}>
                              {appData.periods.map((period, periodIdx) => (
                                <MuiTooltip
                                  key={periodIdx}
                                  title={
                                    <Box>
                                      <Typography variant="caption" display="block">
                                        <strong>{timePeriods[periodIdx].label}</strong>
                                      </Typography>
                                      <Typography variant="caption" display="block">
                                        Availability: {period.availability.toFixed(2)}%
                                      </Typography>
                                      <Typography variant="caption" display="block">
                                        Outage: {formatOutageDuration(period.duration)}
                                      </Typography>
                                      <Typography variant="caption" display="block">
                                        Incidents: {period.incidentCount}
                                      </Typography>
                                      {period.incidents.length > 0 && (
                                        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                          {period.incidents.join(', ')}
                                        </Typography>
                                      )}
                                    </Box>
                                  }
                                  arrow
                                >
                                  <Box
                                    sx={{
                                      minWidth: outageViewMode === 'daily' ? 35 : outageViewMode === 'weekly' ? 100 : 80,
                                      height: 35,
                                      backgroundColor: getAvailabilityColor(period.availability),
                                      borderRadius: 0.5,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      cursor: 'pointer',
                                      transition: 'all 0.2s',
                                      '&:hover': {
                                        transform: 'scale(1.1)',
                                        boxShadow: 2,
                                        zIndex: 10
                                      }
                                    }}
                                  >
                                    {/* Show text only for non-daily views or when hovering */}
                                    {outageViewMode !== 'daily' && period.duration > 0 && (
                                      <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold', fontSize: '9px' }}>
                                        {formatOutageDuration(period.duration)}
                                      </Typography>
                                    )}
                                  </Box>
                                </MuiTooltip>
                              ))}
                            </Box>
                            <Box sx={{ width: 100, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pl: 2 }}>
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '12px' }}>
                                  {appData.avgAvailability.toFixed(2)}%
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '9px', color: 'text.secondary' }}>
                                  {formatOutageDuration(appData.totalDuration)}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        ))}
                        
                        {/* Total Availability Row - Calculate for ALL apps, not just paginated */}
                        {calendarData.length > 0 && (
                          <Box 
                            sx={{ 
                              display: 'flex',
                              mt: 2,
                              pt: 2,
                              borderTop: '1px solid #666',
                              backgroundColor: '#fafafa',
                              borderRadius: 0.5,
                              py: 1
                            }}
                          >
                            <Box sx={{ width: 200, flexShrink: 0, pr: 2, display: 'flex', alignItems: 'center' }}>
                              <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 600, color: '#333' }}>
                                Total Availability
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.5, flex: 1 }}>
                              {timePeriods.map((period, periodIdx) => {
                                // Calculate total availability for this period across ALL apps (not just paginated)
                                const totalAvail = calendarData.reduce((sum, app) => sum + app.periods[periodIdx].availability, 0);
                                const avgAvail = calendarData.length > 0 ? totalAvail / calendarData.length : 100;
                                const totalDuration = calendarData.reduce((sum, app) => sum + app.periods[periodIdx].duration, 0);
                                const totalIncidents = calendarData.reduce((sum, app) => sum + app.periods[periodIdx].incidentCount, 0);
                                
                                return (
                                  <MuiTooltip
                                    key={periodIdx}
                                    title={
                                      <Box>
                                        <Typography variant="caption" display="block">
                                          <strong>{period.label}</strong>
                                        </Typography>
                                        <Typography variant="caption" display="block">
                                          Avg Availability: {avgAvail.toFixed(2)}%
                                        </Typography>
                                        <Typography variant="caption" display="block">
                                          Total Outage: {formatOutageDuration(totalDuration)}
                                        </Typography>
                                        <Typography variant="caption" display="block">
                                          Total Incidents: {totalIncidents}
                                        </Typography>
                                      </Box>
                                    }
                                    arrow
                                  >
                                    <Box
                                      sx={{
                                        minWidth: outageViewMode === 'daily' ? 35 : outageViewMode === 'weekly' ? 100 : 80,
                                        height: 40,
                                        backgroundColor: getAvailabilityColor(avgAvail),
                                        borderRadius: 0.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        border: '1px solid #666',
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                          transform: 'scale(1.05)',
                                          boxShadow: 2,
                                          zIndex: 10
                                        }
                                      }}
                                    >
                                      <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold', fontSize: '10px' }}>
                                        {avgAvail.toFixed(1)}%
                                      </Typography>
                                    </Box>
                                  </MuiTooltip>
                                );
                              })}
                            </Box>
                            <Box sx={{ width: 100, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pl: 2 }}>
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="body2" fontWeight={600} sx={{ fontSize: '13px', color: '#333' }}>
                                  {(() => {
                                    const totalAvail = calendarData.reduce((sum, app) => sum + app.avgAvailability, 0);
                                    const avgAvail = calendarData.length > 0 ? totalAvail / calendarData.length : 100;
                                    return avgAvail.toFixed(2);
                                  })()}%
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '9px', color: 'text.secondary' }}>
                                  {formatOutageDuration(calendarData.reduce((sum, app) => sum + app.totalDuration, 0))}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* Pagination */}
                  {calendarData.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <TablePagination
                        component="div"
                        count={calendarData.length}
                        page={outagePage}
                        onPageChange={(event, newPage) => setOutagePage(newPage)}
                        rowsPerPage={outageRowsPerPage}
                        onRowsPerPageChange={(event) => {
                          setOutageRowsPerPage(parseInt(event.target.value, 10));
                          setOutagePage(0);
                        }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        sx={{
                          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                            fontSize: '13px'
                          }
                        }}
                      />
                    </Box>
                  )}

                  {/* Availability Legend */}
                  {calendarData.length > 0 && (
                    <Box sx={{ mt: 3, p: 2, backgroundColor: '#f9fafb', borderRadius: 1, border: '1px solid #e5e7eb' }}>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
                        Availability Legend:
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 20, height: 20, backgroundColor: '#22c55e', borderRadius: 0.5 }} />
                          <Typography variant="caption">≥99.9% (Excellent)</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 20, height: 20, backgroundColor: '#4ade80', borderRadius: 0.5 }} />
                          <Typography variant="caption">99.5-99.9% (Great)</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 20, height: 20, backgroundColor: '#fbbf24', borderRadius: 0.5 }} />
                          <Typography variant="caption">99-99.5% (Good)</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 20, height: 20, backgroundColor: '#fb923c', borderRadius: 0.5 }} />
                          <Typography variant="caption">95-99% (Fair)</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 20, height: 20, backgroundColor: '#ef4444', borderRadius: 0.5 }} />
                          <Typography variant="caption">&lt;95% (Poor)</Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}

                </>
              );
            })()}
          </Box>
        )}

        {/* Tab 3: Resilience Action Pending Items */}
        {healthSummaryTab === 3 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6">
                  Resilience Actions - Pending Items
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                  Data Source: <Link 
                    href="https://msa.maersk.io/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    sx={{ color: '#0067b3', textDecoration: 'underline', cursor: 'pointer' }}
                  >
                    MSA
                  </Link>
                </Typography>
              </Box>
              
              {/* View Toggle Button */}
              <Box sx={{ display: 'flex', gap: 1, bgcolor: '#f0f0f0', borderRadius: 1, p: 0.5 }}>
                <Button
                  size="small"
                  variant={resilienceActionsView === 'application' ? 'contained' : 'text'}
                  onClick={() => {
                    setResilienceActionsView('application');
                    setSelectedResiliencePlatform('');
                  }}
                  sx={{
                    bgcolor: resilienceActionsView === 'application' ? '#0067b3' : 'transparent',
                    color: resilienceActionsView === 'application' ? 'white' : '#666',
                    '&:hover': {
                      bgcolor: resilienceActionsView === 'application' ? '#005a9e' : 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  Application View
                </Button>
                <Button
                  size="small"
                  variant={resilienceActionsView === 'platform' ? 'contained' : 'text'}
                  onClick={() => setResilienceActionsView('platform')}
                  sx={{
                    bgcolor: resilienceActionsView === 'platform' ? '#0067b3' : 'transparent',
                    color: resilienceActionsView === 'platform' ? 'white' : '#666',
                    '&:hover': {
                      bgcolor: resilienceActionsView === 'platform' ? '#005a9e' : 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  Platform View
                </Button>
              </Box>
            </Box>

            {(!selectedPortfolio || selectedPortfolio === 'all') && (!selectedPlatform || selectedPlatform === 'all') ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                Please select at least a portfolio to view resilience action items
              </Alert>
            ) : resilienceLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : resilienceError ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                {resilienceError}
              </Alert>
            ) : resilienceData && resilienceData.length > 0 ? (
              <Box>
                {/* Application View - shows pending items by platform and app */}
                {resilienceActionsView === 'application' && (
                  <Box>
                    {/* Export Button for Application View */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<FileDownload />}
                        onClick={() => {
                          try {
                            const portfolioName = selectedPortfolio || 'All Portfolios';
                            const csvHeaders = ['Platform', 'Application', 'Pending Parameter', 'Action Count', 'Additional Info'];
                            
                            const csvData: any[] = [];
                            resilienceData.forEach((platform: any) => {
                              platform.applications.forEach((app: any) => {
                                const pendingItems = app.pending_items || [];
                                if (pendingItems.length > 0) {
                                  const actionCount = pendingItems.length;
                                  
                                  // Combine all pending items into a single string
                                  const combinedPendingItems = pendingItems.join('; ');
                                  
                                  // Combine all additional info
                                  const additionalInfoItems: string[] = [];
                                  pendingItems.forEach((item: string) => {
                                    if (item.includes('Failover Test Overdue') && app.failover_tested_date) {
                                      additionalInfoItems.push(`Last tested: ${app.failover_tested_date}`);
                                    } else if (item.includes('Restore Test Overdue') && app.restore_tested_date) {
                                      additionalInfoItems.push(`Last tested: ${app.restore_tested_date}`);
                                    }
                                  });
                                  const combinedAdditionalInfo = additionalInfoItems.join('; ');
                                  
                                  // Create single row per application
                                  csvData.push([
                                    platform.platform_name,
                                    app.application,
                                    combinedPendingItems,
                                    actionCount,
                                    combinedAdditionalInfo
                                  ]);
                                }
                              });
                            });

                            const csvContent = [
                              csvHeaders.join(','),
                              ...csvData.map((row: any[]) => 
                                row.map((field: any) => 
                                  typeof field === 'string' && (field.includes(',') || field.includes('"') || field.includes('\n'))
                                    ? `"${field.replace(/"/g, '""')}"` 
                                    : field
                                ).join(',')
                              )
                            ].join('\n');

                            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                            const link = document.createElement('a');
                            const url = URL.createObjectURL(blob);
                            link.setAttribute('href', url);
                            link.setAttribute('download', `resilience-actions-application-view-${portfolioName.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`);
                            link.style.visibility = 'hidden';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            URL.revokeObjectURL(url);
                          } catch (error) {
                            console.error('Error exporting application view to CSV:', error);
                          }
                        }}
                      >
                        Export to CSV
                      </Button>
                    </Box>

                    {resilienceData.map((platform: any) => (
                    <Box key={platform.platform_name} sx={{ mb: 4 }}>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {platform.platform_name}
                        <Chip 
                          label={`${platform.total_apps} apps`} 
                          size="small" 
                          color="primary"
                        />
                      </Typography>

                      <TableContainer component={Paper} sx={{ mb: 2 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                              <TableCell sx={{ fontWeight: 600 }}>App ID</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Application</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Pending Items</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Action Count</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {platform.applications && platform.applications.map((app: any, idx: number) => {
                              const pendingCount = app.pending_items?.length || 0;

                              // Only show apps with pending items
                              if (pendingCount === 0) return null;

                              return (
                                <TableRow key={idx} hover>
                                  <TableCell>
                                    <a
                                      href={`https://mac.maersk.io/application/${app.app_id}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ color: '#0067b3', textDecoration: 'underline' }}
                                    >
                                      {app.app_id}
                                    </a>
                                  </TableCell>
                                  <TableCell>{app.application}</TableCell>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                      {app.pending_items.map((item: string, itemIdx: number) => {
                                        // Check if this is a test overdue item and prepare tooltip
                                        let tooltipText = '';
                                        if (item.includes('Failover Test Overdue') && app.failover_tested_date) {
                                          tooltipText = `Last tested: ${app.failover_tested_date}`;
                                        } else if (item.includes('Restore Test Overdue') && app.restore_tested_date) {
                                          tooltipText = `Last tested: ${app.restore_tested_date}`;
                                        }

                                        // Color coding based on parameter type
                                        let chipColor = '#fef3c7'; // default yellow
                                        let chipTextColor = '#92400e';
                                        let borderColor = '#fbbf24';
                                        
                                        if (item.includes('Overdue')) {
                                          chipColor = '#fee2e2'; // red for overdue tests
                                          chipTextColor = '#991b1b';
                                          borderColor = '#ef4444';
                                        } else if (item.includes('Not Present') || item.includes('Not Available')) {
                                          chipColor = '#ffedd5'; // orange for missing features
                                          chipTextColor = '#9a3412';
                                          borderColor = '#fb923c';
                                        } else if (item.includes('Not Defined')) {
                                          chipColor = '#fef3c7'; // yellow for undefined parameters
                                          chipTextColor = '#92400e';
                                          borderColor = '#fbbf24';
                                        } else if (item.includes('Not Tested')) {
                                          chipColor = '#fce7f3'; // pink for not tested
                                          chipTextColor = '#831843';
                                          borderColor = '#ec4899';
                                        }

                                        return tooltipText ? (
                                          <MuiTooltip key={itemIdx} title={tooltipText} arrow>
                                            <Chip 
                                              label={item}
                                              size="small"
                                              sx={{ 
                                                fontSize: '11px', 
                                                height: '22px', 
                                                cursor: 'help',
                                                bgcolor: `${chipColor} !important`,
                                                color: `${chipTextColor} !important`,
                                                fontWeight: 500,
                                                border: `1px solid ${borderColor}`,
                                                '& .MuiChip-label': {
                                                  color: `${chipTextColor} !important`
                                                },
                                                '&:hover': {
                                                  bgcolor: `${chipColor} !important`,
                                                  opacity: 0.9
                                                }
                                              }}
                                            />
                                          </MuiTooltip>
                                        ) : (
                                          <Chip 
                                            key={itemIdx}
                                            label={item}
                                            size="small"
                                            sx={{ 
                                              fontSize: '11px', 
                                              height: '22px',
                                              bgcolor: `${chipColor} !important`,
                                              color: `${chipTextColor} !important`,
                                              fontWeight: 500,
                                              border: `1px solid ${borderColor}`,
                                              '& .MuiChip-label': {
                                                color: `${chipTextColor} !important`
                                              }
                                            }}
                                          />
                                        );
                                      })}
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={pendingCount}
                                      size="small"
                                      color="error"
                                      sx={{ fontWeight: 'bold' }}
                                    />
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  ))}
                  </Box>
                )}

                {/* Platform View - shows platform summary and detail table */}
                {resilienceActionsView === 'platform' && (
                  <Box sx={{ mt: 3 }}>
                    {!selectedResiliencePlatform ? (
                      <>
                        {/* Export Button */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<FileDownload />}
                            onClick={() => {
                              try {
                                const portfolioName = selectedPortfolio || 'All Portfolios';
                                const csvHeaders = ['Platform', 'App Count', 'Summary'];
                                
                                const csvData = resilienceData.map((platform: any) => {
                                  // Build summary text
                                  const parameterSummary: { [key: string]: string[] } = {};
                                  platform.applications.forEach((app: any) => {
                                    const pendingItems = app.pending_items || [];
                                    pendingItems.forEach((param: string) => {
                                      if (!parameterSummary[param]) {
                                        parameterSummary[param] = [];
                                      }
                                      if (!parameterSummary[param].includes(app.application)) {
                                        parameterSummary[param].push(app.application);
                                      }
                                    });
                                  });
                                  
                                  let summaryText = '';
                                  Object.entries(parameterSummary).forEach(([parameter, apps]) => {
                                    summaryText += `${parameter} (${apps.join('; ')}); `;
                                  });
                                  
                                  return [
                                    platform.platform_name,
                                    platform.total_apps,
                                    summaryText || 'All resilience parameters completed'
                                  ];
                                });

                                const csvContent = [
                                  csvHeaders.join(','),
                                  ...csvData.map((row: any[]) => 
                                    row.map((field: any) => 
                                      typeof field === 'string' && (field.includes(',') || field.includes('"') || field.includes('\n'))
                                        ? `"${field.replace(/"/g, '""')}"` 
                                        : field
                                    ).join(',')
                                  )
                                ].join('\n');

                                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                                const link = document.createElement('a');
                                const url = URL.createObjectURL(blob);
                                link.setAttribute('href', url);
                                link.setAttribute('download', `resilience-actions-platform-summary-${portfolioName.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`);
                                link.style.visibility = 'hidden';
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                URL.revokeObjectURL(url);
                              } catch (error) {
                                console.error('Error exporting to CSV:', error);
                              }
                            }}
                          >
                            Export to CSV
                          </Button>
                        </Box>
                        
                        {/* Platform Summary Table */}
                        <TableContainer component={Paper}>
                          <Table>
                            <TableHead>
                              <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                                <TableCell><strong>Platform</strong></TableCell>
                                <TableCell align="center"><strong>App Count</strong></TableCell>
                                <TableCell><strong>Summary</strong></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {resilienceData.map((platform: any) => {
                                // Build summary of pending items grouped by parameter
                                const parameterSummary: { [key: string]: string[] } = {};
                                
                                platform.applications.forEach((app: any) => {
                                  const pendingItems = app.pending_items || [];
                                  pendingItems.forEach((param: string) => {
                                    if (!parameterSummary[param]) {
                                      parameterSummary[param] = [];
                                    }
                                    if (!parameterSummary[param].includes(app.application)) {
                                      parameterSummary[param].push(app.application);
                                    }
                                  });
                                });

                                return (
                                  <TableRow 
                                    key={platform.platform_name}
                                    hover
                                    sx={{ 
                                      cursor: 'pointer',
                                      '&:hover': { bgcolor: '#f5f5f5' }
                                    }}
                                    onClick={() => setSelectedResiliencePlatform(platform.platform_name)}
                                  >
                                    <TableCell>
                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {platform.platform_name}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                      <Chip label={platform.total_apps} size="small" color="primary" />
                                    </TableCell>
                                    <TableCell>
                                      <Box sx={{ maxHeight: 150, overflowY: 'auto', fontSize: '12px' }}>
                                        {Object.keys(parameterSummary).length > 0 ? (
                                          <>
                                            {Object.entries(parameterSummary)
                                              .sort(([, a], [, b]) => b.length - a.length)
                                              .map(([parameter, apps]) => (
                                                <Box key={parameter} sx={{ mb: 0.5 }}>
                                                  <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                                                    • {parameter}
                                                  </Typography>
                                                  <Typography variant="caption" sx={{ color: '#666', display: 'block', ml: 1.5, fontSize: '10px' }}>
                                                    {apps.join(', ')}
                                                  </Typography>
                                                </Box>
                                              ))}
                                          </>
                                        ) : (
                                          <Typography variant="caption" sx={{ color: '#666', fontStyle: 'italic' }}>
                                            All resilience parameters completed ✓
                                          </Typography>
                                        )}
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </>
                    ) : (
                      // Platform Detail Table - showing applications with pending items
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Button
                              size="small"
                              startIcon={<ArrowBack />}
                              onClick={() => setSelectedResiliencePlatform('')}
                              variant="outlined"
                            >
                              Back to Platform Summary
                            </Button>
                            <Typography variant="h6">{selectedResiliencePlatform}</Typography>
                          </Box>
                        </Box>

                        <TableContainer component={Paper}>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                                <TableCell sx={{ fontWeight: 600 }}>Application</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Pending Items</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Action Count</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(() => {
                                const selectedPlatformData = resilienceData.find(
                                  (p: any) => p.platform_name === selectedResiliencePlatform
                                );

                                if (!selectedPlatformData) return null;

                                return selectedPlatformData.applications
                                  .filter((app: any) => (app.pending_items?.length || 0) > 0)
                                  .map((app: any, idx: number) => (
                                    <TableRow key={idx} hover>
                                      <TableCell>{app.application}</TableCell>
                                      <TableCell>
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                          {app.pending_items.map((item: string, itemIdx: number) => {
                                            // Check if this is a test overdue item and prepare tooltip
                                            let tooltipText = '';
                                            if (item.includes('Failover Test Overdue') && app.failover_tested_date) {
                                              tooltipText = `Last tested: ${app.failover_tested_date}`;
                                            } else if (item.includes('Restore Test Overdue') && app.restore_tested_date) {
                                              tooltipText = `Last tested: ${app.restore_tested_date}`;
                                            }

                                            // Color coding based on parameter type
                                            let chipColor = '#fef3c7'; // default yellow
                                            let chipTextColor = '#92400e';
                                            let borderColor = '#fbbf24';
                                            
                                            if (item.includes('Overdue')) {
                                              chipColor = '#fee2e2'; // red for overdue tests
                                              chipTextColor = '#991b1b';
                                              borderColor = '#ef4444';
                                            } else if (item.includes('Not Present') || item.includes('Not Available')) {
                                              chipColor = '#ffedd5'; // orange for missing features
                                              chipTextColor = '#9a3412';
                                              borderColor = '#fb923c';
                                            } else if (item.includes('Not Defined')) {
                                              chipColor = '#fef3c7'; // yellow for undefined parameters
                                              chipTextColor = '#92400e';
                                              borderColor = '#fbbf24';
                                            } else if (item.includes('Not Tested')) {
                                              chipColor = '#fce7f3'; // pink for not tested
                                              chipTextColor = '#831843';
                                              borderColor = '#ec4899';
                                            }

                                            return tooltipText ? (
                                              <MuiTooltip key={itemIdx} title={tooltipText} arrow>
                                                <Chip 
                                                  label={item}
                                                  size="small"
                                                  sx={{ 
                                                    fontSize: '11px', 
                                                    height: '22px', 
                                                    cursor: 'help',
                                                    bgcolor: `${chipColor} !important`,
                                                    color: `${chipTextColor} !important`,
                                                    fontWeight: 500,
                                                    border: `1px solid ${borderColor}`,
                                                    '& .MuiChip-label': {
                                                      color: `${chipTextColor} !important`
                                                    },
                                                    '&:hover': {
                                                      bgcolor: `${chipColor} !important`,
                                                      opacity: 0.9
                                                    }
                                                  }}
                                                />
                                              </MuiTooltip>
                                            ) : (
                                              <Chip 
                                                key={itemIdx}
                                                label={item}
                                                size="small"
                                                sx={{ 
                                                  fontSize: '11px', 
                                                  height: '22px',
                                                  bgcolor: `${chipColor} !important`,
                                                  color: `${chipTextColor} !important`,
                                                  fontWeight: 500,
                                                  border: `1px solid ${borderColor}`,
                                                  '& .MuiChip-label': {
                                                    color: `${chipTextColor} !important`
                                                  }
                                                }}
                                              />
                                            );
                                          })}
                                        </Box>
                                      </TableCell>
                                      <TableCell>
                                        <Chip 
                                          label={app.pending_items.length}
                                          size="small"
                                          color="error"
                                          sx={{ fontWeight: 'bold' }}
                                        />
                                      </TableCell>
                                    </TableRow>
                                  ));
                              })()}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            ) : (
              <Box sx={{ p: 4, textAlign: 'center', backgroundColor: '#f9fafb', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  No pending resilience actions found
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Tab 7 (now 6): Application Certification */}
        {healthSummaryTab === 6 && (
          <ApplicationCertification
            certificationData={certificationData}
            filteredCertificationData={filteredCertificationData}
            certificationLoading={certificationLoading}
            changeOnboardingLoading={changeOnboardingLoading}
            certificationError={certificationError}
            mvcStatusFilter={mvcStatusFilter}
            setMvcStatusFilter={setMvcStatusFilter}
            bcaStatusFilter={bcaStatusFilter}
            setBcaStatusFilter={setBcaStatusFilter}
            certificationSearchQuery={certificationSearchQuery}
            setCertificationSearchQuery={setCertificationSearchQuery}
            certificationView={certificationView}
            setCertificationView={setCertificationView}
            platformViewMvcFilter={platformViewMvcFilter}
            setPlatformViewMvcFilter={setPlatformViewMvcFilter}
            platformViewBcaFilter={platformViewBcaFilter}
            setPlatformViewBcaFilter={setPlatformViewBcaFilter}
            platformViewSearchQuery={platformViewSearchQuery}
            setPlatformViewSearchQuery={setPlatformViewSearchQuery}
            uniqueAppIdsCountForCertification={uniqueAppIdsCountForCertification}
            totalCertificationApplications={totalCertificationApplications}
            avgCertification={avgCertification}
            avgCompleteness={avgCompleteness}
            uniqueAppIdsCountForCertificationPlatformView={uniqueAppIdsCountForCertificationPlatformView}
            totalCertificationApplicationsPlatformView={totalCertificationApplicationsPlatformView}
            avgCertificationPlatformView={avgCertificationPlatformView}
            avgCompletenessPlatformView={avgCompletenessPlatformView}
            platformViewData={platformViewData}
            transformPortfolioName={transformPortfolioName}
            handleExportPlatformViewToCSV={handleExportPlatformViewToCSV}
          />
        )}

        {/* Tab 8 (now 7): Change Onboarding */}
        {healthSummaryTab === 7 && (
          <ChangeOnboarding
            changeOnboardingData={changeOnboardingData}
            filteredChangeOnboardingData={filteredChangeOnboardingData}
            changeOnboardingLoading={changeOnboardingLoading}
            changeOnboardingError={changeOnboardingError}
            changeOnboardingMvcFilter={changeOnboardingMvcFilter}
            setChangeOnboardingMvcFilter={setChangeOnboardingMvcFilter}
            changeOnboardingBcaFilter={changeOnboardingBcaFilter}
            setChangeOnboardingBcaFilter={setChangeOnboardingBcaFilter}
            changeOnboardingSearchQuery={changeOnboardingSearchQuery}
            setChangeOnboardingSearchQuery={setChangeOnboardingSearchQuery}
            changeOnboardingStatusFilter={changeOnboardingStatusFilter}
            setChangeOnboardingStatusFilter={setChangeOnboardingStatusFilter}
            changeOnboardingDeploymentChannelFilter={changeOnboardingDeploymentChannelFilter}
            setChangeOnboardingDeploymentChannelFilter={setChangeOnboardingDeploymentChannelFilter}
            changeOnboardingChangeProcessTypeFilter={changeOnboardingChangeProcessTypeFilter}
            setChangeOnboardingChangeProcessTypeFilter={setChangeOnboardingChangeProcessTypeFilter}
            uniqueAppIdsCount={uniqueAppIdsCount}
            onboardedAppsCount={onboardedAppsCount}
            redChannelAppsCount={redChannelAppsCount}
            transformPortfolioName={transformPortfolioName}
            formatChangeDate={formatChangeDate}
          />
        )}



      </Paper>

      {/* Detailed App View */}
      {selectedApp && (
        <Accordion sx={{ mt: 3 }} expanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="h6">Detailed Analysis for {selectedApp}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>SLO Performance</Typography>
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={healthTrends.slice(-7)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis domain={[99, 100]} />
                    <Tooltip />
                    <Area 
                      type="monotone" 
                      dataKey="availability" 
                      stroke="#1976d2" 
                      fill="#1976d2" 
                      fillOpacity={0.3}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>Recommendations</Typography>
                <Alert severity="info" sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    Consider implementing automated failover for improved availability
                  </Typography>
                </Alert>
                <Alert severity="warning">
                  <Typography variant="body2">
                    Monitor alert frequency and implement noise reduction strategies
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}

      {/* AI Insights Modal */}
      <Dialog
        open={aiInsightsOpen}
        onClose={() => setAiInsightsOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '70vh',
            maxHeight: '90vh',
            borderRadius: 2,
            boxShadow: '0 24px 38px 3px rgba(0,0,0,0.14), 0 9px 46px 8px rgba(0,0,0,0.12), 0 11px 15px -7px rgba(0,0,0,0.2)'
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)', 
          background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SmartToy sx={{ fontSize: '28px' }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0 }}>
                AI Portfolio Health Assessment
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.85rem' }}>
                {selectedPortfolio || 'All Portfolios'} • {selectedTimeRange}
              </Typography>
            </Box>
          </Box>
          <IconButton 
            onClick={() => setAiInsightsOpen(false)}
            sx={{ 
              color: 'white',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
            }}
          >
            <Typography sx={{ fontSize: '20px' }}>×</Typography>
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0 }}>
          {aiInsightsError ? (
            <Box sx={{ p: 3 }}>
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  {aiInsightsError}
                </Typography>
              </Alert>
            </Box>
          ) : aiInsightsData ? (
            <Box>
              {/* Portfolio Health Report - Enhanced Markdown Content */}
              <Paper 
                elevation={3}
                sx={{ 
                  backgroundColor: '#ffffff', 
                  borderRadius: 3, 
                  border: '1px solid var(--maersk-border)',
                  overflow: 'hidden',
                  position: 'relative'
                }}
              >
                {/* Content area */}
                <Box sx={{ p: 4, position: 'relative' }}>
                  {/* Floating scroll to top button */}
                  <Fab
                    size="small"
                    color="primary"
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      zIndex: 1,
                      backgroundColor: 'var(--maersk-blue)',
                      '&:hover': {
                        backgroundColor: 'var(--maersk-dark-blue)'
                      }
                    }}
                    onClick={() => {
                      const element = document.querySelector('.ai-insights-content');
                      element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                  >
                    <KeyboardArrowUp />
                  </Fab>


                  <Box 
                    className="ai-insights-content"
                    sx={{
                      backgroundColor: '#ffffff',
                      border: '1px solid #dee2e6',
                      borderRadius: 2,
                      p: 4,
                      fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
                      lineHeight: 1.6,
                      maxHeight: '800px',
                      overflowY: 'auto',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                    }}
                  >
                    <ReactMarkdown
                      components={{
                        h1: ({ children }: { children?: React.ReactNode }) => {
                          // Skip rendering the main title as it's already in the header
                          const childText = typeof children === 'string' ? children : 
                                          (Array.isArray(children) ? children.join('') : String(children));
                          
                          if (childText.toLowerCase().includes('portfolio health') || 
                              childText.toLowerCase().includes('ai portfolio') ||
                              childText.toLowerCase().includes('health assessment')) {
                            return null; // Don't render duplicate titles
                          }
                          
                          return (
                            <Box sx={{ mb: 4 }}>
                              <Typography 
                                variant="h4" 
                                sx={{
                                  fontWeight: 700,
                                  color: 'var(--maersk-dark-blue)',
                                  mb: 2,
                                  pb: 1.5,
                                  borderBottom: '3px solid var(--maersk-blue)',
                                  letterSpacing: '0.5px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  position: 'relative',
                                  '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    left: 0,
                                    bottom: -3,
                                    width: '60px',
                                    height: '3px',
                                    background: 'var(--maersk-blue)',
                                    borderRadius: '2px'
                                  }
                                }}
                              >
                                {children}
                              </Typography>
                            </Box>
                          );
                        },
                        h2: ({ children }: { children?: React.ReactNode }) => (
                          <Typography 
                            variant="h5" 
                            sx={{
                              fontWeight: 600,
                              color: '#000000',
                              mt: 4,
                              mb: 2,
                              fontSize: '1.8em',
                              pb: 1,
                              borderBottom: '2px solid #000000'
                            }}
                          >
                            {children}
                          </Typography>
                        ),
                        h3: ({ children }: { children?: React.ReactNode }) => (
                          <Typography 
                            variant="h6" 
                            sx={{
                              fontWeight: 600,
                              color: '#000000',
                              mt: 3,
                              mb: 2,
                              fontSize: '1.4em',
                              pb: 1,
                              borderBottom: '2px solid #000000',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}
                          >
                            {children}
                          </Typography>
                        ),
                        h4: ({ children }: { children?: React.ReactNode }) => (
                          <Typography 
                            variant="subtitle1" 
                            sx={{
                              fontWeight: 600,
                              color: '#000000',
                              mt: 3,
                              mb: 1.5,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}
                          >
                            • {children}
                          </Typography>
                        ),
                        p: ({ children }: { children?: React.ReactNode }) => (
                          <Typography 
                            variant="body1" 
                            sx={{
                              color: '#333',
                              mb: 2,
                              lineHeight: 1.6,
                              fontSize: '1rem',
                              textAlign: 'justify',
                              fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif'
                            }}
                          >
                            {children}
                          </Typography>
                        ),
                        ul: ({ children }: { children?: React.ReactNode }) => (
                          <Box 
                            component="ul" 
                            sx={{
                              pl: 2,
                              mb: 2,
                              listStyleType: 'disc',
                              listStylePosition: 'outside',
                              '& li': {
                                mb: 1,
                                color: '#333',
                                lineHeight: 1.6,
                                pl: 0.5,
                                '&::marker': {
                                  color: '#000000',
                                  fontSize: '1.1em'
                                }
                              },
                              '& ul': {
                                listStyleType: 'circle',
                                mt: 0.5,
                                mb: 0.5,
                                pl: 2
                              },
                              '& ul ul': {
                                listStyleType: 'square'
                              }
                            }}
                          >
                            {children}
                          </Box>
                        ),
                        ol: ({ children }: { children?: React.ReactNode }) => (
                          <Box 
                            component="ol" 
                            sx={{
                              pl: 2.5,
                              mb: 2,
                              listStyleType: 'decimal',
                              listStylePosition: 'outside',
                              '& li': {
                                mb: 1,
                                color: '#333',
                                lineHeight: 1.6,
                                pl: 0.5,
                                '&::marker': {
                                  color: '#000000',
                                  fontWeight: 'bold'
                                }
                              },
                              '& ol': {
                                listStyleType: 'lower-alpha',
                                mt: 0.5,
                                mb: 0.5,
                                pl: 2
                              },
                              '& ol ol': {
                                listStyleType: 'lower-roman'
                              }
                            }}
                          >
                            {children}
                          </Box>
                        ),
                        li: ({ children }: { children?: React.ReactNode }) => (
                          <Box
                            component="li" 
                            sx={{
                              color: '#333',
                              lineHeight: 1.6,
                              mb: 1,
                              fontSize: '1rem',
                              '& strong': {
                                color: '#000000',
                                fontWeight: 600
                              },
                              '& p': {
                                mb: 0.5,
                                display: 'inline'
                              }
                            }}
                          >
                            {children}
                          </Box>
                        ),
                        strong: ({ children }: { children?: React.ReactNode }) => (
                          <Box
                            component="strong"
                            sx={{
                              fontWeight: 600,
                              color: '#000000'
                            }}
                          >
                            {children}
                          </Box>
                        ),
                        em: ({ children }: { children?: React.ReactNode }) => (
                          <Box
                            component="em"
                            sx={{
                              fontStyle: 'italic',
                              color: '#f57c00',
                              fontWeight: 500,
                              backgroundColor: '#fff8e1',
                              px: 0.5,
                              py: 0.25,
                              borderRadius: 0.5
                            }}
                          >
                            {children}
                          </Box>
                        ),
                        code: ({ children }: { children?: React.ReactNode }) => (
                          <Box
                            component="code"
                            sx={{
                              backgroundColor: '#f5f5f5',
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              fontFamily: '"Fira Code", Monaco, "Lucida Console", monospace',
                              fontSize: '0.9em',
                              color: '#d32f2f',
                              border: '1px solid #e0e0e0',
                              fontWeight: 500
                            }}
                          >
                            {children}
                          </Box>
                        ),
                        blockquote: ({ children }: { children?: React.ReactNode }) => (
                          <Box
                            component="blockquote"
                            sx={{
                              borderLeft: '5px solid var(--maersk-blue)',
                              pl: 3,
                              pr: 2,
                              py: 2,
                              my: 3,
                              backgroundColor: '#f8fbff',
                              borderRadius: '0 8px 8px 0',
                              fontStyle: 'italic',
                              color: 'var(--maersk-blue)',
                              position: 'relative',
                              boxShadow: '0 2px 8px rgba(0,103,179,0.1)',
                              '&::before': {
                                content: '"💡"',
                                position: 'absolute',
                                top: 16,
                                left: -12,
                                backgroundColor: 'var(--maersk-blue)',
                                color: 'white',
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '12px'
                              }
                            }}
                          >
                            {children}
                          </Box>
                        ),
                        a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
                          <Typography
                            component="a"
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: 'var(--maersk-blue)',
                              textDecoration: 'none',
                              fontWeight: 600,
                              borderBottom: '2px solid transparent',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                color: 'var(--maersk-dark-blue)',
                                borderBottom: '2px solid var(--maersk-blue)'
                              }
                            }}
                          >
                            {children} 🔗
                          </Typography>
                        ),
                        hr: () => (
                          <Box
                            component="hr"
                            sx={{
                              my: 4,
                              border: 'none',
                              height: '2px',
                              background: '#000000',
                              borderRadius: 1
                            }}
                          />
                        ),
                        table: ({ children }: { children?: React.ReactNode }) => (
                          <Box sx={{ 
                            overflowX: 'auto', 
                            mb: 3,
                            border: '1px solid #e0e0e0',
                            borderRadius: 2,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                          }}>
                            <Box component="table" sx={{ 
                              width: '100%', 
                              borderCollapse: 'collapse',
                              backgroundColor: 'white'
                            }}>
                              {children}
                            </Box>
                          </Box>
                        ),
                        thead: ({ children }: { children?: React.ReactNode }) => (
                          <Box component="thead" sx={{ 
                            backgroundColor: 'var(--maersk-light-blue)',
                            '& th': {
                              color: 'var(--maersk-dark-blue)',
                              fontWeight: 700,
                              p: 2,
                              borderBottom: '2px solid var(--maersk-blue)'
                            }
                          }}>
                            {children}
                          </Box>
                        ),
                        tbody: ({ children }: { children?: React.ReactNode }) => (
                          <Box component="tbody" sx={{
                            '& tr:nth-of-type(even)': {
                              backgroundColor: '#fafafa'
                            },
                            '& td': {
                              p: 2,
                              borderBottom: '1px solid #e0e0e0'
                            }
                          }}>
                            {children}
                          </Box>
                        )
                      }}
                    >
                      {(() => {
                        // Clean up the markdown content
                        let cleanContent = aiInsightsData?.data.health_assessment.portfolio_health_report || '';
                        
                        // Remove markdown code block markers
                        cleanContent = cleanContent.replace(/^```markdown\s*\n?/i, '');
                        cleanContent = cleanContent.replace(/\n?```\s*$/i, '');
                        
                        // Convert \n to actual line breaks
                        cleanContent = cleanContent.replace(/\\n/g, '\n');
                        
                        // Ensure proper spacing around headers
                        cleanContent = cleanContent.replace(/\n(#{1,6})\s*/g, '\n\n$1 ');
                        
                        // Ensure proper spacing around lists
                        cleanContent = cleanContent.replace(/\n(-|\*|\+|\d+\.)\s*/g, '\n$1 ');
                        
                        // Clean up extra whitespace
                        cleanContent = cleanContent.replace(/\n{3,}/g, '\n\n');
                        cleanContent = cleanContent.trim();
                        
                        return cleanContent;
                      })()}
                    </ReactMarkdown>
                  </Box>
                </Box>
              </Paper>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 6 }}>
              <CircularProgress size={40} sx={{ mb: 2 }} />
              <Typography variant="body2" color="textSecondary">
                Generating AI insights for your portfolio...
              </Typography>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ 
          p: 3, 
          backgroundColor: '#f8f9fa',
          borderTop: '1px solid #e9ecef',
          display: 'flex',
          justifyContent: 'space-between'
        }}>
          <Typography variant="caption" sx={{ color: '#666', alignSelf: 'center' }}>
            Powered by Advanced AI Analytics
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            {aiInsightsData && (
              <Button
                variant="contained"
                startIcon={<PictureAsPdfIcon />}
                onClick={() => handleExportAIInsightsToPDF(aiInsightsData!)}
                sx={{
                  backgroundColor: '#1565c0',
                  color: 'white',
                  fontWeight: 500,
                  px: 3,
                  '&:hover': {
                    backgroundColor: '#0d47a1',
                  },
                }}
              >
                Export PDF
              </Button>
            )}
            <Button 
              onClick={() => setAiInsightsOpen(false)}
              variant="outlined"
              sx={{
                borderColor: '#1565c0',
                color: '#1565c0',
                px: 3,
                '&:hover': {
                  backgroundColor: 'rgba(21, 101, 192, 0.04)',
                }
              }}
            >
              Close
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* AI Insights Validation Dialog */}
      <Dialog
        open={showAIValidationDialog}
        onClose={() => setShowAIValidationDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogContent sx={{ p: 3, textAlign: 'center' }}>
          <Box sx={{ mb: 2 }}>
            <SmartToy sx={{ fontSize: 42, color: '#1565c0', mb: 1.5 }} />
          </Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: '#2c3e50', fontSize: '16px' }}>
            Portfolio Selection Required
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5, color: '#555', lineHeight: 1.6, fontSize: '13px' }}>
            Please select a specific portfolio to get AI Insights.
          </Typography>
          <Typography variant="caption" sx={{ color: '#888', fontSize: '12px' }}>
            AI Insights is not available for "All Portfolios" selection.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ 
          p: 2.5, 
          pt: 0,
          justifyContent: 'center'
        }}>
          <Button
            onClick={() => setShowAIValidationDialog(false)}
            variant="contained"
            sx={{
              minWidth: 100,
              backgroundColor: '#1565c0',
              color: 'white',
              textTransform: 'none',
              fontWeight: 500,
              px: 3,
              py: 0.75,
              borderRadius: 1,
              '&:hover': {
                backgroundColor: '#0d47a1',
              }
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* P1/P2 Incident Details Dialog */}
      <Dialog
        open={p1p2IncidentDialogOpen}
        onClose={() => setP1P2IncidentDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '70vh',
            maxHeight: '90vh',
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: '#1565c0',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ErrorIcon sx={{ fontSize: '24px' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              P1/P2 Incidents - {p1p2ViewMode === 'impacted' ? 'Impacted View' : 'Caused By View'}
            </Typography>
          </Box>
          <IconButton 
            onClick={() => setP1P2IncidentDialogOpen(false)}
            sx={{ 
              color: 'white',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
            }}
          >
            <Typography sx={{ fontSize: '20px' }}>×</Typography>
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0' }}>Incident Number</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0' }}>Impacted Application</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0' }}>App ID</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0' }}>Impacted Portfolio</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0' }}>Impacted Platform</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0' }}>Caused By Portfolio</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0' }}>Caused By Platform</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0' }}>Caused By App</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0' }}>Priority</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0' }}>Resolved Date</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0' }}>MTTD</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0' }}>MTTR</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0' }}>Captured by Alert</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0' }}>Caused by Change</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {incidentData
                  .filter(inc => {
                    const priorityMatch = inc.priority === '1' || inc.priority === '2';
                    const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || 
                      (p1p2ViewMode === 'impacted' 
                        ? inc.portfolio_name === selectedPortfolio
                        : inc.caused_by_portfolio === selectedPortfolio);
                    const platformMatch = !selectedPlatform || selectedPlatform === 'all' || 
                      (p1p2ViewMode === 'impacted'
                        ? inc.platform_name === selectedPlatform
                        : inc.caused_by_platform === selectedPlatform);
                    return priorityMatch && portfolioMatch && platformMatch;
                  })
                  .sort((a, b) => {
                    // Sort by resolved date (most recent first)
                    if (!a.resolved_at) return 1;
                    if (!b.resolved_at) return -1;
                    return new Date(b.resolved_at).getTime() - new Date(a.resolved_at).getTime();
                  })
                  .slice(p1p2DialogPage * p1p2DialogRowsPerPage, p1p2DialogPage * p1p2DialogRowsPerPage + p1p2DialogRowsPerPage)
                  .map((inc, index) => (
                    <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                      <TableCell>
                        <Link
                          href={`https://maersk.service-now.com/nav_to.do?uri=incident_list.do?sysparm_query=number=${inc.number}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ 
                            color: '#1565c0', 
                            textDecoration: 'none',
                            fontWeight: 600,
                            '&:hover': {
                              textDecoration: 'underline'
                            }
                          }}
                        >
                          {inc.number}
                        </Link>
                      </TableCell>
                      <TableCell>{inc.cmdb_ci_display_value || 'N/A'}</TableCell>
                      <TableCell>{inc.cmdb_ci_u_application_id || 'N/A'}</TableCell>
                      <TableCell>{inc.portfolio_name || 'N/A'}</TableCell>
                      <TableCell>{inc.platform_name || 'N/A'}</TableCell>
                      <TableCell>{inc.caused_by_portfolio || 'N/A'}</TableCell>
                      <TableCell>{inc.caused_by_platform || 'N/A'}</TableCell>
                      <TableCell>{inc.caused_by_app || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip 
                          label={`P${inc.priority}`} 
                          color="error" 
                          size="small" 
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        {inc.resolved_at ? new Date(inc.resolved_at).toLocaleDateString() : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {formatOutageDuration(getMTTDMinutes(inc.number))}
                      </TableCell>
                      <TableCell>
                        {formatOutageDuration(getMTTRMinutes(inc.number))}
                      </TableCell>
                      <TableCell>
                        {inc.contact_type_display_value === 'Alert' ? (
                          <Chip 
                            label="Yes" 
                            size="small" 
                            sx={{ 
                              fontWeight: 600, 
                              backgroundColor: '#4caf50 !important', 
                              color: 'white !important',
                              border: 'none'
                            }} 
                          />
                        ) : (
                          <Chip 
                            label="No" 
                            size="small" 
                            sx={{ 
                              fontWeight: 600, 
                              backgroundColor: '#ff9800 !important', 
                              color: 'white !important',
                              border: 'none'
                            }} 
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {inc.caused_by_display_value && inc.caused_by_display_value.startsWith('CHG') ? (
                          <Box 
                            component="span"
                            title={inc.caused_by_display_value}
                            sx={{ cursor: 'help' }}
                          >
                            <Chip 
                              label="Yes" 
                              size="small" 
                              sx={{ 
                                fontWeight: 600, 
                                backgroundColor: '#ff9800 !important', 
                                color: 'white !important',
                                border: 'none'
                              }}
                            />
                          </Box>
                        ) : (
                          <Chip 
                            label="N/A" 
                            size="small" 
                            sx={{ 
                              backgroundColor: '#9e9e9e !important', 
                              color: 'white !important',
                              border: 'none'
                            }} 
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {incidentData.filter(inc => {
            const priorityMatch = inc.priority === '1' || inc.priority === '2';
            const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || 
              (p1p2ViewMode === 'impacted' 
                ? inc.portfolio_name === selectedPortfolio
                : inc.caused_by_portfolio === selectedPortfolio);
            const platformMatch = !selectedPlatform || selectedPlatform === 'all' || 
              (p1p2ViewMode === 'impacted'
                ? inc.platform_name === selectedPlatform
                : inc.caused_by_platform === selectedPlatform);
            return priorityMatch && portfolioMatch && platformMatch;
          }).length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="textSecondary">
                No P1/P2 incidents found for the selected filters.
              </Typography>
            </Box>
          )}
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={incidentData.filter(inc => {
              const priorityMatch = inc.priority === '1' || inc.priority === '2';
              const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || 
                (p1p2ViewMode === 'impacted' 
                  ? inc.portfolio_name === selectedPortfolio
                  : inc.caused_by_portfolio === selectedPortfolio);
              const platformMatch = !selectedPlatform || selectedPlatform === 'all' || 
                (p1p2ViewMode === 'impacted'
                  ? inc.platform_name === selectedPlatform
                  : inc.caused_by_platform === selectedPlatform);
              return priorityMatch && portfolioMatch && platformMatch;
            }).length}
            rowsPerPage={p1p2DialogRowsPerPage}
            page={p1p2DialogPage}
            onPageChange={(event, newPage) => setP1P2DialogPage(newPage)}
            onRowsPerPageChange={(event) => {
              setP1P2DialogRowsPerPage(parseInt(event.target.value, 10));
              setP1P2DialogPage(0);
            }}
          />
        </DialogContent>
        
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between' }}>
          <Button 
            onClick={() => {
              const filteredData = incidentData.filter(inc => {
                const priorityMatch = inc.priority === '1' || inc.priority === '2';
                const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || 
                  (p1p2ViewMode === 'impacted' 
                    ? inc.portfolio_name === selectedPortfolio
                    : inc.caused_by_portfolio === selectedPortfolio);
                const platformMatch = !selectedPlatform || selectedPlatform === 'all' || 
                  (p1p2ViewMode === 'impacted'
                    ? inc.platform_name === selectedPlatform
                    : inc.caused_by_platform === selectedPlatform);
                return priorityMatch && portfolioMatch && platformMatch;
              }).map(inc => ({
                'Incident Number': inc.number,
                'Impacted Application': inc.cmdb_ci_display_value || 'N/A',
                'Impacted Portfolio': inc.portfolio_name || 'N/A',
                'Impacted Platform': inc.platform_name || 'N/A',
                'Caused By Portfolio': inc.caused_by_portfolio || 'N/A',
                'Caused By Platform': inc.caused_by_platform || 'N/A',
                'Caused By App': inc.caused_by_app || 'N/A',
                'Priority': `P${inc.priority}`,
                'Created Date': inc.sys_created_on_value ? new Date(inc.sys_created_on_value).toLocaleDateString() : 'N/A',
                'MTTD': formatOutageDuration(getMTTDMinutes(inc.number)),
                'MTTR': formatOutageDuration(getMTTRMinutes(inc.number)),
                'Captured by Alert': inc.contact_type_display_value === 'Alert' ? 'Yes' : 'No',
                'Caused by Change': inc.caused_by_display_value && inc.caused_by_display_value.startsWith('CHG') ? `Yes (${inc.caused_by_display_value})` : 'N/A'
              }));
              exportToCSV(filteredData, `P1P2_Incidents_${new Date().toISOString().split('T')[0]}.csv`, 
                ['Incident Number', 'Impacted Application', 'App ID', 'Impacted Portfolio', 'Impacted Platform', 
                 'Caused By Portfolio', 'Caused By Platform', 'Caused By App', 'Priority', 'Created Date', 
                 'MTTD', 'MTTR', 'Captured by Alert', 'Caused by Change']);
            }}
            variant="outlined"
            sx={{ 
              borderColor: '#1565c0',
              color: '#1565c0',
              '&:hover': { borderColor: '#0d47a1', backgroundColor: '#e3f2fd' }
            }}
          >
            Export to CSV
          </Button>
          <Button 
            onClick={() => setP1P2IncidentDialogOpen(false)}
            variant="contained"
            sx={{ 
              backgroundColor: '#1565c0',
              '&:hover': { backgroundColor: '#0d47a1' }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Outage Duration Dialog */}
      <Dialog
        open={outageDialogOpen}
        onClose={() => setOutageDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '70vh',
            maxHeight: '90vh',
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: '#1565c0',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Timeline sx={{ fontSize: '24px' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              P1/P2 Outage Duration Details
            </Typography>
          </Box>
          <IconButton 
            onClick={() => setOutageDialogOpen(false)}
            sx={{ 
              color: 'white',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
            }}
          >
            <Typography sx={{ fontSize: '20px' }}>×</Typography>
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0' }}>Incident Number</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0' }}>Impacted Application</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0' }}>App ID</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0' }}>Impacted Portfolio</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0' }}>Impacted Platform</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0' }}>Caused By Portfolio</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0' }}>Caused By Platform</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0' }}>Caused By App</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0' }}>Priority</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0' }}>Created Date</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0' }}>Outage Duration</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {incidentData
                  .filter(inc => {
                    const priorityMatch = inc.priority === '1' || inc.priority === '2';
                    const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || 
                      (p1p2ViewMode === 'impacted' 
                        ? inc.portfolio_name === selectedPortfolio
                        : inc.caused_by_portfolio === selectedPortfolio);
                    const platformMatch = !selectedPlatform || selectedPlatform === 'all' || 
                      (p1p2ViewMode === 'impacted'
                        ? inc.platform_name === selectedPlatform
                        : inc.caused_by_platform === selectedPlatform);
                    return priorityMatch && portfolioMatch && platformMatch;
                  })
                  .slice(outageDialogPage * outageDialogRowsPerPage, outageDialogPage * outageDialogRowsPerPage + outageDialogRowsPerPage)
                  .map((inc, index) => (
                    <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                      <TableCell>
                        <Link
                          href={`https://maersk.service-now.com/nav_to.do?uri=incident_list.do?sysparm_query=number=${inc.number}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ 
                            color: '#1565c0', 
                            textDecoration: 'none',
                            fontWeight: 600,
                            '&:hover': {
                              textDecoration: 'underline'
                            }
                          }}
                        >
                          {inc.number}
                        </Link>
                      </TableCell>
                      <TableCell>{inc.cmdb_ci_display_value || 'N/A'}</TableCell>
                      <TableCell>{inc.cmdb_ci_u_application_id || 'N/A'}</TableCell>
                      <TableCell>{inc.portfolio_name || 'N/A'}</TableCell>
                      <TableCell>{inc.platform_name || 'N/A'}</TableCell>
                      <TableCell>{inc.caused_by_portfolio || 'N/A'}</TableCell>
                      <TableCell>{inc.caused_by_platform || 'N/A'}</TableCell>
                      <TableCell>{inc.caused_by_app || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip 
                          label={`P${inc.priority}`} 
                          color="error" 
                          size="small" 
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        {inc.sys_created_on_value ? new Date(inc.sys_created_on_value).toLocaleDateString() : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <MuiTooltip title={inc.outage_number || 'No outage number'} arrow>
                          <Chip 
                            label={formatOutageDuration(getOutageDurationMinutes(inc))}
                            sx={{ 
                              backgroundColor: getOutageDurationMinutes(inc) > 0 ? '#9c27b0' : '#9e9e9e',
                              color: 'white',
                              fontWeight: 600,
                              cursor: 'help'
                            }}
                            size="small"
                          />
                        </MuiTooltip>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {incidentData.filter(inc => {
            const priorityMatch = inc.priority === '1' || inc.priority === '2';
            const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || 
              (p1p2ViewMode === 'impacted' 
                ? inc.portfolio_name === selectedPortfolio
                : inc.caused_by_portfolio === selectedPortfolio);
            const platformMatch = !selectedPlatform || selectedPlatform === 'all' || 
              (p1p2ViewMode === 'impacted'
                ? inc.platform_name === selectedPlatform
                : inc.caused_by_platform === selectedPlatform);
            return priorityMatch && portfolioMatch && platformMatch;
          }).length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="textSecondary">
                No P1/P2 incidents with outage duration found for the selected filters.
              </Typography>
            </Box>
          )}
        </DialogContent>
        
        <TablePagination
          component="div"
          count={incidentData.filter(inc => {
            const priorityMatch = inc.priority === '1' || inc.priority === '2';
            const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || 
              (p1p2ViewMode === 'impacted' 
                ? inc.portfolio_name === selectedPortfolio
                : inc.caused_by_portfolio === selectedPortfolio);
            const platformMatch = !selectedPlatform || selectedPlatform === 'all' || 
              (p1p2ViewMode === 'impacted'
                ? inc.platform_name === selectedPlatform
                : inc.caused_by_platform === selectedPlatform);
            return priorityMatch && portfolioMatch && platformMatch;
          }).length}
          page={outageDialogPage}
          onPageChange={(event, newPage) => setOutageDialogPage(newPage)}
          rowsPerPage={outageDialogRowsPerPage}
          onRowsPerPageChange={(event) => {
            setOutageDialogRowsPerPage(parseInt(event.target.value, 10));
            setOutageDialogPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
        
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0', justifyContent: 'space-between' }}>
          <Button 
            onClick={() => {
              const filteredData = incidentData.filter(inc => {
                const priorityMatch = inc.priority === '1' || inc.priority === '2';
                const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || 
                  (p1p2ViewMode === 'impacted' 
                    ? inc.portfolio_name === selectedPortfolio
                    : inc.caused_by_portfolio === selectedPortfolio);
                const platformMatch = !selectedPlatform || selectedPlatform === 'all' || 
                  (p1p2ViewMode === 'impacted'
                    ? inc.platform_name === selectedPlatform
                    : inc.caused_by_platform === selectedPlatform);
                return priorityMatch && portfolioMatch && platformMatch;
              });
              const csvData = filteredData.map(inc => ({
                'Incident Number': inc.number || 'N/A',
                'Impacted Application': inc.cmdb_ci_display_value || 'N/A',
                'Impacted Portfolio': inc.portfolio_name || 'N/A',
                'Impacted Platform': inc.platform_name || 'N/A',
                'Caused By Portfolio': inc.caused_by_portfolio || 'N/A',
                'Caused By Platform': inc.caused_by_platform || 'N/A',
                'Caused By App': inc.caused_by_app || 'N/A',
                'Priority': `P${inc.priority || 'N/A'}`,
                'Created Date': inc.opened_at ? new Date(inc.opened_at).toLocaleDateString() : 'N/A',
                'Outage Duration': formatOutageDuration(getOutageDurationMinutes(inc)),
                'Outage Number': inc.outage_number || 'N/A'
              }));
              exportToCSV(csvData, 'outage_duration_incidents.csv', Object.keys(csvData[0] || {}));
            }}
            variant="outlined"
            sx={{ 
              borderColor: '#1565c0',
              color: '#1565c0',
              '&:hover': { borderColor: '#0d47a1', backgroundColor: 'rgba(21, 101, 192, 0.04)' }
            }}
          >
            Export to CSV
          </Button>
          <Button 
            onClick={() => setOutageDialogOpen(false)}
            variant="contained"
            sx={{ 
              backgroundColor: '#1565c0',
              '&:hover': { backgroundColor: '#0d47a1' }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* P3/P4 Incident Details Dialog */}
      <Dialog
        open={p3p4IncidentDialogOpen}
        onClose={() => setP3P4IncidentDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '70vh',
            maxHeight: '90vh',
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: '#7cb342',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning sx={{ fontSize: '24px' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              P3/P4 Incidents - Medium & Low Priority
            </Typography>
          </Box>
          <IconButton 
            onClick={() => setP3P4IncidentDialogOpen(false)}
            sx={{ 
              color: 'white',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
            }}
          >
            <Typography sx={{ fontSize: '20px' }}>×</Typography>
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0' }}>Incident Number</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0' }}>Impacted Application</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0' }}>App ID</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0' }}>Impacted Portfolio</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0' }}>Impacted Platform</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0' }}>Priority</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0' }}>Resolved Date</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0' }}>MTTR</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0' }}>Captured by Alert</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0' }}>Caused by Change</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {p3p4IncidentData
                  .filter(inc => {
                    const priorityMatch = inc.priority === '3' || inc.priority === '4';
                    const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || inc.portfolio_name === selectedPortfolio;
                    const platformMatch = !selectedPlatform || selectedPlatform === 'all' || inc.platform_name === selectedPlatform;
                    return priorityMatch && portfolioMatch && platformMatch;
                  })
                  .sort((a, b) => {
                    // Sort by resolved date (most recent first)
                    if (!a.resolved_at) return 1;
                    if (!b.resolved_at) return -1;
                    return new Date(b.resolved_at).getTime() - new Date(a.resolved_at).getTime();
                  })
                  .slice(p3p4DialogPage * p3p4DialogRowsPerPage, p3p4DialogPage * p3p4DialogRowsPerPage + p3p4DialogRowsPerPage)
                  .map((inc, index) => (
                    <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                      <TableCell>
                        <Link
                          href={`https://maersk.service-now.com/nav_to.do?uri=incident_list.do?sysparm_query=number=${inc.number}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ 
                            color: '#1565c0', 
                            textDecoration: 'none',
                            fontWeight: 600,
                            '&:hover': {
                              textDecoration: 'underline'
                            }
                          }}
                        >
                          {inc.number}
                        </Link>
                      </TableCell>
                      <TableCell>{inc.cmdb_ci_display_value || 'N/A'}</TableCell>
                      <TableCell>{inc.cmdb_ci_u_application_id || 'N/A'}</TableCell>
                      <TableCell>{inc.portfolio_name || 'N/A'}</TableCell>
                      <TableCell>{inc.platform_name || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip 
                          label={`P${inc.priority}`} 
                          sx={{ 
                            fontWeight: 600,
                            backgroundColor: '#7cb342',
                            color: 'white'
                          }}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {inc.resolved_at ? new Date(inc.resolved_at).toLocaleDateString() : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} color="#7cb342">
                          {formatOutageDuration(getMTTRMinutes(inc.number))}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {inc.contact_type_display_value === 'Alert' ? (
                          <Chip 
                            label="Yes" 
                            size="small" 
                            sx={{ 
                              fontWeight: 600, 
                              backgroundColor: '#4caf50 !important', 
                              color: 'white !important',
                              border: 'none'
                            }} 
                          />
                        ) : (
                          <Chip 
                            label="No" 
                            size="small" 
                            sx={{ 
                              fontWeight: 600, 
                              backgroundColor: '#ff9800 !important', 
                              color: 'white !important',
                              border: 'none'
                            }} 
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {inc.caused_by_display_value && inc.caused_by_display_value.startsWith('CHG') ? (
                          <Box 
                            component="span"
                            title={inc.caused_by_display_value}
                            sx={{ cursor: 'help' }}
                          >
                            <Chip 
                              label="Yes" 
                              size="small" 
                              sx={{ 
                                fontWeight: 600, 
                                backgroundColor: '#ff9800 !important', 
                                color: 'white !important',
                                border: 'none'
                              }}
                            />
                          </Box>
                        ) : (
                          <Chip 
                            label="N/A" 
                            size="small" 
                            sx={{ 
                              backgroundColor: '#9e9e9e !important', 
                              color: 'white !important',
                              border: 'none'
                            }} 
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {p3p4IncidentData.filter(inc => {
            const priorityMatch = inc.priority === '3' || inc.priority === '4';
            const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || inc.portfolio_name === selectedPortfolio;
            const platformMatch = !selectedPlatform || selectedPlatform === 'all' || inc.platform_name === selectedPlatform;
            return priorityMatch && portfolioMatch && platformMatch;
          }).length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="textSecondary">
                No P3/P4 incidents found for the selected filters.
              </Typography>
            </Box>
          )}
        </DialogContent>
        
        <TablePagination
          component="div"
          count={p3p4IncidentData.filter(inc => {
            const priorityMatch = inc.priority === '3' || inc.priority === '4';
            const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || inc.portfolio_name === selectedPortfolio;
            const platformMatch = !selectedPlatform || selectedPlatform === 'all' || inc.platform_name === selectedPlatform;
            return priorityMatch && portfolioMatch && platformMatch;
          }).length}
          page={p3p4DialogPage}
          onPageChange={(event, newPage) => setP3P4DialogPage(newPage)}
          rowsPerPage={p3p4DialogRowsPerPage}
          onRowsPerPageChange={(event) => {
            setP3P4DialogRowsPerPage(parseInt(event.target.value, 10));
            setP3P4DialogPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
        
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0', justifyContent: 'space-between' }}>
          <Button 
            onClick={() => {
              const filteredData = p3p4IncidentData.filter(inc => {
                const priorityMatch = inc.priority === '3' || inc.priority === '4';
                const portfolioMatch = !selectedPortfolio || selectedPortfolio === 'all' || inc.portfolio_name === selectedPortfolio;
                const platformMatch = !selectedPlatform || selectedPlatform === 'all' || inc.platform_name === selectedPlatform;
                return priorityMatch && portfolioMatch && platformMatch;
              });
              const csvData = filteredData.map(inc => ({
                'Incident Number': inc.number || 'N/A',
                'Application': inc.cmdb_ci_display_value || 'N/A',
                'Portfolio': inc.portfolio_name || 'N/A',
                'Platform': inc.platform_name || 'N/A',
                'Priority': `P${inc.priority || 'N/A'}`,
                'Created Date': inc.sys_created_on_value ? new Date(inc.sys_created_on_value).toLocaleDateString() : 'N/A',
                'MTTR': formatOutageDuration(getMTTRMinutes(inc.number)),
                'Captured by Alert': inc.contact_type_display_value === 'Alert' ? 'Yes' : 'No',
                'Caused by Change': (inc.caused_by_display_value && inc.caused_by_display_value.startsWith('CHG')) ? 'Yes' : 'No'
              }));
              exportToCSV(csvData, 'p3p4_incidents.csv', Object.keys(csvData[0] || {}));
            }}
            variant="outlined"
            sx={{ 
              borderColor: '#7cb342',
              color: '#7cb342',
              '&:hover': { borderColor: '#689f38', backgroundColor: 'rgba(124, 179, 66, 0.04)' }
            }}
          >
            Export to CSV
          </Button>
          <Button 
            onClick={() => setP3P4IncidentDialogOpen(false)}
            variant="contained"
            sx={{ 
              backgroundColor: '#7cb342',
              '&:hover': { backgroundColor: '#689f38' }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share Link Snackbar */}
      <Snackbar
        open={shareLinkCopied}
        autoHideDuration={3000}
        onClose={() => setShareLinkCopied(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message="Share link copied to clipboard!"
        sx={{
          '& .MuiSnackbarContent-root': {
            backgroundColor: '#4caf50',
            color: 'white',
            fontWeight: 500,
          }
        }}
      />

      {/* Tag Success Notification */}
      <Snackbar
        open={!!tagSuccessMessage}
        autoHideDuration={3000}
        onClose={() => setTagSuccessMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        message={tagSuccessMessage}
        sx={{
          '& .MuiSnackbarContent-root': {
            backgroundColor: 'rgba(76, 175, 80, 0.8)',
            color: 'white',
            fontWeight: 500,
          }
        }}
      />

    </Box>
  );
};

export default PortfolioHealthTracker;